import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import SearchIcon from '@mui/icons-material/Search';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { persistCatalogWorkspace, persistSelectedCatalogMerchant } from '../utils/catalogWorkspaceStorage';
import { useAuth } from '../auth/AuthContext';
import PageMenuButton from '../components/PageMenuButton';
import {
  findFlatOption,
  getItemExternalCode,
  getProductExternalCode,
  requireConfirmedItemExternalCode,
  requireConfirmedOptionExternalCode,
  requireEffectiveContextCodes,
  requireItemExternalCodeReadback,
  requireItemProductIdentity,
  requireOptionExternalCodeReadback,
} from './catalogExternalCodeUi';

const API_URL = getApiUrl();
const LEGACY_CREDENTIALS_STORAGE_KEY = 'brutos-catalog-homologation-credentials';
const LEGACY_WORKSPACE_STORAGE_KEY = 'brutos-catalog-homologation-workspace';
const WORKSPACE_STORAGE_PREFIX = 'brutos-catalog-workspace';
const SELECTED_MERCHANT_STORAGE_PREFIX = 'brutos-catalog-selected-merchant';

const defaultCategory = {
  name: 'Nova categoria',
  status: 'AVAILABLE',
  template: 'DEFAULT',
  sequence: 0,
};

const emptyOfferForm = {
  step: 0,
  productType: 'prepared',
  name: '',
  description: '',
  price: '',
  originalPrice: '',
  externalCode: '',
  imagePath: '',
  stockEnabled: true,
  stockControlEnabled: false,
  inventoryAmount: '',
  highlightOpen: false,
  dietaryRestrictions: [],
  beverageTags: [],
  serving: 'SERVES_1',
  quantityValue: '',
  quantityUnit: 'g',
  availabilityMode: 'always',
  shifts: [],
  categoryMode: 'existing',
  categoryId: '',
  categoryName: '',
  groups: [],
};

const emptyStockProductForm = {
  name: '',
  description: '',
  price: '',
  originalPrice: '',
  externalCode: '',
  status: 'AVAILABLE',
  imagePath: '',
  stockControlEnabled: false,
  inventoryAmount: '',
  highlightOpen: false,
  dietaryRestrictions: [],
  beverageTags: [],
  serving: 'SERVES_1',
  quantityValue: '',
  quantityUnit: 'g',
  availabilityMode: 'always',
  shifts: [],
};

const emptyProductPromotionDialog = {
  open: false,
  product: null,
  promotionalPrice: '',
  discountPercent: '',
  basePrice: 0,
};

const emptyComboForm = {
  step: 0,
  name: '',
  description: '',
  price: '',
  originalPrice: '',
  externalCode: '',
  imagePath: '',
  groupName: 'Escolha os itens do combo',
  min: 1,
  max: 1,
  priceMode: 'combo',
  availabilityMode: 'always',
  shifts: [],
};

const emptyComboDrawer = {
  open: false,
  mode: 'products',
  groupKind: 'main',
  groupDraftId: '',
  step: 'select',
  search: '',
  selectedIds: [],
  groupName: '',
  max: 1,
};

const comboWeekDays = [
  { key: 'sunday', label: 'D', name: 'Domingo' },
  { key: 'monday', label: 'S', name: 'Segunda-feira' },
  { key: 'tuesday', label: 'T', name: 'Terca-feira' },
  { key: 'wednesday', label: 'Q', name: 'Quarta-feira' },
  { key: 'thursday', label: 'Q', name: 'Quinta-feira' },
  { key: 'friday', label: 'S', name: 'Sexta-feira' },
  { key: 'saturday', label: 'S', name: 'Sabado' },
];

const preparedProductTypes = [
  {
    value: 'prepared',
    label: 'Preparado',
    description: 'Produtos produzidos pela sua loja, como marmitas, bolos, lanches e etc.',
    icon: <Inventory2OutlinedIcon />,
  },
  {
    value: 'pizza',
    label: 'Pizza',
    description: 'Defina tamanho, tipos de massa, borda e sabores.',
    icon: <RestaurantMenuRoundedIcon />,
  },
];

const dietaryRestrictionOptions = [
  { value: 'VEGAN', label: 'Vegano' },
  { value: 'VEGETARIAN', label: 'Vegetariano' },
  { value: 'ORGANIC', label: 'Organico' },
  { value: 'SUGAR_FREE', label: 'Sem acucar' },
  { value: 'LACTOSE_FREE', label: 'Sem lactose' },
  { value: 'GLUTEN_FREE', label: 'Sem gluten' },
];

const beverageTagOptions = [
  { value: 'COLD', label: 'Bebida gelada' },
  { value: 'ALCOHOLIC_DRINK', label: 'Bebida alcoolica' },
  { value: 'NATURAL', label: 'Natural' },
];

const servingOptions = [
  { value: 'SERVES_1', label: '1 pessoa' },
  { value: 'SERVES_2', label: '2 pessoas' },
  { value: 'SERVES_3', label: '3 pessoas' },
  { value: 'SERVES_4', label: '4 pessoas' },
  { value: 'NOT_APPLICABLE', label: 'Nao se aplica' },
];

const quantityUnitOptions = ['g', 'kg', 'ml', 'l', 'un'];

const createComboScheduleDrafts = (shifts = []) => {
  const drafts = comboWeekDays.map((day) => ({
    ...day,
    enabled: false,
    startTime: '09:00',
    endTime: '23:59',
  }));
  toArray(shifts).forEach((shift) => {
    comboWeekDays.forEach((day, index) => {
      if (!shift?.[day.key]) return;
      drafts[index] = {
        ...drafts[index],
        enabled: true,
        startTime: cleanText(shift.startTime) || drafts[index].startTime,
        endTime: cleanText(shift.endTime) || drafts[index].endTime,
      };
    });
  });
  return drafts;
};

const emptyNewComplementForm = {
  name: '',
  description: '',
  price: '',
  externalCode: '',
  imagePath: '',
};

const emptyProductGroupForm = {
  groupName: '',
  groupExternalCode: '',
  min: '0',
  max: '1',
  optionName: '',
  optionDescription: '',
  optionPrice: '',
  optionExternalCode: '',
  optionImagePath: '',
  options: [],
  activeOptionIndex: 0,
};

const stockProductGridColumns = {
  xs: '1fr',
  lg: 'minmax(0, 1.55fr) minmax(160px, .62fr) minmax(220px, .9fr) minmax(112px, 148px)',
};

const catalogPageSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  px: { xs: 1.5, sm: 2 },
  pt: 1.5,
  pb: 4,
  overflowX: 'hidden',
  '& .MuiPaper-root, & .MuiGrid-container, & .MuiGrid-item': {
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiTableContainer-root': {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
  },
  '& .MuiFormControl-root, & .MuiTextField-root, & .MuiInputBase-root, & .MuiButton-root, & .MuiChip-root': {
    maxWidth: '100%',
  },
  '& .MuiInputBase-root': {
    minWidth: 0,
  },
  '& .MuiChip-label': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const catalogScrollableTabsSx = {
  minWidth: 0,
  maxWidth: '100%',
  '& .MuiTabs-scroller': {
    overflowX: 'auto !important',
  },
  '& .MuiTabs-flexContainer': {
    width: 'max-content',
  },
  '& .MuiTab-root': {
    flexShrink: 0,
    maxWidth: { xs: 220, sm: 'none' },
  },
};

const createUuid = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    const next = char === 'x' ? value : ((value & 0x3) | 0x8);
    return next.toString(16);
  });
};

const createProductGroupOptionDraft = (overrides = {}) => ({
  draftId: createUuid(),
  name: '',
  description: '',
  price: '',
  externalCode: '',
  imagePath: '',
  ...overrides,
});

const createEmptyProductGroupForm = () => ({
  ...emptyProductGroupForm,
  options: [createProductGroupOptionDraft()],
  activeOptionIndex: 0,
});

const pizzaCategoryTabs = [
  { value: 'details', label: 'Detalhes' },
  { value: 'sizes', label: 'Tamanho' },
  { value: 'doughs', label: 'Massa' },
  { value: 'edges', label: 'Borda' },
  { value: 'availability', label: 'Disponibilidade' },
];

const pizzaTimeOptions = Array.from({ length: 48 }, (_, index) => {
  const hour = String(Math.floor(index / 2)).padStart(2, '0');
  const minutes = index % 2 ? '30' : '00';
  return `${hour}:${minutes}`;
});

const createPizzaSlot = (source = {}) => ({
  id: source.id || createUuid(),
  startTime: cleanText(source.startTime) || '18:00',
  endTime: cleanText(source.endTime) || '23:59',
});

const createPizzaAvailabilityDrafts = (shifts = []) => {
  const drafts = new Map(comboWeekDays.map((day) => [
    day.key,
    {
      ...day,
      enabled: false,
      slots: [],
    },
  ]));

  toArray(shifts).forEach((shift) => {
    comboWeekDays.forEach((day) => {
      if (!shift?.[day.key]) return;
      const draft = drafts.get(day.key);
      draft.enabled = true;
      draft.slots.push(createPizzaSlot(shift));
    });
  });

  return comboWeekDays.map((day) => {
    const draft = drafts.get(day.key);
    return {
      ...draft,
      slots: draft.slots.length ? draft.slots : [createPizzaSlot()],
    };
  });
};

const createPizzaSizeDraft = (source = {}) => ({
  id: source.id || createUuid(),
  name: firstValue(source.name, ''),
  slices: firstValue(source.slices, ''),
  maxFlavors: Number(firstValue(source.maxFlavors, 1)) || 1,
  externalCode: firstValue(source.externalCode, ''),
  imagePath: firstValue(source.imagePath, ''),
  status: firstValue(source.status, 'AVAILABLE'),
});

const createPizzaOptionDraft = (source = {}) => ({
  id: source.id || createUuid(),
  name: firstValue(source.name, ''),
  price: firstValue(source.price, '0,00'),
  externalCode: firstValue(source.externalCode, '000'),
  status: firstValue(source.status, 'AVAILABLE'),
});

const createEmptyPizzaCategoryForm = () => ({
  tab: 'details',
  name: '',
  sizes: [
    createPizzaSizeDraft({ name: 'Pequena', slices: '1', maxFlavors: 1 }),
    createPizzaSizeDraft({ name: 'Media', slices: '6', maxFlavors: 2 }),
    createPizzaSizeDraft({ name: 'Grande', slices: '8', maxFlavors: 3 }),
  ],
  doughs: [
    createPizzaOptionDraft({ name: 'Tradicional' }),
  ],
  edges: [
    createPizzaOptionDraft({ name: 'Tradicional' }),
  ],
  availabilityMode: 'always',
  availabilityDrafts: createPizzaAvailabilityDrafts(),
});

const safeJsonParse = (value) => {
  try {
    return { ok: true, value: JSON.parse(value) };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

const stringifyJson = (value) => JSON.stringify(value, null, 2);

const readStoredJson = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const buildWorkspaceStorageKey = (companyId, merchantId) => (
  `${WORKSPACE_STORAGE_PREFIX}:${cleanText(companyId) || 'sem-empresa'}:${cleanText(merchantId) || 'sem-loja'}`
);

const buildSelectedMerchantStorageKey = (companyId) => (
  `${SELECTED_MERCHANT_STORAGE_PREFIX}:${cleanText(companyId) || 'sem-empresa'}`
);

const findFirstCatalogId = (data) => {
  const list = Array.isArray(data) ? data : [];
  const first = list[0] || {};
  return first.catalogId || first.id || first.groupId || '';
};

const findFirstCategoryId = (data) => {
  const list = Array.isArray(data) ? data : [];
  return list[0]?.id || '';
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const cleanText = (value) => String(value ?? '').trim();

const firstValue = (...values) => values.find((value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
});

const toArray = (value) => (Array.isArray(value) ? value : []);

const parseMoneyNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value && typeof value === 'object') {
    return parseMoneyNumber(firstValue(value.value, value.currentValue, value.amount, value.originalValue));
  }
  const normalized = String(value)
    .replace(/\s/g, '')
    .replace(/^R\$/i, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeCatalogContext = (value) => cleanText(value).toUpperCase();

const normalizePriceSource = (source, fallbackContext = '') => {
  if (source === null || source === undefined || source === '') return null;
  if (typeof source !== 'object') {
    const value = parseMoneyNumber(source);
    return value === null ? null : { value, originalValue: value, catalogContext: normalizeCatalogContext(fallbackContext) };
  }

  const nestedPrice = firstValue(source.price, source.itemPrice, source.unitPrice);
  const value = parseMoneyNumber(firstValue(
    source.value,
    source.currentValue,
    source.amount,
    nestedPrice && nestedPrice !== source ? nestedPrice.value : undefined,
    nestedPrice && nestedPrice !== source ? nestedPrice.currentValue : undefined,
    nestedPrice && nestedPrice !== source ? nestedPrice.amount : undefined
  ));
  const originalValue = parseMoneyNumber(firstValue(
    source.originalValue,
    source.baseValue,
    source.listPrice,
    source.strikeThroughValue,
    nestedPrice && nestedPrice !== source ? nestedPrice.originalValue : undefined,
    value
  ));

  if (value === null && originalValue === null) return null;
  const resolvedValue = value ?? originalValue ?? 0;
  return {
    value: resolvedValue,
    originalValue: Math.max(originalValue ?? resolvedValue, resolvedValue),
    catalogContext: normalizeCatalogContext(firstValue(source.catalogContext, source.context, fallbackContext)),
  };
};

const collectCatalogPriceCandidates = (payload = {}, context = {}) => {
  const fallbackContext = normalizeCatalogContext(firstValue(
    context.catalogContext,
    payload.catalogContext,
    payload.context,
    payload.raw?.catalogContext,
    payload.raw?.context,
    ''
  ));
  const directSources = [
    payload.price,
    payload.itemPrice,
    payload.unitPrice,
    payload.priceValue,
    payload.value,
    payload.amount,
  ];
  const contextualSources = [
    ...toArray(payload.priceByCatalog),
    ...toArray(payload.itemPriceByCatalog),
    ...toArray(payload.pricesByCatalog),
    ...toArray(payload.contextModifiers).filter((modifier) => (
      modifier?.price !== undefined
      || modifier?.value !== undefined
      || modifier?.amount !== undefined
      || modifier?.originalValue !== undefined
    )),
    ...(Array.isArray(payload.contextModifier)
      ? payload.contextModifier
      : payload.contextModifier
        ? [payload.contextModifier]
        : []).filter((modifier) => (
      modifier?.price !== undefined
      || modifier?.value !== undefined
      || modifier?.amount !== undefined
      || modifier?.originalValue !== undefined
    )),
  ];

  return [
    ...contextualSources.map((source) => normalizePriceSource(source, fallbackContext)),
    ...directSources.map((source) => normalizePriceSource(source, fallbackContext)),
  ].filter(Boolean);
};

const resolveCatalogPrice = (payload = {}, context = {}) => {
  const candidates = collectCatalogPriceCandidates(payload, context);
  const targetContext = normalizeCatalogContext(firstValue(
    context.catalogContext,
    payload.catalogContext,
    payload.context,
    payload.raw?.catalogContext,
    payload.raw?.context,
    ''
  ));
  const hasRealPrice = (candidate) => Number(candidate?.value || 0) > 0 || Number(candidate?.originalValue || 0) > 0;
  const matchesContext = (candidate) => targetContext && normalizeCatalogContext(candidate.catalogContext) === targetContext;
  const selected = candidates.find((candidate) => matchesContext(candidate) && hasRealPrice(candidate))
    || candidates.find(hasRealPrice)
    || candidates.find((candidate) => matchesContext(candidate))
    || candidates[0]
    || { value: 0, originalValue: 0, catalogContext: targetContext || 'DEFAULT' };
  const value = Number(selected.value || 0);
  const originalValue = Math.max(Number(selected.originalValue ?? value), value);
  return {
    value,
    originalValue,
    catalogContext: normalizeCatalogContext(selected.catalogContext || targetContext || 'DEFAULT') || 'DEFAULT',
  };
};

const extractArray = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const getMerchantId = (merchant = {}) => cleanText(firstValue(merchant.id, merchant.merchantId));

const getMerchantName = (merchant = {}) => cleanText(firstValue(
  merchant.displayName,
  merchant.name,
  merchant.tradingName,
  merchant.corporateName,
  merchant.merchantName,
  getMerchantId(merchant)
));

const normalizeMerchantList = (payload) => extractArray(payload, ['merchants', 'data', 'items'])
  .map((merchant) => {
    const id = getMerchantId(merchant);
    return id ? {
      ...merchant,
      id,
      merchantId: id,
      displayName: getMerchantName({ ...merchant, id }),
    } : null;
  })
  .filter(Boolean);

const firstFiniteNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return 0;
};

const normalizeCatalogStatus = (value, fallback = 'AVAILABLE') => {
  const normalized = cleanText(value).toUpperCase();
  return normalized || fallback;
};

const getNextCatalogStatus = (value) => (
  normalizeCatalogStatus(value) === 'AVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE'
);

const getStockPlural = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const getCatalogItemType = (entry = {}) => cleanText(firstValue(entry.type, entry.itemType, entry.raw?.type, entry.raw?.item?.type, 'DEFAULT')).toUpperCase();

const isCatalogCombo = (entry = {}) => getCatalogItemType(entry) === 'COMBO_V2';

const isPausedCatalogStatus = (status) => normalizeCatalogStatus(status, '') === 'UNAVAILABLE';

const formatAvailabilityList = (values = []) => {
  const unique = [...new Set(values.map(cleanText).filter(Boolean))];
  if (unique.length <= 2) return unique.join(', ');
  return `${unique.slice(0, 2).join(', ')} + ${unique.length - 2}`;
};

const buildIfoodImageUrl = (path) => {
  const value = cleanText(path);
  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) return value;

  const normalizedPath = value.replace(/^\/+/, '');
  const transformMatch = normalizedPath.match(/^(t_[^/]+)\/(.*)$/);
  const transform = transformMatch ? transformMatch[1] : 't_medium';
  let remainder = transformMatch ? transformMatch[2] : normalizedPath;
  const hasFolderPrefix = remainder.startsWith('pratos/') || remainder.startsWith('logos/') || remainder.startsWith('menus/');
  if (!hasFolderPrefix) remainder = `pratos/${remainder}`;
  return `https://static-images.ifood.com.br/image/upload/${transform}/${remainder}`;
};

const normalizeIfoodImagePathForPayload = (path) => {
  let value = cleanText(path);
  if (!value || value.startsWith('data:')) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) {
    try {
      const parsed = new URL(value);
      const marker = '/image/upload/';
      const markerIndex = parsed.pathname.indexOf(marker);
      value = markerIndex >= 0 ? parsed.pathname.slice(markerIndex + marker.length) : parsed.pathname;
    } catch {
      return '';
    }
  }
  return value
    .replace(/^\/+/, '')
    .replace(/^t_[^/]+\//, '')
    .replace(/^(pratos|logos|menus)\//, '');
};

const withCatalogOptionalFields = (base = {}, optional = {}) => {
  const next = { ...base };
  Object.entries(optional).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === 'string' && !cleanText(value)) return;
    next[key] = value;
  });
  return next;
};

const formatMoneyInputValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(numeric)) return cleanText(value);
  return numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hasCatalogInventoryControl = (product = {}) => (
  product.stock !== null
  && product.stock !== undefined
  && String(product.stock).trim() !== ''
);

const extractCatalogQuantityValue = (quantity = {}) => cleanText(firstValue(
  quantity.value,
  quantity.amount,
  quantity.quantity,
  ''
));

const extractCatalogQuantityUnit = (quantity = {}) => cleanText(firstValue(
  quantity.unit,
  quantity.unitType,
  quantity.measure,
  'g'
));

const buildCatalogQuantityPayload = (value, unit) => {
  const numeric = Number(String(value || '').replace(',', '.'));
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return {
    value: numeric,
    unit: cleanText(unit) || 'g',
  };
};

const CATALOG_PRODUCT_UPDATE_FIELDS = [
  'name',
  'shifts',
  'serving',
  'description',
  'additionalInformation',
  'externalCode',
  'image',
  'imagePath',
  'dietaryRestrictions',
  'ean',
  'weight',
  'multipleImages',
];

const buildCatalogProductUpdatePayload = (source = {}) => CATALOG_PRODUCT_UPDATE_FIELDS.reduce((payload, field) => {
  if (Object.prototype.hasOwnProperty.call(source, field) && source[field] !== undefined) {
    payload[field] = source[field];
  }
  return payload;
}, {});

const createStockProductForm = (product = {}) => {
  const rawProduct = product.raw?.product || {};
  const itemRaw = product.raw || {};
  const shifts = toArray(itemRaw.shifts);
  const price = firstValue(product.price, itemRaw.price?.value, '');
  const originalPrice = firstValue(product.originalPrice, itemRaw.price?.originalValue, price, '');
  const imagePath = firstValue(product.imagePath, rawProduct.imagePath, rawProduct.image, '');
  const quantity = rawProduct.weight || rawProduct.quantity || {};

  return {
    ...emptyStockProductForm,
    name: cleanText(firstValue(product.name, rawProduct.name, '')),
    description: cleanText(firstValue(product.description, rawProduct.description, rawProduct.additionalInformation, '')),
    price: formatMoneyInputValue(price),
    originalPrice: formatMoneyInputValue(originalPrice),
    externalCode: getItemExternalCode(product),
    status: normalizeCatalogStatus(product.status),
    imagePath: cleanText(imagePath),
    stockControlEnabled: hasCatalogInventoryControl(product),
    inventoryAmount: hasCatalogInventoryControl(product) ? cleanText(product.stock) : '',
    dietaryRestrictions: toArray(rawProduct.dietaryRestrictions),
    serving: cleanText(rawProduct.serving) || 'SERVES_1',
    quantityValue: extractCatalogQuantityValue(quantity),
    quantityUnit: extractCatalogQuantityUnit(quantity),
    availabilityMode: shifts.length ? 'scheduled' : 'always',
    shifts,
  };
};

const getCatalogId = (catalog = {}) => cleanText(firstValue(catalog.catalogId, catalog.id, catalog.groupId, catalog.context));
const getCategoryId = (category = {}) => cleanText(firstValue(category.id, category.categoryId));
const getItemId = (item = {}) => cleanText(firstValue(item.id, item.itemId));
const getOptionId = (option = {}) => cleanText(firstValue(option.id, option.optionId));

const extractImagePath = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data !== 'object') return '';

  const direct = firstValue(
    data.imagePath,
    data.path,
    data.url,
    data.fileName,
    data.logoUrl,
    data.imageUrl
  );
  if (typeof direct === 'string' && direct.trim()) return direct;

  const nested = firstValue(data.image, data.product, data.productData, data.images?.[0], data.imageUrls?.[0]);
  if (nested && nested !== data) return extractImagePath(nested);
  return '';
};

const findProductById = (products, productId) => toArray(products).find((entry) => (
  cleanText(firstValue(entry.id, entry.productId)) === cleanText(productId)
)) || {};

const resolveCatalogProductImage = (...sources) => {
  for (const source of sources) {
    const path = cleanText(extractImagePath(source));
    if (path) return path;
  }
  return '';
};

const extractEvidenceItem = (categories) => {
  const list = Array.isArray(categories) ? categories : [];
  for (const category of list) {
    const item = Array.isArray(category.items) ? category.items[0] : null;
    if (item) {
      return {
        categoryName: category.name || '',
        name: item.name || item.itemName || '',
        description: item.description || item.itemDescription || '',
        price: resolveCatalogPrice(item, { catalogContext: category.catalogContext || category.context }).value,
        imagePath: item.imagePath || item.image || '',
      };
    }
  }
  return null;
};

const emptyVisualCatalog = {
  catalogs: [],
  categories: [],
  items: [],
  optionGroups: [],
  options: [],
  lastImportedAt: null,
  source: '',
};

const dedupeById = (items, fallbackPrefix) => {
  const map = new Map();
  toArray(items).forEach((item, index) => {
    const id = cleanText(item?.id) || `${fallbackPrefix}-${index}`;
    const scope = [
      cleanText(item?.merchantId),
      cleanText(item?.catalogId),
      id,
    ].filter(Boolean).join('::');
    map.set(scope || id, { ...item, id });
  });
  return Array.from(map.values());
};

const dedupeByKey = (items, fallbackPrefix, getKey) => {
  const map = new Map();
  toArray(items).forEach((item, index) => {
    const id = cleanText(item?.id) || `${fallbackPrefix}-${index}`;
    const normalized = { ...item, id };
    const key = cleanText(getKey?.(normalized, index)) || id;
    map.set(key, normalized);
  });
  return Array.from(map.values());
};

const scopedCatalogKey = (...parts) => parts.map(cleanText).filter(Boolean).join('::');
const getVisualOptionGroupKey = (group = {}) => scopedCatalogKey(group.merchantId, group.catalogId, group.itemId, group.id) || cleanText(group.id);
const getVisualOptionKey = (option = {}) => (
  scopedCatalogKey(option.merchantId, option.catalogId, option.itemId, option.groupId, option.id)
  || scopedCatalogKey(option.merchantId, option.catalogId, option.itemId, option.id)
  || cleanText(option.id)
);
const isSameVisualGroup = (left = {}, right = {}) => (
  cleanText(left.id) === cleanText(right.id)
  && cleanText(left.itemId) === cleanText(right.itemId)
);
const getSharedOptionGroupKey = (group = {}) => cleanText(group.id) || getVisualOptionGroupKey(group);
const isSameVisualOption = (left = {}, right = {}) => (
  cleanText(left.id) === cleanText(right.id)
  && cleanText(left.itemId) === cleanText(right.itemId)
  && cleanText(left.groupId) === cleanText(right.groupId)
);
const getSharedComplementKey = (option = {}) => {
  const productId = cleanText(option.productId);
  const optionId = cleanText(option.id);
  if (productId) return `product:${productId}`;
  if (optionId) return `option:${optionId}`;
  return '';
};
const isSharedComplement = (left = {}, right = {}) => {
  const leftProductId = cleanText(left.productId);
  const rightProductId = cleanText(right.productId);
  if (leftProductId && rightProductId) return leftProductId === rightProductId;
  const leftOptionId = cleanText(left.id);
  const rightOptionId = cleanText(right.id);
  return Boolean(leftOptionId && rightOptionId && leftOptionId === rightOptionId);
};
const findVisualOptionGroup = (groups, key) => (
  toArray(groups).find((group) => getVisualOptionGroupKey(group) === key)
  || toArray(groups).find((group) => cleanText(group.id) === cleanText(key))
);

const countVisualEntities = (visualCatalog = emptyVisualCatalog) => ({
  catalogs: toArray(visualCatalog.catalogs).length,
  categories: toArray(visualCatalog.categories).length,
  items: toArray(visualCatalog.items).length,
  optionGroups: toArray(visualCatalog.optionGroups).length,
  options: toArray(visualCatalog.options).length,
});

const mergeVisualCatalog = (current = emptyVisualCatalog, incoming = {}) => ({
  catalogs: dedupeById([...(current.catalogs || []), ...(incoming.catalogs || [])], 'catalog'),
  categories: dedupeById([...(current.categories || []), ...(incoming.categories || [])], 'category'),
  items: dedupeById([...(current.items || []), ...(incoming.items || [])], 'item'),
  optionGroups: dedupeByKey([...(current.optionGroups || []), ...(incoming.optionGroups || [])], 'option-group', getVisualOptionGroupKey),
  options: dedupeByKey([...(current.options || []), ...(incoming.options || [])], 'option', getVisualOptionKey),
  lastImportedAt: incoming.lastImportedAt || new Date().toISOString(),
  source: incoming.source || current.source || '',
});

const normalizeCatalogsForVisual = (data, context = {}) => extractArray(data, ['catalogs', 'data', 'items']).map((catalog, index) => ({
  id: getCatalogId(catalog) || `catalog-${index + 1}`,
  merchantId: cleanText(context.merchantId),
  merchantName: cleanText(context.merchantName),
  name: cleanText(firstValue(catalog.name, catalog.catalogName, catalog.groupName, catalog.context, `Catalogo ${index + 1}`)),
  status: cleanText(firstValue(catalog.status, catalog.availability, catalog.type, '')),
  context: cleanText(firstValue(catalog.context, catalog.catalogContext, catalog.type, '')),
  raw: catalog,
}));

const findCatalogContext = (catalogs = [], catalogId = '') => {
  const selected = toArray(catalogs).find((catalog) => cleanText(catalog.id) === cleanText(catalogId))
    || toArray(catalogs).find((catalog) => cleanText(firstValue(catalog.catalogId, catalog.raw?.id)) === cleanText(catalogId))
    || {};
  return cleanText(firstValue(selected.context, selected.catalogContext, selected.raw?.context, selected.raw?.catalogContext, ''));
};

const normalizeVisualItem = (item = {}, context = {}) => {
  const product = item.product || item.productData || {};
  const productId = cleanText(firstValue(item.productId, product.id, item.itemProductId));
  const name = cleanText(firstValue(item.name, item.itemName, product.name, item.title, 'Item sem nome'));
  const description = cleanText(firstValue(
    item.description,
    item.itemDescription,
    product.description,
    product.additionalInformation,
    item.additionalInformation,
    ''
  ));
  const imagePath = resolveCatalogProductImage(item, product);
  const priceInfo = resolveCatalogPrice(item, context);

  return {
    id: getItemId(item) || cleanText(firstValue(item.itemId, item.externalCode, item.itemExternalCode, name)),
    merchantId: cleanText(context.merchantId),
    merchantName: cleanText(context.merchantName),
    name,
    description,
    type: cleanText(firstValue(item.type, item.itemType, 'DEFAULT')),
    status: cleanText(firstValue(item.status, item.itemStatus, item.availability, '')),
    price: priceInfo.value,
    originalPrice: priceInfo.originalValue,
    priceCatalogContext: priceInfo.catalogContext,
    catalogContext: cleanText(firstValue(context.catalogContext, item.catalogContext, item.context, priceInfo.catalogContext, '')),
    externalCode: getItemExternalCode(item),
    productExternalCode: cleanText(firstValue(product.externalCode, product.productExternalCode, '')),
    productId,
    categoryId: cleanText(firstValue(item.categoryId, context.categoryId, '')),
    categoryName: cleanText(firstValue(item.categoryName, context.categoryName, '')),
    catalogId: cleanText(context.catalogId),
    imagePath,
    imageUrl: buildIfoodImageUrl(imagePath),
    raw: item,
  };
};

const normalizeEmbeddedOptionGroups = (item = {}, visualItem = {}) => {
  const groups = extractArray(item, ['itemOptionGroups', 'optionGroups', 'groups']);
  return groups.map((group, index) => {
    const id = cleanText(firstValue(group.id, group.optionGroupId, group.groupId, `${visualItem.id}-group-${index + 1}`));
    return {
      id,
      merchantId: visualItem.merchantId || '',
      merchantName: visualItem.merchantName || '',
      catalogId: visualItem.catalogId || '',
      itemId: visualItem.id,
      name: cleanText(firstValue(group.name, group.groupName, group.title, `Grupo ${index + 1}`)),
      status: cleanText(firstValue(group.status, group.availability, '')),
      min: Number(firstValue(group.min, group.minimum, group.minPermitted, 0) || 0),
      max: Number(firstValue(group.max, group.maximum, group.maxPermitted, 0) || 0),
      externalCode: cleanText(firstValue(group.externalCode, group.groupExternalCode, '')),
      optionGroupType: cleanText(firstValue(group.optionGroupType, group.type, '')),
      optionIds: toArray(group.optionIds).map(cleanText).filter(Boolean),
      raw: group,
    };
  });
};

const normalizeEmbeddedOptions = (item = {}, groups = [], visualItem = {}) => groups.flatMap((group) => {
  const rawGroup = group.raw || {};
  const relatedProducts = extractArray(item, ['products', 'productList', 'relatedProducts']);
  return extractArray(rawGroup, ['options', 'items']).map((option, index) => {
    const optionProductId = cleanText(firstValue(option.productId, option.optionProductId, option.product?.id, option.productData?.id, ''));
    const optionProduct = findProductById(relatedProducts, optionProductId);
    const imagePath = resolveCatalogProductImage(option, option.product, option.productData, optionProduct);
    const priceInfo = resolveCatalogPrice(option, { catalogContext: visualItem.catalogContext || visualItem.priceCatalogContext });
    return {
      id: getOptionId(option) || cleanText(firstValue(option.externalCode, `${group.id}-option-${index + 1}`)),
      merchantId: visualItem.merchantId || '',
      merchantName: visualItem.merchantName || '',
      catalogId: visualItem.catalogId || '',
      itemId: visualItem.id,
      groupId: group.id,
      productId: optionProductId,
      name: cleanText(firstValue(option.name, option.optionName, option.product?.name, option.productData?.name, optionProduct.name, `Complemento ${index + 1}`)),
      description: cleanText(firstValue(
        option.description,
        option.optionDescription,
        option.product?.description,
        option.productData?.description,
        optionProduct.description,
        optionProduct.additionalInformation,
        ''
      )),
      status: cleanText(firstValue(option.status, option.availability, '')),
      price: priceInfo.value,
      originalPrice: priceInfo.originalValue,
      priceCatalogContext: priceInfo.catalogContext,
      catalogContext: visualItem.catalogContext || priceInfo.catalogContext,
      externalCode: getItemExternalCode(option),
      productExternalCode: cleanText(firstValue(option.product?.externalCode, option.productData?.externalCode, optionProduct.externalCode, '')),
      imagePath,
      imageUrl: buildIfoodImageUrl(imagePath),
      rawProduct: optionProduct,
      raw: option,
    };
  });
});

const normalizeVisualFromCategories = (data, context = {}) => {
  const categories = extractArray(data, ['categories', 'data', 'items']).map((category, index) => {
    const id = getCategoryId(category) || cleanText(firstValue(category.name, `category-${index + 1}`));
    const rawItems = extractArray(category, ['items', 'products']);
    return {
      id,
      merchantId: cleanText(context.merchantId),
      merchantName: cleanText(context.merchantName),
      catalogId: cleanText(firstValue(category.catalogId, context.catalogId, '')),
      catalogContext: cleanText(firstValue(category.catalogContext, category.context, context.catalogContext, '')),
      name: cleanText(firstValue(category.name, category.categoryName, `Categoria ${index + 1}`)),
      status: cleanText(firstValue(category.status, '')),
      template: cleanText(firstValue(category.template, category.categoryTemplate, '')),
      sequence: firstValue(category.sequence, category.index, index),
      itemCount: rawItems.length,
      raw: category,
    };
  });

  const items = [];
  const optionGroups = [];
  const options = [];

  extractArray(data, ['categories', 'data', 'items']).forEach((category) => {
    const categoryContext = {
      catalogId: cleanText(firstValue(category.catalogId, context.catalogId, '')),
      catalogContext: cleanText(firstValue(category.catalogContext, category.context, context.catalogContext, '')),
      merchantId: cleanText(context.merchantId),
      merchantName: cleanText(context.merchantName),
      categoryId: getCategoryId(category),
      categoryName: cleanText(firstValue(category.name, category.categoryName, '')),
    };
    extractArray(category, ['items', 'products']).forEach((item) => {
      const visualItem = normalizeVisualItem({ ...item, categoryId: categoryContext.categoryId, categoryName: categoryContext.categoryName }, categoryContext);
      const groups = normalizeEmbeddedOptionGroups(item, visualItem);
      items.push(visualItem);
      optionGroups.push(...groups);
      options.push(...normalizeEmbeddedOptions(item, groups, visualItem));
    });
  });

  return { categories, items, optionGroups, options };
};

const normalizeVisualFromItems = (data, context = {}) => {
  const items = [];
  const optionGroups = [];
  const options = [];

  extractArray(data, ['items', 'sellableItems', 'unsellableItems', 'data', 'products']).forEach((item) => {
    const visualItem = normalizeVisualItem(item, context);
    const groups = normalizeEmbeddedOptionGroups(item, visualItem);
    items.push(visualItem);
    optionGroups.push(...groups);
    options.push(...normalizeEmbeddedOptions(item, groups, visualItem));
  });

  return { items, optionGroups, options };
};

const normalizeVisualFromFlat = (data, context = {}) => {
  const root = data || {};
  const item = root.item || root;
  const products = extractArray(root, ['products']);
  const optionGroupsRaw = extractArray(root, ['optionGroups']);
  const optionsRaw = extractArray(root, ['options']);
  const product = products.find((entry) => cleanText(entry.id) === cleanText(item.productId)) || {};
  const productGroupRelationsById = new Map(toArray(product.optionGroups).map((relation) => [
    cleanText(relation.id),
    relation,
  ]));
  const visualItem = normalizeVisualItem({ ...item, product }, context);

  const optionGroups = optionGroupsRaw.map((group, index) => {
    const id = cleanText(firstValue(group.id, `${visualItem.id}-group-${index + 1}`));
    const relation = productGroupRelationsById.get(id) || {};
    return {
      id,
      merchantId: visualItem.merchantId || '',
      merchantName: visualItem.merchantName || '',
      catalogId: visualItem.catalogId || '',
      itemId: visualItem.id,
      name: cleanText(firstValue(group.name, `Grupo ${index + 1}`)),
      status: cleanText(firstValue(group.status, '')),
      min: firstFiniteNumber(group.min, group.minimum, relation.min, relation.minimum, 0),
      max: firstFiniteNumber(group.max, group.maximum, relation.max, relation.maximum, 0),
      externalCode: cleanText(firstValue(group.externalCode, '')),
      optionGroupType: cleanText(firstValue(group.optionGroupType, group.type, '')),
      optionIds: [...new Set([
        ...toArray(group.optionIds),
        ...toArray(relation.optionIds),
      ].map(cleanText).filter(Boolean))],
      associationType: cleanText(firstValue(group.associationType, relation.associationType, '')),
      relationIndex: firstFiniteNumber(relation.index, group.index, index),
      raw: { ...group, relation },
    };
  });

  const options = optionsRaw.map((option, index) => {
    const optionProduct = products.find((entry) => cleanText(entry.id) === cleanText(option.productId)) || {};
    const explicitGroupId = cleanText(firstValue(option.optionGroupId, option.groupId, option.parentOptionGroupId, option.customizationGroupId, ''));
    const group = optionGroups.find((candidate) => candidate.id === explicitGroupId)
      || optionGroups.find((candidate) => candidate.optionIds.includes(cleanText(option.id)))
      || optionGroups[0]
      || {};
    const imagePath = resolveCatalogProductImage(option, optionProduct);
    const priceInfo = resolveCatalogPrice(option, { catalogContext: visualItem.catalogContext || visualItem.priceCatalogContext });
    return {
      id: getOptionId(option) || cleanText(firstValue(option.externalCode, `${visualItem.id}-option-${index + 1}`)),
      merchantId: visualItem.merchantId || '',
      merchantName: visualItem.merchantName || '',
      catalogId: visualItem.catalogId || '',
      itemId: visualItem.id,
      groupId: group.id || '',
      productId: cleanText(firstValue(option.productId, optionProduct.id, '')),
      name: cleanText(firstValue(option.name, optionProduct.name, `Complemento ${index + 1}`)),
      description: cleanText(firstValue(option.description, optionProduct.description, optionProduct.additionalInformation, '')),
      status: cleanText(firstValue(option.status, '')),
      price: priceInfo.value,
      originalPrice: priceInfo.originalValue,
      priceCatalogContext: priceInfo.catalogContext,
      catalogContext: visualItem.catalogContext || priceInfo.catalogContext,
      externalCode: getItemExternalCode(option),
      productExternalCode: cleanText(firstValue(optionProduct.externalCode, optionProduct.productExternalCode, '')),
      imagePath,
      imageUrl: buildIfoodImageUrl(imagePath),
      rawProduct: optionProduct,
      raw: option,
    };
  });

  return {
    items: [{
      ...visualItem,
      firstOptionId: options[0]?.id || '',
      firstOptionProductId: options[0]?.productId || '',
      firstOptionExternalCode: options[0]?.externalCode || '',
    }],
    optionGroups,
    options,
  };
};

const buildDemoItemPayload = ({ categoryId = '', imagePath = '' } = {}) => {
  const stamp = Date.now();
  const itemId = createUuid();
  const itemProductId = createUuid();
  const optionGroupId = createUuid();
  const optionId = createUuid();
  const optionProductId = createUuid();
  const itemExternalCode = `catalog-hml-item-${stamp}`;
  const optionExternalCode = `catalog-hml-option-${stamp}`;

  return {
    refs: {
      itemId,
      itemProductId,
      optionGroupId,
      optionId,
      optionProductId,
      itemExternalCode,
      optionExternalCode,
    },
    payload: {
      item: {
        id: itemId,
        type: 'DEFAULT',
        categoryId,
        status: 'AVAILABLE',
        price: {
          value: 29.9,
          originalValue: 34.9,
        },
        externalCode: itemExternalCode,
        index: 0,
        productId: itemProductId,
        shifts: [
          {
            startTime: '00:00',
            endTime: '23:59',
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          },
        ],
        tags: null,
      },
      products: [
        {
          id: itemProductId,
          externalCode: `catalog-hml-product-${stamp}`,
          name: 'Produto Catalog',
          description: 'Pao, carne, queijo e salada',
          additionalInformation: 'Item temporario para operacao da API Catalog.',
          imagePath,
          ean: '',
          serving: 'SERVES_1',
          dietaryRestrictions: [],
          quantity: null,
          optionGroups: [
            {
              id: optionGroupId,
              min: 0,
              max: 1,
            },
          ],
        },
        {
          id: optionProductId,
          externalCode: `catalog-hml-option-product-${stamp}`,
          name: 'Refrigerante lata',
          description: '350 ml',
          additionalInformation: '',
          imagePath: '',
          ean: '',
          serving: 'SERVES_1',
          dietaryRestrictions: [],
          quantity: null,
          optionGroups: [],
        },
      ],
      optionGroups: [
        {
          id: optionGroupId,
          name: 'Escolha sua bebida',
          externalCode: `catalog-hml-group-${stamp}`,
          status: 'AVAILABLE',
          index: 0,
          optionGroupType: 'DEFAULT',
          optionIds: [optionId],
        },
      ],
      options: [
        {
          id: optionId,
          status: 'AVAILABLE',
          index: 0,
          productId: optionProductId,
          price: {
            value: 5,
            originalValue: 7,
          },
          contextModifiers: [],
          fractions: null,
          externalCode: optionExternalCode,
        },
      ],
    },
  };
};

const buildPatchPayloads = ({ itemId = '', optionId = '', itemExternalCode = '', optionExternalCode = '', itemProductId = '' } = {}) => ({
  itemPrice: stringifyJson({
    itemId,
    price: { value: 31.9, originalValue: 34.9 },
    priceByCatalog: [{ value: 31.9, originalValue: 34.9, catalogContext: 'DEFAULT' }],
  }),
  itemStatus: stringifyJson({
    itemId,
    status: 'AVAILABLE',
    statusByCatalog: [{ status: 'UNAVAILABLE', catalogContext: 'DEFAULT' }],
  }),
  itemExternalCode: stringifyJson({
    catalogItemId: itemId,
    productId: itemProductId,
    externalCode: `${itemExternalCode || 'catalog-hml-item'}-edit`,
  }),
  optionPrice: stringifyJson({
    optionId,
    parentCustomizationOptionId: null,
    price: { value: 6, originalValue: 8 },
    priceByCatalog: [{ value: 6, originalValue: 8, catalogContext: 'DEFAULT' }],
  }),
  optionStatus: stringifyJson({
    optionId,
    parentCustomizationOptionId: null,
    status: 'AVAILABLE',
    statusByCatalog: [{ status: 'UNAVAILABLE', catalogContext: 'DEFAULT' }],
  }),
  optionExternalCode: stringifyJson({
    catalogItemId: itemId,
    productId: itemProductId,
    optionId,
    parentCustomizationOptionId: null,
    externalCode: `${optionExternalCode || 'catalog-hml-option'}-edit`,
  }),
  batchPrice: stringifyJson([
    {
      externalCode: itemExternalCode,
      price: { value: 32.9, originalValue: 36.9 },
      resources: ['ITEM'],
    },
  ]),
  batchStatus: stringifyJson([
    {
      externalCode: itemExternalCode,
      status: 'AVAILABLE',
      resources: ['ITEM'],
    },
  ]),
  inventoryCreate: stringifyJson({
    productId: itemProductId,
    amount: 10,
  }),
  inventoryDelete: stringifyJson({
    productIds: itemProductId ? [itemProductId] : [],
  }),
});

const Field = ({ label, value, onChange, ...props }) => (
  <TextField
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    fullWidth
    size="small"
    {...props}
  />
);

const ActionStatusPanel = ({ result }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
          Status da ultima acao
        </Typography>
        <Chip
          size="small"
          label={result?.ok ? `HTTP ${result.status || 200}` : result?.error ? 'Erro' : 'Aguardando'}
          color={result?.ok ? 'success' : result?.error ? 'error' : 'default'}
        />
      </Stack>
      {result?.label ? (
        <Typography sx={{ fontWeight: 800 }}>{result.label}</Typography>
      ) : (
        <Typography color="text.secondary">Nenhuma chamada executada nesta sessao.</Typography>
      )}
      {result?.request?.method || result?.request?.path ? (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {result.request?.method || ''} {result.request?.path || ''}
        </Typography>
      ) : null}
      {result?.error ? (
        <Alert severity="error">{result.error}</Alert>
      ) : null}
      {result?.data?.id || result?.data?.name ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {result.data?.id ? <Chip size="small" variant="outlined" label={`id: ${result.data.id}`} /> : null}
          {result.data?.name ? <Chip size="small" variant="outlined" label={result.data.name} /> : null}
        </Stack>
      ) : null}
    </Stack>
  </Paper>
);

const MetricBox = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 130 }}>
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 900 }}>{value}</Typography>
  </Paper>
);

const EntityId = ({ label, value }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
    {label}: {value || '-'}
  </Typography>
);

const CatalogVisualPanel = ({
  visualCatalog,
  selectedItemId,
  onSelectCatalog,
  onSelectCategory,
  onSelectItem,
  onClear,
  title = 'Catalogo importado',
  compact = false,
}) => {
  const stats = countVisualEntities(visualCatalog);
  const items = toArray(visualCatalog.items);
  const categories = toArray(visualCatalog.categories);
  const optionGroups = toArray(visualCatalog.optionGroups);
  const options = toArray(visualCatalog.options);
  const activeItem = items.find((item) => item.id === selectedItemId) || items[0] || null;
  const groupsByItem = optionGroups.filter((group) => group.itemId === activeItem?.id);
  const optionsByGroup = (groupId) => {
    const scoped = options.filter((option) => option.groupId === groupId);
    if (scoped.length) return scoped;
    return options.filter((option) => option.itemId === activeItem?.id && !option.groupId);
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 850 }}>{title}</Typography>
              <Typography variant="body2" color="text.secondary">
                Dados carregados da loja iFood selecionada para operação do catálogo.
              </Typography>
            </Box>
            {onClear ? (
              <Button size="small" variant="outlined" color="inherit" startIcon={<DeleteOutlineRoundedIcon />} onClick={onClear}>
                Limpar visual
              </Button>
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <MetricBox label="Catalogos" value={stats.catalogs} />
            <MetricBox label="Categorias" value={stats.categories} />
            <MetricBox label="Produtos" value={stats.items} />
            <MetricBox label="Grupos" value={stats.optionGroups} />
            <MetricBox label="Complementos" value={stats.options} />
          </Stack>
          {visualCatalog.lastImportedAt ? (
            <Typography variant="caption" color="text.secondary">
              Ultima importacao visual: {new Date(visualCatalog.lastImportedAt).toLocaleString('pt-BR')}
              {visualCatalog.source ? ` | ${visualCatalog.source}` : ''}
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      {!compact ? (
        <>
      {toArray(visualCatalog.catalogs).length ? (
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Catalogos</Typography>
            <Grid container spacing={1.5}>
              {visualCatalog.catalogs.map((catalog) => (
                <Grid item xs={12} md={6} key={catalog.id}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 850 }} noWrap>{catalog.name}</Typography>
                          <EntityId label="catalogId" value={catalog.id} />
                        </Box>
                        {catalog.status ? <Chip size="small" label={catalog.status} /> : null}
                      </Stack>
                      <Button size="small" variant="outlined" onClick={() => onSelectCatalog?.(catalog)}>
                        Usar catalogId
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      {categories.length ? (
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Categorias</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell align="right">Itens</TableCell>
                    <TableCell align="right">Acao</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 800 }}>{category.name}</Typography>
                        <EntityId label="categoryId" value={category.id} />
                      </TableCell>
                      <TableCell>{category.status || '-'}</TableCell>
                      <TableCell>{category.template || '-'}</TableCell>
                      <TableCell align="right">{category.itemCount || items.filter((item) => item.categoryId === category.id).length}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => onSelectCategory?.(category)}>
                          Usar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      ) : null}

      {items.length ? (
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Produtos importados</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Preco</TableCell>
                    <TableCell align="right">Complementos</TableCell>
                    <TableCell align="right">Acao</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover selected={item.id === activeItem?.id}>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography sx={{ fontWeight: 850 }}>{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>{item.description || 'Sem descricao'}</Typography>
                        <EntityId label="itemId" value={item.id} />
                        <EntityId label="externalCode" value={item.externalCode} />
                      </TableCell>
                      <TableCell>{item.categoryName || categories.find((category) => category.id === item.categoryId)?.name || '-'}</TableCell>
                      <TableCell>{item.status || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                      <TableCell align="right">{options.filter((option) => option.itemId === item.id).length}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => onSelectItem?.(item)}>
                          Usar IDs
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      ) : null}

      {activeItem ? (
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={activeItem.imageUrl ? 8 : 12}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={activeItem.status || 'Sem status'} />
                  {activeItem.externalCode ? <Chip size="small" variant="outlined" label={activeItem.externalCode} /> : null}
                  {activeItem.productId ? <Chip size="small" variant="outlined" label={`productId: ${activeItem.productId}`} /> : null}
                </Stack>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{activeItem.name}</Typography>
                  <Typography color="text.secondary">{activeItem.description || 'Sem descricao'}</Typography>
                  <Typography sx={{ mt: 1, fontWeight: 900 }}>{formatCurrency(activeItem.price)}</Typography>
                  <EntityId label="itemId" value={activeItem.id} />
                  <EntityId label="categoryId" value={activeItem.categoryId} />
                </Box>
                {groupsByItem.length ? (
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Grupos e complementos</Typography>
                    {groupsByItem.map((group) => (
                      <Paper key={group.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                            <Box>
                              <Typography sx={{ fontWeight: 850 }}>{group.name}</Typography>
                              <EntityId label="optionGroupId" value={group.id} />
                            </Box>
                            <Chip size="small" label={`min ${group.min} / max ${group.max}`} />
                          </Stack>
                          {optionsByGroup(group.id).length ? (
                            <Grid container spacing={1}>
                              {optionsByGroup(group.id).map((option) => (
                                <Grid item xs={12} md={6} key={option.id}>
                                  <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2, bgcolor: '#fafafa' }}>
                                    <Typography sx={{ fontWeight: 800 }}>{option.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">{option.description || 'Sem descricao'}</Typography>
                                    <Typography sx={{ fontWeight: 850 }}>{formatCurrency(option.price)}</Typography>
                                    <EntityId label="optionId" value={option.id} />
                                    <EntityId label="externalCode" value={option.externalCode} />
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <Alert severity="info">Grupo sem complementos importados.</Alert>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">Produto sem grupos de complementos importados.</Alert>
                )}
              </Stack>
            </Grid>
            {activeItem.imageUrl ? (
              <Grid item xs={12} md={4}>
                <Box
                  component="img"
                  src={activeItem.imageUrl}
                  alt={activeItem.name}
                  sx={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e7eb' }}
                />
              </Grid>
            ) : null}
          </Grid>
        </Paper>
      ) : null}
        </>
      ) : null}

      {!stats.catalogs && !stats.categories && !stats.items ? (
        <Alert severity="info">
          Use `Listar catalogos`, `Listar categorias` com includeItems ou `Item flat` para preencher esta visao.
        </Alert>
      ) : null}
    </Stack>
  );
};

function CatalogHomologacao() {
  const auth = useAuth();
  const companyScope = cleanText(auth?.user?.companyId) || 'sem-empresa';
  const selectedMerchantStorageKey = useMemo(() => buildSelectedMerchantStorageKey(companyScope), [companyScope]);
  const initialSelectedMerchantId = useMemo(() => readStoredJson(selectedMerchantStorageKey, ''), [selectedMerchantStorageKey]);
  const [selectedMerchantId, setSelectedMerchantId] = useState(initialSelectedMerchantId || '');
  const selectedWorkspaceKey = useMemo(
    () => buildWorkspaceStorageKey(companyScope, selectedMerchantId),
    [companyScope, selectedMerchantId]
  );
  const storedWorkspace = useMemo(() => (
    selectedMerchantId ? readStoredJson(selectedWorkspaceKey, {}) : {}
  ), [selectedMerchantId, selectedWorkspaceKey]);
  const initialDemo = useMemo(() => buildDemoItemPayload({
    categoryId: storedWorkspace.selectedCategoryId || '',
    imagePath: storedWorkspace.imagePath || '',
  }), [storedWorkspace.imagePath, storedWorkspace.selectedCategoryId]);
  const initialPatchPayloads = useMemo(() => buildPatchPayloads(initialDemo.refs), [initialDemo.refs]);

  const tab = 8;
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantsError, setMerchantsError] = useState('');
  const [selectedCatalogId, setSelectedCatalogId] = useState(storedWorkspace.selectedCatalogId || '');
  const [selectedCategoryId, setSelectedCategoryId] = useState(storedWorkspace.selectedCategoryId || '');
  const [includeItems, setIncludeItems] = useState(true);
  const [stockSearchTerm, setStockSearchTerm] = useState('');
  const [stockCategoryFilter, setStockCategoryFilter] = useState('all');
  const [stockInnerTab, setStockInnerTab] = useState(0);
  const [stockProductStatusFilter, setStockProductStatusFilter] = useState('all');
  const [stockSelectedEntryIds, setStockSelectedEntryIds] = useState([]);
  const [stockGroupSearchTerm, setStockGroupSearchTerm] = useState('');
  const [stockSelectedGroupIds, setStockSelectedGroupIds] = useState([]);
  const [pdvDrafts, setPdvDrafts] = useState({});
  const [collapsedStockMerchants, setCollapsedStockMerchants] = useState({});
  const [collapsedStockCategories, setCollapsedStockCategories] = useState({});
  const [stockMenuAnchor, setStockMenuAnchor] = useState(null);
  const [stockMenuCategory, setStockMenuCategory] = useState(null);
  const [stockProductMenuAnchor, setStockProductMenuAnchor] = useState(null);
  const [stockMenuProduct, setStockMenuProduct] = useState(null);
  const [stockGroupMenuAnchor, setStockGroupMenuAnchor] = useState(null);
  const [stockMenuGroup, setStockMenuGroup] = useState(null);
  const [stockOptionMenuAnchor, setStockOptionMenuAnchor] = useState(null);
  const [stockMenuOption, setStockMenuOption] = useState(null);
  const [expandedComplementProductId, setExpandedComplementProductId] = useState('');
  const [collapsedInlineComplementGroups, setCollapsedInlineComplementGroups] = useState({});
  const [productEditorTab, setProductEditorTab] = useState(0);
  const [expandedProductEditorGroups, setExpandedProductEditorGroups] = useState({});
  const [inlineComplementPriceDrafts, setInlineComplementPriceDrafts] = useState({});
  const [stockInventoryDrafts, setStockInventoryDrafts] = useState({});
  const [stockInventoryEditingIds, setStockInventoryEditingIds] = useState([]);
  const [complementEditDialog, setComplementEditDialog] = useState({ open: false, option: null });
  const [complementEditForm, setComplementEditForm] = useState({ name: '', description: '', imagePath: '' });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'create', category: null });
  const [categoryOrderDialog, setCategoryOrderDialog] = useState({ open: false, ids: [], draggingId: '' });
  const [categoryForm, setCategoryForm] = useState(defaultCategory);
  const [productTypeDialog, setProductTypeDialog] = useState({ open: false, category: null, selected: 'prepared' });
  const [pizzaCategoryDialog, setPizzaCategoryDialog] = useState({ open: false });
  const [pizzaCategoryForm, setPizzaCategoryForm] = useState(createEmptyPizzaCategoryForm);
  const [offerDialog, setOfferDialog] = useState({ open: false, category: null });
  const [offerForm, setOfferForm] = useState(emptyOfferForm);
  const [offerGroupDialog, setOfferGroupDialog] = useState({ open: false, mode: 'choice', copySearch: '' });
  const [productGroupDialog, setProductGroupDialog] = useState({ open: false, mode: 'choice', copySearch: '' });
  const [productGroupForm, setProductGroupForm] = useState(createEmptyProductGroupForm);
  const [groupOrderDialog, setGroupOrderDialog] = useState({ open: false, ids: [], draggingId: '' });
  const [productScheduleDialog, setProductScheduleDialog] = useState({
    open: false,
    source: 'offer',
    mode: 'always',
    drafts: createComboScheduleDrafts(),
  });
  const [comboDialog, setComboDialog] = useState({ open: false, category: null });
  const [comboForm, setComboForm] = useState(emptyComboForm);
  const [comboGroups, setComboGroups] = useState([]);
  const [comboDrawer, setComboDrawer] = useState(emptyComboDrawer);
  const [comboScheduleDialog, setComboScheduleDialog] = useState({ open: false, drafts: createComboScheduleDrafts() });
  const [groupEditor, setGroupEditor] = useState({ open: false, groupId: '', tab: 0, search: '', linkProductId: '' });
  const [groupOptionDrafts, setGroupOptionDrafts] = useState({});
  const [newComplementForm, setNewComplementForm] = useState(emptyNewComplementForm);
  const [showNewComplementForm, setShowNewComplementForm] = useState(false);
  const [selectedStockProduct, setSelectedStockProduct] = useState(null);
  const [stockProductForm, setStockProductForm] = useState(emptyStockProductForm);
  const [productPromotionDialog, setProductPromotionDialog] = useState(emptyProductPromotionDialog);
  const [category, setCategory] = useState({ ...defaultCategory, ...(storedWorkspace.category || {}) });
  const [refs, setRefs] = useState({ ...initialDemo.refs, ...(storedWorkspace.refs || {}) });
  const [itemPayload, setItemPayload] = useState(storedWorkspace.itemPayload || stringifyJson(initialDemo.payload));
  const [patchPayloads, setPatchPayloads] = useState({ ...initialPatchPayloads, ...(storedWorkspace.patchPayloads || {}) });
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [imagePath, setImagePath] = useState(storedWorkspace.imagePath || '');
  const [batchId, setBatchId] = useState(storedWorkspace.batchId || '');
  const [evidenceItem, setEvidenceItem] = useState(storedWorkspace.evidenceItem || null);
  const [visualCatalog, setVisualCatalog] = useState(storedWorkspace.visualCatalog || emptyVisualCatalog);
  const [busyAction, setBusyAction] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [workspacePersistence, setWorkspacePersistence] = useState('complete');
  const [merchantSelectionPersisted, setMerchantSelectionPersisted] = useState(true);
  const [workspaceHydratedKey, setWorkspaceHydratedKey] = useState(selectedWorkspaceKey);
  const selectedMerchant = useMemo(() => (
    merchants.find((merchant) => getMerchantId(merchant) === selectedMerchantId) || null
  ), [merchants, selectedMerchantId]);
  const selectedMerchantName = selectedMerchant ? getMerchantName(selectedMerchant) : '';
  const catalogScope = useMemo(() => ({
    merchantId: selectedMerchantId,
    merchantName: selectedMerchantName,
  }), [selectedMerchantId, selectedMerchantName]);
  const selectedMerchantIsAuthorized = Boolean(
    selectedMerchantId
    && merchants.some((merchant) => getMerchantId(merchant) === selectedMerchantId)
  );
  const merchantActionDisabled = !selectedMerchantId || !selectedMerchantIsAuthorized || Boolean(busyAction);

  const payloadPreviewVisual = useMemo(() => {
    const parsed = safeJsonParse(itemPayload);
    if (!parsed.ok) return emptyVisualCatalog;
    return normalizeVisualFromFlat(parsed.value, {
      ...catalogScope,
      catalogId: selectedCatalogId,
      catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
      categoryId: selectedCategoryId,
      categoryName: category.name,
    });
  }, [catalogScope, category.name, itemPayload, selectedCatalogId, selectedCategoryId, visualCatalog.catalogs]);

  const stockCategories = useMemo(() => {
    const term = stockSearchTerm.trim().toLowerCase();
    const categories = toArray(visualCatalog.categories).filter((item) => (
      !selectedMerchantId || !item.merchantId || item.merchantId === selectedMerchantId
    ));
    const uncategorized = {
      id: 'uncategorized',
      name: 'Sem categoria',
      status: '',
      template: '',
      sequence: 9999,
      itemCount: 0,
    };
    const categoriesById = new Map(categories.map((item) => [item.id, { ...item, products: [] }]));

    toArray(visualCatalog.items).filter((item) => (
      !selectedMerchantId || !item.merchantId || item.merchantId === selectedMerchantId
    )).forEach((item) => {
      const categoryId = item.categoryId || 'uncategorized';
      if (!categoriesById.has(categoryId)) {
        categoriesById.set(categoryId, {
          ...uncategorized,
          id: categoryId,
          name: item.categoryName || uncategorized.name,
          products: [],
        });
      }
      const categoryBucket = categoriesById.get(categoryId);
      categoryBucket.products.push({
        ...item,
        categoryStatus: categoryBucket.status,
        categoryName: item.categoryName || categoryBucket.name,
      });
    });

    return Array.from(categoriesById.values())
      .map((item) => {
        const products = toArray(item.products).filter((product) => {
          if (stockCategoryFilter !== 'all' && item.id !== stockCategoryFilter) return false;
          if (!term) return true;
          return [
            product.name,
            product.description,
            product.externalCode,
            item.name,
          ].some((value) => cleanText(value).toLowerCase().includes(term));
        });
        return { ...item, products };
      })
      .filter((item) => {
        if (stockCategoryFilter !== 'all' && item.id !== stockCategoryFilter) return false;
        return item.products.length || (!term && item.id !== 'uncategorized');
      })
      .sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));
  }, [selectedMerchantId, stockCategoryFilter, stockSearchTerm, visualCatalog.categories, visualCatalog.items]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(LEGACY_CREDENTIALS_STORAGE_KEY);
      window.localStorage.removeItem(LEGACY_WORKSPACE_STORAGE_KEY);
    } catch {
      // Browser storage is optional for this screen.
    }
  }, []);

  useEffect(() => {
    let active = true;
    const loadMerchants = async () => {
      setMerchantsLoading(true);
      setMerchantsError('');
      try {
        const response = await apiFetch(`${API_URL}/api/ifood/merchants`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
          throw new Error(data.error || 'Falha ao carregar lojas iFood autorizadas.');
        }
        if (!active) return;
        const list = normalizeMerchantList(data);
        setMerchants(list);
        setSelectedMerchantId((current) => {
          if (current && list.some((merchant) => getMerchantId(merchant) === current)) return current;
          if (list.length === 1) return getMerchantId(list[0]);
          return '';
        });
      } catch (error) {
        if (!active) return;
        setMerchants([]);
        setMerchantsError(error.message || 'Falha ao carregar lojas iFood autorizadas.');
      } finally {
        if (active) setMerchantsLoading(false);
      }
    };

    loadMerchants();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setMerchantSelectionPersisted(persistSelectedCatalogMerchant(
      window.localStorage, selectedMerchantStorageKey, selectedMerchantId
    ));
  }, [selectedMerchantId, selectedMerchantStorageKey]);

  useEffect(() => {
    const workspace = selectedMerchantId ? readStoredJson(selectedWorkspaceKey, {}) : {};
    const demo = buildDemoItemPayload({
      categoryId: workspace.selectedCategoryId || '',
      imagePath: workspace.imagePath || '',
    });
    setSelectedCatalogId(workspace.selectedCatalogId || '');
    setSelectedCategoryId(workspace.selectedCategoryId || '');
    setCategory({ ...defaultCategory, ...(workspace.category || {}) });
    setRefs({ ...demo.refs, ...(workspace.refs || {}) });
    setItemPayload(workspace.itemPayload || stringifyJson(demo.payload));
    setPatchPayloads({ ...buildPatchPayloads(demo.refs), ...(workspace.patchPayloads || {}) });
    setImagePath(workspace.imagePath || '');
    setBatchId(workspace.batchId || '');
    setEvidenceItem(workspace.evidenceItem || null);
    setVisualCatalog(workspace.visualCatalog || emptyVisualCatalog);
    setCollapsedStockMerchants(workspace.collapsedStockMerchants || {});
    setCollapsedStockCategories(workspace.collapsedStockCategories || {});
    setStockSelectedEntryIds([]);
    setStockSelectedGroupIds([]);
    setPdvDrafts({});
    setExpandedComplementProductId('');
    setSelectedStockProduct(null);
    setProductEditorTab(0);
    setExpandedProductEditorGroups({});
    setStockProductForm(emptyStockProductForm);
    setProductPromotionDialog(emptyProductPromotionDialog);
    setWorkspaceHydratedKey(selectedWorkspaceKey);
  }, [selectedMerchantId, selectedWorkspaceKey]);

  useEffect(() => {
    if (!selectedMerchantId) {
      setWorkspacePersistence('complete');
      return;
    }
    if (workspaceHydratedKey !== selectedWorkspaceKey) return;
    const workspace = {
      merchantId: selectedMerchantId,
      merchantName: selectedMerchantName,
      selectedCatalogId,
      selectedCategoryId,
      category,
      refs,
      itemPayload,
      patchPayloads,
      imagePath,
      batchId,
      evidenceItem,
      visualCatalog,
      collapsedStockMerchants,
      collapsedStockCategories,
    };
    setWorkspacePersistence(persistCatalogWorkspace(window.localStorage, selectedWorkspaceKey, workspace));
  }, [
    batchId,
    category,
    collapsedStockCategories,
    collapsedStockMerchants,
    evidenceItem,
    visualCatalog,
    imagePath,
    itemPayload,
    patchPayloads,
    refs,
    selectedMerchantId,
    selectedMerchantName,
    selectedWorkspaceKey,
    workspaceHydratedKey,
    selectedCatalogId,
    selectedCategoryId,
  ]);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const importVisualData = useCallback((incoming, source = '') => {
    const scopedIncoming = {
      ...incoming,
      catalogs: toArray(incoming.catalogs).map((item) => ({ ...catalogScope, ...item, merchantId: item.merchantId || catalogScope.merchantId, merchantName: item.merchantName || catalogScope.merchantName })),
      categories: toArray(incoming.categories).map((item) => ({ ...catalogScope, ...item, merchantId: item.merchantId || catalogScope.merchantId, merchantName: item.merchantName || catalogScope.merchantName })),
      items: toArray(incoming.items).map((item) => ({ ...catalogScope, ...item, merchantId: item.merchantId || catalogScope.merchantId, merchantName: item.merchantName || catalogScope.merchantName })),
      optionGroups: toArray(incoming.optionGroups).map((item) => ({ ...catalogScope, ...item, merchantId: item.merchantId || catalogScope.merchantId, merchantName: item.merchantName || catalogScope.merchantName })),
      options: toArray(incoming.options).map((item) => ({ ...catalogScope, ...item, merchantId: item.merchantId || catalogScope.merchantId, merchantName: item.merchantName || catalogScope.merchantName })),
    };
    const stats = countVisualEntities(scopedIncoming);
    if (!stats.catalogs && !stats.categories && !stats.items && !stats.optionGroups && !stats.options) {
      return false;
    }

    setVisualCatalog((prev) => mergeVisualCatalog(prev, {
      ...scopedIncoming,
      lastImportedAt: new Date().toISOString(),
      source,
    }));
    return true;
  }, [catalogScope]);

  const selectVisualCatalog = (catalog) => {
    setSelectedCatalogId(catalog.id);
    showToast('catalogId aplicado.');
  };

  const selectVisualCategory = (nextCategory) => {
    setSelectedCategoryId(nextCategory.id);
    setCategory((prev) => ({
      ...prev,
      name: nextCategory.name || prev.name,
      status: nextCategory.status || prev.status,
      template: nextCategory.template || prev.template,
      sequence: nextCategory.sequence ?? prev.sequence,
    }));
    showToast('categoryId aplicado.');
  };

  const selectVisualItem = (item) => {
    const relatedOption = visualCatalog.options.find((option) => option.itemId === item.id);
    setRefs((prev) => ({
      ...prev,
      itemId: item.id || prev.itemId,
      itemProductId: item.productId || prev.itemProductId,
      itemExternalCode: item.externalCode || prev.itemExternalCode,
      optionId: relatedOption?.id || item.firstOptionId || prev.optionId,
      optionProductId: relatedOption?.productId || item.firstOptionProductId || prev.optionProductId,
      optionExternalCode: relatedOption?.externalCode || item.firstOptionExternalCode || prev.optionExternalCode,
    }));
    showToast('IDs do produto aplicados.');
  };

  const resolveCatalogContextForExternalCode = (source = {}) => {
    const catalogId = cleanText(firstValue(source.catalogId, selectedCatalogId));
    const catalog = toArray(visualCatalog.catalogs).find((item) => cleanText(item.id) === catalogId) || {};
    return cleanText(firstValue(
      source.priceCatalogContext,
      source.catalogContext,
      source.context,
      source.raw?.priceCatalogContext,
      source.raw?.catalogContext,
      source.raw?.context,
      catalog.catalogContext,
      catalog.context,
      catalog.raw?.catalogContext,
      catalog.raw?.context,
      'DEFAULT'
    )).toUpperCase() || 'DEFAULT';
  };

  const buildExternalCodeByCatalogPayload = (externalCode, source = {}) => ([{
    externalCode: cleanText(externalCode),
    catalogContext: resolveCatalogContextForExternalCode(source),
  }]);

  const buildPriceByCatalogPayload = (value, originalValue, source = {}) => ([{
    value,
    originalValue,
    catalogContext: resolveCatalogContextForExternalCode({
      ...source,
      catalogContext: source.priceCatalogContext || source.catalogContext,
    }),
  }]);

  const applyConfirmedItemExternalCode = (itemId, confirmedItem, confirmedProduct, source = {}) => {
    if (!itemId || !confirmedItem) return;
    const confirmedVisualItem = normalizeVisualItem(
      { ...confirmedItem, product: confirmedProduct || {} },
      {
        ...catalogScope,
        catalogId: source.catalogId || selectedCatalogId,
        catalogContext: source.catalogContext || source.priceCatalogContext || resolveCatalogContextForExternalCode(source),
        categoryId: source.categoryId,
        categoryName: source.categoryName,
      }
    );
    setVisualCatalog((prev) => ({
      ...prev,
      items: toArray(prev.items).map((item) => (
        item.id === itemId
          ? {
            ...item,
            ...confirmedVisualItem,
            categoryId: confirmedVisualItem.categoryId || item.categoryId,
            categoryName: confirmedVisualItem.categoryName || item.categoryName,
          }
          : item
      )),
      lastImportedAt: new Date().toISOString(),
      source: 'Codigo PDV sincronizado no Catalog',
    }));
    setPdvDrafts((prev) => {
      const next = { ...prev };
      delete next[`item:${itemId}`];
      return next;
    });
  };

  const syncItemExternalCodeAfterUpsert = async ({ itemId, productId, externalCode, source = {}, actionId = 'item-external-code-sync' }) => {
    const nextCode = cleanText(externalCode);
    const resolvedProductId = cleanText(productId || source.productId);
    if (!itemId || !resolvedProductId || !nextCode) {
      showToast('Item, Product e codigo PDV sao obrigatorios para confirmar a sincronizacao.', 'error');
      return false;
    }
    let productExternalCodeBefore;
    try {
      const initialFlatResult = await catalogFetch('/items/flat', { itemId });
      const initialIdentity = requireItemProductIdentity(initialFlatResult.data, {
        catalogItemId: itemId,
        productId: resolvedProductId,
      });
      productExternalCodeBefore = cleanText(
        initialIdentity.product.externalCode || initialIdentity.product.productExternalCode
      );
    } catch (error) {
      const message = `Nao foi possivel confirmar Item e Product antes de alterar o codigo PDV: ${error.message}`;
      setLastResult({ ok: false, error: message, action: `${actionId}-preflight` });
      showToast(message, 'error');
      return false;
    }
    const result = await runAction({
      id: actionId,
      path: '/items/external-code',
      payload: {
        payload: {
          catalogItemId: itemId,
          productId: resolvedProductId,
          externalCode: nextCode,
        },
      },
      label: 'Atualizacao do codigo PDV aceita; confirmando no iFood.',
      announceSuccess: false,
    });
    if (!result?.ok) return false;

    setBusyAction(`${actionId}-readback`);
    try {
      const confirmation = requireConfirmedItemExternalCode(result, {
        catalogItemId: itemId,
        externalCode: nextCode,
      });
      const flatResult = await catalogFetch('/items/flat', { itemId });
      const confirmed = requireItemExternalCodeReadback(flatResult.data, {
        catalogItemId: itemId,
        productId: resolvedProductId,
        externalCode: nextCode,
        productExternalCode: productExternalCodeBefore,
      });
      requireEffectiveContextCodes(confirmed.item, confirmation.effectiveContextCodes);
      applyConfirmedItemExternalCode(itemId, confirmed.item, confirmed.product, source);
      setLastResult({
        ...result,
        ok: true,
        confirmed: true,
        action: `${actionId}-readback`,
        label: 'Codigo PDV confirmado no Item oficial do iFood.',
      });
      showToast('Codigo PDV confirmado no Item oficial do iFood.');
      return true;
    } catch (error) {
      setLastResult({ ok: false, error: error.message, action: `${actionId}-readback` });
      showToast(error.message, 'error');
      return false;
    } finally {
      setBusyAction('');
    }
  };

  const openCreateCategoryDialog = () => {
    setCategoryDialog({ open: true, mode: 'create', category: null });
    setCategoryForm({
      ...defaultCategory,
      sequence: toArray(visualCatalog.categories).length,
    });
  };

  const openEditCategoryDialog = (nextCategory) => {
    setCategoryDialog({ open: true, mode: 'edit', category: nextCategory });
    setCategoryForm({
      name: nextCategory.name || '',
      status: normalizeCatalogStatus(nextCategory.status),
      template: nextCategory.template || 'DEFAULT',
      sequence: nextCategory.sequence ?? 0,
    });
  };

  const closeCategoryDialog = () => {
    setCategoryDialog({ open: false, mode: 'create', category: null });
    setCategoryForm(defaultCategory);
  };

  const openStockProductDialog = (product) => {
    selectVisualItem(product);
    setSelectedStockProduct(product);
    setProductEditorTab(0);
    setExpandedProductEditorGroups({});
    setStockProductForm(createStockProductForm(product));
  };

  const closeStockProductDialog = () => {
    setSelectedStockProduct(null);
    setProductEditorTab(0);
    setExpandedProductEditorGroups({});
    setStockProductForm(emptyStockProductForm);
  };

  const parseJsonOrToast = (value, label) => {
    const parsed = safeJsonParse(value);
    if (!parsed.ok) {
      showToast(`${label}: JSON invalido (${parsed.error})`, 'error');
      return null;
    }
    return parsed.value;
  };

  const buildCatalogRequest = (path, payload = {}) => {
    const base = `${API_URL}/api/ifood/catalog/merchants/${encodeURIComponent(selectedMerchantId)}`;
    const withBody = (method, url, body) => ({
      method,
      url,
      options: {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      },
    });
    const get = (url) => ({ method: 'GET', url, options: { method: 'GET' } });
    const catalogId = encodeURIComponent(cleanText(payload.catalogId || selectedCatalogId));
    const categoryId = encodeURIComponent(cleanText(payload.categoryId || selectedCategoryId));
    const itemId = encodeURIComponent(cleanText(payload.itemId || refs.itemId));
    const optionProductId = encodeURIComponent(cleanText(payload.productId || refs.itemProductId));
    const optionGroupId = encodeURIComponent(cleanText(payload.optionGroupId));
    const batchValue = encodeURIComponent(cleanText(payload.batchId || batchId));

    switch (path) {
      case '/catalogs':
        return get(`${base}/catalogs`);
      case '/categories/list':
        return get(`${base}/catalogs/${catalogId}/categories?includeItems=${payload.includeItems ? 'true' : 'false'}`);
      case '/categories/create':
        return withBody('POST', `${base}/catalogs/${catalogId}/categories`, payload.category || {});
      case '/categories/update':
        return withBody('PATCH', `${base}/catalogs/${catalogId}/categories/${encodeURIComponent(cleanText(payload.categoryId))}`, payload.category || {});
      case '/categories/delete':
        return { method: 'DELETE', url: `${base}/categories/${encodeURIComponent(cleanText(payload.categoryId))}`, options: { method: 'DELETE' } };
      case '/items/upsert':
        return withBody('PUT', `${base}/items`, payload.payload || {});
      case '/items/list':
        return get(`${base}/categories/${categoryId}/items`);
      case '/items/flat':
        return get(`${base}/items/${itemId}/flat`);
      case '/items/delete':
        return { method: 'DELETE', url: `${base}/categories/${categoryId}/products/${optionProductId}`, options: { method: 'DELETE' } };
      case '/products/update':
        return withBody('PUT', `${base}/products/${optionProductId}`, payload.payload || {});
      case '/items/price':
        return withBody('PATCH', `${base}/items/price`, payload.payload || {});
      case '/items/status':
        return withBody('PATCH', `${base}/items/status`, payload.payload || {});
      case '/items/external-code':
        return withBody('PATCH', `${base}/items/externalCode`, payload.payload || {});
      case '/options/price':
        return withBody('PATCH', `${base}/options/price`, payload.payload || {});
      case '/options/status':
        return withBody('PATCH', `${base}/options/status`, payload.payload || {});
      case '/options/external-code':
        return withBody('PATCH', `${base}/options/externalCode`, payload.payload || {});
      case '/options/delete':
        return { method: 'DELETE', url: `${base}/optionGroups/${optionGroupId}/products/${optionProductId}/option`, options: { method: 'DELETE' } };
      case '/products/price-batch':
        return withBody('PATCH', `${base}/products/price`, Array.isArray(payload.payload) ? payload.payload : []);
      case '/products/status-batch':
        return withBody('PATCH', `${base}/products/status`, Array.isArray(payload.payload) ? payload.payload : []);
      case '/image/upload':
        return withBody('POST', `${base}/image/upload`, { image: payload.image || '' });
      case '/sellable-items':
        return get(`${base}/catalogs/${catalogId}/sellableItems`);
      case '/unsellable-items':
        return get(`${base}/catalogs/${catalogId}/unsellableItems`);
      case '/batch/status':
        return get(`${base}/batch/${batchValue}`);
      case '/inventory/create':
        return withBody('POST', `${base}/inventory`, payload.payload || {});
      case '/inventory/get':
        return get(`${base}/inventory/${optionProductId}`);
      case '/inventory/delete':
        return withBody('POST', `${base}/inventory/batchDelete`, payload.payload || {});
      default:
        throw new Error(`Ação Catalog não mapeada no fluxo operacional: ${path}`);
    }
  };

  const unwrapCatalogData = (path, data = {}) => {
    if (data.data !== undefined) return data.data;
    if (path === '/catalogs') return data.catalogs || [];
    if (path === '/categories/list') return data.categories || [];
    if (path === '/categories/create') return data.category || data.result || data;
    if (path === '/items/list') return data.items || [];
    if (path === '/items/flat') return data.itemFlat || data;
    if (path === '/sellable-items' || path === '/unsellable-items') return data.items || [];
    return data.result || data.category || data.product || data.optionGroups || data;
  };

  const catalogFetch = async (path, payload = {}) => {
    if (!selectedMerchantId) {
      throw new Error('Selecione uma loja iFood antes de executar esta ação.');
    }
    if (!selectedMerchantIsAuthorized) {
      throw new Error('A loja iFood selecionada não está autorizada para este login/CNPJ.');
    }

    const request = buildCatalogRequest(path, payload);
    const response = await apiFetch(request.url, request.options);
    const data = await response.json().catch(() => ({}));
    if (response.status === 404 && (path === '/items/delete' || path === '/options/delete')) {
      return { ...data, ok: true, alreadyMissing: true, data };
    }
    if (!response.ok || data.ok === false || data.success === false) {
      if (data.reason === 'ifood-catalog-forbidden' && data.error) {
        throw new Error(data.error);
      }
      const detailsMessage = typeof data.details === 'string'
        ? data.details
        : firstValue(
          data.details?.message,
          data.details?.error?.message,
          data.details?.error,
          toArray(data.details?.errors).map((item) => item.message || item.error || item.code).filter(Boolean).join('; ')
        );
      const message = [data.error, detailsMessage]
        .map(cleanText)
        .filter(Boolean)
        .filter((item, index, list) => list.indexOf(item) === index)
        .join(' - ') || 'Falha na chamada Catalog.';
      const error = new Error(message);
      error.status = response.status;
      error.reason = data.reason;
      throw error;
    }
    return {
      ...data,
      ok: true,
      data: unwrapCatalogData(path, data),
    };
  };

  const runAction = async ({ id, path, payload, label, onSuccess, visualImport, announceSuccess = true }) => {
    setBusyAction(id);
    try {
      const result = await catalogFetch(path, payload);
      const enrichedResult = { ...result, action: id, label };
      setLastResult(enrichedResult);
      if (visualImport) {
        importVisualData(visualImport(enrichedResult), label || id);
      }
      if (onSuccess) onSuccess(enrichedResult);
      if (announceSuccess) showToast(label || 'Chamada executada.');
      return enrichedResult;
    } catch (error) {
      setLastResult({ ok: false, error: error.message, action: id });
      showToast(error.message || 'Falha na chamada Catalog.', 'error');
      return null;
    } finally {
      setBusyAction('');
    }
  };

  const waitForCatalogBatchIfPresent = async (actionResult, actionLabel) => {
    const batchId = cleanText(firstValue(
      actionResult?.data?.batchId,
      actionResult?.result?.batchId,
      actionResult?.batchId
    ));
    if (!batchId) return true;

    for (let attempt = 0; attempt < 10; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
      const batchResult = await catalogFetch('/batch/status', { batchId });
      const batch = batchResult.data || {};
      const status = cleanText(firstValue(batch.batchStatus, batch.status)).toUpperCase();
      const failedResults = toArray(batch.results).filter((entry) => (
        ['FAILURE', 'FAILED', 'ERROR'].includes(cleanText(firstValue(entry.result, entry.status)).toUpperCase())
      ));
      const failureCount = Number(firstValue(batch.failureCount, failedResults.length, 0));
      if (failureCount > 0 || failedResults.length > 0 || ['FAILURE', 'FAILED', 'ERROR'].includes(status)) {
        const detail = failedResults
          .map((entry) => cleanText(firstValue(entry.message, entry.error, entry.reason, entry.resourceId)))
          .filter(Boolean)
          .join('; ');
        throw new Error(`${actionLabel} falhou no processamento do iFood${detail ? `: ${detail}` : '.'}`);
      }
      if (['COMPLETED', 'SUCCESS', 'SUCCEEDED'].includes(status)) return true;
    }

    throw new Error(`${actionLabel} ainda esta em processamento no iFood. Tente novamente em instantes.`);
  };

  const regenerateDemoPayload = () => {
    const demo = buildDemoItemPayload({ categoryId: selectedCategoryId, imagePath });
    setRefs(demo.refs);
    setItemPayload(stringifyJson(demo.payload));
    setPatchPayloads(buildPatchPayloads(demo.refs));
    showToast('Massa demo gerada.');
  };

  const syncRefsFromPayload = () => {
    const parsed = parseJsonOrToast(itemPayload, 'Item completo');
    if (!parsed) return;
    const nextRefs = {
      itemId: parsed.item?.id || refs.itemId,
      itemProductId: parsed.item?.productId || refs.itemProductId,
      optionGroupId: parsed.optionGroups?.[0]?.id || refs.optionGroupId,
      optionId: parsed.options?.[0]?.id || refs.optionId,
      optionProductId: parsed.options?.[0]?.productId || refs.optionProductId,
      itemExternalCode: parsed.item?.externalCode || refs.itemExternalCode,
      optionExternalCode: parsed.options?.[0]?.externalCode || refs.optionExternalCode,
    };
    setRefs(nextRefs);
    setPatchPayloads(buildPatchPayloads(nextRefs));
    showToast('IDs sincronizados.');
  };

  const handleImageFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('Imagem acima de 5MB.', 'error');
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    setImageDataUrl(String(dataUrl || ''));
    showToast('Imagem carregada.');
  };

  const listCatalogs = () => runAction({
    id: 'catalogs',
    path: '/catalogs',
    label: 'Catalogos listados.',
    checkId: 'catalogs',
    visualImport: (result) => ({ catalogs: normalizeCatalogsForVisual(result.data, catalogScope) }),
    onSuccess: (result) => {
      const catalogId = findFirstCatalogId(result.data);
      if (catalogId && !selectedCatalogId) setSelectedCatalogId(catalogId);
    },
  });

  const listCategories = () => runAction({
    id: 'categories-list',
    path: '/categories/list',
    payload: { catalogId: selectedCatalogId, includeItems },
    label: 'Categorias listadas.',
    checkId: 'categories-list',
    visualImport: (result) => normalizeVisualFromCategories(result.data, {
      ...catalogScope,
      catalogId: selectedCatalogId,
      catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
    }),
    onSuccess: (result) => {
      const categoryId = findFirstCategoryId(result.data);
      if (categoryId && !selectedCategoryId) setSelectedCategoryId(categoryId);
      const item = extractEvidenceItem(result.data);
      if (item) setEvidenceItem(item);
    },
  });

  const createCategory = () => runAction({
    id: 'category-create',
    path: '/categories/create',
    payload: { catalogId: selectedCatalogId, category: { ...category, sequence: Number(category.sequence || 0) } },
    label: 'Categoria criada.',
    checkId: 'category-create',
    visualImport: (result) => ({
      categories: [{
        id: result.data?.id || result.data?.categoryId || category.name,
        ...catalogScope,
        catalogId: selectedCatalogId,
        name: result.data?.name || category.name,
        status: result.data?.status || category.status,
        template: result.data?.template || category.template,
        sequence: result.data?.sequence ?? category.sequence,
        itemCount: 0,
        raw: result.data,
      }],
    }),
    onSuccess: (result) => {
      if (result.data?.id) setSelectedCategoryId(result.data.id);
    },
  });

  const upsertItem = () => {
    const payload = parseJsonOrToast(itemPayload, 'Item completo');
    if (!payload) return null;
    return runAction({
      id: 'item-upsert',
      path: '/items/upsert',
      payload: { payload },
      label: 'Item completo enviado.',
      checkId: 'item-upsert',
      visualImport: () => normalizeVisualFromFlat(payload, {
        ...catalogScope,
        catalogId: selectedCatalogId,
        catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
        categoryId: selectedCategoryId,
        categoryName: category.name,
      }),
    });
  };

  const runJsonPatch = async (id, path, jsonValue, label, checkId) => {
    const payload = parseJsonOrToast(jsonValue, label);
    if (!payload) return null;
    if (path === '/items/external-code') {
      const itemId = cleanText(payload.catalogItemId || payload.itemId);
      const source = toArray(visualCatalog.items).find((item) => cleanText(item.id) === itemId) || {};
      return syncItemExternalCodeAfterUpsert({
        itemId,
        productId: cleanText(payload.productId || source.productId),
        externalCode: payload.externalCode,
        source,
        actionId: id,
      });
    }
    if (path === '/options/external-code') {
      const optionId = cleanText(payload.optionId);
      const source = toArray(visualCatalog.options).find((option) => cleanText(option.id) === optionId);
      if (!source) {
        showToast('Importe ou selecione a Option e seu Item pai antes de alterar o codigo PDV.', 'warning');
        return false;
      }
      return syncSharedOptionExternalCode(source, payload.externalCode, id);
    }
    return runAction({ id, path, payload: { payload }, label, checkId });
  };

  const openStockCategoryMenu = (event, nextCategory) => {
    event.stopPropagation();
    setStockMenuAnchor(event.currentTarget);
    setStockMenuCategory(nextCategory);
  };

  const closeStockCategoryMenu = () => {
    setStockMenuAnchor(null);
    setStockMenuCategory(null);
  };

  const openStockProductMenu = (event, product) => {
    event.stopPropagation();
    setStockProductMenuAnchor(event.currentTarget);
    setStockMenuProduct(product);
  };

  const closeStockProductMenu = () => {
    setStockProductMenuAnchor(null);
    setStockMenuProduct(null);
  };

  const openStockGroupMenu = (event, group) => {
    event.stopPropagation();
    setStockGroupMenuAnchor(event.currentTarget);
    setStockMenuGroup(group);
  };

  const closeStockGroupMenu = () => {
    setStockGroupMenuAnchor(null);
    setStockMenuGroup(null);
  };

  const openStockOptionMenu = (event, option) => {
    event.stopPropagation();
    setStockOptionMenuAnchor(event.currentTarget);
    setStockMenuOption(option);
  };

  const closeStockOptionMenu = () => {
    setStockOptionMenuAnchor(null);
    setStockMenuOption(null);
  };

  const updateVisualCategory = (nextCategory) => {
    setVisualCatalog((prev) => ({
      ...prev,
      categories: dedupeById(toArray(prev.categories).map((item) => (
        item.id === nextCategory.id ? { ...item, ...nextCategory } : item
      )), 'category'),
      lastImportedAt: new Date().toISOString(),
      source: 'Categoria atualizada no estoque visual',
    }));
  };

  const patchStockCategory = async (nextCategory, overrides = {}, label = 'Categoria atualizada no iFood.') => {
    if (!selectedCatalogId) {
      showToast('Selecione um catalogo antes de alterar categoria.', 'warning');
      return null;
    }
    if (!nextCategory?.id || nextCategory.id === 'uncategorized') {
      showToast('Categoria sem identificador para alterar no iFood.', 'warning');
      return null;
    }

    const payload = {
      name: cleanText(overrides.name ?? nextCategory.name),
      status: normalizeCatalogStatus(overrides.status ?? nextCategory.status),
      template: cleanText(overrides.template ?? nextCategory.template) || 'DEFAULT',
      sequence: Number(overrides.sequence ?? nextCategory.sequence ?? 0),
    };

    const result = await runAction({
      id: 'stock-category-update',
      path: '/categories/update',
      payload: { catalogId: selectedCatalogId, categoryId: nextCategory.id, category: payload },
      label,
      visualImport: (response) => ({
        categories: [{
          ...nextCategory,
          id: response.data?.id || response.data?.categoryId || nextCategory.id,
          name: response.data?.name || payload.name,
          status: response.data?.status || payload.status,
          template: response.data?.template || payload.template,
          sequence: response.data?.sequence ?? payload.sequence,
          raw: response.data || payload,
        }],
      }),
    });

    if (result?.ok) {
      updateVisualCategory({
        ...nextCategory,
        name: result.data?.name || payload.name,
        status: result.data?.status || payload.status,
        template: result.data?.template || payload.template,
        sequence: result.data?.sequence ?? payload.sequence,
        raw: result.data || payload,
      });
    }
    return result;
  };

  const toggleStockCategoryStatus = (nextCategory) => patchStockCategory(
    nextCategory,
    { status: getNextCatalogStatus(nextCategory.status) },
    normalizeCatalogStatus(nextCategory.status) === 'AVAILABLE'
      ? 'Categoria pausada no iFood.'
      : 'Categoria reativada no iFood.'
  );

  const duplicateStockCategory = async (nextCategory = stockMenuCategory) => {
    closeStockCategoryMenu();
    if (!selectedCatalogId) {
      showToast('Selecione um catalogo antes de duplicar categoria.', 'warning');
      return;
    }
    if (!nextCategory?.id || nextCategory.id === 'uncategorized') {
      showToast('Categoria sem identificador para duplicar.', 'warning');
      return;
    }

    const payload = {
      name: `${cleanText(nextCategory.name) || 'Categoria'} copia`,
      status: normalizeCatalogStatus(nextCategory.status),
      template: cleanText(nextCategory.template) || 'DEFAULT',
      sequence: Number(toArray(visualCatalog.categories).length || 0),
    };

    const result = await runAction({
      id: 'stock-category-duplicate',
      path: '/categories/create',
      payload: { catalogId: selectedCatalogId, category: payload },
      label: 'Categoria duplicada no iFood.',
      checkId: 'category-create',
      visualImport: (response) => ({
        categories: [{
          id: response.data?.id || response.data?.categoryId || payload.name,
          catalogId: selectedCatalogId,
          name: response.data?.name || payload.name,
          status: response.data?.status || payload.status,
          template: response.data?.template || payload.template,
          sequence: response.data?.sequence ?? payload.sequence,
          itemCount: 0,
          raw: response.data || payload,
        }],
      }),
    });
    if (result?.data?.id) setSelectedCategoryId(result.data.id);
  };

  const removeStockCategory = async (nextCategory = stockMenuCategory) => {
    closeStockCategoryMenu();
    if (!nextCategory?.id || nextCategory.id === 'uncategorized') {
      showToast('Categoria sem identificador para remover no iFood.', 'warning');
      return;
    }
    const confirmed = window.confirm(`Remover a categoria "${nextCategory.name}" no iFood? Esta acao tambem remove o conteudo dela no cardapio do iFood.`);
    if (!confirmed) return;

    const result = await runAction({
      id: 'stock-category-delete',
      path: '/categories/delete',
      payload: { categoryId: nextCategory.id },
      label: 'Categoria removida no iFood.',
    });
    if (!result?.ok) return;

    const removedItemIds = new Set(toArray(visualCatalog.items)
      .filter((item) => item.categoryId === nextCategory.id)
      .map((item) => item.id));
    setVisualCatalog((prev) => ({
      ...prev,
      categories: toArray(prev.categories).filter((item) => item.id !== nextCategory.id),
      items: toArray(prev.items).filter((item) => item.categoryId !== nextCategory.id),
      optionGroups: toArray(prev.optionGroups).filter((group) => !removedItemIds.has(group.itemId)),
      options: toArray(prev.options).filter((option) => !removedItemIds.has(option.itemId)),
      lastImportedAt: new Date().toISOString(),
      source: 'Categoria removida do estoque visual',
    }));
  };

  const createStockItem = (nextCategory, kind = 'oferta') => {
    const validCategory = nextCategory?.id && nextCategory.id !== 'uncategorized' ? nextCategory : null;
    if (kind === 'combo' && !validCategory) {
      showToast('Selecione uma categoria valida para criar item.', 'warning');
      return;
    }

    if (kind === 'combo') {
      setComboDialog({ open: true, category: validCategory });
      setComboForm({ ...emptyComboForm, imagePath, externalCode: `COMBO${Date.now().toString().slice(-6)}` });
      setComboGroups([]);
      setComboDrawer(emptyComboDrawer);
      setComboScheduleDialog({ open: false, drafts: createComboScheduleDrafts() });
      return;
    }

    const fallbackCategory = validCategory
      || toArray(visualCatalog.categories).find((item) => item.id && item.id !== 'uncategorized')
      || null;
    setProductTypeDialog({ open: true, category: fallbackCategory, selected: 'prepared' });
  };

  const closeProductTypeDialog = () => {
    setProductTypeDialog({ open: false, category: null, selected: 'prepared' });
  };

  const continueProductTypeDialog = () => {
    if (productTypeDialog.selected === 'pizza') {
      closeProductTypeDialog();
      setPizzaCategoryForm(createEmptyPizzaCategoryForm());
      setPizzaCategoryDialog({ open: true });
      return;
    }
    const fallbackCategory = productTypeDialog.category
      || toArray(visualCatalog.categories).find((item) => item.id && item.id !== 'uncategorized')
      || null;
    closeProductTypeDialog();
    setOfferDialog({ open: true, category: fallbackCategory });
    setOfferForm({
      ...emptyOfferForm,
      externalCode: `PROD${Date.now().toString().slice(-6)}`,
      imagePath,
      categoryId: fallbackCategory?.id || '',
      categoryName: fallbackCategory?.name || '',
    });
  };

  const closeOfferDialog = () => {
    setOfferDialog({ open: false, category: null });
    setOfferForm(emptyOfferForm);
    setOfferGroupDialog({ open: false, mode: 'choice', copySearch: '' });
  };

  const closeComboDialog = () => {
    setComboDialog({ open: false, category: null });
    setComboForm(emptyComboForm);
    setComboGroups([]);
    setComboDrawer(emptyComboDrawer);
    setComboScheduleDialog({ open: false, drafts: createComboScheduleDrafts() });
  };

  const parseCatalogMoney = (value) => parseMoneyNumber(value) || 0;
  const parseCatalogPercent = (value) => {
    const numeric = parseMoneyNumber(value);
    return numeric === null ? 0 : numeric;
  };
  const formatPercentInputValue = (value) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return '';
    return numeric.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const buildCatalogProductPayload = (product, optionGroups = []) => withCatalogOptionalFields(
    {
      id: product.productId,
      name: product.name,
      description: product.description || '',
    },
    {
      externalCode: cleanText(firstValue(product.raw?.product?.externalCode, product.productExternalCode, '')),
      additionalInformation: cleanText(firstValue(product.raw?.product?.additionalInformation, product.description, '')),
      imagePath: normalizeIfoodImagePathForPayload(product.imagePath),
      ean: product.raw?.product?.ean || '',
      serving: product.raw?.product?.serving || 'SERVES_1',
      dietaryRestrictions: product.raw?.product?.dietaryRestrictions || [],
      quantity: product.raw?.product?.quantity || null,
      optionGroups: toArray(optionGroups).length ? optionGroups : undefined,
    }
  );

  const updateOfferForm = (field, value) => {
    setOfferForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateOfferGroupDraft = (groupDraftId, field, value) => {
    setOfferForm((prev) => ({
      ...prev,
      groups: toArray(prev.groups).map((group) => (
        group.draftId === groupDraftId ? { ...group, [field]: value } : group
      )),
    }));
  };

  const updateOfferGroupOptionDraft = (groupDraftId, optionDraftId, field, value) => {
    setOfferForm((prev) => ({
      ...prev,
      groups: toArray(prev.groups).map((group) => {
        if (group.draftId !== groupDraftId) return group;
        return {
          ...group,
          options: toArray(group.options).map((option) => (
            option.draftId === optionDraftId ? { ...option, [field]: value } : option
          )),
        };
      }),
    }));
  };

  const createOfferOptionDraft = (source = {}) => {
    const rawProduct = source.rawProduct || source.raw?.product || {};
    const imagePath = cleanText(firstValue(source.imagePath, rawProduct.imagePath, rawProduct.image, ''));
    const price = firstValue(source.price, source.raw?.price?.value, '');
    return {
      draftId: createUuid(),
      id: cleanText(source.id) || createUuid(),
      productId: cleanText(firstValue(source.productId, rawProduct.id, '')) || createUuid(),
      name: cleanText(firstValue(source.name, rawProduct.name, '')),
      description: cleanText(firstValue(source.description, rawProduct.description, rawProduct.additionalInformation, '')),
      price: price || price === 0 ? formatStockMoneyInput(price) : '',
      originalPrice: firstValue(source.originalPrice, source.raw?.price?.originalValue, price, ''),
      externalCode: cleanText(firstValue(source.externalCode, '')),
      productExternalCode: cleanText(firstValue(source.productExternalCode, rawProduct.externalCode, '')),
      imagePath,
      imageUrl: buildIfoodImageUrl(imagePath),
      status: normalizeCatalogStatus(source.status),
      rawProduct,
      raw: source.raw || {},
      locked: Boolean(source.id && source.productId),
    };
  };

  const createOfferGroupDraft = () => ({
    draftId: createUuid(),
    id: createUuid(),
    source: 'new',
    name: '',
    externalCode: '',
    status: 'AVAILABLE',
    min: 0,
    max: 1,
    optionGroupType: 'DEFAULT',
    associationType: '',
    locked: false,
    options: [createOfferOptionDraft()],
  });

  const getOfferSharedGroupOptions = (sharedGroup) => {
    const sourceGroups = toArray(sharedGroup?.linkedGroups).length
      ? toArray(sharedGroup.linkedGroups)
      : [sharedGroup || {}];
    const options = sourceGroups.flatMap((sourceGroup) => allStockComplementEntries.filter((option) => (
      option.groupId === sourceGroup.id && option.itemId === sourceGroup.itemId
    )));
    return dedupeByKey(options, 'shared-option', getSharedComplementKey);
  };

  const createCopiedOfferGroupDraft = (sharedGroup) => {
    const sourceGroup = toArray(sharedGroup?.linkedGroups)[0] || sharedGroup || {};
    const options = getOfferSharedGroupOptions(sharedGroup);
    const max = Math.max(1, firstFiniteNumber(sourceGroup.max, sharedGroup?.max, options.length, 1));
    const min = Math.min(max, Math.max(0, firstFiniteNumber(sourceGroup.min, sharedGroup?.min, 0)));
    return {
      draftId: createUuid(),
      id: cleanText(sourceGroup.id || sharedGroup?.id) || createUuid(),
      source: 'copied',
      sharedGroupKey: sharedGroup?.sharedGroupKey || getSharedOptionGroupKey(sourceGroup),
      sourceItemId: sourceGroup.itemId || '',
      name: cleanText(firstValue(sharedGroup?.name, sourceGroup.name, 'Grupo de complementos')),
      externalCode: cleanText(firstValue(sourceGroup.externalCode, sharedGroup?.externalCode, '')),
      status: normalizeCatalogStatus(firstValue(sourceGroup.status, sharedGroup?.status, 'AVAILABLE')),
      min,
      max,
      optionGroupType: cleanText(firstValue(sourceGroup.optionGroupType, sourceGroup.raw?.optionGroupType, sharedGroup?.optionGroupType, 'DEFAULT')),
      associationType: cleanText(firstValue(sourceGroup.associationType, sourceGroup.raw?.relation?.associationType, '')),
      locked: true,
      options: options.map((option) => ({ ...createOfferOptionDraft(option), locked: true })),
      availableIn: sharedGroup?.availableIn || '',
    };
  };

  const addOfferEmptyGroup = () => {
    setOfferForm((prev) => ({
      ...prev,
      groups: [...toArray(prev.groups), createOfferGroupDraft()],
    }));
    setOfferGroupDialog({ open: false, mode: 'choice', copySearch: '' });
  };

  const addOfferCopiedGroup = (sharedGroup) => {
    if (!sharedGroup) return;
    const draft = createCopiedOfferGroupDraft(sharedGroup);
    if (!toArray(draft.options).length) {
      showToast('Grupo importado sem complementos para reutilizar.', 'warning');
      return;
    }
    const alreadyLinked = toArray(offerForm.groups).some((group) => group.source === 'copied' && group.id === draft.id);
    if (alreadyLinked) {
      showToast('Este grupo ja esta no produto em criacao.', 'info');
      return;
    }
    setOfferForm((prev) => ({
      ...prev,
      groups: [...toArray(prev.groups), draft],
    }));
    setOfferGroupDialog({ open: false, mode: 'choice', copySearch: '' });
  };

  const addOfferGroupOption = (groupDraftId) => {
    setOfferForm((prev) => ({
      ...prev,
      groups: toArray(prev.groups).map((group) => (
        group.draftId === groupDraftId
          ? {
            ...group,
            max: Math.max(Number(group.max || 1), toArray(group.options).length + 1),
            options: [...toArray(group.options), createOfferOptionDraft()],
          }
          : group
      )),
    }));
  };

  const removeOfferGroup = (groupDraftId) => {
    setOfferForm((prev) => ({
      ...prev,
      groups: toArray(prev.groups).filter((group) => group.draftId !== groupDraftId),
    }));
  };

  const removeOfferGroupOption = (groupDraftId, optionDraftId) => {
    setOfferForm((prev) => ({
      ...prev,
      groups: toArray(prev.groups).map((group) => (
        group.draftId === groupDraftId
          ? {
            ...group,
            options: toArray(group.options).filter((option) => option.draftId !== optionDraftId),
          }
          : group
      )),
    }));
  };

  const uploadOfferImage = async (file) => {
    const dataUrl = await readImageFileAsFourByThreeDataUrl(file);
    if (!dataUrl) return;
    const result = await runAction({
      id: 'stock-offer-image-upload',
      path: '/image/upload',
      payload: { image: dataUrl },
      label: 'Imagem do produto enviada ao iFood.',
      checkId: 'image-upload',
    });
    const uploadedPath = extractImagePath(result?.data);
    if (!result?.ok || !uploadedPath) return;
    updateOfferForm('imagePath', uploadedPath);
  };

  const uploadOfferGroupOptionImage = async (groupDraftId, optionDraftId, file) => {
    const dataUrl = await readImageFileAsFourByThreeDataUrl(file);
    if (!dataUrl) return;
    const result = await runAction({
      id: 'stock-offer-option-image-upload',
      path: '/image/upload',
      payload: { image: dataUrl },
      label: 'Imagem do complemento enviada ao iFood.',
      checkId: 'image-upload',
    });
    const uploadedPath = extractImagePath(result?.data);
    if (!result?.ok || !uploadedPath) return;
    updateOfferGroupOptionDraft(groupDraftId, optionDraftId, 'imagePath', uploadedPath);
    updateOfferGroupOptionDraft(groupDraftId, optionDraftId, 'imageUrl', buildIfoodImageUrl(uploadedPath));
  };

  const uploadStockProductImage = async (file) => {
    const dataUrl = await readImageFileAsFourByThreeDataUrl(file);
    if (!dataUrl) return;
    const result = await runAction({
      id: 'stock-product-image-upload',
      path: '/image/upload',
      payload: { image: dataUrl },
      label: 'Imagem do produto enviada ao iFood.',
      checkId: 'image-upload',
    });
    const uploadedPath = extractImagePath(result?.data);
    if (!result?.ok || !uploadedPath) return;
    setStockProductForm((prev) => ({ ...prev, imagePath: uploadedPath }));
  };

  const closePizzaCategoryDialog = () => {
    setPizzaCategoryDialog({ open: false });
    setPizzaCategoryForm(createEmptyPizzaCategoryForm());
  };

  const updatePizzaCategoryForm = (field, value) => {
    setPizzaCategoryForm((prev) => ({ ...prev, [field]: value }));
  };

  const goToPizzaCategoryTab = (tab) => {
    setPizzaCategoryForm((prev) => ({ ...prev, tab }));
  };

  const goToNextPizzaCategoryTab = () => {
    const currentIndex = pizzaCategoryTabs.findIndex((item) => item.value === pizzaCategoryForm.tab);
    const nextTab = pizzaCategoryTabs[Math.min(currentIndex + 1, pizzaCategoryTabs.length - 1)]?.value;
    if (!nextTab) return;
    if (pizzaCategoryForm.tab === 'details' && !cleanText(pizzaCategoryForm.name)) {
      showToast('Informe o nome da categoria Pizza para continuar.', 'warning');
      return;
    }
    goToPizzaCategoryTab(nextTab);
  };

  const updatePizzaSizeDraft = (sizeId, field, value) => {
    setPizzaCategoryForm((prev) => ({
      ...prev,
      sizes: toArray(prev.sizes).map((size) => (
        size.id === sizeId ? { ...size, [field]: value } : size
      )),
    }));
  };

  const addPizzaSizeDraft = () => {
    setPizzaCategoryForm((prev) => ({
      ...prev,
      sizes: [...toArray(prev.sizes), createPizzaSizeDraft({ name: 'Novo tamanho', slices: '8', maxFlavors: 1 })],
    }));
  };

  const removePizzaSizeDraft = (sizeId) => {
    setPizzaCategoryForm((prev) => ({
      ...prev,
      sizes: toArray(prev.sizes).filter((size) => size.id !== sizeId),
    }));
  };

  const uploadPizzaSizeImage = async (sizeId, file) => {
    const dataUrl = await readImageFileAsFourByThreeDataUrl(file);
    if (!dataUrl) return;
    const result = await runAction({
      id: `pizza-size-image-upload-${sizeId}`,
      path: '/image/upload',
      payload: { image: dataUrl },
      label: 'Imagem do tamanho enviada ao iFood.',
      checkId: 'image-upload',
    });
    const uploadedPath = extractImagePath(result?.data);
    if (!result?.ok || !uploadedPath) return;
    updatePizzaSizeDraft(sizeId, 'imagePath', uploadedPath);
  };

  const updatePizzaOptionDraft = (kind, optionId, field, value) => {
    const fieldName = kind === 'edge' ? 'edges' : 'doughs';
    setPizzaCategoryForm((prev) => ({
      ...prev,
      [fieldName]: toArray(prev[fieldName]).map((option) => (
        option.id === optionId ? { ...option, [field]: value } : option
      )),
    }));
  };

  const addPizzaOptionDraft = (kind) => {
    const fieldName = kind === 'edge' ? 'edges' : 'doughs';
    setPizzaCategoryForm((prev) => ({
      ...prev,
      [fieldName]: [
        ...toArray(prev[fieldName]),
        createPizzaOptionDraft({ name: kind === 'edge' ? 'Nova borda' : 'Nova massa' }),
      ],
    }));
  };

  const removePizzaOptionDraft = (kind, optionId) => {
    const fieldName = kind === 'edge' ? 'edges' : 'doughs';
    setPizzaCategoryForm((prev) => ({
      ...prev,
      [fieldName]: toArray(prev[fieldName]).filter((option) => option.id !== optionId),
    }));
  };

  const togglePizzaAvailabilityDay = (dayKey) => {
    setPizzaCategoryForm((prev) => ({
      ...prev,
      availabilityDrafts: toArray(prev.availabilityDrafts).map((draft) => (
        draft.key === dayKey ? { ...draft, enabled: !draft.enabled } : draft
      )),
    }));
  };

  const updatePizzaAvailabilitySlot = (dayKey, slotId, field, value) => {
    setPizzaCategoryForm((prev) => ({
      ...prev,
      availabilityDrafts: toArray(prev.availabilityDrafts).map((draft) => (
        draft.key === dayKey
          ? {
            ...draft,
            slots: toArray(draft.slots).map((slot) => (
              slot.id === slotId ? { ...slot, [field]: value } : slot
            )),
          }
          : draft
      )),
    }));
  };

  const addPizzaAvailabilitySlot = (dayKey) => {
    setPizzaCategoryForm((prev) => ({
      ...prev,
      availabilityDrafts: toArray(prev.availabilityDrafts).map((draft) => (
        draft.key === dayKey
          ? { ...draft, enabled: true, slots: [...toArray(draft.slots), createPizzaSlot()] }
          : draft
      )),
    }));
  };

  const removePizzaAvailabilitySlot = (dayKey, slotId) => {
    setPizzaCategoryForm((prev) => ({
      ...prev,
      availabilityDrafts: toArray(prev.availabilityDrafts).map((draft) => {
        if (draft.key !== dayKey) return draft;
        const nextSlots = toArray(draft.slots).filter((slot) => slot.id !== slotId);
        return { ...draft, slots: nextSlots.length ? nextSlots : [createPizzaSlot()] };
      }),
    }));
  };

  const buildPizzaAvailabilityPayload = () => {
    if (pizzaCategoryForm.availabilityMode !== 'scheduled') return [];
    return toArray(pizzaCategoryForm.availabilityDrafts).flatMap((draft) => (
      draft.enabled
        ? toArray(draft.slots)
          .filter((slot) => cleanText(slot.startTime) && cleanText(slot.endTime) && slot.startTime < slot.endTime)
          .map((slot) => ({
            [draft.key]: true,
            startTime: slot.startTime,
            endTime: slot.endTime,
          }))
        : []
    ));
  };

  const buildPizzaCategoryMetadata = () => ({
    type: 'PIZZA',
    sizes: toArray(pizzaCategoryForm.sizes).map((size, index) => ({
      ...size,
      sequence: index,
      slices: Number(cleanText(size.slices).replace(',', '.')) || 0,
      maxFlavors: Number(size.maxFlavors) || 1,
      imagePath: normalizeIfoodImagePathForPayload(size.imagePath),
    })),
    doughs: toArray(pizzaCategoryForm.doughs).map((item, index) => ({
      ...item,
      sequence: index,
      price: parseCatalogMoney(item.price),
    })),
    edges: toArray(pizzaCategoryForm.edges).map((item, index) => ({
      ...item,
      sequence: index,
      price: parseCatalogMoney(item.price),
    })),
    availabilityMode: pizzaCategoryForm.availabilityMode,
    shifts: buildPizzaAvailabilityPayload(),
  });

  const validatePizzaCategoryForm = () => {
    if (!selectedCatalogId) {
      showToast('Liste ou selecione um catalogo antes de criar a categoria Pizza.', 'warning');
      return false;
    }
    if (!cleanText(pizzaCategoryForm.name)) {
      showToast('Informe o nome da categoria Pizza.', 'warning');
      goToPizzaCategoryTab('details');
      return false;
    }
    if (!toArray(pizzaCategoryForm.sizes).length) {
      showToast('Cadastre pelo menos um tamanho de pizza.', 'warning');
      goToPizzaCategoryTab('sizes');
      return false;
    }
    const invalidSize = toArray(pizzaCategoryForm.sizes).find((size) => !cleanText(size.name) || !Number(cleanText(size.slices).replace(',', '.')));
    if (invalidSize) {
      showToast('Preencha nome e quantidade de pedacos em todos os tamanhos.', 'warning');
      goToPizzaCategoryTab('sizes');
      return false;
    }
    const invalidDough = toArray(pizzaCategoryForm.doughs).find((item) => !cleanText(item.name));
    if (invalidDough) {
      showToast('Preencha o nome de todas as massas.', 'warning');
      goToPizzaCategoryTab('doughs');
      return false;
    }
    const invalidEdge = toArray(pizzaCategoryForm.edges).find((item) => !cleanText(item.name));
    if (invalidEdge) {
      showToast('Preencha o nome de todas as bordas.', 'warning');
      goToPizzaCategoryTab('edges');
      return false;
    }
    if (pizzaCategoryForm.availabilityMode === 'scheduled' && !buildPizzaAvailabilityPayload().length) {
      showToast('Configure pelo menos um horario valido para a disponibilidade.', 'warning');
      goToPizzaCategoryTab('availability');
      return false;
    }
    return true;
  };

  const submitPizzaCategory = async () => {
    if (!validatePizzaCategoryForm()) return;
    const metadata = buildPizzaCategoryMetadata();
    const payload = {
      name: cleanText(pizzaCategoryForm.name),
      status: 'AVAILABLE',
      template: 'PIZZA',
      sequence: toArray(visualCatalog.categories).length,
    };
    const result = await runAction({
      id: 'pizza-category-create',
      path: '/categories/create',
      payload: { catalogId: selectedCatalogId, category: payload },
      label: 'Categoria Pizza criada no iFood.',
      visualImport: (response) => ({
        catalogId: selectedCatalogId,
        categories: [{
          id: response.data?.id || createUuid(),
          catalogId: selectedCatalogId,
          name: response.data?.name || payload.name,
          status: response.data?.status || payload.status,
          template: 'PIZZA',
          sequence: response.data?.sequence ?? payload.sequence,
          itemCount: 0,
          raw: {
            ...(response.data || payload),
            pizzaMetadata: metadata,
          },
        }],
      }),
    });
    if (result?.ok) closePizzaCategoryDialog();
  };

  const validateOfferProductWizard = () => {
    const name = cleanText(offerForm.name);
    const externalCode = cleanText(offerForm.externalCode);
    if (!name || !externalCode) {
      showToast('Informe nome e PDV para criar o produto.', 'warning');
      return false;
    }
    if (offerForm.categoryMode === 'new') {
      if (!selectedCatalogId) {
        showToast('Selecione ou liste um catalogo antes de criar nova categoria.', 'warning');
        return false;
      }
      if (!cleanText(offerForm.categoryName)) {
        showToast('Informe o nome da nova categoria.', 'warning');
        return false;
      }
    } else if (!cleanText(offerForm.categoryId)) {
      showToast('Selecione uma categoria para disponibilizar o produto.', 'warning');
      return false;
    }
    if (offerForm.stockControlEnabled) {
      const amount = Number(cleanText(offerForm.inventoryAmount).replace(',', '.'));
      if (!Number.isFinite(amount) || amount < 0) {
        showToast('Informe a quantidade de estoque do produto.', 'warning');
        return false;
      }
    }

    const invalidGroup = toArray(offerForm.groups).find((group) => {
      if (!cleanText(group.name)) return true;
      const options = toArray(group.options);
      const max = Number(group.max || 0);
      const min = Number(group.min || 0);
      if (min < 0 || max < 1 || min > max || !options.length) return true;
      if (group.source === 'copied') return false;
      return options.some((option) => !cleanText(option.name) || !cleanText(option.externalCode));
    });
    if (invalidGroup) {
      showToast('Revise grupos e complementos: nome, limites, complementos e PDV sao obrigatorios nos novos complementos.', 'warning');
      return false;
    }
    return true;
  };

  const resolveOfferCategoryForSubmit = async () => {
    if (offerForm.categoryMode === 'new') {
      const nextCategoryPayload = {
        name: cleanText(offerForm.categoryName),
        status: 'AVAILABLE',
        template: 'DEFAULT',
        sequence: toArray(visualCatalog.categories).length,
      };
      const result = await runAction({
        id: 'stock-offer-category-create',
        path: '/categories/create',
        payload: { catalogId: selectedCatalogId, category: nextCategoryPayload },
        label: 'Categoria criada para o produto.',
        checkId: 'category-create',
        visualImport: (response) => ({
          categories: [{
            id: response.data?.id || response.data?.categoryId || nextCategoryPayload.name,
            catalogId: selectedCatalogId,
            name: response.data?.name || nextCategoryPayload.name,
            status: response.data?.status || nextCategoryPayload.status,
            template: response.data?.template || nextCategoryPayload.template,
            sequence: response.data?.sequence ?? nextCategoryPayload.sequence,
            itemCount: 0,
            raw: response.data || nextCategoryPayload,
          }],
        }),
      });
      if (!result?.ok) return null;
      const categoryId = result.data?.id || result.data?.categoryId || nextCategoryPayload.name;
      const resolved = {
        id: categoryId,
        name: result.data?.name || nextCategoryPayload.name,
      };
      setSelectedCategoryId(categoryId);
      setOfferForm((prev) => ({
        ...prev,
        categoryMode: 'existing',
        categoryId,
        categoryName: resolved.name,
      }));
      return resolved;
    }

    const categoryId = cleanText(offerForm.categoryId);
    const resolved = toArray(visualCatalog.categories).find((item) => item.id === categoryId)
      || offerDialog.category
      || null;
    if (!resolved?.id) {
      showToast('Categoria invalida para criar produto.', 'warning');
      return null;
    }
    return resolved;
  };

  const buildOfferItemPayload = (nextCategory) => {
    const itemId = createUuid();
    const productId = createUuid();
    const price = parseCatalogMoney(offerForm.price);
    const originalPrice = parseCatalogMoney(offerForm.originalPrice) || price;
    const groups = toArray(offerForm.groups).map((group, groupIndex) => {
      const optionCount = toArray(group.options).length || 1;
      const max = Math.max(1, Number(group.max || optionCount));
      const min = Math.min(max, Math.max(0, Number(group.min || 0)));
      return {
        ...group,
        index: groupIndex,
        min,
        max,
        options: toArray(group.options).map((option, optionIndex) => ({
          ...option,
          index: optionIndex,
          status: normalizeCatalogStatus(option.status),
        })),
      };
    });
    const relationGroups = groups.map((group) => withCatalogOptionalFields(
      {
        id: group.id,
        min: group.min,
        max: group.max,
        index: group.index,
      },
      { associationType: group.associationType }
    ));
    const buildProductPayload = ({
      id,
      externalCode,
      name,
      description,
      additionalInformation,
      imagePath: nextImagePath,
      rawProduct = {},
      optionGroups = [],
    }) => withCatalogOptionalFields(
      {
        id,
        name: cleanText(name),
        description: cleanText(description),
      },
      {
        externalCode: cleanText(externalCode),
        additionalInformation: cleanText(additionalInformation),
        imagePath: normalizeIfoodImagePathForPayload(nextImagePath),
        ean: rawProduct.ean,
        serving: rawProduct.serving || 'SERVES_1',
        dietaryRestrictions: rawProduct.dietaryRestrictions || [],
        quantity: rawProduct.quantity || null,
        optionGroups,
      }
    );
    const optionProductMap = new Map();
    groups.forEach((group) => {
      group.options.forEach((option) => {
        if (!option.productId || optionProductMap.has(option.productId)) return;
        optionProductMap.set(option.productId, buildProductPayload({
          id: option.productId,
          externalCode: cleanText(option.productExternalCode),
          name: option.name,
          description: option.description || '',
          additionalInformation: option.rawProduct?.additionalInformation || option.description || '',
          imagePath: option.imagePath,
          rawProduct: option.rawProduct || {},
          optionGroups: option.rawProduct?.optionGroups || [],
        }));
      });
    });

    const payload = {
      item: withCatalogOptionalFields(
        {
          id: itemId,
          type: 'DEFAULT',
          categoryId: nextCategory.id,
          status: offerForm.stockEnabled ? 'AVAILABLE' : 'UNAVAILABLE',
          price: { value: price, originalValue: originalPrice },
          externalCode: cleanText(offerForm.externalCode),
          index: Number(toArray(visualCatalog.items).filter((item) => item.categoryId === nextCategory.id).length || 0),
          productId,
        },
        {
          externalCodeByCatalog: buildExternalCodeByCatalogPayload(cleanText(offerForm.externalCode), {
            ...nextCategory,
            catalogId: nextCategory.catalogId || selectedCatalogId,
          }),
          shifts: offerForm.availabilityMode === 'scheduled' ? toArray(offerForm.shifts) : null,
          tags: null,
        }
      ),
      products: [
        buildProductPayload({
          id: productId,
          externalCode: '',
          name: offerForm.name,
          description: offerForm.description || '',
          additionalInformation: offerForm.description || '',
          imagePath: offerForm.imagePath,
          rawProduct: {
            serving: offerForm.serving || 'SERVES_1',
            dietaryRestrictions: toArray(offerForm.dietaryRestrictions),
            quantity: buildCatalogQuantityPayload(offerForm.quantityValue, offerForm.quantityUnit),
          },
          optionGroups: relationGroups,
        }),
        ...Array.from(optionProductMap.values()),
      ],
      optionGroups: groups.map((group) => withCatalogOptionalFields(
        {
          id: group.id,
          name: group.name,
          status: normalizeCatalogStatus(group.status),
          index: group.index,
          optionGroupType: group.optionGroupType || 'DEFAULT',
          optionIds: group.options.map((option) => option.id),
        },
        { externalCode: group.externalCode }
      )),
      options: groups.flatMap((group) => group.options.map((option) => withCatalogOptionalFields(
        {
          id: option.id,
          status: normalizeCatalogStatus(option.status),
          index: option.index,
          productId: option.productId,
          price: {
            value: parseCatalogMoney(option.price),
            originalValue: parseCatalogMoney(option.originalPrice) || parseCatalogMoney(option.price),
          },
        },
        {
          contextModifiers: option.raw?.contextModifiers || [],
          fractions: option.raw?.fractions || null,
          externalCode: option.externalCode,
        }
      ))),
    };

    return { itemId, productId, groups, payload };
  };

  const submitOfferItem = async () => {
    if (!validateOfferProductWizard()) return;
    const nextCategory = await resolveOfferCategoryForSubmit();
    if (!nextCategory?.id) return;

    const { itemId, productId, payload, groups } = buildOfferItemPayload(nextCategory);
    const firstOption = groups.flatMap((group) => group.options)[0] || {};

    setRefs((prev) => ({
      ...prev,
      itemId,
      itemProductId: productId,
      itemExternalCode: cleanText(offerForm.externalCode),
      optionGroupId: groups[0]?.id || '',
      optionId: firstOption.id || '',
      optionProductId: firstOption.productId || '',
      optionExternalCode: firstOption.externalCode || '',
    }));
    setItemPayload(stringifyJson(payload));
    setPatchPayloads(buildPatchPayloads({
      itemId,
      itemProductId: productId,
      optionId: firstOption.id || '',
      itemExternalCode: cleanText(offerForm.externalCode),
      optionExternalCode: firstOption.externalCode || '',
    }));

    const result = await runAction({
      id: 'stock-offer-create',
      path: '/items/upsert',
      payload: { payload },
      label: 'Produto criado no iFood.',
      checkId: 'item-upsert',
      visualImport: () => normalizeVisualFromFlat(payload, {
        ...catalogScope,
        catalogId: nextCategory.catalogId || selectedCatalogId,
        catalogContext: findCatalogContext(visualCatalog.catalogs, nextCategory.catalogId || selectedCatalogId),
        categoryId: nextCategory.id,
        categoryName: nextCategory.name,
      }),
    });
    if (result?.ok) {
      const synced = await syncItemExternalCodeAfterUpsert({
        itemId,
        productId,
        externalCode: cleanText(offerForm.externalCode),
        source: {
          ...nextCategory,
          productId,
          productExternalCode: '',
          catalogId: nextCategory.catalogId || selectedCatalogId,
        },
        actionId: 'stock-offer-external-code',
      });
      if (!synced) return;
    }
    if (result?.ok && offerForm.stockControlEnabled) {
      const amount = Number(cleanText(offerForm.inventoryAmount).replace(',', '.'));
      if (Number.isFinite(amount) && amount >= 0) {
        await runAction({
          id: 'stock-offer-inventory-create',
          path: '/inventory/create',
          payload: { payload: { productId, amount } },
          label: 'Controle de estoque do produto criado no iFood.',
        });
        setVisualCatalog((prev) => ({
          ...prev,
          items: toArray(prev.items).map((item) => (
            item.id === itemId ? { ...item, stock: amount } : item
          )),
          lastImportedAt: new Date().toISOString(),
          source: 'Produto criado com controle de estoque',
        }));
      }
    }
    if (result?.ok) closeOfferDialog();
  };

  const updateComboForm = (field, value) => {
    setComboForm((prev) => ({ ...prev, [field]: value }));
  };

  const uploadComboImage = async (file) => {
    const dataUrl = await readImageFileAsFourByThreeDataUrl(file);
    if (!dataUrl) return;
    const result = await runAction({
      id: 'stock-combo-image-upload',
      path: '/image/upload',
      payload: { image: dataUrl },
      label: 'Imagem do combo enviada ao iFood.',
      checkId: 'image-upload',
    });
    const uploadedPath = extractImagePath(result?.data);
    if (!result?.ok || !uploadedPath) return;
    updateComboForm('imagePath', uploadedPath);
  };

  const createComboSelectionFromProduct = (product, index = 0, source = {}) => ({
    id: cleanText(source.id) || createUuid(),
    itemId: cleanText(product?.id || source.itemId || source.productId || `combo-option-${index + 1}`),
    productId: cleanText(product?.productId || source.productId || ''),
    name: cleanText(firstValue(product?.name, source.name, 'Produto')),
    description: cleanText(firstValue(product?.description, source.description, '')),
    categoryName: cleanText(firstValue(product?.categoryName, source.categoryName, '')),
    imagePath: cleanText(firstValue(product?.imagePath, source.imagePath, '')),
    imageUrl: firstValue(product?.imageUrl, source.imageUrl, buildIfoodImageUrl(source.imagePath), ''),
    price: formatStockMoneyInput(firstFiniteNumber(product?.price, source.price, 0)),
    optionPrice: formatStockMoneyInput(firstFiniteNumber(source.optionPrice, source.price, product?.price, 0)),
    optionExternalCode: cleanText(firstValue(source.optionExternalCode, source.externalCode, product?.externalCode, '')),
    externalCode: cleanText(firstValue(product?.externalCode, source.externalCode, '')),
    status: normalizeCatalogStatus(firstValue(source.status, product?.status, 'AVAILABLE')),
    rawProduct: source.rawProduct || product?.raw?.product || {},
    raw: source.raw || product?.raw || {},
    product,
  });

  const getReusableComboGroupOptions = (sharedGroup) => {
    const sourceGroups = toArray(sharedGroup?.linkedGroups).length
      ? toArray(sharedGroup.linkedGroups)
      : [sharedGroup || {}];
    const allowedProductIds = new Set(availableComboProducts.map((product) => cleanText(product.productId)).filter(Boolean));
    const options = sourceGroups.flatMap((sourceGroup) => allStockComplementEntries.filter((option) => (
      option.groupId === sourceGroup.id && option.itemId === sourceGroup.itemId
    )));
    return dedupeByKey(options, 'combo-option', getSharedComplementKey)
      .map((option) => {
        const product = allStockProducts.find((item) => cleanText(item.productId) === cleanText(option.productId)) || {};
        return { option, product };
      })
      .filter(({ option, product }) => (
        cleanText(option.productId)
        && allowedProductIds.has(cleanText(option.productId))
        && !isPausedCatalogStatus(option.status)
        && !isPausedCatalogStatus(product.status)
        && !isPausedCatalogStatus(product.categoryStatus)
      ))
      .map(({ option, product }, index) => (
        createComboSelectionFromProduct(product, index, {
          id: option.id,
          itemId: product.id || option.productId,
          productId: option.productId,
          name: option.name,
          description: option.description,
          status: option.status,
          categoryName: option.categoryName,
          imagePath: option.imagePath,
          imageUrl: option.imageUrl,
          price: option.price,
          optionPrice: option.price,
          externalCode: option.externalCode,
          optionExternalCode: option.externalCode,
          rawProduct: option.rawProduct || {},
          raw: option.raw || {},
        })
      ));
  };

  const createComboGroupDraft = ({
    kind = 'main',
    source = 'new',
    sourceGroup = {},
    name = '',
    max = 1,
    options = [],
  } = {}) => {
    const optionCount = toArray(options).length || 1;
    const nextMax = Math.max(1, Math.min(Math.max(1, Number(max || optionCount)), optionCount));
    return {
      draftId: createUuid(),
      id: cleanText(sourceGroup.id) || createUuid(),
      kind,
      source,
      sourceItemId: cleanText(sourceGroup.itemId || ''),
      sharedGroupKey: sourceGroup.sharedGroupKey || getSharedOptionGroupKey(sourceGroup),
      name: cleanText(name || sourceGroup.name || (kind === 'main' ? comboForm.groupName : 'Produtos adicionais')),
      externalCode: cleanText(sourceGroup.externalCode || ''),
      status: normalizeCatalogStatus(sourceGroup.status || 'AVAILABLE'),
      min: kind === 'main' ? 1 : 0,
      max: nextMax,
      optionGroupType: cleanText(firstValue(sourceGroup.optionGroupType, sourceGroup.raw?.optionGroupType, 'OFFER_UNIT')),
      associationType: kind === 'main' ? 'MAIN' : '',
      options: toArray(options),
      availableIn: sourceGroup.availableIn || '',
    };
  };

  const closeComboDrawer = () => {
    setComboDrawer(emptyComboDrawer);
  };

  const openComboProductDrawer = (kind = 'main', groupDraftId = '') => {
    const current = comboGroups.find((group) => group.draftId === groupDraftId)
      || (kind === 'main' ? comboGroups.find((group) => group.kind === 'main') : null);
    setComboDrawer({
      ...emptyComboDrawer,
      open: true,
      mode: 'products',
      groupKind: kind,
      groupDraftId: current?.draftId || groupDraftId,
      selectedIds: toArray(current?.options).map((option) => option.itemId).filter(Boolean),
      groupName: current?.name || (kind === 'main' ? comboForm.groupName : 'Produtos adicionais'),
      max: Math.max(1, Number(current?.max || 1)),
    });
  };

  const openComboGroupDrawer = () => {
    setComboDrawer({
      ...emptyComboDrawer,
      open: true,
      mode: 'groups',
      groupKind: 'optional',
    });
  };

  const toggleComboDrawerProduct = (productId) => {
    setComboDrawer((prev) => {
      const selectedIds = toArray(prev.selectedIds);
      const exists = selectedIds.includes(productId);
      const nextIds = exists ? selectedIds.filter((item) => item !== productId) : [...selectedIds, productId];
      return {
        ...prev,
        selectedIds: nextIds,
        max: Math.max(1, Math.min(Number(prev.max || 1), Math.max(1, nextIds.length))),
      };
    });
  };

  const saveComboProductDrawer = () => {
    const selectedProducts = dedupeByKey(
      toArray(comboDrawer.selectedIds)
        .map((itemId) => availableComboProducts.find((product) => product.id === itemId))
        .filter((product) => product?.productId),
      'combo-product',
      (product) => product.productId
    );
    const groupName = cleanText(comboDrawer.groupName);
    if (!selectedProducts.length) {
      showToast('Selecione ao menos um produto principal para o grupo.', 'warning');
      return;
    }
    if (!groupName) {
      showToast('Informe o nome dos produtos agrupados.', 'warning');
      return;
    }
    const max = Math.max(1, Math.min(Number(comboDrawer.max || 1), selectedProducts.length));
    const current = comboGroups.find((group) => group.draftId === comboDrawer.groupDraftId);
    const options = selectedProducts.map((product, index) => {
      const previous = toArray(current?.options).find((option) => option.itemId === product.id || option.productId === product.productId);
      return createComboSelectionFromProduct(product, index, previous || {});
    });
    const nextGroup = {
      ...(current || createComboGroupDraft({ kind: comboDrawer.groupKind, options })),
      kind: comboDrawer.groupKind,
      source: 'new',
      name: groupName,
      min: comboDrawer.groupKind === 'main' ? 1 : 0,
      max,
      associationType: comboDrawer.groupKind === 'main' ? 'MAIN' : '',
      optionGroupType: 'OFFER_UNIT',
      options,
    };

    setComboGroups((prev) => {
      const withoutMain = comboDrawer.groupKind === 'main'
        ? toArray(prev).filter((group) => group.kind !== 'main' && group.draftId !== nextGroup.draftId)
        : toArray(prev).filter((group) => group.draftId !== nextGroup.draftId);
      return comboDrawer.groupKind === 'main'
        ? [nextGroup, ...withoutMain]
        : [...withoutMain, nextGroup];
    });
    if (comboDrawer.groupKind === 'main') {
      setComboForm((prev) => ({ ...prev, groupName, min: 1, max }));
    }
    closeComboDrawer();
  };

  const addComboCopiedGroup = (sharedGroup) => {
    const options = getReusableComboGroupOptions(sharedGroup);
    if (!options.length) {
      showToast('Grupo importado sem produtos para reutilizar no combo.', 'warning');
      return;
    }
    const alreadyLinked = comboGroups.some((group) => group.source === 'copied' && group.id === sharedGroup.id);
    if (alreadyLinked) {
      showToast('Este grupo ja esta no combo.', 'info');
      return;
    }
    const sourceGroup = toArray(sharedGroup?.linkedGroups)[0] || sharedGroup || {};
    const nextGroup = createComboGroupDraft({
      kind: 'optional',
      source: 'copied',
      sourceGroup: { ...sourceGroup, ...sharedGroup },
      name: sharedGroup?.name || sourceGroup.name || 'Grupo adicional',
      max: firstFiniteNumber(sourceGroup.max, sharedGroup?.max, options.length, 1),
      options,
    });
    setComboGroups((prev) => [...toArray(prev), nextGroup]);
    closeComboDrawer();
  };

  const removeComboGroup = (groupDraftId) => {
    setComboGroups((prev) => toArray(prev).filter((group) => group.draftId !== groupDraftId));
  };

  const updateComboGroupOption = (groupDraftId, optionId, field, value) => {
    setComboGroups((prev) => toArray(prev).map((group) => (
      group.draftId === groupDraftId
        ? {
          ...group,
          options: toArray(group.options).map((option) => (
            option.id === optionId || option.itemId === optionId
              ? { ...option, [field]: value }
              : option
          )),
        }
        : group
    )));
  };

  const getComboStartingPrice = (groups = comboGroups) => toArray(groups).reduce((total, group) => {
    const min = Math.max(0, Number(group.min || 0));
    if (!min) return total;
    const prices = toArray(group.options)
      .map((option) => parseCatalogMoney(option.optionPrice))
      .sort((left, right) => left - right);
    return total + prices.slice(0, min).reduce((sum, price) => sum + price, 0);
  }, 0);

  const buildComboSchedulePayload = (drafts = []) => toArray(drafts)
    .filter((draft) => draft.enabled)
    .map((draft) => ({
      startTime: draft.startTime,
      endTime: draft.endTime,
      sunday: draft.key === 'sunday',
      monday: draft.key === 'monday',
      tuesday: draft.key === 'tuesday',
      wednesday: draft.key === 'wednesday',
      thursday: draft.key === 'thursday',
      friday: draft.key === 'friday',
      saturday: draft.key === 'saturday',
    }));

  const openComboScheduleDialog = () => {
    setComboScheduleDialog({
      open: true,
      drafts: createComboScheduleDrafts(comboForm.shifts),
    });
  };

  const updateComboScheduleDraft = (dayKey, field, value) => {
    setComboScheduleDialog((prev) => ({
      ...prev,
      drafts: toArray(prev.drafts).map((draft) => (
        draft.key === dayKey ? { ...draft, [field]: value } : draft
      )),
    }));
  };

  const applyComboSchedule = () => {
    const enabledDrafts = toArray(comboScheduleDialog.drafts).filter((draft) => draft.enabled);
    if (!enabledDrafts.length) {
      showToast('Selecione ao menos um dia para disponibilidade especifica.', 'warning');
      return;
    }
    const invalid = enabledDrafts.some((draft) => !draft.startTime || !draft.endTime || draft.startTime >= draft.endTime);
    if (invalid) {
      showToast('Revise os horarios: o fim deve ser maior que o inicio.', 'warning');
      return;
    }
    const shifts = buildComboSchedulePayload(enabledDrafts);
    setComboForm((prev) => ({ ...prev, availabilityMode: 'scheduled', shifts }));
    setComboScheduleDialog({ open: false, drafts: createComboScheduleDrafts(shifts) });
  };

  const openProductScheduleDialog = (source = 'offer') => {
    const form = source === 'edit' ? stockProductForm : offerForm;
    setProductScheduleDialog({
      open: true,
      source,
      mode: form.availabilityMode || (toArray(form.shifts).length ? 'scheduled' : 'always'),
      drafts: createComboScheduleDrafts(form.shifts),
    });
  };

  const closeProductScheduleDialog = () => {
    const form = productScheduleDialog.source === 'edit' ? stockProductForm : offerForm;
    setProductScheduleDialog({
      open: false,
      source: 'offer',
      mode: 'always',
      drafts: createComboScheduleDrafts(form.shifts),
    });
  };

  const updateProductScheduleDraft = (dayKey, field, value) => {
    setProductScheduleDialog((prev) => ({
      ...prev,
      drafts: toArray(prev.drafts).map((draft) => (
        draft.key === dayKey ? { ...draft, [field]: value } : draft
      )),
    }));
  };

  const applyProductScheduleDialog = () => {
    const source = productScheduleDialog.source;
    if (productScheduleDialog.mode !== 'scheduled') {
      const updater = (prev) => ({ ...prev, availabilityMode: 'always', shifts: [] });
      if (source === 'edit') setStockProductForm(updater);
      else setOfferForm(updater);
      closeProductScheduleDialog();
      return;
    }

    const enabledDrafts = toArray(productScheduleDialog.drafts).filter((draft) => draft.enabled);
    if (!enabledDrafts.length) {
      showToast('Selecione ao menos um dia para disponibilidade especifica.', 'warning');
      return;
    }
    const invalid = enabledDrafts.some((draft) => !draft.startTime || !draft.endTime || draft.startTime >= draft.endTime);
    if (invalid) {
      showToast('Revise os horarios: o fim deve ser maior que o inicio.', 'warning');
      return;
    }
    const shifts = buildComboSchedulePayload(enabledDrafts);
    const updater = (prev) => ({ ...prev, availabilityMode: 'scheduled', shifts });
    if (source === 'edit') setStockProductForm(updater);
    else setOfferForm(updater);
    setProductScheduleDialog({
      open: false,
      source: 'offer',
      mode: 'always',
      drafts: createComboScheduleDrafts(shifts),
    });
  };

  const validateComboWizard = () => {
    const mainGroups = comboGroups.filter((group) => group.kind === 'main');
    if (mainGroups.length !== 1) {
      showToast('O combo precisa ter exatamente um grupo principal.', 'warning');
      return false;
    }
    const mainGroup = mainGroups[0];
    if (!toArray(mainGroup.options).length) {
      showToast('Selecione produtos principais para o combo.', 'warning');
      return false;
    }
    if (Number(mainGroup.max || 1) > toArray(mainGroup.options).length) {
      showToast('A quantidade maxima do grupo principal nao pode superar os produtos selecionados.', 'warning');
      return false;
    }
    if (!comboDialog.category?.id) {
      showToast('Categoria invalida para criar combo.', 'warning');
      return false;
    }
    if (!cleanText(comboForm.name) || !cleanText(comboForm.externalCode)) {
      showToast('Informe nome e codigo PDV do combo.', 'warning');
      return false;
    }
    if (comboForm.priceMode === 'combo' && parseCatalogMoney(comboForm.price) <= 0) {
      showToast('Informe o preco do combo.', 'warning');
      return false;
    }
    if (comboForm.availabilityMode === 'scheduled' && !toArray(comboForm.shifts).length) {
      showToast('Configure os dias e horarios especificos ou use sempre disponivel.', 'warning');
      return false;
    }
    return true;
  };

  const buildComboProductPayloadFromSelection = (selection) => {
    const product = selection.product || allStockProducts.find((item) => cleanText(item.productId) === cleanText(selection.productId)) || {};
    if (product?.productId) return buildCatalogProductPayload(product);
    return withCatalogOptionalFields(
      {
        id: selection.productId,
        name: selection.name,
        description: selection.description || '',
      },
      {
        externalCode: cleanText(firstValue(selection.externalCode, selection.optionExternalCode, '')),
        additionalInformation: selection.description || '',
        imagePath: normalizeIfoodImagePathForPayload(selection.imagePath),
        serving: selection.rawProduct?.serving || 'SERVES_1',
        dietaryRestrictions: selection.rawProduct?.dietaryRestrictions || [],
        quantity: selection.rawProduct?.quantity || null,
      }
    );
  };

  const submitComboItem = async () => {
    if (!validateComboWizard()) return;

    const nextCategory = comboDialog.category;
    const itemId = createUuid();
    const itemProductId = createUuid();
    const externalCode = cleanText(comboForm.externalCode);
    const groups = toArray(comboGroups).map((group, groupIndex) => {
      const options = dedupeByKey(toArray(group.options), 'combo-option', (option) => option.productId || option.id);
      const max = Math.max(1, Math.min(Number(group.max || options.length || 1), Math.max(1, options.length)));
      return {
        ...group,
        index: groupIndex,
        min: group.kind === 'main' ? 1 : 0,
        max,
        options: options.map((option, optionIndex) => ({
          ...option,
          index: optionIndex,
          id: cleanText(option.id) || createUuid(),
          productId: cleanText(option.productId),
        })),
      };
    });
    const mainGroupCount = groups.filter((group) => group.kind === 'main').length;
    if (mainGroupCount !== 1) {
      showToast('Nao foi possivel montar o combo. Reabra a criacao e selecione o grupo principal novamente.', 'warning');
      return;
    }

    const relationGroups = groups.map((group) => withCatalogOptionalFields(
      {
        id: group.id,
        min: group.min,
        max: group.max,
        index: group.index,
      },
      { associationType: group.kind === 'main' ? 'MAIN' : null }
    ));
    const productMap = new Map();
    groups.flatMap((group) => group.options).forEach((selection) => {
      if (!selection.productId || productMap.has(selection.productId)) return;
      productMap.set(selection.productId, buildComboProductPayloadFromSelection(selection));
    });

    const computedPrice = getComboStartingPrice(groups);
    const price = comboForm.priceMode === 'products' ? computedPrice : parseCatalogMoney(comboForm.price);
    const originalPrice = parseCatalogMoney(comboForm.originalPrice) || price;
    const payload = {
      item: withCatalogOptionalFields(
        {
          id: itemId,
          type: 'COMBO_V2',
          categoryId: nextCategory.id,
          status: 'AVAILABLE',
          price: { value: price, originalValue: originalPrice },
          externalCode,
          index: Number(toArray(visualCatalog.items).filter((item) => item.categoryId === nextCategory.id).length || 0),
          productId: itemProductId,
        },
        {
          shifts: comboForm.availabilityMode === 'scheduled' ? comboForm.shifts : null,
          tags: null,
        }
      ),
      products: [
        withCatalogOptionalFields(
          {
            id: itemProductId,
            externalCode,
            name: cleanText(comboForm.name),
            description: cleanText(comboForm.description),
          },
          {
            additionalInformation: cleanText(comboForm.description),
            imagePath: normalizeIfoodImagePathForPayload(comboForm.imagePath),
            ean: '',
            serving: 'SERVES_1',
            dietaryRestrictions: [],
            quantity: null,
            optionGroups: relationGroups,
          }
        ),
        ...Array.from(productMap.values()),
      ],
      optionGroups: groups.map((group) => withCatalogOptionalFields(
        {
          id: group.id,
          name: group.name,
          status: normalizeCatalogStatus(group.status),
          index: group.index,
          optionGroupType: group.optionGroupType || 'OFFER_UNIT',
          optionIds: group.options.map((option) => option.id),
        },
        { externalCode: group.externalCode || `${externalCode}-grupo-${group.index + 1}` }
      )),
      options: groups.flatMap((group) => group.options.map((option) => withCatalogOptionalFields(
        {
          id: option.id,
          status: normalizeCatalogStatus(option.status || 'AVAILABLE'),
          index: option.index,
          productId: option.productId,
          price: { value: parseCatalogMoney(option.optionPrice) },
        },
        {
          contextModifiers: option.raw?.contextModifiers || [],
          fractions: option.raw?.fractions || null,
          externalCode: cleanText(option.optionExternalCode) || `${externalCode}-opcao-${group.index + 1}-${option.index + 1}`,
        }
      ))),
    };

    const firstGroup = groups[0] || {};
    const firstOption = toArray(firstGroup.options)[0] || {};
    setRefs((prev) => ({
      ...prev,
      itemId,
      itemProductId,
      optionGroupId: firstGroup.id || '',
      optionId: firstOption.id || '',
      optionProductId: firstOption.productId || '',
      itemExternalCode: externalCode,
      optionExternalCode: cleanText(firstOption.optionExternalCode),
    }));
    setItemPayload(stringifyJson(payload));
    setPatchPayloads(buildPatchPayloads({
      itemId,
      itemProductId,
      optionId: firstOption.id || '',
      itemExternalCode: externalCode,
      optionExternalCode: cleanText(firstOption.optionExternalCode),
    }));

    const result = await runAction({
      id: 'stock-combo-create',
      path: '/items/upsert',
      payload: { payload },
      label: 'Combo criado no iFood.',
      checkId: 'item-upsert',
      visualImport: () => normalizeVisualFromFlat(payload, {
        ...catalogScope,
        catalogId: nextCategory.catalogId || selectedCatalogId,
        catalogContext: findCatalogContext(visualCatalog.catalogs, nextCategory.catalogId || selectedCatalogId),
        categoryId: nextCategory.id,
        categoryName: nextCategory.name,
      }),
    });
    if (result?.ok) closeComboDialog();
  };

  const refreshStockProductDetails = async (product, { openDialog = false } = {}) => {
    if (!product?.id) {
      showToast('Produto sem identificador para atualizar detalhes.', 'warning');
      return null;
    }

    const result = await runAction({
      id: 'stock-item-flat',
      path: '/items/flat',
      payload: { itemId: product.id },
      label: 'Produto e complementos atualizados do iFood.',
      visualImport: (response) => normalizeVisualFromFlat(response.data, {
        ...catalogScope,
        catalogId: product.catalogId || selectedCatalogId,
        catalogContext: product.catalogContext || product.priceCatalogContext || findCatalogContext(visualCatalog.catalogs, product.catalogId || selectedCatalogId),
        categoryId: product.categoryId,
        categoryName: product.categoryName,
      }),
    });
    if (openDialog) openStockProductDialog(product);
    return result;
  };

  const getStockInventoryKey = (product = {}) => cleanText(firstValue(product.productId, product.id));

  const hasStockInventoryControl = (product = {}) => hasCatalogInventoryControl(product);

  const getStockInventoryDraftValue = (product) => {
    const inventoryKey = getStockInventoryKey(product);
    if (stockInventoryDrafts[inventoryKey] !== undefined) return stockInventoryDrafts[inventoryKey];
    return hasStockInventoryControl(product) ? String(product.stock) : '';
  };

  const updateStockInventoryLocally = (product, amount) => {
    setVisualCatalog((prev) => ({
      ...prev,
      items: toArray(prev.items).map((item) => (
        item.id === product.id ? { ...item, stock: amount } : item
      )),
      lastImportedAt: new Date().toISOString(),
      source: amount === null ? 'Controle de estoque removido no iFood' : 'Controle de estoque atualizado no iFood',
    }));
  };

  const openStockInventoryControl = async (product) => {
    if (!product?.productId) {
      showToast('Produto sem identificador de inventario no iFood.', 'warning');
      return;
    }
    const inventoryKey = getStockInventoryKey(product);
    setStockInventoryEditingIds((prev) => [...new Set([...prev, inventoryKey])]);
    setStockInventoryDrafts((prev) => ({
      ...prev,
      [inventoryKey]: hasStockInventoryControl(product) ? String(product.stock) : '',
    }));

    if (hasStockInventoryControl(product)) return;
    try {
      const result = await catalogFetch('/inventory/get', { productId: product.productId });
      const amount = firstValue(result.data?.amount, result.data?.stock, result.data?.quantity, result.data?.available);
      if (amount !== undefined) {
        updateStockInventoryLocally(product, amount);
        setStockInventoryDrafts((prev) => ({ ...prev, [inventoryKey]: String(amount) }));
      }
    } catch {
      setStockInventoryDrafts((prev) => ({ ...prev, [inventoryKey]: '' }));
    }
  };

  const closeStockInventoryControl = (inventoryKey) => {
    setStockInventoryEditingIds((prev) => prev.filter((item) => item !== inventoryKey));
    setStockInventoryDrafts((prev) => {
      const next = { ...prev };
      delete next[inventoryKey];
      return next;
    });
  };

  const saveStockInventoryControl = async (product) => {
    if (!product?.productId) {
      showToast('Produto sem identificador de inventario no iFood.', 'warning');
      return;
    }
    const inventoryKey = getStockInventoryKey(product);
    const rawAmount = cleanText(getStockInventoryDraftValue(product)).replace(',', '.');

    if (!rawAmount) {
      if (!hasStockInventoryControl(product)) {
        closeStockInventoryControl(inventoryKey);
        return;
      }
      const result = await runAction({
        id: 'stock-inventory-delete',
        path: '/inventory/delete',
        payload: { payload: { productIds: [product.productId] } },
        label: 'Controle de estoque removido no iFood.',
      });
      if (!result?.ok) return;
      updateStockInventoryLocally(product, null);
      closeStockInventoryControl(inventoryKey);
      return;
    }

    const amount = Number(rawAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      showToast('Informe uma quantidade de estoque valida.', 'warning');
      return;
    }

    const result = await runAction({
      id: 'stock-inventory-create',
      path: '/inventory/create',
      payload: { payload: { productId: product.productId, amount } },
      label: 'Controle de estoque atualizado no iFood.',
    });
    if (!result?.ok) return;
    updateStockInventoryLocally(product, amount);
    closeStockInventoryControl(inventoryKey);
  };

  const syncStockProductPrice = async (product, value, originalValue, label = 'Preco sincronizado no iFood.') => {
    if (!product?.id) {
      showToast('Produto sem identificador para atualizar preco.', 'warning');
      return;
    }
    const nextValue = Number(value || 0);
    const nextOriginalValue = Math.max(Number(originalValue || nextValue), nextValue);
    const result = await runAction({
      id: 'stock-row-item-price',
      path: '/items/price',
      payload: {
        payload: {
          itemId: product.id,
          price: { value: nextValue, originalValue: nextOriginalValue },
          priceByCatalog: buildPriceByCatalogPayload(nextValue, nextOriginalValue, product),
        },
      },
      label,
      checkId: 'item-price',
    });
    if (result?.ok) {
      setVisualCatalog((prev) => ({
        ...prev,
        items: toArray(prev.items).map((item) => (
          item.id === product.id
            ? {
              ...item,
              price: nextValue,
              originalPrice: nextOriginalValue,
              priceCatalogContext: resolveCatalogContextForExternalCode(product),
              raw: {
                ...(item.raw || {}),
                price: { value: nextValue, originalValue: nextOriginalValue },
                priceByCatalog: buildPriceByCatalogPayload(nextValue, nextOriginalValue, product),
              },
            }
            : item
        )),
        lastImportedAt: new Date().toISOString(),
        source: 'Preco do produto atualizado no iFood',
      }));
      if (selectedStockProduct?.id === product.id) {
        setStockProductForm((prev) => ({
          ...prev,
          price: formatMoneyInputValue(nextValue),
          originalPrice: nextOriginalValue > nextValue ? formatMoneyInputValue(nextOriginalValue) : '',
        }));
      }
    }
    return result;
  };

  const getPromotionProductSnapshot = (product = {}) => {
    const isEditingSelectedProduct = selectedStockProduct?.id && selectedStockProduct.id === product.id;
    if (!isEditingSelectedProduct) return product;
    const formPrice = cleanText(stockProductForm.price) ? parseCatalogMoney(stockProductForm.price) : Number(product.price || 0);
    const formOriginalPrice = cleanText(stockProductForm.originalPrice)
      ? parseCatalogMoney(stockProductForm.originalPrice)
      : formPrice;
    return {
      ...product,
      name: cleanText(stockProductForm.name) || product.name,
      description: cleanText(stockProductForm.description) || product.description,
      imagePath: cleanText(stockProductForm.imagePath) || product.imagePath,
      imageUrl: buildIfoodImageUrl(stockProductForm.imagePath) || product.imageUrl,
      price: formPrice,
      originalPrice: Math.max(formOriginalPrice, formPrice),
    };
  };

  const openProductPromotionDialog = (product) => {
    if (!product?.id) {
      showToast('Produto sem identificador para aplicar desconto.', 'warning');
      return;
    }
    const source = getPromotionProductSnapshot(product);
    const currentPrice = Number(source.price || 0);
    const currentOriginalPrice = Number(source.originalPrice || currentPrice);
    const basePrice = currentOriginalPrice > currentPrice ? currentOriginalPrice : currentPrice;
    const hasDiscount = basePrice > currentPrice;
    const discountPercent = hasDiscount && basePrice > 0
      ? ((basePrice - currentPrice) / basePrice) * 100
      : 0;
    setProductPromotionDialog({
      open: true,
      product: source,
      promotionalPrice: hasDiscount ? formatMoneyInputValue(currentPrice) : '',
      discountPercent: hasDiscount ? formatPercentInputValue(discountPercent) : '0',
      basePrice,
    });
  };

  const closeProductPromotionDialog = () => {
    setProductPromotionDialog(emptyProductPromotionDialog);
  };

  const updateProductPromotionDialog = (field, value) => {
    setProductPromotionDialog((prev) => {
      const basePrice = Number(prev.basePrice || 0);
      if (field === 'promotionalPrice') {
        const promotionalPrice = parseCatalogMoney(value);
        const discountPercent = basePrice > 0 && cleanText(value)
          ? Math.max(0, ((basePrice - promotionalPrice) / basePrice) * 100)
          : 0;
        return {
          ...prev,
          promotionalPrice: value,
          discountPercent: formatPercentInputValue(discountPercent),
        };
      }

      const discountPercent = Math.min(100, Math.max(0, parseCatalogPercent(value)));
      const promotionalPrice = basePrice > 0 ? Math.max(0, basePrice * (1 - (discountPercent / 100))) : 0;
      return {
        ...prev,
        discountPercent: value,
        promotionalPrice: formatMoneyInputValue(promotionalPrice),
      };
    });
  };

  const applyProductPromotion = async () => {
    const product = productPromotionDialog.product;
    const basePrice = Number(productPromotionDialog.basePrice || 0);
    const promotionalPrice = parseCatalogMoney(productPromotionDialog.promotionalPrice);
    if (!product?.id) {
      showToast('Produto sem identificador para aplicar desconto.', 'warning');
      return;
    }
    if (!basePrice || basePrice <= 0) {
      showToast('Informe o preco original antes de aplicar desconto.', 'warning');
      return;
    }
    if (!Number.isFinite(promotionalPrice) || promotionalPrice <= 0 || promotionalPrice >= basePrice) {
      showToast('Informe um preco promocional menor que o preco original.', 'warning');
      return;
    }

    const result = await syncStockProductPrice(
      product,
      promotionalPrice,
      basePrice,
      'Preco promocional aplicado no iFood.'
    );
    if (result?.ok) closeProductPromotionDialog();
  };

  const removeProductPromotion = async () => {
    const product = productPromotionDialog.product;
    const basePrice = Number(productPromotionDialog.basePrice || product?.originalPrice || product?.price || 0);
    if (!product?.id || !basePrice) {
      closeProductPromotionDialog();
      return;
    }
    const result = await syncStockProductPrice(
      product,
      basePrice,
      basePrice,
      'Preco promocional removido no iFood.'
    );
    if (result?.ok) closeProductPromotionDialog();
  };

  const toggleStockProductStatus = async (product) => {
    if (!product?.id) {
      showToast('Produto sem identificador para alterar status.', 'warning');
      return;
    }
    const nextStatus = getNextCatalogStatus(product.status);
    const result = await runAction({
      id: 'stock-row-item-status',
      path: '/items/status',
      payload: {
        payload: {
          itemId: product.id,
          status: nextStatus,
          statusByCatalog: [{ status: nextStatus, catalogContext: 'DEFAULT' }],
        },
      },
      label: nextStatus === 'AVAILABLE' ? 'Produto reativado no iFood.' : 'Produto pausado no iFood.',
      checkId: 'item-status',
    });
    if (result?.ok) {
      setVisualCatalog((prev) => ({
        ...prev,
        items: toArray(prev.items).map((item) => (
          item.id === product.id ? { ...item, status: nextStatus } : item
        )),
        lastImportedAt: new Date().toISOString(),
        source: 'Status do produto atualizado no iFood',
      }));
    }
  };

  const updateStockOptionStatus = async (option) => {
    if (!option?.id) {
      showToast('Complemento sem identificador para alterar status.', 'warning');
      return;
    }
    const nextStatus = getNextCatalogStatus(option.status);
    const ok = await runSharedOptionPatch(option, (optionId) => ({
      id: 'stock-option-status',
      path: '/options/status',
      payload: {
        payload: {
          optionId,
          parentCustomizationOptionId: null,
          status: nextStatus,
          statusByCatalog: [{ status: nextStatus, catalogContext: 'DEFAULT' }],
        },
      },
      label: nextStatus === 'AVAILABLE' ? 'Complemento reativado no iFood.' : 'Complemento pausado no iFood.',
      checkId: 'option-status',
    }));
    if (ok) {
      setVisualCatalog((prev) => ({
        ...prev,
        options: toArray(prev.options).map((item) => (
          isSharedComplement(item, option) ? { ...item, status: nextStatus } : item
        )),
        lastImportedAt: new Date().toISOString(),
        source: 'Status do complemento atualizado no iFood',
      }));
    }
  };

  const updateStockEntryStatus = (entry) => (
    entry?.entryType === 'option' ? updateStockOptionStatus(entry) : toggleStockProductStatus(entry)
  );

  const getSharedComplementEntries = (option) => {
    const key = getSharedComplementKey(option);
    if (!key) return option?.id ? [option] : [];
    const entries = toArray(allStockComplementEntries).filter((entry) => getSharedComplementKey(entry) === key);
    return dedupeByKey(entries.length ? entries : [option], 'shared-complement', getVisualOptionKey);
  };

  const getSharedComplementOptionIds = (option) => [
    ...new Set(getSharedComplementEntries(option).map((entry) => cleanText(entry.id)).filter(Boolean)),
  ];

  const findStockGroupForOption = (option) => (
    stockGroups.find((group) => group.id === option?.groupId && group.itemId === option?.itemId)
    || stockGroups.find((group) => group.id === option?.groupId)
  );

  const getSharedComplementGroups = (option) => (
    dedupeByKey(
      getSharedComplementEntries(option)
        .map((entry) => findStockGroupForOption(entry))
        .filter(Boolean),
      'shared-complement-group',
      getVisualOptionGroupKey
    )
  );

  const runSharedOptionPatch = async (option, buildAction) => {
    const optionIds = getSharedComplementOptionIds(option);
    if (!optionIds.length) {
      showToast('Complemento sem identificador para atualizar no iFood.', 'warning');
      return false;
    }

    for (const optionId of optionIds) {
      // eslint-disable-next-line no-await-in-loop
      const result = await runAction(buildAction(optionId));
      if (!result?.ok) return false;
    }
    return true;
  };

  const syncSharedOptionExternalCode = async (option, externalCode, actionId = 'option-external-code-sync') => {
    const nextCode = cleanText(externalCode);
    const entries = getSharedComplementEntries(option);
    if (!nextCode || !entries.length) {
      showToast('Option, Item pai e codigo PDV sao obrigatorios para confirmar a sincronizacao.', 'error');
      return false;
    }

    const targets = dedupeByKey(entries, 'option-external-code-target', (entry) => (
      `${cleanText(entry.itemId)}:${cleanText(entry.id)}`
    ));
    for (const target of targets) {
      const parentItem = toArray(visualCatalog.items).find((item) => cleanText(item.id) === cleanText(target.itemId));
      if (!parentItem?.id || !parentItem?.productId || !target.id) {
        showToast('Nao foi possivel provar a relacao entre Option, Item e Product no catalogo carregado.', 'error');
        return false;
      }
      let parentProductExternalCode;
      try {
        // eslint-disable-next-line no-await-in-loop
        const initialFlatResult = await catalogFetch('/items/flat', { itemId: parentItem.id });
        const initialIdentity = requireItemProductIdentity(initialFlatResult.data, {
          catalogItemId: parentItem.id,
          productId: parentItem.productId,
        });
        if (!findFlatOption(initialIdentity.flat, target.id)) {
          throw new Error('A Option nao pertence ao Item pai informado.');
        }
        parentProductExternalCode = cleanText(
          initialIdentity.product.externalCode || initialIdentity.product.productExternalCode
        );
      } catch (error) {
        const message = `Nao foi possivel confirmar Option, Item e Product antes de alterar o codigo PDV: ${error.message}`;
        setLastResult({ ok: false, error: message, action: `${actionId}-preflight` });
        showToast(message, 'error');
        return false;
      }
      // eslint-disable-next-line no-await-in-loop
      const result = await runAction({
        id: actionId,
        path: '/options/external-code',
        payload: {
          payload: {
            catalogItemId: parentItem.id,
            productId: parentItem.productId,
            optionId: target.id,
            parentCustomizationOptionId: null,
            externalCode: nextCode,
          },
        },
        label: 'Atualizacao do codigo PDV do complemento aceita; confirmando no iFood.',
        announceSuccess: false,
      });
      if (!result?.ok) return false;

      try {
        const confirmation = requireConfirmedOptionExternalCode(result, {
          catalogItemId: parentItem.id,
          optionId: target.id,
          externalCode: nextCode,
        });
        // eslint-disable-next-line no-await-in-loop
        const flatResult = await catalogFetch('/items/flat', { itemId: parentItem.id });
        const confirmedOption = requireOptionExternalCodeReadback(flatResult.data, {
          optionId: target.id,
          externalCode: nextCode,
          parentProductId: parentItem.productId,
          parentProductExternalCode,
        });
        requireEffectiveContextCodes(confirmedOption, confirmation.effectiveContextCodes);
      } catch (error) {
        setLastResult({ ok: false, error: error.message, action: `${actionId}-readback` });
        showToast(error.message, 'error');
        return false;
      }
    }

    setVisualCatalog((prev) => ({
      ...prev,
      options: toArray(prev.options).map((entry) => (
        isSharedComplement(entry, option)
          ? { ...entry, externalCode: nextCode, raw: { ...(entry.raw || {}), externalCode: nextCode } }
          : entry
      )),
      lastImportedAt: new Date().toISOString(),
      source: 'Codigo PDV do complemento confirmado no Catalog',
    }));
    setLastResult({
      ok: true,
      confirmed: true,
      action: `${actionId}-readback`,
      label: 'Codigo PDV do complemento confirmado no iFood.',
    });
    showToast('Codigo PDV do complemento confirmado no iFood.');
    return true;
  };

  const upsertSharedComplementContent = async (option, updates, label) => {
    const groups = getSharedComplementGroups(option);
    if (!groups.length) {
      showToast('Complemento sem grupo importado para salvar no iFood.', 'warning');
      return false;
    }

    const entries = getSharedComplementEntries(option);
    const scopedEntry = entries.find((entry) => isSameVisualOption(entry, option))
      || entries.find((entry) => (
        cleanText(entry.itemId) === cleanText(option.itemId)
        && cleanText(entry.groupId) === cleanText(option.groupId)
      ))
      || entries[0]
      || option;
    const group = findStockGroupForOption(scopedEntry) || groups[0];
    const result = await upsertGroupPayload(
      group,
      (currentGroups) => currentGroups,
      label,
      (options) => options.map((item) => (
        isSharedComplement(item, option)
          ? { ...item, ...updates }
          : item
      ))
    );
    return Boolean(result?.ok);
  };

  const verifyCatalogAssociationRemoved = async ({ entryType, categoryId, groupId, productId }) => {
    let verificationError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 750));
      }
      try {
        const catalogsResult = await catalogFetch('/catalogs');
        const catalogs = normalizeCatalogsForVisual(catalogsResult.data, catalogScope);
        if (!catalogs.length) throw new Error('Nenhum catalogo retornado para confirmar a exclusao global.');
        const catalogViews = await Promise.all(catalogs.map(async (catalogEntry) => {
          const result = await catalogFetch('/categories/list', { catalogId: catalogEntry.id, includeItems: true });
          return normalizeVisualFromCategories(result.data, {
            ...catalogScope,
            catalogId: catalogEntry.id,
            catalogContext: catalogEntry.context,
          });
        }));
        const stillAssociated = catalogViews.some((catalog) => {
          if (entryType === 'option') {
            return toArray(catalog.options).some((option) => (
              cleanText(option.groupId) === cleanText(groupId)
              && cleanText(option.productId) === cleanText(productId)
            ));
          }
          return toArray(catalog.items).some((item) => (
            cleanText(item.categoryId) === cleanText(categoryId)
            && cleanText(item.productId) === cleanText(productId)
          ));
        });
        if (!stillAssociated) return true;
      } catch (error) {
        verificationError = error;
      }
    }

    const message = verificationError?.message
      || 'O iFood recebeu a exclusao, mas o vinculo ainda aparece na releitura do Catalog.';
    setLastResult({ ok: false, error: message, action: 'stock-catalog-delete-verify' });
    showToast(message, 'error');
    return false;
  };

  const removeStockEntryFromBrutos = async (entry) => {
    if (!entry?.id || !entry?.productId) {
      showToast('Item sem identificador para remover.', 'warning');
      return;
    }
    if (entry.entryType === 'option' && !entry.groupId) {
      showToast('Complemento sem grupo identificado no iFood.', 'warning');
      return;
    }
    if (entry.entryType !== 'option' && !entry.categoryId) {
      showToast('Produto sem categoria identificada no iFood.', 'warning');
      return;
    }
    const confirmed = window.confirm(`Remover "${entry.name}" do Brutos e do cardapio iFood em todos os contextos?`);
    if (!confirmed) return;

    const result = await runAction({
      id: entry.entryType === 'option' ? 'stock-option-delete' : 'stock-item-delete',
      path: entry.entryType === 'option' ? '/options/delete' : '/items/delete',
      payload: entry.entryType === 'option'
        ? { optionGroupId: entry.groupId, productId: entry.productId }
        : { categoryId: entry.categoryId, productId: entry.productId },
      label: entry.entryType === 'option'
        ? 'Exclusao do complemento enviada ao iFood.'
        : 'Exclusao do produto enviada ao iFood.',
    });
    if (!result?.ok) return;
    const removed = await verifyCatalogAssociationRemoved({
      entryType: entry.entryType,
      categoryId: entry.categoryId,
      groupId: entry.groupId,
      productId: entry.productId,
    });
    if (!removed) return;

    const rowId = `${entry.entryType || 'item'}:${entry.id}`;
    setVisualCatalog((prev) => {
      if (entry.entryType === 'option') {
        return {
          ...prev,
          options: toArray(prev.options).filter((option) => !isSameVisualOption(option, entry)),
          lastImportedAt: new Date().toISOString(),
          source: 'Complemento removido do Brutos e do iFood',
        };
      }

      const removedItem = toArray(prev.items).find((item) => item.id === entry.id);
      return {
        ...prev,
        categories: toArray(prev.categories).map((category) => (
          removedItem?.categoryId && category.id === removedItem.categoryId
            ? { ...category, itemCount: Math.max(0, Number(category.itemCount || 0) - 1) }
            : category
        )),
        items: toArray(prev.items).filter((item) => item.id !== entry.id),
        optionGroups: toArray(prev.optionGroups).filter((group) => group.itemId !== entry.id),
        options: toArray(prev.options).filter((option) => option.itemId !== entry.id),
        lastImportedAt: new Date().toISOString(),
        source: 'Produto removido do Brutos e do iFood',
      };
    });
    setStockSelectedEntryIds((prev) => prev.filter((id) => id !== rowId));
    setPdvDrafts((prev) => {
      const next = { ...prev };
      delete next[rowId];
      delete next[`item:${entry.id}`];
      delete next[`option:${entry.id}`];
      return next;
    });
    showToast(entry.entryType === 'option' ? 'Complemento removido do Brutos e do iFood.' : 'Produto removido do Brutos e do iFood.');
  };

  const updateStockEntryExternalCode = async (entry, value) => {
    const nextCode = cleanText(value);
    if (!entry?.id) {
      showToast('Item sem identificador para salvar PDV.', 'warning');
      return;
    }
    const sharedExternalCodeDiffers = entry.entryType === 'option'
      && getSharedComplementEntries(entry).some((option) => cleanText(option.externalCode) !== nextCode);
    if (nextCode === cleanText(entry.externalCode) && !sharedExternalCodeDiffers) {
      return;
    }
    if (!nextCode) {
      showToast('Informe o codigo PDV antes de salvar.', 'warning');
      return;
    }

    const isOption = entry.entryType === 'option';
    const ok = isOption
      ? await syncSharedOptionExternalCode(entry, nextCode, 'stock-option-external-code')
      : await syncItemExternalCodeAfterUpsert({
        itemId: entry.id,
        productId: entry.productId,
        externalCode: nextCode,
        source: entry,
        actionId: 'stock-item-external-code',
      });
    if (ok) {
      setPdvDrafts((prev) => {
        const next = { ...prev };
        delete next[`${entry.entryType === 'option' ? 'option' : 'item'}:${entry.id}`];
        if (isOption) {
          getSharedComplementEntries(entry).forEach((option) => {
            delete next[`option:${option.id}`];
            delete next[`option:${getVisualOptionKey(option)}`];
          });
        }
        return next;
      });
    }
  };

  const buildVisualItemPayload = (
    product,
    groupTransform = (groups) => groups,
    optionTransform = (options) => options,
    sourceCatalog = visualCatalog
  ) => {
    const groups = toArray(sourceCatalog.optionGroups).filter((group) => group.itemId === product.id);
    const nextGroups = groupTransform(groups);
    const nextGroupIds = new Set(nextGroups.map((group) => group.id));
    const baseOptions = toArray(sourceCatalog.options).filter((option) => option.itemId === product.id);
    const options = optionTransform(baseOptions, nextGroups).filter((option) => (
      option.itemId === product.id && (!option.groupId || nextGroupIds.has(option.groupId))
    ));
    const optionProducts = Array.from(new Map(options
      .filter((option) => option.productId)
      .map((option) => [option.productId, option])).values());
    const buildProductGroupRelation = (group) => {
      const optionCount = options.filter((option) => option.groupId === group.id).length || toArray(group.optionIds).length || 1;
      const rawMax = firstFiniteNumber(group.max, group.raw?.relation?.max, group.raw?.relation?.maximum, optionCount);
      const max = Math.max(1, rawMax || optionCount);
      const min = Math.min(max, Math.max(0, firstFiniteNumber(group.min, group.raw?.relation?.min, group.raw?.relation?.minimum, 0)));
      const relation = { id: group.id, min, max };
      const index = firstFiniteNumber(group.relationIndex, group.raw?.relation?.index, group.index, group.raw?.index, 0);
      if (Number.isFinite(index)) relation.index = index;
      const associationType = cleanText(firstValue(group.associationType, group.raw?.relation?.associationType, ''));
      if (associationType) relation.associationType = associationType;
      return relation;
    };
    const buildCatalogProduct = ({
      id,
      externalCode,
      name,
      description,
      additionalInformation,
      imagePath: nextImagePath,
      rawProduct = {},
      optionGroups = [],
    }) => withCatalogOptionalFields(
      {
        id,
        name: cleanText(name),
        description: cleanText(description),
      },
      {
        externalCode: cleanText(externalCode),
        additionalInformation: cleanText(additionalInformation),
        imagePath: normalizeIfoodImagePathForPayload(nextImagePath),
        ean: rawProduct.ean,
        serving: rawProduct.serving || 'SERVES_1',
        dietaryRestrictions: rawProduct.dietaryRestrictions,
        quantity: rawProduct.quantity,
        optionGroups,
      }
    );
    const itemRaw = product.raw || {};
    const itemPayload = withCatalogOptionalFields(
      {
        id: product.id,
        type: itemRaw.type || 'DEFAULT',
        categoryId: product.categoryId || selectedCategoryId,
        status: normalizeCatalogStatus(product.status),
        price: {
          value: Number(product.price || 0),
          originalValue: Math.max(Number(product.originalPrice || product.price || 0), Number(product.price || 0)),
        },
        index: Number(itemRaw.index || 0),
        productId: product.productId,
      },
      {
        externalCode: product.externalCode,
        shifts: itemRaw.shifts,
        tags: itemRaw.tags,
        contextModifiers: itemRaw.contextModifiers,
      }
    );
    const mainRawProduct = product.raw?.product || {};

    return {
      item: itemPayload,
      products: [
        buildCatalogProduct({
          id: product.productId,
          externalCode: mainRawProduct.externalCode || product.productExternalCode || '',
          name: product.name,
          description: product.description || '',
          additionalInformation: mainRawProduct.additionalInformation || product.description || '',
          imagePath: product.imagePath,
          rawProduct: mainRawProduct,
          optionGroups: nextGroups.map((group) => buildProductGroupRelation(group)),
        }),
        ...optionProducts
          .map((option) => {
            const rawProduct = option.rawProduct || option.raw?.product || {};
            return buildCatalogProduct({
              id: option.productId,
              externalCode: cleanText(firstValue(rawProduct.externalCode, option.productExternalCode, '')),
              name: cleanText(firstValue(option.name, rawProduct.name, '')),
              description: cleanText(firstValue(option.description, rawProduct.description, '')),
              additionalInformation: cleanText(firstValue(rawProduct.additionalInformation, option.description, '')),
              imagePath: firstValue(option.imagePath, rawProduct.imagePath, rawProduct.image, ''),
              rawProduct,
              optionGroups: rawProduct.optionGroups,
            });
          }),
      ],
      optionGroups: nextGroups.map((group, index) => {
        const scopedOptionIds = options.filter((option) => option.groupId === group.id).map((option) => option.id);
        const explicitOptionIds = toArray(group.optionIds).map(cleanText).filter(Boolean);
        return {
          id: group.id,
          name: group.name,
          externalCode: group.externalCode || '',
          status: normalizeCatalogStatus(group.status),
          index,
          optionGroupType: group.raw?.optionGroupType || 'DEFAULT',
          optionIds: scopedOptionIds.length ? scopedOptionIds : explicitOptionIds,
        };
      }),
      options: options.map((option, index) => withCatalogOptionalFields(
        {
          id: option.id,
          status: normalizeCatalogStatus(option.status),
          index,
          productId: option.productId,
          price: {
            value: Number(option.price || 0),
            originalValue: Math.max(Number(option.originalPrice || option.price || 0), Number(option.price || 0)),
          },
        },
        {
          contextModifiers: option.raw?.contextModifiers,
          fractions: option.raw?.fractions,
          externalCode: option.externalCode,
        }
      )),
    };
  };

  const findGroupProduct = (group) => allStockProducts.find((product) => product.id === group?.itemId);

  const hydrateStockItemForUpsert = async (product) => {
    try {
      const result = await catalogFetch('/items/flat', { itemId: product.id });
      const catalog = normalizeVisualFromFlat(result.data, {
        ...catalogScope,
        catalogId: product.catalogId || selectedCatalogId,
        catalogContext: product.catalogContext || product.priceCatalogContext || findCatalogContext(visualCatalog.catalogs, product.catalogId || selectedCatalogId),
        categoryId: product.categoryId,
        categoryName: product.categoryName,
      });
      const hydratedProduct = toArray(catalog.items).find((item) => item.id === product.id) || {};
      return {
        catalog,
        flat: result.data,
        product: {
          ...product,
          ...hydratedProduct,
          categoryId: hydratedProduct.categoryId || product.categoryId,
          categoryName: hydratedProduct.categoryName || product.categoryName,
        },
      };
    } catch (error) {
      const message = `Nao foi possivel carregar o detalhe real do item no iFood antes de salvar: ${error.message}`;
      setLastResult({ ok: false, error: message, action: 'stock-item-flat' });
      showToast(message, 'error');
      return null;
    }
  };

  const upsertProductGroupsPayload = async (product, groupTransform, label, optionTransform = (options) => options) => {
    if (!product?.id) {
      showToast('Produto importado nao encontrado para atualizar grupos.', 'warning');
      return null;
    }
    const hydrated = await hydrateStockItemForUpsert(product);
    if (!hydrated) return null;
    const payload = buildVisualItemPayload(
      hydrated.product,
      groupTransform,
      optionTransform,
      hydrated.catalog
    );
    const result = await runAction({
      id: 'stock-option-group-upsert',
      path: '/items/upsert',
      payload: { payload },
      label,
      checkId: 'item-upsert',
      visualImport: () => normalizeVisualFromFlat(payload, {
        ...catalogScope,
        catalogId: hydrated.product.catalogId || product.catalogId || selectedCatalogId,
        catalogContext: hydrated.product.catalogContext || hydrated.product.priceCatalogContext || product.catalogContext || product.priceCatalogContext || findCatalogContext(visualCatalog.catalogs, hydrated.product.catalogId || product.catalogId || selectedCatalogId),
        categoryId: hydrated.product.categoryId || product.categoryId,
        categoryName: hydrated.product.categoryName || product.categoryName,
      }),
    });
    return result;
  };

  const upsertGroupPayload = async (group, groupTransform, label, optionTransform = (options) => options) => {
    const product = findGroupProduct(group);
    if (!product) {
      showToast('Produto vinculado ao grupo nao foi importado.', 'warning');
      return null;
    }
    return upsertProductGroupsPayload(product, groupTransform, label, optionTransform);
  };

  const toggleStockGroupStatus = (group) => upsertGroupPayload(
    group,
    (groups) => groups.map((item) => (
      isSameVisualGroup(item, group) ? { ...item, status: getNextCatalogStatus(item.status) } : item
    )),
    normalizeCatalogStatus(group.status) === 'AVAILABLE'
      ? 'Grupo de complementos pausado no iFood.'
      : 'Grupo de complementos reativado no iFood.'
  );

  const editStockGroup = (group) => {
    const groupOptions = allStockComplementEntries.filter((option) => option.groupId === group.id && option.itemId === group.itemId);
    setGroupOptionDrafts(groupOptions.reduce((drafts, option) => ({
      ...drafts,
      [getVisualOptionKey(option)]: {
        name: option.name || '',
        description: option.description || '',
        price: String(firstValue(option.price, '')),
        externalCode: option.externalCode || '',
        imagePath: option.imagePath || '',
      },
    }), {}));
    setNewComplementForm(emptyNewComplementForm);
    setShowNewComplementForm(false);
    setGroupEditor({ open: true, groupId: getVisualOptionGroupKey(group), tab: 0, search: '', linkProductId: '' });
  };

  const removeStockGroup = async (group) => {
    const confirmed = window.confirm(`Remover o grupo "${group.name}" do item no iFood?`);
    if (!confirmed) return;
    const result = await upsertGroupPayload(
      group,
      (groups) => groups.filter((item) => !isSameVisualGroup(item, group)),
      'Grupo de complementos removido no iFood.'
    );
    if (result?.ok) {
      setVisualCatalog((prev) => ({
        ...prev,
        optionGroups: toArray(prev.optionGroups).filter((item) => !isSameVisualGroup(item, group)),
        options: toArray(prev.options).filter((option) => !(option.groupId === group.id && option.itemId === group.itemId)),
        lastImportedAt: new Date().toISOString(),
        source: 'Grupo removido do estoque visual',
      }));
    }
  };

  const closeGroupEditor = () => {
    setGroupEditor({ open: false, groupId: '', tab: 0, search: '', linkProductId: '' });
    setGroupOptionDrafts({});
    setNewComplementForm(emptyNewComplementForm);
    setShowNewComplementForm(false);
  };

  const closeProductGroupDialog = () => {
    setProductGroupDialog({ open: false, mode: 'choice', copySearch: '' });
    setProductGroupForm(createEmptyProductGroupForm());
  };

  const openProductGroupDialog = () => {
    if (!currentSelectedStockProduct?.id) {
      showToast('Abra um produto importado antes de adicionar grupos.', 'warning');
      return;
    }
    setProductGroupDialog({ open: true, mode: 'choice', copySearch: '' });
    setProductGroupForm(createEmptyProductGroupForm());
  };

  const updateProductGroupForm = (field, value) => {
    setProductGroupForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateProductGroupOptionDraft = (index, field, value) => {
    setProductGroupForm((prev) => ({
      ...prev,
      options: toArray(prev.options).map((option, optionIndex) => (
        optionIndex === index ? { ...option, [field]: value } : option
      )),
    }));
  };

  const addProductGroupOptionDraft = () => {
    setProductGroupForm((prev) => {
      const options = [...toArray(prev.options), createProductGroupOptionDraft()];
      return {
        ...prev,
        options,
        activeOptionIndex: options.length - 1,
      };
    });
  };

  const updateGroupOptionDraft = (optionId, field, value) => {
    setGroupOptionDrafts((prev) => ({
      ...prev,
      [optionId]: {
        ...(prev[optionId] || {}),
        [field]: value,
      },
    }));
  };

  const readImageFileAsDataUrl = async (file) => {
    if (!file) return '';
    if (file.size > 5 * 1024 * 1024) {
      showToast('Imagem acima de 5MB.', 'error');
      return '';
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const readImageFileAsFourByThreeDataUrl = async (file) => {
    const dataUrl = await readImageFileAsDataUrl(file);
    if (!dataUrl) return '';
    if (typeof window === 'undefined') return dataUrl;

    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const naturalWidth = image.naturalWidth || image.width;
        const naturalHeight = image.naturalHeight || image.height;
        if (!naturalWidth || !naturalHeight) {
          resolve(dataUrl);
          return;
        }

        const targetRatio = 4 / 3;
        const sourceRatio = naturalWidth / naturalHeight;
        let cropWidth = naturalWidth;
        let cropHeight = naturalHeight;
        if (sourceRatio > targetRatio) {
          cropWidth = naturalHeight * targetRatio;
        } else if (sourceRatio < targetRatio) {
          cropHeight = naturalWidth / targetRatio;
        }

        const cropX = Math.max(0, (naturalWidth - cropWidth) / 2);
        const cropY = Math.max(0, (naturalHeight - cropHeight) / 2);
        const canvas = document.createElement('canvas');
        const outputWidth = Math.min(1200, Math.round(cropWidth));
        canvas.width = outputWidth;
        canvas.height = Math.round(outputWidth / targetRatio);
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(dataUrl);
          return;
        }
        context.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      image.onerror = () => resolve(dataUrl);
      image.src = dataUrl;
    });
  };

  const uploadGroupComplementImage = async (optionId, file) => {
    const dataUrl = await readImageFileAsFourByThreeDataUrl(file);
    if (!dataUrl) return;
    const result = await runAction({
      id: 'stock-option-image-upload',
      path: '/image/upload',
      payload: { image: dataUrl },
      label: 'Imagem do complemento enviada ao iFood.',
      checkId: 'image-upload',
    });
    const uploadedPath = extractImagePath(result?.data);
    if (!result?.ok || !uploadedPath) return;

    if (optionId === '__new') {
      setNewComplementForm((prev) => ({ ...prev, imagePath: uploadedPath }));
      return;
    }
    if (optionId === '__productGroupNew') {
      setProductGroupForm((prev) => ({
        ...prev,
        optionImagePath: uploadedPath,
        options: toArray(prev.options).map((option, index) => (
          index === Number(prev.activeOptionIndex || 0) ? { ...option, imagePath: uploadedPath } : option
        )),
      }));
      return;
    }
    if (String(optionId).startsWith('__productGroupNew:')) {
      const draftId = String(optionId).replace('__productGroupNew:', '');
      setProductGroupForm((prev) => ({
        ...prev,
        options: toArray(prev.options).map((option) => (
          option.draftId === draftId ? { ...option, imagePath: uploadedPath } : option
        )),
      }));
      return;
    }
    updateGroupOptionDraft(optionId, 'imagePath', uploadedPath);
  };

  const createProductEditorGroup = async () => {
    const product = currentSelectedStockProduct;
    const groupName = cleanText(productGroupForm.groupName);
    const optionDrafts = (toArray(productGroupForm.options).length
      ? toArray(productGroupForm.options)
      : [createProductGroupOptionDraft({
        name: productGroupForm.optionName,
        description: productGroupForm.optionDescription,
        price: productGroupForm.optionPrice,
        externalCode: productGroupForm.optionExternalCode,
        imagePath: productGroupForm.optionImagePath,
      })]
    ).map((draft) => ({
      ...draft,
      name: cleanText(draft.name),
      description: cleanText(draft.description),
      externalCode: cleanText(draft.externalCode),
      imagePath: cleanText(draft.imagePath),
    }));

    if (!product?.id) {
      showToast('Produto importado nao encontrado para criar grupo.', 'warning');
      return;
    }
    if (!groupName) {
      showToast('Informe o nome do grupo de complementos.', 'warning');
      return;
    }
    if (!optionDrafts.length || optionDrafts.some((draft) => !draft.name)) {
      showToast('Informe o nome de todos os complementos.', 'warning');
      return;
    }

    const minNumber = Number.parseInt(productGroupForm.min, 10);
    const maxNumber = Number.parseInt(productGroupForm.max, 10);
    const min = Number.isFinite(minNumber) ? Math.max(0, minNumber) : 0;
    const max = Number.isFinite(maxNumber) ? Math.max(1, maxNumber) : 1;
    if (min > max) {
      showToast('A quantidade minima nao pode ser maior que a maxima.', 'warning');
      return;
    }

    const groupId = createUuid();
    const relationIndex = selectedStockGroups.length;
    const nextOptions = optionDrafts.map((draft, index) => {
      const optionId = createUuid();
      const optionProductId = createUuid();
      const price = parseCatalogMoney(draft.price);
      return {
        id: optionId,
        itemId: product.id,
        groupId,
        productId: optionProductId,
        name: draft.name,
        description: draft.description,
        status: 'AVAILABLE',
        price,
        originalPrice: price,
        externalCode: draft.externalCode,
        productExternalCode: '',
        imagePath: draft.imagePath,
        imageUrl: buildIfoodImageUrl(draft.imagePath),
        raw: { index },
        rawProduct: {
          id: optionProductId,
          externalCode: '',
          name: draft.name,
          description: draft.description,
          imagePath: draft.imagePath,
        },
      };
    });
    const nextGroup = {
      id: groupId,
      itemId: product.id,
      name: groupName,
      externalCode: cleanText(productGroupForm.groupExternalCode),
      status: 'AVAILABLE',
      min,
      max,
      relationIndex,
      optionIds: nextOptions.map((option) => option.id),
      optionGroupType: 'DEFAULT',
      associationType: '',
      raw: {
        optionGroupType: 'DEFAULT',
        relation: {
          id: groupId,
          min,
          max,
          index: relationIndex,
        },
      },
    };

    const result = await upsertProductGroupsPayload(
      product,
      (groups) => [...groups, nextGroup],
      'Grupo de complementos criado no iFood.',
      (options) => [...options, ...nextOptions]
    );
    if (result?.ok) {
      setExpandedProductEditorGroups((prev) => ({ ...prev, [groupId]: true }));
      setProductEditorTab(1);
      closeProductGroupDialog();
    }
  };

  const copyProductEditorGroup = async (sharedGroup) => {
    const product = currentSelectedStockProduct;
    const sourceGroup = toArray(sharedGroup?.linkedGroups)[0] || sharedGroup || {};
    const groupId = cleanText(sourceGroup.id || sharedGroup?.id);
    if (!product?.id || !groupId) {
      showToast('Grupo importado nao encontrado para reutilizar.', 'warning');
      return;
    }
    if (selectedStockGroups.some((group) => cleanText(group.id) === groupId)) {
      showToast('Este grupo ja esta vinculado a este produto.', 'info');
      return;
    }

    const uniqueOptions = getOfferSharedGroupOptions(sharedGroup);
    if (!uniqueOptions.length) {
      showToast('Grupo importado sem complementos para reutilizar.', 'warning');
      return;
    }

    const optionIds = uniqueOptions.map((option) => cleanText(option.id)).filter(Boolean);
    const rawMax = firstFiniteNumber(sourceGroup.max, sharedGroup?.max, optionIds.length, 1);
    const max = Math.max(1, rawMax || optionIds.length || 1);
    const min = Math.min(max, Math.max(0, firstFiniteNumber(sourceGroup.min, sharedGroup?.min, 0)));
    const relationIndex = selectedStockGroups.length;
    const linkedGroup = {
      ...sourceGroup,
      ...sharedGroup,
      id: groupId,
      itemId: product.id,
      optionIds,
      min,
      max,
      relationIndex,
      raw: {
        ...(sourceGroup.raw || sharedGroup?.raw || {}),
        relation: {
          ...(sourceGroup.raw?.relation || sharedGroup?.raw?.relation || {}),
          id: groupId,
          min,
          max,
          index: relationIndex,
        },
      },
    };
    const linkedOptions = uniqueOptions.map((option) => ({
      ...option,
      itemId: product.id,
      groupId,
      raw: option.raw || {},
      rawProduct: option.rawProduct || option.raw?.product || {},
    }));

    const result = await upsertProductGroupsPayload(
      product,
      (groups) => (groups.some((group) => cleanText(group.id) === groupId) ? groups : [...groups, linkedGroup]),
      'Grupo de complementos reutilizado no iFood.',
      (options) => {
        const existingKeys = new Set(options.map(getVisualOptionKey));
        return [
          ...options,
          ...linkedOptions.filter((option) => !existingKeys.has(getVisualOptionKey(option))),
        ];
      }
    );
    if (result?.ok) {
      setExpandedProductEditorGroups((prev) => ({ ...prev, [groupId]: true }));
      setProductEditorTab(1);
      closeProductGroupDialog();
    }
  };

  const formatStockMoneyInput = (value) => Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const getInlineComplementPriceValue = (option) => (
    inlineComplementPriceDrafts[getVisualOptionKey(option)] ?? formatStockMoneyInput(option.price)
  );

  const updateInlineComplementPrice = async (option, value) => {
    const nextPrice = parseCatalogMoney(value);
    if (!option?.id) {
      showToast('Complemento sem identificador para alterar preco.', 'warning');
      return;
    }
    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      showToast('Informe um preco valido para o complemento.', 'warning');
      return;
    }
    const sharedPriceDiffers = getSharedComplementEntries(option).some((entry) => Number(entry.price || 0) !== nextPrice);
    if (nextPrice === Number(option.price || 0) && !sharedPriceDiffers) {
      setInlineComplementPriceDrafts((prev) => {
        const next = { ...prev };
        delete next[getVisualOptionKey(option)];
        return next;
      });
      return;
    }

    const ok = await runSharedOptionPatch(option, (optionId) => ({
      id: 'stock-inline-option-price',
      path: '/options/price',
      payload: {
        payload: {
          optionId,
          parentCustomizationOptionId: null,
          price: { value: nextPrice, originalValue: nextPrice },
          priceByCatalog: buildPriceByCatalogPayload(nextPrice, nextPrice, option),
        },
      },
      label: 'Preco do complemento salvo no iFood.',
      checkId: 'option-price',
    }));
    if (!ok) return;

    setVisualCatalog((prev) => ({
      ...prev,
      options: toArray(prev.options).map((item) => (
        isSharedComplement(item, option) ? { ...item, price: nextPrice, originalPrice: nextPrice } : item
      )),
      lastImportedAt: new Date().toISOString(),
      source: 'Preco do complemento atualizado no iFood',
    }));
    setInlineComplementPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[getVisualOptionKey(option)];
      return next;
    });
  };

  const updateStockGroupRules = async (group, overrides = {}) => {
    if (!group?.id) {
      showToast('Grupo de complementos sem identificador.', 'warning');
      return;
    }
    const currentMax = Math.max(1, Number(group.max || 1));
    const nextMax = Math.max(1, Number(firstValue(overrides.max, group.max, currentMax) || currentMax));
    const nextMin = Math.min(nextMax, Math.max(0, Number(firstValue(overrides.min, group.min, 0) || 0)));
    const result = await upsertGroupPayload(
      group,
      (groups) => groups.map((item) => (
        isSameVisualGroup(item, group) ? { ...item, min: nextMin, max: nextMax } : item
      )),
      'Regras do grupo de complementos salvas no iFood.'
    );
    if (!result?.ok) return;

    setVisualCatalog((prev) => ({
      ...prev,
      optionGroups: toArray(prev.optionGroups).map((item) => (
        isSameVisualGroup(item, group) ? { ...item, min: nextMin, max: nextMax } : item
      )),
      lastImportedAt: new Date().toISOString(),
      source: 'Regras do grupo atualizadas no iFood',
    }));
  };

  const openComplementEditDialog = (option) => {
    closeStockOptionMenu();
    setComplementEditDialog({ open: true, option });
    setComplementEditForm({
      name: option?.name || '',
      description: option?.description || '',
      imagePath: option?.imagePath || '',
    });
  };

  const closeComplementEditDialog = () => {
    setComplementEditDialog({ open: false, option: null });
    setComplementEditForm({ name: '', description: '', imagePath: '' });
  };

  const uploadComplementEditImage = async (file) => {
    const dataUrl = await readImageFileAsFourByThreeDataUrl(file);
    if (!dataUrl) return;
    const result = await runAction({
      id: 'stock-option-modal-image-upload',
      path: '/image/upload',
      payload: { image: dataUrl },
      label: 'Imagem do complemento enviada ao iFood.',
      checkId: 'image-upload',
    });
    const uploadedPath = extractImagePath(result?.data);
    if (!result?.ok || !uploadedPath) return;
    setComplementEditForm((prev) => ({ ...prev, imagePath: uploadedPath }));
  };

  const saveComplementEditDialog = async () => {
    const dialogOption = complementEditDialog.option;
    const option = allStockComplementEntries.find((item) => isSameVisualOption(item, dialogOption)) || dialogOption;
    const group = stockGroups.find((item) => item.id === option?.groupId && item.itemId === option?.itemId)
      || stockGroups.find((item) => item.id === option?.groupId);
    if (!option?.id || !group?.id) {
      showToast('Complemento sem grupo importado para salvar no iFood.', 'warning');
      return;
    }

    const nextName = cleanText(complementEditForm.name);
    const nextDescription = cleanText(complementEditForm.description);
    const nextImagePath = cleanText(complementEditForm.imagePath);
    if (!nextName) {
      showToast('Informe o nome do complemento.', 'warning');
      return;
    }

    const changed = nextName !== cleanText(option.name)
      || nextDescription !== cleanText(option.description)
      || nextImagePath !== cleanText(option.imagePath);
    const sharedContentDiffers = getSharedComplementEntries(option).some((entry) => (
      nextName !== cleanText(entry.name)
      || nextDescription !== cleanText(entry.description)
      || nextImagePath !== cleanText(entry.imagePath)
    ));
    if (!changed && !sharedContentDiffers) {
      closeComplementEditDialog();
      return;
    }

    const updates = {
      name: nextName,
      description: nextDescription,
      imagePath: nextImagePath,
      imageUrl: buildIfoodImageUrl(nextImagePath),
    };
    const ok = await upsertSharedComplementContent(
      option,
      updates,
      'Complemento compartilhado atualizado no iFood.'
    );
    if (!ok) return;

    setVisualCatalog((prev) => ({
      ...prev,
      options: toArray(prev.options).map((item) => (
        isSharedComplement(item, option)
          ? { ...item, ...updates }
          : item
      )),
      lastImportedAt: new Date().toISOString(),
      source: 'Complemento atualizado no iFood',
    }));
    closeComplementEditDialog();
  };

  const saveGroupComplementDrafts = async () => {
    const group = findVisualOptionGroup(stockGroups, groupEditor.groupId);
    if (!group) {
      showToast('Grupo de complementos nao encontrado.', 'warning');
      return;
    }
    const groupOptions = allStockComplementEntries.filter((option) => option.groupId === group.id && option.itemId === group.itemId);
    const changes = groupOptions.map((option) => {
      const draft = groupOptionDrafts[getVisualOptionKey(option)] || {};
      const nextImagePath = cleanText(draft.imagePath ?? option.imagePath ?? '');
      const nextPrice = parseCatalogMoney(draft.price ?? option.price);
      const nextExternalCode = cleanText(draft.externalCode ?? option.externalCode ?? '');
      const nextName = cleanText(draft.name ?? option.name ?? '');
      const nextDescription = cleanText(draft.description ?? option.description ?? '');
      return {
        option,
        nextName,
        nextDescription,
        nextPrice,
        nextExternalCode,
        nextImagePath,
        contentChanged: getSharedComplementEntries(option).some((entry) => (
          nextName !== cleanText(entry.name)
          || nextDescription !== cleanText(entry.description)
          || nextImagePath !== cleanText(entry.imagePath)
        )),
        priceChanged: Number.isFinite(nextPrice) && getSharedComplementEntries(option).some((entry) => (
          nextPrice !== Number(entry.price || 0)
        )),
        externalCodeChanged: Boolean(nextExternalCode) && getSharedComplementEntries(option).some((entry) => (
          nextExternalCode !== cleanText(entry.externalCode)
        )),
      };
    });
    const hasContentChanges = changes.some((change) => change.contentChanged);
    const hasPatchChanges = changes.some((change) => change.priceChanged || change.externalCodeChanged);

    if (!hasContentChanges && !hasPatchChanges) {
      showToast('Nenhuma alteracao encontrada para salvar.', 'info');
      closeGroupEditor();
      return;
    }

    if (hasContentChanges) {
      for (const change of changes.filter((item) => item.contentChanged)) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await upsertSharedComplementContent(
          change.option,
          {
            name: change.nextName,
            description: change.nextDescription,
            imagePath: change.nextImagePath,
            imageUrl: buildIfoodImageUrl(change.nextImagePath),
          },
          'Nome, descricao ou foto do complemento compartilhado salvos no iFood.'
        );
        if (!ok) return;
      }
    }

    for (const change of changes) {
      if (change.priceChanged) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await runSharedOptionPatch(change.option, (optionId) => ({
          id: 'stock-editor-option-price',
          path: '/options/price',
          payload: {
            payload: {
              optionId,
              parentCustomizationOptionId: null,
              price: { value: change.nextPrice, originalValue: change.nextPrice },
              priceByCatalog: buildPriceByCatalogPayload(change.nextPrice, change.nextPrice, change.option),
            },
          },
          label: 'Preco do complemento salvo no iFood.',
          checkId: 'option-price',
        }));
        if (!ok) return;
      }

      if (change.externalCodeChanged) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await syncSharedOptionExternalCode(
          change.option,
          change.nextExternalCode,
          'stock-editor-option-external-code'
        );
        if (!ok) return;
      }
    }

    setVisualCatalog((prev) => ({
      ...prev,
      options: toArray(prev.options).map((option) => {
        const change = changes.find((item) => isSharedComplement(item.option, option));
        if (!change) return option;
        return {
          ...option,
          name: change.nextName,
          description: change.nextDescription,
          price: change.nextPrice,
          originalPrice: change.nextPrice,
          externalCode: change.nextExternalCode,
          imagePath: change.nextImagePath,
          imageUrl: buildIfoodImageUrl(change.nextImagePath),
        };
      }),
      lastImportedAt: new Date().toISOString(),
      source: 'Complementos do grupo atualizados no iFood',
    }));
    showToast('Complemento atualizado no iFood.');
    closeGroupEditor();
  };

  const addGroupComplement = async () => {
    const group = findVisualOptionGroup(stockGroups, groupEditor.groupId);
    const name = cleanText(newComplementForm.name);
    const externalCode = cleanText(newComplementForm.externalCode);
    if (!group) {
      showToast('Grupo de complementos nao encontrado.', 'warning');
      return;
    }
    if (!name || !externalCode) {
      showToast('Informe nome e PDV para criar o complemento.', 'warning');
      return;
    }

    const optionId = createUuid();
    const productId = createUuid();
    const price = parseCatalogMoney(newComplementForm.price);
    const nextOption = {
      id: optionId,
      itemId: group.itemId,
      groupId: group.id,
      productId,
      name,
      description: cleanText(newComplementForm.description),
      status: 'AVAILABLE',
      price,
      originalPrice: price,
      externalCode,
      imagePath: cleanText(newComplementForm.imagePath),
      imageUrl: buildIfoodImageUrl(newComplementForm.imagePath),
      raw: {},
    };
    const result = await upsertGroupPayload(
      group,
      (groups) => groups.map((item) => (
        isSameVisualGroup(item, group)
          ? { ...item, optionIds: [...new Set([...toArray(item.optionIds), optionId])] }
          : item
      )),
      'Complemento criado no grupo do iFood.',
      (options) => [...options, nextOption]
    );
    if (result?.ok) {
      setNewComplementForm(emptyNewComplementForm);
      setShowNewComplementForm(false);
      setGroupOptionDrafts((prev) => ({
        ...prev,
        [getVisualOptionKey(nextOption)]: {
          name,
          description: nextOption.description,
          price: String(price),
          externalCode,
          imagePath: nextOption.imagePath,
        },
      }));
    }
  };

  const removeGroupComplement = async (option) => {
    const group = stockGroups.find((item) => item.id === option.groupId && item.itemId === option.itemId)
      || stockGroups.find((item) => item.id === option.groupId);
    if (!group) return;
    const confirmed = window.confirm(`Remover o complemento "${option.name}" deste grupo no iFood em todos os contextos?`);
    if (!confirmed) return;

    if (!option.productId) {
      showToast('Complemento sem productId para remover no iFood.', 'warning');
      return;
    }
    const result = await runAction({
      id: 'stock-group-option-delete',
      path: '/options/delete',
      payload: { optionGroupId: group.id, productId: option.productId },
      label: 'Exclusao do complemento enviada ao iFood.',
    });
    if (result?.ok && await verifyCatalogAssociationRemoved({
      entryType: 'option',
      groupId: group.id,
      productId: option.productId,
    })) {
      setVisualCatalog((prev) => ({
        ...prev,
        optionGroups: toArray(prev.optionGroups).map((item) => (
          isSameVisualGroup(item, group)
            ? { ...item, optionIds: toArray(item.optionIds).filter((id) => id !== option.id) }
            : item
        )),
        options: toArray(prev.options).filter((item) => !isSameVisualOption(item, option)),
        lastImportedAt: new Date().toISOString(),
        source: 'Complemento removido do Brutos e do iFood',
      }));
      showToast('Complemento removido do Brutos e do iFood.');
    }
  };

  const addGroupToExistingProduct = async () => {
    const sourceGroup = findVisualOptionGroup(stockGroups, groupEditor.groupId);
    if (!sourceGroup) return;
    const targetProduct = allStockProducts.find((product) => product.id === groupEditor.linkProductId);
    if (!targetProduct) {
      showToast('Selecione um produto importado do iFood para vincular o grupo.', 'warning');
      return;
    }

    const sourceOptions = allStockComplementEntries.filter((option) => option.groupId === sourceGroup.id && option.itemId === sourceGroup.itemId);
    const clonedGroupId = createUuid();
    const clonedOptionIds = sourceOptions.map(() => createUuid());
    const clonedGroup = {
      ...sourceGroup,
      id: clonedGroupId,
      itemId: targetProduct.id,
      optionIds: clonedOptionIds,
      raw: {},
    };
    const clonedOptions = sourceOptions.map((option, index) => ({
      ...option,
      id: clonedOptionIds[index],
      itemId: targetProduct.id,
      groupId: clonedGroupId,
      raw: {},
    }));
    const payload = buildVisualItemPayload(
      targetProduct,
      (groups) => [...groups, clonedGroup],
      (options) => [...options, ...clonedOptions]
    );
    const result = await runAction({
      id: 'stock-group-link-product',
      path: '/items/upsert',
      payload: { payload },
      label: 'Grupo vinculado ao produto no iFood.',
      checkId: 'item-upsert',
      visualImport: () => normalizeVisualFromFlat(payload, {
        ...catalogScope,
        catalogId: targetProduct.catalogId || selectedCatalogId,
        catalogContext: targetProduct.catalogContext || targetProduct.priceCatalogContext || findCatalogContext(visualCatalog.catalogs, targetProduct.catalogId || selectedCatalogId),
        categoryId: targetProduct.categoryId,
        categoryName: targetProduct.categoryName,
      }),
    });
    if (result?.ok) {
      setGroupEditor((prev) => ({ ...prev, linkProductId: '' }));
    }
  };

  const getOrderableStockCategories = () => toArray(visualCatalog.categories)
    .filter((item) => item.id && item.id !== 'uncategorized')
    .sort((left, right) => (
      Number(left.sequence || 0) - Number(right.sequence || 0)
      || cleanText(left.name).localeCompare(cleanText(right.name), 'pt-BR')
    ));

  const reorderStockCategories = () => {
    const ordered = getOrderableStockCategories();
    if (!ordered.length) {
      showToast('Importe categorias antes de ordenar.', 'warning');
      return;
    }
    setCategoryOrderDialog({ open: true, ids: ordered.map((item) => item.id), draggingId: '' });
  };

  const closeCategoryOrderDialog = () => {
    setCategoryOrderDialog((prev) => ({ ...prev, open: false, draggingId: '' }));
  };

  const resetCategoryOrderDialog = () => {
    const ordered = getOrderableStockCategories();
    setCategoryOrderDialog((prev) => ({ ...prev, ids: ordered.map((item) => item.id), draggingId: '' }));
  };

  const moveCategoryOrder = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setCategoryOrderDialog((prev) => {
      const currentIds = [...prev.ids];
      const sourceIndex = currentIds.indexOf(sourceId);
      const targetIndex = currentIds.indexOf(targetId);
      if (sourceIndex < 0 || targetIndex < 0) return prev;
      const [movedId] = currentIds.splice(sourceIndex, 1);
      currentIds.splice(targetIndex, 0, movedId);
      return { ...prev, ids: currentIds, draggingId: '' };
    });
  };

  const promptCategoryOrderPosition = (categoryId, currentIndex) => {
    const value = window.prompt('Informe a nova posicao da categoria.', String(currentIndex + 1));
    if (value === null) return;
    const nextIndex = Number(value) - 1;
    if (!Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= categoryOrderDialog.ids.length) {
      showToast('Posicao invalida para reordenar categoria.', 'warning');
      return;
    }
    setCategoryOrderDialog((prev) => {
      const currentIds = [...prev.ids];
      const sourceIndex = currentIds.indexOf(categoryId);
      if (sourceIndex < 0 || sourceIndex === nextIndex) return prev;
      const [movedId] = currentIds.splice(sourceIndex, 1);
      currentIds.splice(nextIndex, 0, movedId);
      return { ...prev, ids: currentIds };
    });
  };

  const saveCategoryOrder = async () => {
    const categoriesById = new Map(toArray(visualCatalog.categories).map((item) => [item.id, item]));
    const ordered = categoryOrderDialog.ids
      .map((id) => categoriesById.get(id))
      .filter((item) => item?.id && item.id !== 'uncategorized');
    if (!ordered.length) {
      showToast('Importe categorias antes de ordenar.', 'warning');
      return;
    }
    for (let index = 0; index < ordered.length; index += 1) {
      const nextCategory = ordered[index];
      // eslint-disable-next-line no-await-in-loop
      const result = await patchStockCategory(nextCategory, { sequence: index }, `Categoria ${nextCategory.name} reordenada no iFood.`);
      if (!result?.ok) return;
    }
    setVisualCatalog((prev) => ({
      ...prev,
      categories: toArray(prev.categories).map((categoryItem) => {
        const nextIndex = categoryOrderDialog.ids.indexOf(categoryItem.id);
        return nextIndex >= 0 ? { ...categoryItem, sequence: nextIndex } : categoryItem;
      }),
      lastImportedAt: new Date().toISOString(),
      source: 'Categorias reordenadas no iFood',
    }));
    closeCategoryOrderDialog();
    showToast('Categorias reordenadas no iFood.');
  };

  const reorderProductGroups = () => {
    if (!currentSelectedStockProduct?.id) {
      showToast('Abra um produto importado antes de reordenar grupos.', 'warning');
      return;
    }
    if (!selectedStockGroups.length) {
      showToast('Produto sem grupos de complementos para reordenar.', 'warning');
      return;
    }
    setGroupOrderDialog({
      open: true,
      ids: selectedStockGroups.map((group) => getVisualOptionGroupKey(group)),
      draggingId: '',
    });
  };

  const closeGroupOrderDialog = () => {
    setGroupOrderDialog((prev) => ({ ...prev, open: false, draggingId: '' }));
  };

  const resetGroupOrderDialog = () => {
    setGroupOrderDialog((prev) => ({
      ...prev,
      ids: selectedStockGroups.map((group) => getVisualOptionGroupKey(group)),
      draggingId: '',
    }));
  };

  const moveGroupOrder = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setGroupOrderDialog((prev) => {
      const currentIds = [...prev.ids];
      const sourceIndex = currentIds.indexOf(sourceId);
      const targetIndex = currentIds.indexOf(targetId);
      if (sourceIndex < 0 || targetIndex < 0) return prev;
      const [movedId] = currentIds.splice(sourceIndex, 1);
      currentIds.splice(targetIndex, 0, movedId);
      return { ...prev, ids: currentIds, draggingId: '' };
    });
  };

  const promptGroupOrderPosition = (groupKey, currentIndex) => {
    const value = window.prompt('Informe a nova posicao do grupo.', String(currentIndex + 1));
    if (value === null) return;
    const nextIndex = Number(value) - 1;
    if (!Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= groupOrderDialog.ids.length) {
      showToast('Posicao invalida para reordenar grupo.', 'warning');
      return;
    }
    setGroupOrderDialog((prev) => {
      const currentIds = [...prev.ids];
      const sourceIndex = currentIds.indexOf(groupKey);
      if (sourceIndex < 0 || sourceIndex === nextIndex) return prev;
      const [movedId] = currentIds.splice(sourceIndex, 1);
      currentIds.splice(nextIndex, 0, movedId);
      return { ...prev, ids: currentIds };
    });
  };

  const saveGroupOrder = async () => {
    const product = currentSelectedStockProduct;
    if (!product?.id) return;
    const orderMap = new Map(groupOrderDialog.ids.map((id, index) => [id, index]));
    if (!orderMap.size) {
      showToast('Produto sem grupos de complementos para reordenar.', 'warning');
      return;
    }

    const result = await upsertProductGroupsPayload(
      product,
      (groups) => groups
        .map((group) => {
          const groupKey = getVisualOptionGroupKey(group);
          if (!orderMap.has(groupKey)) return group;
          const nextIndex = orderMap.get(groupKey);
          return {
            ...group,
            relationIndex: nextIndex,
            index: nextIndex,
            raw: {
              ...(group.raw || {}),
              index: nextIndex,
              relation: {
                ...(group.raw?.relation || {}),
                id: group.id,
                min: group.min,
                max: group.max,
                index: nextIndex,
              },
            },
          };
        })
        .sort((left, right) => (
          firstFiniteNumber(left.relationIndex, left.index, 0) - firstFiniteNumber(right.relationIndex, right.index, 0)
        )),
      'Grupos de complementos reordenados no iFood.'
    );
    if (!result?.ok) return;

    setVisualCatalog((prev) => ({
      ...prev,
      optionGroups: toArray(prev.optionGroups).map((group) => {
        if (group.itemId !== product.id) return group;
        const groupKey = getVisualOptionGroupKey(group);
        if (!orderMap.has(groupKey)) return group;
        const nextIndex = orderMap.get(groupKey);
        return {
          ...group,
          relationIndex: nextIndex,
          index: nextIndex,
          raw: {
            ...(group.raw || {}),
            index: nextIndex,
            relation: {
              ...(group.raw?.relation || {}),
              id: group.id,
              min: group.min,
              max: group.max,
              index: nextIndex,
            },
          },
        };
      }),
      lastImportedAt: new Date().toISOString(),
      source: 'Grupos de complementos reordenados no iFood',
    }));
    closeGroupOrderDialog();
    showToast('Grupos de complementos reordenados no iFood.');
  };

  const saveCategoryFromStock = async () => {
    if (!selectedCatalogId) {
      showToast('Liste ou selecione um catalogo antes de salvar categoria.', 'warning');
      return;
    }
    const payload = {
      name: cleanText(categoryForm.name),
      status: normalizeCatalogStatus(categoryForm.status),
      template: cleanText(categoryForm.template) || 'DEFAULT',
      sequence: Number(categoryForm.sequence || 0),
    };
    if (!payload.name) {
      showToast('Informe o nome da categoria.', 'warning');
      return;
    }

    const isEditing = categoryDialog.mode === 'edit';
    const currentCategory = categoryDialog.category || {};
    if (isEditing && !currentCategory.id) {
      showToast('Categoria sem identificador para editar no iFood.', 'error');
      return;
    }

    const result = await runAction({
      id: isEditing ? 'category-update' : 'category-create-stock',
      path: isEditing ? '/categories/update' : '/categories/create',
      payload: isEditing
        ? { catalogId: selectedCatalogId, categoryId: currentCategory.id, category: payload }
        : { catalogId: selectedCatalogId, category: payload },
      label: isEditing ? 'Categoria atualizada no iFood.' : 'Categoria criada no iFood.',
      checkId: isEditing ? undefined : 'category-create',
      visualImport: (response) => ({
        categories: [{
          id: response.data?.id || response.data?.categoryId || currentCategory.id || payload.name,
          catalogId: selectedCatalogId,
          name: response.data?.name || payload.name,
          status: response.data?.status || payload.status,
          template: response.data?.template || payload.template,
          sequence: response.data?.sequence ?? payload.sequence,
          itemCount: currentCategory.itemCount || 0,
          raw: response.data || payload,
        }],
      }),
    });

    if (!result?.ok) return;
    const nextCategory = {
      ...currentCategory,
      id: result.data?.id || result.data?.categoryId || currentCategory.id || payload.name,
      catalogId: selectedCatalogId,
      name: result.data?.name || payload.name,
      status: result.data?.status || payload.status,
      template: result.data?.template || payload.template,
      sequence: result.data?.sequence ?? payload.sequence,
      itemCount: currentCategory.itemCount || 0,
      raw: result.data || payload,
    };
    setVisualCatalog((prev) => {
      const withoutCurrent = toArray(prev.categories).filter((item) => item.id !== nextCategory.id);
      return {
        ...prev,
        categories: dedupeById([...withoutCurrent, nextCategory], 'category'),
        lastImportedAt: new Date().toISOString(),
        source: result.label,
      };
    });
    setSelectedCategoryId(nextCategory.id);
    closeCategoryDialog();
  };

  const saveStockProduct = async () => {
    const currentProduct = currentSelectedStockProduct || selectedStockProduct;
    if (!currentProduct?.id || !currentProduct?.productId) {
      showToast('Selecione um produto valido.', 'warning');
      return;
    }

    const nextName = cleanText(stockProductForm.name);
    if (!nextName) {
      showToast('Informe o nome do produto.', 'warning');
      return;
    }

    if (cleanText(stockProductForm.price) === '') {
      showToast('Informe o preco do produto.', 'warning');
      return;
    }
    const nextPrice = parseCatalogMoney(stockProductForm.price);
    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      showToast('Informe um preco valido.', 'warning');
      return;
    }

    const currentExternalCode = getItemExternalCode(currentProduct);
    const nextExternalCode = cleanText(stockProductForm.externalCode || currentExternalCode);

    const nextStatus = normalizeCatalogStatus(stockProductForm.status);
    const parsedOriginalPrice = cleanText(stockProductForm.originalPrice)
      ? parseCatalogMoney(stockProductForm.originalPrice)
      : nextPrice;
    const nextOriginalPrice = Math.max(parsedOriginalPrice, nextPrice);
    const nextDescription = cleanText(stockProductForm.description);
    const nextImagePath = cleanText(firstValue(stockProductForm.imagePath, currentProduct.imagePath, currentProduct.raw?.product?.imagePath, ''));
    const visualCurrentPrice = Number(firstValue(currentProduct.price, currentProduct.raw?.price?.value, 0));
    const visualCurrentOriginalPrice = Number(firstValue(currentProduct.originalPrice, currentProduct.raw?.price?.originalValue, visualCurrentPrice));
    const externalCodeWasEdited = nextExternalCode !== currentExternalCode;
    const priceWasEdited = nextPrice !== visualCurrentPrice || nextOriginalPrice !== visualCurrentOriginalPrice;
    const statusWasEdited = nextStatus !== normalizeCatalogStatus(currentProduct.status);
    const visualCurrentShifts = toArray(currentProduct.raw?.shifts);
    const nextShifts = stockProductForm.availabilityMode === 'scheduled'
      ? toArray(stockProductForm.shifts)
      : [];
    const scheduleWasEdited = JSON.stringify(visualCurrentShifts) !== JSON.stringify(nextShifts);
    const inventoryAmount = cleanText(stockProductForm.inventoryAmount).replace(',', '.');
    if (stockProductForm.stockControlEnabled) {
      const amount = Number(inventoryAmount);
      if (!inventoryAmount || !Number.isFinite(amount) || amount < 0) {
        showToast('Informe uma quantidade de estoque valida.', 'warning');
        return;
      }
    }

    const hydrated = await hydrateStockItemForUpsert(currentProduct);
    if (!hydrated?.product) return;

    const sourceProduct = hydrated.product;
    const rawProduct = sourceProduct.raw?.product || {};
    const officialExternalCode = getItemExternalCode(sourceProduct);
    const officialPrice = Number(firstValue(sourceProduct.price, sourceProduct.raw?.price?.value, 0));
    const officialOriginalPrice = Number(firstValue(sourceProduct.originalPrice, sourceProduct.raw?.price?.originalValue, officialPrice));
    const officialStatus = normalizeCatalogStatus(sourceProduct.status);
    const effectiveExternalCode = externalCodeWasEdited ? nextExternalCode : officialExternalCode;
    const effectivePrice = priceWasEdited ? nextPrice : officialPrice;
    const effectiveOriginalPrice = priceWasEdited ? nextOriginalPrice : officialOriginalPrice;
    const effectiveStatus = statusWasEdited ? nextStatus : officialStatus;
    const effectiveCatalogContext = resolveCatalogContextForExternalCode(sourceProduct);
    const nextQuantity = buildCatalogQuantityPayload(stockProductForm.quantityValue, stockProductForm.quantityUnit);
    const nextWeight = nextQuantity
      ? { quantity: nextQuantity.value, unit: nextQuantity.unit }
      : null;
    const nextRawProduct = buildCatalogProductUpdatePayload({
      ...rawProduct,
      name: nextName,
      description: nextDescription,
      additionalInformation: nextDescription,
      ...((Object.prototype.hasOwnProperty.call(rawProduct, 'imagePath') || nextImagePath)
        ? { imagePath: nextImagePath }
        : {}),
      ...((Object.prototype.hasOwnProperty.call(rawProduct, 'serving') || stockProductForm.serving !== 'SERVES_1')
        ? { serving: stockProductForm.serving || rawProduct.serving }
        : {}),
      ...((Object.prototype.hasOwnProperty.call(rawProduct, 'dietaryRestrictions') || toArray(stockProductForm.dietaryRestrictions).length)
        ? { dietaryRestrictions: toArray(stockProductForm.dietaryRestrictions) }
        : {}),
      ...((Object.prototype.hasOwnProperty.call(rawProduct, 'weight') || nextWeight)
        ? { weight: nextWeight }
        : {}),
    });
    const nextProduct = {
      ...sourceProduct,
      name: nextName,
      description: nextDescription,
      price: effectivePrice,
      originalPrice: effectiveOriginalPrice,
      externalCode: effectiveExternalCode,
      productExternalCode: getProductExternalCode(sourceProduct),
      status: effectiveStatus,
      imagePath: nextImagePath,
      imageUrl: buildIfoodImageUrl(nextImagePath) || sourceProduct.imageUrl,
      raw: {
        ...(sourceProduct.raw || {}),
        status: effectiveStatus,
        price: { value: effectivePrice, originalValue: effectiveOriginalPrice },
        priceByCatalog: buildPriceByCatalogPayload(effectivePrice, effectiveOriginalPrice, sourceProduct),
        externalCode: effectiveExternalCode,
        shifts: nextShifts,
        product: nextRawProduct,
      },
    };
    setItemPayload(stringifyJson({ product: nextRawProduct }));
    setRefs((prev) => ({
      ...prev,
      itemId: currentProduct.id || prev.itemId,
      itemProductId: currentProduct.productId || prev.itemProductId,
      itemExternalCode: effectiveExternalCode || prev.itemExternalCode,
    }));

    const result = await runAction({
      id: 'stock-product-update',
      path: '/products/update',
      payload: { productId: currentProduct.productId, payload: nextRawProduct },
      label: 'Dados do produto enviados ao iFood; confirmando descricao.',
    });
    if (!result?.ok) return;
    try {
      await waitForCatalogBatchIfPresent(result, 'Atualizacao do produto');
    } catch (error) {
      setLastResult({ ok: false, error: error.message, action: 'stock-product-update-batch' });
      showToast(error.message, 'error');
      return;
    }

    let productUpdateConfirmed = false;
    let latestProductFlat = null;
    let productVerificationError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 750));
      }
      try {
        const verification = await catalogFetch('/items/flat', { itemId: currentProduct.id });
        latestProductFlat = verification.data;
        const product = toArray(verification.data?.products).find((item) => (
          cleanText(item.id) === cleanText(currentProduct.productId)
        ));
        if (product
          && cleanText(product.name) === nextName
          && cleanText(firstValue(product.description, product.additionalInformation, '')) === nextDescription) {
          productUpdateConfirmed = true;
          break;
        }
      } catch (error) {
        productVerificationError = error;
      }
    }

    if (!productUpdateConfirmed) {
      const freshCatalog = latestProductFlat?.item
        ? normalizeVisualFromFlat(latestProductFlat, {
          ...catalogScope,
          catalogId: currentProduct.catalogId || selectedCatalogId,
          catalogContext: effectiveCatalogContext,
          categoryId: currentProduct.categoryId,
          categoryName: currentProduct.categoryName,
        })
        : null;
      const freshProduct = toArray(freshCatalog?.items).find((product) => (
        cleanText(product.id) === cleanText(currentProduct.id)
        && cleanText(product.productId) === cleanText(currentProduct.productId)
      ));
      if (!freshProduct) {
        const message = productVerificationError?.message
          || 'A releitura fresca nao retornou a estrutura completa para o fallback seguro.';
        setLastResult({ ok: false, error: message, action: 'stock-item-product-fallback' });
        showToast(message, 'error');
        return;
      }
      const fallbackProduct = {
        ...freshProduct,
        name: nextName,
        description: nextDescription,
        raw: {
          ...(freshProduct.raw || {}),
          product: {
            ...(freshProduct.raw?.product || {}),
            ...nextRawProduct,
            id: currentProduct.productId,
          },
        },
      };
      const sanitizedItemPayload = buildVisualItemPayload(
        fallbackProduct,
        (groups) => groups,
        (options) => options,
        freshCatalog
      );
      const fallbackResult = await runAction({
        id: 'stock-item-product-fallback',
        path: '/items/upsert',
        payload: { payload: sanitizedItemPayload },
        label: 'Descricao sincronizada no item legado do iFood.',
        checkId: 'item-upsert',
      });
      if (!fallbackResult?.ok) return;
      try {
        await waitForCatalogBatchIfPresent(fallbackResult, 'Atualizacao do item legado');
      } catch (error) {
        setLastResult({ ok: false, error: error.message, action: 'stock-item-product-fallback-batch' });
        showToast(error.message, 'error');
        return;
      }
    }

    if (scheduleWasEdited) {
      const globalPrice = Number(firstValue(sourceProduct.raw?.price?.value, officialPrice));
      const globalOriginalPrice = Number(firstValue(sourceProduct.raw?.price?.originalValue, globalPrice));
      const scheduleProduct = {
        ...nextProduct,
        price: globalPrice,
        originalPrice: globalOriginalPrice,
        status: normalizeCatalogStatus(sourceProduct.raw?.status || officialStatus),
        externalCode: cleanText(firstValue(sourceProduct.raw?.externalCode, officialExternalCode)),
        raw: {
          ...(nextProduct.raw || {}),
          price: { value: globalPrice, originalValue: globalOriginalPrice },
          status: normalizeCatalogStatus(sourceProduct.raw?.status || officialStatus),
          externalCode: cleanText(firstValue(sourceProduct.raw?.externalCode, officialExternalCode)),
          shifts: nextShifts,
        },
      };
      const itemPayload = buildVisualItemPayload(scheduleProduct, (groups) => groups, (options) => options, hydrated.catalog);
      delete itemPayload.item.externalCodeByCatalog;
      setItemPayload(stringifyJson(itemPayload));
      const scheduleResult = await runAction({
        id: 'stock-item-schedule-upsert',
        path: '/items/upsert',
        payload: { payload: itemPayload },
        label: 'Disponibilidade semanal atualizada no iFood.',
        checkId: 'item-upsert',
      });
      if (!scheduleResult?.ok) return;
    }

    if (statusWasEdited && nextStatus !== officialStatus) {
      const statusResult = await runAction({
        id: 'stock-item-status',
        path: '/items/status',
        payload: {
          payload: {
            itemId: currentProduct.id,
            status: nextStatus,
            statusByCatalog: [{
              status: nextStatus,
              catalogContext: effectiveCatalogContext,
            }],
          },
        },
        label: 'Status do produto atualizado no iFood.',
      });
      if (!statusResult?.ok) return;
    }
    if (externalCodeWasEdited && nextExternalCode && nextExternalCode !== officialExternalCode) {
      const syncedExternalCode = await syncItemExternalCodeAfterUpsert({
        itemId: currentProduct.id,
        productId: currentProduct.productId,
        externalCode: nextExternalCode,
        source: nextProduct,
        actionId: 'stock-item-upsert-external-code',
      });
      if (!syncedExternalCode) return;
    }
    if (priceWasEdited && (nextPrice !== officialPrice || nextOriginalPrice !== officialOriginalPrice)) {
      const syncedPrice = await syncStockProductPrice(
        nextProduct,
        nextPrice,
        nextOriginalPrice,
        nextOriginalPrice > nextPrice
          ? 'Preco e desconto sincronizados no iFood.'
          : 'Preco sincronizado no iFood.'
      );
      if (!syncedPrice?.ok) return;
    }

    const hadInventoryControl = hasStockInventoryControl(currentProduct);
    let nextInventoryAmount = hadInventoryControl ? currentProduct.stock : null;
    if (stockProductForm.stockControlEnabled
      && (!hadInventoryControl || Number(currentProduct.stock) !== Number(inventoryAmount))) {
      const amount = Number(inventoryAmount);
      const inventoryResult = await runAction({
        id: 'stock-inventory-create',
        path: '/inventory/create',
        payload: { payload: { productId: currentProduct.productId, amount } },
        label: 'Controle de estoque atualizado no iFood.',
      });
      if (!inventoryResult?.ok) return;
      nextInventoryAmount = amount;
    } else if (!stockProductForm.stockControlEnabled && hadInventoryControl) {
      const inventoryResult = await runAction({
        id: 'stock-inventory-delete',
        path: '/inventory/delete',
        payload: { payload: { productIds: [currentProduct.productId] } },
        label: 'Controle de estoque removido no iFood.',
      });
      if (!inventoryResult?.ok) return;
      nextInventoryAmount = null;
    }

    let verifiedProduct = null;
    let verifiedItem = null;
    let verificationError = null;
    setBusyAction('stock-item-description-verify');
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 750));
        }
        try {
          const verification = await catalogFetch('/items/flat', { itemId: currentProduct.id });
          verifiedItem = verification.data?.item || {};
          verifiedProduct = toArray(verification.data?.products).find((product) => (
            cleanText(product.id) === cleanText(currentProduct.productId)
          )) || null;
          const verifiedDescription = cleanText(firstValue(verifiedProduct?.description, verifiedProduct?.additionalInformation, ''));
          const productMatches = verifiedProduct
            && cleanText(verifiedProduct.name) === nextName
            && verifiedDescription === nextDescription;
          const verifiedPriceInfo = resolveCatalogPrice(verifiedItem, { catalogContext: effectiveCatalogContext });
          const itemMatches = cleanText(verifiedItem.externalCode) === effectiveExternalCode
            && Number(verifiedPriceInfo.value || 0) === effectivePrice
            && Number(firstValue(verifiedPriceInfo.originalValue, verifiedPriceInfo.value, 0)) === effectiveOriginalPrice
            && normalizeCatalogStatus(verifiedItem.status) === effectiveStatus;
          if (productMatches && itemMatches) break;
          verifiedProduct = null;
          verifiedItem = null;
        } catch (error) {
          verificationError = error;
        }
      }
    } finally {
      setBusyAction('');
    }
    if (!verifiedProduct || !verifiedItem) {
      const message = verificationError?.message
        || 'O iFood respondeu ao salvamento, mas a descricao relida nao corresponde ao valor informado.';
      setLastResult({ ok: false, error: message, action: 'stock-item-description-verify' });
      showToast(message, 'error');
      return;
    }
    setLastResult({ ok: true, action: 'stock-item-description-verify', label: 'Descricao confirmada no iFood.' });
    showToast('Descricao confirmada no iFood.');

    setVisualCatalog((prev) => ({
      ...prev,
      items: toArray(prev.items).map((item) => (
        item.id === currentProduct.id
          ? {
            ...item,
            name: nextName,
            description: nextDescription,
            price: effectivePrice,
            originalPrice: effectiveOriginalPrice,
            externalCode: effectiveExternalCode,
            productExternalCode: getProductExternalCode(sourceProduct),
            status: effectiveStatus,
            imagePath: nextImagePath,
            imageUrl: buildIfoodImageUrl(nextImagePath) || item.imageUrl,
            stock: nextInventoryAmount,
            raw: nextProduct.raw,
          }
          : item
      )),
      lastImportedAt: new Date().toISOString(),
      source: 'Produto atualizado no Catalog',
    }));
    closeStockProductDialog();
  };

  const uploadImage = () => runAction({
    id: 'image-upload',
    path: '/image/upload',
    payload: { image: imageDataUrl },
    label: 'Imagem enviada.',
    checkId: 'image-upload',
    onSuccess: (result) => {
      const path = extractImagePath(result.data);
      if (path) setImagePath(path);
    },
  });

  const hydrateCatalogItemsFromFlat = async (items = []) => {
    const uniqueItems = dedupeById(toArray(items).filter((item) => item.id), 'flat-item');
    if (!uniqueItems.length) return;

    setBusyAction('visual-items-flat');
    let hydrated = 0;
    let failed = 0;
    try {
      for (const item of uniqueItems) {
        try {
          const result = await catalogFetch('/items/flat', { itemId: item.id });
          const imported = normalizeVisualFromFlat(result.data, {
            ...catalogScope,
            catalogId: item.catalogId || selectedCatalogId,
            catalogContext: item.catalogContext || item.priceCatalogContext || findCatalogContext(visualCatalog.catalogs, item.catalogId || selectedCatalogId),
            categoryId: item.categoryId,
            categoryName: item.categoryName,
          });
          if (importVisualData(imported, 'Detalhes e imagens dos complementos importados do iFood')) {
            hydrated += 1;
          }
        } catch {
          failed += 1;
        }
      }

      if (hydrated) {
        showToast(
          failed
            ? `Fotos dos complementos atualizadas em ${hydrated} item(ns); ${failed} item(ns) nao retornaram detalhes.`
            : 'Fotos dos complementos atualizadas pelo item flat do iFood.',
          failed ? 'warning' : 'success'
        );
      } else if (failed) {
        showToast('Nao foi possivel buscar detalhes dos itens para atualizar fotos dos complementos.', 'warning');
      }
    } finally {
      setBusyAction('');
    }
  };

  const importFullCatalogVisual = async () => {
    const catalogsResult = await runAction({
      id: 'visual-catalogs',
      path: '/catalogs',
      label: 'Catalogos importados para visual.',
      checkId: 'catalogs',
      visualImport: (result) => ({ catalogs: normalizeCatalogsForVisual(result.data, catalogScope) }),
    });
    const catalogId = selectedCatalogId || findFirstCatalogId(catalogsResult?.data);
    if (!catalogId) {
      showToast('Nenhum catalogId retornado para importar categorias.', 'warning');
      return;
    }

    setSelectedCatalogId(catalogId);
    const catalogContext = findCatalogContext(normalizeCatalogsForVisual(catalogsResult?.data, catalogScope), catalogId);
    const categoriesResult = await runAction({
      id: 'visual-categories',
      path: '/categories/list',
      payload: { catalogId, includeItems: true },
      label: 'Categorias, produtos e complementos importados.',
      checkId: 'categories-list',
      visualImport: (result) => normalizeVisualFromCategories(result.data, { ...catalogScope, catalogId, catalogContext }),
      onSuccess: (result) => {
        const categoryId = findFirstCategoryId(result.data);
        if (categoryId && !selectedCategoryId) setSelectedCategoryId(categoryId);
        const item = extractEvidenceItem(result.data);
        if (item) setEvidenceItem(item);
      },
    });

    if (categoriesResult?.data) {
      const imported = normalizeVisualFromCategories(categoriesResult.data, { ...catalogScope, catalogId, catalogContext });
      await hydrateCatalogItemsFromFlat(imported.items);
    }
  };

  const syncStockCatalogFromIfood = async () => {
    setBusyAction('stock-catalog-sync');
    try {
      const catalogsResult = await catalogFetch('/catalogs');
      const catalogs = normalizeCatalogsForVisual(catalogsResult.data, catalogScope);
      const catalogId = selectedCatalogId || findFirstCatalogId(catalogsResult.data);
      if (!catalogId) {
        showToast('Nenhum catalogId retornado para atualizar o cardápio.', 'warning');
        return;
      }

      setSelectedCatalogId(catalogId);
      const catalogContext = findCatalogContext(catalogs, catalogId);
      const categoriesResult = await catalogFetch('/categories/list', { catalogId, includeItems: true });
      const imported = normalizeVisualFromCategories(categoriesResult.data, { ...catalogScope, catalogId, catalogContext });
      let nextVisualCatalog = mergeVisualCatalog(emptyVisualCatalog, {
        catalogs,
        ...imported,
        lastImportedAt: new Date().toISOString(),
        source: 'Cardápio sincronizado integralmente do iFood',
      });

      const categoryId = findFirstCategoryId(categoriesResult.data);
      if (categoryId) setSelectedCategoryId(categoryId);
      const item = extractEvidenceItem(categoriesResult.data);
      if (item) setEvidenceItem(item);

      let hydrated = 0;
      let failed = 0;
      const uniqueItems = dedupeById(toArray(imported.items).filter((entry) => entry.id), 'stock-sync-item');
      for (const itemEntry of uniqueItems) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const flatResult = await catalogFetch('/items/flat', { itemId: itemEntry.id });
          const flatImported = normalizeVisualFromFlat(flatResult.data, {
            ...catalogScope,
            catalogId: itemEntry.catalogId || catalogId,
            catalogContext: itemEntry.catalogContext || itemEntry.priceCatalogContext || catalogContext,
            categoryId: itemEntry.categoryId,
            categoryName: itemEntry.categoryName,
          });
          nextVisualCatalog = mergeVisualCatalog(nextVisualCatalog, flatImported);
          hydrated += 1;
        } catch {
          failed += 1;
        }
      }

      const finalVisualCatalog = {
        ...nextVisualCatalog,
        lastImportedAt: new Date().toISOString(),
        source: failed
          ? `Cardápio sincronizado do iFood com ${failed} item(ns) sem detalhe flat`
          : 'Cardápio sincronizado integralmente do iFood',
      };
      setVisualCatalog(finalVisualCatalog);
      setLastResult({
        ...categoriesResult,
        action: 'stock-catalog-sync',
        label: 'Cardápio atualizado pelo iFood.',
        hydratedItems: hydrated,
        failedItems: failed,
      });
      setStockSelectedEntryIds([]);
      setStockSelectedGroupIds([]);
      setPdvDrafts({});
      setCollapsedStockCategories({});
      setExpandedComplementProductId('');
      closeStockProductDialog();

      showToast(
        failed
          ? `Cardápio atualizado; ${failed} item(ns) não retornaram detalhes completos.`
          : 'Cardápio atualizado com todos os dados retornados pelo iFood.',
        failed ? 'warning' : 'success'
      );
    } catch (error) {
      setLastResult({ ok: false, error: error.message, action: 'stock-catalog-sync' });
      showToast(error.message || 'Falha ao atualizar cardápio pelo iFood.', 'error');
    } finally {
      setBusyAction('');
    }
  };

  const clearImportedStockProducts = () => {
    const totalProducts = toArray(visualCatalog.items).filter((item) => !item.merchantId || item.merchantId === selectedMerchantId).length;
    const totalCategories = toArray(visualCatalog.categories).filter((item) => !item.merchantId || item.merchantId === selectedMerchantId).length;
    if (!totalProducts && !totalCategories) {
      showToast('Não há produtos ou categorias importados para apagar.', 'info');
      return;
    }
    const confirmed = window.confirm('Apagar todos os produtos e categorias importados somente desta aba Catalog? O iFood e o sistema principal não serão alterados.');
    if (!confirmed) return;

    setVisualCatalog((prev) => ({
      ...prev,
      categories: toArray(prev.categories).filter((item) => item.merchantId && item.merchantId !== selectedMerchantId),
      items: toArray(prev.items).filter((item) => item.merchantId && item.merchantId !== selectedMerchantId),
      optionGroups: toArray(prev.optionGroups).filter((item) => item.merchantId && item.merchantId !== selectedMerchantId),
      options: toArray(prev.options).filter((item) => item.merchantId && item.merchantId !== selectedMerchantId),
      lastImportedAt: new Date().toISOString(),
      source: 'Produtos e categorias importados removidos somente do workspace Catalog',
    }));
    setSelectedCategoryId('');
    setEvidenceItem(null);
    setStockSelectedEntryIds([]);
    setStockSelectedGroupIds([]);
    setPdvDrafts({});
    setCollapsedStockCategories({});
    setExpandedComplementProductId('');
    closeStockProductDialog();
    showToast('Produtos e categorias importados apagados somente do Catalog.');
  };

  const importLastResultToVisual = () => {
    if (!lastResult?.data) {
      showToast('Nao ha ultima resposta para importar.', 'warning');
      return;
    }

    const action = lastResult.action || '';
    let imported = emptyVisualCatalog;
    if (action.includes('catalog')) {
      imported = { catalogs: normalizeCatalogsForVisual(lastResult.data, catalogScope) };
    }
    if (action.includes('categories')) {
      imported = normalizeVisualFromCategories(lastResult.data, {
        ...catalogScope,
        catalogId: selectedCatalogId,
        catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
      });
    }
    if (action.includes('items-list') || action.includes('sellable') || action.includes('unsellable')) {
      imported = normalizeVisualFromItems(lastResult.data, {
        ...catalogScope,
        catalogId: selectedCatalogId,
        catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
        categoryId: selectedCategoryId,
      });
    }
    if (action.includes('items-flat')) {
      imported = normalizeVisualFromFlat(lastResult.data, {
        ...catalogScope,
        catalogId: selectedCatalogId,
        catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
        categoryId: selectedCategoryId,
      });
    }

    if (!importVisualData(imported, `Importado manualmente de ${action || 'ultima resposta'}`)) {
      showToast('Esse retorno nao contem catalogos, categorias ou produtos reconheciveis.', 'warning');
      return;
    }
    showToast('Retorno importado para a visao humana.');
  };

  const visualCategories = toArray(visualCatalog.categories).filter((item) => (
    !selectedMerchantId || !item.merchantId || item.merchantId === selectedMerchantId
  ));
  const categoryProductCounts = toArray(visualCatalog.items).reduce((countMap, item) => {
    const categoryId = item.categoryId || 'uncategorized';
    countMap.set(categoryId, Number(countMap.get(categoryId) || 0) + 1);
    return countMap;
  }, new Map());
  const categoryOrderItems = categoryOrderDialog.ids
    .map((id) => visualCategories.find((categoryItem) => categoryItem.id === id))
    .filter(Boolean);
  const baseStockProducts = stockCategories.flatMap((stockCategory) => (
    toArray(stockCategory.products).map((product) => ({
      ...product,
      entryType: 'item',
      categoryName: product.categoryName || stockCategory.name,
      categoryStatus: product.categoryStatus || stockCategory.status,
    }))
  ));
  const baseStockProductIds = new Set(baseStockProducts.map((product) => product.id));
  const stockGroups = toArray(visualCatalog.optionGroups).filter((group) => baseStockProductIds.has(group.itemId));
  const stockGroupsByScopeKey = new Map(stockGroups.map((group) => [getVisualOptionGroupKey(group), group]));
  const getStockGroupForOption = (option) => (
    stockGroupsByScopeKey.get(getVisualOptionGroupKey({ id: option.groupId, itemId: option.itemId }))
    || stockGroups.find((group) => group.id === option.groupId && group.itemId === option.itemId)
    || stockGroups.find((group) => group.id === option.groupId)
    || {}
  );
  const baseProductsById = new Map(baseStockProducts.map((product) => [product.id, product]));
  const allStockOptions = toArray(visualCatalog.options).filter((option) => (
    baseStockProductIds.has(option.itemId)
  ));
  const optionUsageByProductId = allStockOptions.reduce((usageMap, option) => {
    const productId = cleanText(option.productId);
    if (!productId) return usageMap;
    const group = getStockGroupForOption(option);
    const parent = baseProductsById.get(option.itemId) || {};
    const current = usageMap.get(productId) || { groups: [], parents: [] };
    if (group.name) current.groups.push(group.name);
    if (parent.name) current.parents.push(parent.name);
    usageMap.set(productId, current);
    return usageMap;
  }, new Map());
  const allStockProducts = baseStockProducts.map((product) => {
    const usage = optionUsageByProductId.get(product.productId);
    return {
      ...product,
      classification: usage ? 'Item principal, Complemento' : 'Item principal',
      availableIn: formatAvailabilityList([
        product.categoryName,
        ...(usage?.groups || []),
      ]) || product.categoryName,
      complementUsage: usage || null,
    };
  });
  const stockProductsById = new Map(allStockProducts.map((product) => [product.id, product]));
  const stockProductsByProductId = new Map(allStockProducts
    .filter((product) => product.productId)
    .map((product) => [product.productId, product]));
  const allStockComplementEntries = allStockOptions.map((option) => {
    const group = getStockGroupForOption(option);
    const parent = stockProductsById.get(option.itemId) || {};
    const optionProduct = stockProductsByProductId.get(option.productId) || {};
    const imagePath = option.imagePath || optionProduct.imagePath || '';
    const imageUrl = option.imageUrl || optionProduct.imageUrl || buildIfoodImageUrl(imagePath);
    return {
      ...option,
      entryType: 'option',
      classification: 'Complemento',
      name: option.name || optionProduct.name,
      description: option.description || optionProduct.description,
      categoryName: parent.categoryName || '',
      categoryStatus: parent.categoryStatus || '',
      availableIn: group.name || parent.name || 'Complementos',
      parentName: parent.name || '',
      groupName: group.name || '',
      imagePath,
      imageUrl,
    };
  });
  const allStockRows = allStockProducts;
  const allStockProductRows = allStockRows.filter((entry) => {
    const term = cleanText(stockSearchTerm).toLowerCase();
    const matchesTerm = !term || [
      entry.name,
      entry.description,
      entry.classification,
      entry.availableIn,
      entry.externalCode,
    ].some((value) => cleanText(value).toLowerCase().includes(term));
    if (!matchesTerm) return false;
    if (stockProductStatusFilter === 'paused') return normalizeCatalogStatus(entry.status) === 'UNAVAILABLE';
    if (stockProductStatusFilter === 'active') return normalizeCatalogStatus(entry.status) !== 'UNAVAILABLE';
    return true;
  });
  const pausedStockRowsCount = allStockRows.filter((entry) => normalizeCatalogStatus(entry.status) === 'UNAVAILABLE').length;
  const activeStockRowsCount = allStockRows.length - pausedStockRowsCount;
  const availableComboProducts = allStockProducts.filter((product) => (
    product.productId
    && !isCatalogCombo(product)
    && !isPausedCatalogStatus(product.status)
    && !isPausedCatalogStatus(product.categoryStatus)
  ));
  const stockSharedGroups = Array.from(stockGroups.reduce((groupMap, group) => {
    const sharedGroupKey = getSharedOptionGroupKey(group);
    if (!sharedGroupKey) return groupMap;
    const parent = stockProductsById.get(group.itemId) || {};
    const current = groupMap.get(sharedGroupKey) || {
      ...group,
      sharedGroupKey,
      linkedGroups: [],
      linkedProductNames: [],
      linkedProductIds: [],
    };
    current.linkedGroups.push(group);
    if (parent.id && !current.linkedProductIds.includes(parent.id)) {
      current.linkedProductIds.push(parent.id);
    }
    if (parent.name && !current.linkedProductNames.includes(parent.name)) {
      current.linkedProductNames.push(parent.name);
    }
    current.availableIn = formatAvailabilityList(current.linkedProductNames) || parent.name || 'Produto importado';
    current.itemCount = current.linkedProductNames.length || current.linkedGroups.length;
    groupMap.set(sharedGroupKey, current);
    return groupMap;
  }, new Map()).values());
  const getStockGroupSelectionId = (group) => `group:${group.sharedGroupKey || getVisualOptionGroupKey(group)}`;
  const filteredStockGroups = stockSharedGroups.filter((group) => {
    const term = cleanText(stockGroupSearchTerm || stockSearchTerm).toLowerCase();
    if (!term) return true;
    return [
      group.name,
      group.externalCode,
      group.availableIn,
      ...toArray(group.linkedProductNames),
    ].some((value) => cleanText(value).toLowerCase().includes(term));
  });
  const editingGroup = groupEditor.open
    ? findVisualOptionGroup(stockGroups, groupEditor.groupId)
    : null;
  const editingGroupOptions = editingGroup
    ? allStockComplementEntries.filter((option) => {
      if (option.groupId !== editingGroup.id || option.itemId !== editingGroup.itemId) return false;
      const term = cleanText(groupEditor.search).toLowerCase();
      if (!term) return true;
      return [option.name, option.description, option.externalCode]
        .some((value) => cleanText(value).toLowerCase().includes(term));
    })
    : [];
  const editingGroupLinkedGroups = editingGroup
    ? stockGroups.filter((group) => getSharedOptionGroupKey(group) === getSharedOptionGroupKey(editingGroup))
    : [];
  const editingGroupLinkedProducts = editingGroupLinkedGroups.map((group) => ({
    group,
    product: stockProductsById.get(group.itemId) || {},
  })).filter(({ product }) => product.id);
  const editingGroupLinkedProductIds = new Set(editingGroupLinkedProducts.map(({ product }) => product.id));
  const editingGroupAvailableProducts = editingGroup
    ? allStockProducts.filter((product) => !editingGroupLinkedProductIds.has(product.id))
    : [];
  const currentSelectedStockProduct = selectedStockProduct
    ? stockProductsById.get(selectedStockProduct.id) || selectedStockProduct
    : null;
  const selectedStockCategory = currentSelectedStockProduct
    ? visualCategories.find((item) => item.id === currentSelectedStockProduct.categoryId)
    : null;
  const selectedStockGroups = currentSelectedStockProduct
    ? toArray(visualCatalog.optionGroups)
      .filter((group) => group.itemId === currentSelectedStockProduct.id)
      .sort((left, right) => (
        firstFiniteNumber(left.relationIndex, left.raw?.relation?.index, left.index, 0)
        - firstFiniteNumber(right.relationIndex, right.raw?.relation?.index, right.index, 0)
      ))
    : [];
  const selectedStockOptions = currentSelectedStockProduct
    ? allStockComplementEntries.filter((option) => option.itemId === currentSelectedStockProduct.id)
    : [];
  const groupOrderItems = groupOrderDialog.ids
    .map((id) => findVisualOptionGroup(selectedStockGroups, id))
    .filter(Boolean);
  const filteredProductGroupCopyGroups = stockSharedGroups.filter((group) => {
    if (currentSelectedStockProduct?.id && toArray(group.linkedProductIds).includes(currentSelectedStockProduct.id)) {
      return false;
    }
    const term = cleanText(productGroupDialog.copySearch).toLowerCase();
    if (!term) return true;
    return [
      group.name,
      group.externalCode,
      group.availableIn,
      ...toArray(group.linkedProductNames),
    ].some((value) => cleanText(value).toLowerCase().includes(term));
  });
  const getProductComplementCount = (product) => {
    if (!product?.id) return 0;
    const optionIds = new Set(allStockComplementEntries
      .filter((option) => option.itemId === product.id)
      .map((option) => cleanText(option.id))
      .filter(Boolean));
    if (optionIds.size) return optionIds.size;

    return new Set(toArray(visualCatalog.options)
      .filter((option) => option.itemId === product.id)
      .map((option) => cleanText(option.id))
      .filter(Boolean)).size;
  };

  const getProductComplementGroups = (product) => {
    if (!product?.id) return [];
    const groups = stockGroups
      .filter((group) => group.itemId === product.id)
      .sort((left, right) => Number(left.relationIndex ?? left.index ?? 0) - Number(right.relationIndex ?? right.index ?? 0));
    if (groups.length) return groups;

    const ungroupedOptions = allStockComplementEntries.filter((option) => option.itemId === product.id && !option.groupId);
    if (!ungroupedOptions.length) return [];
    return [{
      id: `${product.id}:sem-grupo`,
      itemId: product.id,
      name: 'Complementos',
      min: 0,
      max: ungroupedOptions.length,
      status: '',
      virtual: true,
    }];
  };

  const getProductComplementOptions = (product, group) => {
    if (!product?.id || !group?.id) return [];
    return allStockComplementEntries
      .filter((option) => (
        option.itemId === product.id
        && (group.virtual ? !option.groupId : option.groupId === group.id)
      ))
      .sort((left, right) => Number(left.raw?.index ?? left.index ?? 0) - Number(right.raw?.index ?? right.index ?? 0));
  };

  const renderCatalogImageBox = (entry, size = 64) => (
    <Box
      sx={{
        width: size,
        height: Math.round(size * 0.75),
        aspectRatio: '4 / 3',
        borderRadius: 2,
        bgcolor: '#f4f4f5',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {entry?.imageUrl ? (
        <Box component="img" src={entry.imageUrl} alt={entry.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <ImageOutlinedIcon color="action" />
      )}
    </Box>
  );

  const renderInlineComplementOption = (option) => {
    const isUnavailable = normalizeCatalogStatus(option.status) === 'UNAVAILABLE';
    const priceValue = getInlineComplementPriceValue(option);
    const optionScopeKey = getVisualOptionKey(option);
    return (
      <Box
        key={optionScopeKey}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 1fr) 160px 60px 48px' },
          gap: 1.5,
          alignItems: 'center',
          px: 1.5,
          py: 1.25,
          borderTop: '1px solid #f0f0f0',
          bgcolor: '#fff',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {renderCatalogImageBox(option, 56)}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }} noWrap>{option.name || 'Complemento sem nome'}</Typography>
            {option.description ? (
              <Typography color="text.secondary" noWrap>{option.description}</Typography>
            ) : null}
          </Box>
        </Stack>
        <TextField
          value={priceValue}
          onChange={(event) => setInlineComplementPriceDrafts((prev) => ({ ...prev, [optionScopeKey]: event.target.value }))}
          onBlur={() => updateInlineComplementPrice(option, priceValue)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
          size="small"
          label="Preco"
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
          sx={{
            width: { xs: '100%', md: 150 },
            justifySelf: { xs: 'stretch', md: 'end' },
            '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 54, bgcolor: '#fff' },
            '& input': { fontSize: 18, fontWeight: 700 },
          }}
        />
        <IconButton
          aria-label={isUnavailable ? 'Ativar complemento' : 'Pausar complemento'}
          title={isUnavailable ? 'Ativar complemento no iFood' : 'Pausar complemento no iFood'}
          onClick={() => updateStockOptionStatus(option)}
          sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: 48, height: 48, justifySelf: { xs: 'start', md: 'end' } }}
        >
          {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
        </IconButton>
        <IconButton
          aria-label="Opcoes do complemento"
          title="Opcoes do complemento"
          onClick={(event) => openStockOptionMenu(event, option)}
          sx={{ width: 48, height: 48, justifySelf: { xs: 'start', md: 'end' } }}
        >
          <MoreVertRoundedIcon />
        </IconButton>
      </Box>
    );
  };

  const renderInlineComplementGroup = (product, group) => {
    const options = getProductComplementOptions(product, group);
    const groupScopeKey = getVisualOptionGroupKey(group);
    const isCollapsed = Boolean(collapsedInlineComplementGroups[groupScopeKey]);
    const isUnavailable = normalizeCatalogStatus(group.status) === 'UNAVAILABLE';
    const reused = !group.virtual && stockGroups.filter((item) => (
      cleanText(item.name).toLowerCase() === cleanText(group.name).toLowerCase()
    )).length > 1;

    return (
      <Paper key={groupScopeKey} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: '#fff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ px: 1.5, py: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 850 }} noWrap>{group.name || 'Grupo de complementos'}</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              <Chip size="small" label={Number(group.min || 0) > 0 ? 'Obrigatorio' : 'Opcional'} sx={{ height: 24, fontWeight: 800, bgcolor: '#eeeeef' }} />
              {reused ? (
                <Chip
                  size="small"
                  icon={<LinkRoundedIcon sx={{ fontSize: 16 }} />}
                  label="Reutilizado"
                  sx={{ height: 24, fontWeight: 800, bgcolor: '#eeeeef' }}
                />
              ) : null}
              <Chip size="small" label={`${options.length} complemento${options.length === 1 ? '' : 's'}`} sx={{ height: 24, fontWeight: 800, bgcolor: '#eeeeef' }} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center">
            {!group.virtual ? (
              <IconButton
                aria-label={isUnavailable ? 'Ativar grupo' : 'Pausar grupo'}
                title={isUnavailable ? 'Ativar grupo no iFood' : 'Pausar grupo no iFood'}
                onClick={() => toggleStockGroupStatus(group)}
                sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: 50, height: 50 }}
              >
                {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
              </IconButton>
            ) : null}
            {!group.virtual ? (
              <IconButton
                aria-label="Opcoes do grupo"
                title="Opcoes do grupo"
                onClick={(event) => openStockGroupMenu(event, group)}
                sx={{ width: 50, height: 50 }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            ) : null}
            <IconButton
              aria-label={isCollapsed ? 'Expandir grupo' : 'Recolher grupo'}
              title={isCollapsed ? 'Expandir grupo' : 'Recolher grupo'}
              onClick={() => setCollapsedInlineComplementGroups((prev) => ({ ...prev, [groupScopeKey]: !prev[groupScopeKey] }))}
              sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: 50, height: 50 }}
            >
              {isCollapsed ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}
            </IconButton>
          </Stack>
        </Stack>
        {!isCollapsed ? (
          <Box sx={{ mx: 1.5, mb: 1.5, borderRadius: 3, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
            {options.length ? (
              options.map((option) => renderInlineComplementOption(option))
            ) : (
              <Box sx={{ p: 2 }}>
                <Alert severity="info">Grupo sem complementos importados da API Catalog.</Alert>
              </Box>
            )}
          </Box>
        ) : null}
      </Paper>
    );
  };

  const renderProductComplementPanel = (product) => {
    const groups = getProductComplementGroups(product);
    return (
      <Box sx={{ gridColumn: '1 / -1', px: { xs: 1, md: 2 }, pb: 2.5 }}>
        {groups.length ? (
          <Stack spacing={2}>
            {groups.map((group) => renderInlineComplementGroup(product, group))}
          </Stack>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Produto sem grupos de complementos importados da API Catalog.
          </Alert>
        )}
      </Box>
    );
  };

  const renderStockProductRow = (product) => {
    const complementCount = getProductComplementCount(product);
    const isUnavailable = normalizeCatalogStatus(product.status) === 'UNAVAILABLE';
    const itemIsCombo = isCatalogCombo(product);
    const categoryPaused = isPausedCatalogStatus(product.categoryStatus);
    const currentPrice = Number(product.price || 0);
    const originalPrice = Number(product.originalPrice || 0);
    const isComplementPanelOpen = expandedComplementProductId === product.id;
    const inventoryKey = getStockInventoryKey(product);
    const inventoryControlOpen = hasStockInventoryControl(product) || stockInventoryEditingIds.includes(inventoryKey);
    const inventoryValue = getStockInventoryDraftValue(product);

    return (
      <Box
        key={product.id}
        sx={{
          borderTop: '1px solid #f0f0f0',
          bgcolor: isComplementPanelOpen ? '#dedede' : '#fff',
          transition: 'background-color 160ms ease',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 1fr) auto' },
            gap: 1.5,
            alignItems: 'center',
            px: { xs: 1.5, md: 2 },
            py: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            {renderCatalogImageBox(product, 72)}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 850 }} noWrap>{product.name}</Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" variant={itemIsCombo ? 'filled' : 'outlined'} label={itemIsCombo ? 'Combo' : 'Oferta simples'} sx={{ height: 24, fontWeight: 700 }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {product.description || 'Sem descricao'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant={isComplementPanelOpen ? 'contained' : 'outlined'}
              color="inherit"
              onClick={() => setExpandedComplementProductId((current) => (current === product.id ? '' : product.id))}
              sx={{
                borderRadius: 3,
                minHeight: 48,
                fontWeight: 900,
                px: 2.5,
                bgcolor: isComplementPanelOpen ? '#111' : '#fff',
                color: isComplementPanelOpen ? '#fff' : '#111',
                border: isComplementPanelOpen ? '2px solid #1976d2' : '1px solid #d1d5db',
                '&:hover': {
                  bgcolor: isComplementPanelOpen ? '#111' : '#f9fafb',
                  borderColor: isComplementPanelOpen ? '#1976d2' : '#9ca3af',
                },
              }}
            >
              Complementos
              <Chip
                size="small"
                label={complementCount}
                sx={{
                  ml: 1,
                  height: 24,
                  minWidth: 24,
                  fontWeight: 900,
                  bgcolor: isComplementPanelOpen ? '#f4f4f5' : '#eeeeef',
                  color: '#111',
                }}
              />
            </Button>
            {inventoryControlOpen ? (
              <Tooltip title="Estoque">
                <TextField
                  value={inventoryValue}
                  onChange={(event) => setStockInventoryDrafts((prev) => ({ ...prev, [inventoryKey]: event.target.value }))}
                  onBlur={() => saveStockInventoryControl(product)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                    if (event.key === 'Escape') {
                      closeStockInventoryControl(inventoryKey);
                    }
                  }}
                  size="small"
                  placeholder="Estoque"
                  inputProps={{ inputMode: 'decimal', min: 0 }}
                  sx={{
                    width: 112,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      minHeight: 44,
                      bgcolor: '#fff',
                    },
                    '& input': {
                      fontWeight: 850,
                      textAlign: 'center',
                    },
                  }}
                />
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<Inventory2OutlinedIcon />}
                onClick={() => openStockInventoryControl(product)}
                sx={{ borderRadius: 2, minHeight: 44 }}
              >
                Estoque
              </Button>
            )}
            <Stack direction="row" alignItems="center" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', minHeight: 44, bgcolor: '#fff' }}>
              {originalPrice > currentPrice ? (
                <Typography color="text.secondary" sx={{ px: 1.5, textDecoration: 'line-through', bgcolor: '#f4f4f5' }}>
                  {formatCurrency(originalPrice)}
                </Typography>
              ) : null}
              {itemIsCombo ? (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
                      A partir de
                    </Typography>
                    <Typography sx={{ fontWeight: 850 }}>{formatCurrency(currentPrice)}</Typography>
                  </Box>
                  <Tooltip title="Menor valor do combo antes das escolhas de itens e adicionais.">
                    <InfoRoundedIcon fontSize="small" color="action" />
                  </Tooltip>
                </Stack>
              ) : (
                <Typography sx={{ px: 1.5, fontWeight: 850 }}>
                  {formatCurrency(currentPrice)}
                </Typography>
              )}
              <IconButton
                aria-label="Aplicar desconto"
                title="Aplicar desconto"
                onClick={() => openProductPromotionDialog(product)}
                sx={{ borderLeft: '1px solid #e5e7eb', borderRadius: 0, color: '#166534' }}
              >
                <LocalOfferRoundedIcon />
              </IconButton>
            </Stack>
            {categoryPaused ? (
              <Tooltip
                title={(
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>O produto esta em uma categoria pausada</Typography>
                    <Typography variant="body2">Ative a categoria em que o produto esta para que ele fique ativo em seu cardapio.</Typography>
                  </Box>
                )}
              >
                <ErrorRoundedIcon sx={{ color: '#a66a00' }} />
              </Tooltip>
            ) : null}
            <IconButton
              aria-label={isUnavailable ? 'Reativar produto' : 'Pausar produto'}
              title={isUnavailable ? 'Reativar produto no iFood' : 'Pausar produto no iFood'}
              onClick={() => toggleStockProductStatus(product)}
              sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: 44, height: 44, bgcolor: '#fff' }}
            >
              {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
            </IconButton>
            <IconButton
              aria-label="Acoes do produto"
              title="Acoes do produto"
              onClick={(event) => openStockProductMenu(event, product)}
              sx={{ width: 44, height: 44 }}
            >
              <MoreVertRoundedIcon />
            </IconButton>
          </Stack>
        </Box>
        {isComplementPanelOpen ? renderProductComplementPanel(product) : null}
      </Box>
    );
  };

  const renderStockCategoryBlock = (stockCategory) => {
    const isCollapsed = Boolean(collapsedStockCategories[stockCategory.id]);
    const isUnavailable = normalizeCatalogStatus(stockCategory.status) === 'UNAVAILABLE';
    const hasProducts = stockCategory.products.length > 0;

    return (
      <Paper key={stockCategory.id} elevation={0} sx={{ borderRadius: 3, bgcolor: '#f4f4f5', overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between" sx={{ px: 2, py: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <IconButton
              aria-label="Editar categoria"
              title="Editar categoria"
              onClick={() => openEditCategoryDialog(stockCategory)}
              size="small"
            >
              <EditRoundedIcon />
            </IconButton>
            <Button
              color="inherit"
              onClick={() => openEditCategoryDialog(stockCategory)}
              sx={{ p: 0, minWidth: 0, textDecoration: 'underline', fontWeight: 900, fontSize: 18 }}
            >
              {stockCategory.name || 'Sem categoria'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              ({getStockPlural(stockCategory.products.length, 'item', 'itens')})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="contained" color="inherit" disableElevation onClick={() => createStockItem(stockCategory, 'combo')} sx={{ bgcolor: '#fff', borderRadius: 2, fontWeight: 850 }}>
              Criar combo
            </Button>
            <Button variant="contained" color="inherit" disableElevation onClick={() => createStockItem(stockCategory, 'oferta')} sx={{ bgcolor: '#fff', borderRadius: 2, fontWeight: 850 }}>
              Criar oferta
            </Button>
            <IconButton
              aria-label={isUnavailable ? 'Reativar categoria' : 'Pausar categoria'}
              title={isUnavailable ? 'Reativar categoria no iFood' : 'Pausar categoria no iFood'}
              onClick={() => toggleStockCategoryStatus(stockCategory)}
              sx={{ bgcolor: '#fff', borderRadius: 2, width: 44, height: 44 }}
            >
              {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
            </IconButton>
            <IconButton
              aria-label="Acoes da categoria"
              title="Acoes da categoria"
              onClick={(event) => openStockCategoryMenu(event, stockCategory)}
              sx={{ bgcolor: '#fff', borderRadius: 2, width: 44, height: 44 }}
            >
              <MoreVertRoundedIcon />
            </IconButton>
            <IconButton
              aria-label={isCollapsed ? 'Expandir categoria' : 'Recolher categoria'}
              title={isCollapsed ? 'Expandir categoria' : 'Recolher categoria'}
              onClick={() => setCollapsedStockCategories((prev) => ({ ...prev, [stockCategory.id]: !prev[stockCategory.id] }))}
              sx={{ bgcolor: '#fff', borderRadius: 2, width: 44, height: 44 }}
            >
              {isCollapsed ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}
            </IconButton>
          </Stack>
        </Stack>

        {!isCollapsed ? (
          <Box sx={{ mx: 0.75, mb: 0.75, borderRadius: 3, bgcolor: '#fff', overflow: 'hidden' }}>
            {hasProducts ? (
              stockCategory.products.map((product) => renderStockProductRow(product))
            ) : (
              <Box sx={{ p: 1.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  startIcon={<AddIcon />}
                  onClick={() => createStockItem(stockCategory, 'oferta')}
                  sx={{ minHeight: 48, borderRadius: 2, fontWeight: 850 }}
                >
                  Criar oferta
                </Button>
              </Box>
            )}
          </Box>
        ) : null}
      </Paper>
    );
  };

  const renderMerchantRootBlock = (children, { empty = false } = {}) => {
    const merchantKey = selectedMerchantId || 'sem-loja';
    const isCollapsed = Boolean(collapsedStockMerchants[merchantKey]);
    return (
      <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: '#f4f4f5', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ px: 2, py: 2, bgcolor: '#eeeeef' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <StorefrontRoundedIcon color="primary" />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, fontSize: 20 }} noWrap>
                {selectedMerchantName || 'Loja iFood'}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={isCollapsed ? 'Expandir loja' : 'Recolher loja'}
            title={isCollapsed ? 'Expandir loja' : 'Recolher loja'}
            onClick={() => setCollapsedStockMerchants((prev) => ({ ...prev, [merchantKey]: !prev[merchantKey] }))}
            sx={{ bgcolor: '#fff', borderRadius: 2, width: 44, height: 44, alignSelf: { xs: 'flex-start', md: 'center' } }}
          >
            {isCollapsed ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}
          </IconButton>
        </Stack>
        {!isCollapsed ? (
          <Box sx={{ p: empty ? 2 : 1.5 }}>
            {children}
          </Box>
        ) : null}
      </Paper>
    );
  };

  const renderProductListRow = (entry) => {
    const rowId = `${entry.entryType}:${entry.id}`;
    const isUnavailable = normalizeCatalogStatus(entry.status) === 'UNAVAILABLE';
    const categoryPaused = isPausedCatalogStatus(entry.categoryStatus);
    return (
      <Box
        key={rowId}
        sx={{
          display: 'grid',
          gridTemplateColumns: stockProductGridColumns,
          gap: 2,
          alignItems: 'center',
          px: 2,
          py: 1.5,
          minHeight: 92,
          bgcolor: '#fff',
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Checkbox
            checked={stockSelectedEntryIds.includes(rowId)}
            onChange={(event) => {
              setStockSelectedEntryIds((prev) => (
                event.target.checked
                  ? [...new Set([...prev, rowId])]
                  : prev.filter((id) => id !== rowId)
              ));
            }}
          />
          <Box
            sx={{
              width: 72,
              height: 54,
              aspectRatio: '4 / 3',
              borderRadius: 2,
              bgcolor: '#f4f4f5',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {entry.imageUrl ? (
              <Box component="img" src={entry.imageUrl} alt={entry.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <ImageOutlinedIcon />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 850 }} noWrap>{entry.name}</Typography>
            <Typography color="text.secondary" noWrap>{entry.description || 'Sem descricao'}</Typography>
          </Box>
        </Stack>
        <Typography>{entry.classification}</Typography>
        <Typography>{entry.availableIn || '-'}</Typography>
        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
          {categoryPaused ? (
            <Tooltip
              title={(
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>O produto esta em uma categoria pausada</Typography>
                  <Typography variant="body2">Ative a categoria em que o produto esta para que ele fique ativo em seu cardapio.</Typography>
                </Box>
              )}
            >
              <ErrorRoundedIcon sx={{ color: '#a66a00' }} />
            </Tooltip>
          ) : null}
          <IconButton
            aria-label={isUnavailable ? 'Reativar' : 'Pausar'}
            title={isUnavailable ? 'Reativar no iFood' : 'Pausar no iFood'}
            onClick={() => updateStockEntryStatus(entry)}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: 48, height: 48 }}
          >
            {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
          </IconButton>
          <IconButton
            aria-label="Remover"
            title="Remover"
            onClick={() => removeStockEntryFromBrutos(entry)}
            sx={{ width: 48, height: 48 }}
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  const renderOptionGroupCard = (group) => {
    const parent = stockProductsById.get(group.itemId) || {};
    const groupId = getStockGroupSelectionId(group);
    const isUnavailable = normalizeCatalogStatus(group.status) === 'UNAVAILABLE';
    return (
      <Grid item xs={12} md={6} xl={4} key={groupId}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            minHeight: 190,
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            position: 'relative',
            overflow: 'hidden',
            '&:hover .grupo-acoes': { opacity: 1, transform: 'translateY(0)' },
          }}
        >
          <Checkbox
            checked={stockSelectedGroupIds.includes(groupId)}
            onChange={(event) => {
              setStockSelectedGroupIds((prev) => (
                event.target.checked
                  ? [...new Set([...prev, groupId])]
                  : prev.filter((id) => id !== groupId)
              ));
            }}
            sx={{ p: 0, mb: 3 }}
          />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <Inventory2OutlinedIcon color="action" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>{group.name}</Typography>
          </Stack>
          <Typography color="text.secondary">
            Disponivel em: {group.availableIn || parent.name || 'Produto importado'}
          </Typography>
          <Stack
            className="grupo-acoes"
            direction="row"
            spacing={1}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              opacity: 0,
              transform: 'translateY(-4px)',
              transition: 'all .16s ease',
              bgcolor: '#fff',
              borderRadius: 2,
              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
              p: 0.5,
            }}
          >
            <IconButton title="Editar grupo no iFood" onClick={() => editStockGroup(group)}>
              <EditRoundedIcon />
            </IconButton>
            <IconButton title={isUnavailable ? 'Reativar grupo no iFood' : 'Pausar grupo no iFood'} onClick={() => toggleStockGroupStatus(group)}>
              {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
            </IconButton>
            <IconButton title="Remover grupo no iFood" onClick={() => removeStockGroup(group)}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Stack>
        </Paper>
      </Grid>
    );
  };

  const renderGroupEditorOptionRow = (option) => {
    const optionScopeKey = getVisualOptionKey(option);
    const draft = groupOptionDrafts[optionScopeKey] || {};
    const imagePathValue = firstValue(draft.imagePath, option.imagePath, '');
    const imageUrl = buildIfoodImageUrl(imagePathValue) || option.imageUrl;
    const isUnavailable = normalizeCatalogStatus(option.status) === 'UNAVAILABLE';
    return (
      <Box
        key={optionScopeKey}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '110px minmax(260px, 1.2fr) minmax(180px, .7fr) minmax(160px, .55fr) minmax(160px, .6fr) 112px' },
          gap: 2,
          alignItems: 'center',
          py: 2,
          borderTop: '1px solid #eeeeef',
        }}
      >
        <Stack spacing={1} alignItems="flex-start">
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2,
              bgcolor: '#f4f4f5',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              border: '1px solid #ececef',
            }}
          >
            {imageUrl ? (
              <Box component="img" src={imageUrl} alt={option.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <AddPhotoAlternateRoundedIcon color="action" />
            )}
          </Box>
          <Button component="label" size="small" color="inherit" sx={{ px: 0, fontWeight: 800 }}>
            Foto
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(event) => uploadGroupComplementImage(optionScopeKey, event.target.files?.[0])}
            />
          </Button>
        </Stack>
        <Stack spacing={1}>
          <TextField
            label="Nome"
            value={firstValue(draft.name, option.name, '')}
            onChange={(event) => updateGroupOptionDraft(optionScopeKey, 'name', event.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Descricao"
            value={firstValue(draft.description, option.description, '')}
            onChange={(event) => updateGroupOptionDraft(optionScopeKey, 'description', event.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" color="error" label="iFood" />
          <Typography color="text.secondary">Aplicativo iFood</Typography>
        </Stack>
        <TextField
          label="Preco"
          value={firstValue(draft.price, option.price, '')}
          onChange={(event) => updateGroupOptionDraft(optionScopeKey, 'price', event.target.value)}
          size="small"
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
        />
        <TextField
          label="Codigo PDV"
          value={firstValue(draft.externalCode, option.externalCode, '')}
          onChange={(event) => updateGroupOptionDraft(optionScopeKey, 'externalCode', event.target.value)}
          size="small"
          fullWidth
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <IconButton title={isUnavailable ? 'Ativar complemento no iFood' : 'Pausar complemento no iFood'} onClick={() => updateStockOptionStatus(option)}>
            {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
          </IconButton>
          <IconButton title="Remover complemento do grupo no iFood" onClick={() => removeGroupComplement(option)}>
            <DeleteOutlineRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  const renderNewGroupComplementForm = () => (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #fee2e2', bgcolor: '#fffafa' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={2}>
          <Stack spacing={1}>
            <Box
              sx={{
                width: 84,
                height: 63,
                aspectRatio: '4 / 3',
                borderRadius: 2,
                bgcolor: '#fff',
                border: '1px dashed #ef4444',
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
              }}
            >
              {newComplementForm.imagePath ? (
                <Box component="img" src={buildIfoodImageUrl(newComplementForm.imagePath)} alt="Novo complemento" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <AddPhotoAlternateRoundedIcon color="error" />
              )}
            </Box>
            <Button component="label" size="small" color="error" sx={{ px: 0, fontWeight: 800 }}>
              Foto
              <input hidden type="file" accept="image/*" onChange={(event) => uploadGroupComplementImage('__new', event.target.files?.[0])} />
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={1}>
            <TextField
              label="Nome"
              value={newComplementForm.name}
              onChange={(event) => setNewComplementForm((prev) => ({ ...prev, name: event.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Descricao"
              value={newComplementForm.description}
              onChange={(event) => setNewComplementForm((prev) => ({ ...prev, description: event.target.value }))}
              size="small"
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Preco"
            value={newComplementForm.price}
            onChange={(event) => setNewComplementForm((prev) => ({ ...prev, price: event.target.value }))}
            size="small"
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Codigo PDV"
            value={newComplementForm.externalCode}
            onChange={(event) => setNewComplementForm((prev) => ({ ...prev, externalCode: event.target.value }))}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button variant="contained" color="error" onClick={addGroupComplement} disabled={Boolean(busyAction)} fullWidth>
            Criar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderGroupLinkedProductRow = ({ group, product }) => (
    <Box
      key={group.id}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '120px minmax(260px, 1fr) minmax(220px, .7fr) 150px 150px 84px' },
        gap: 2,
        alignItems: 'center',
        py: 2,
        borderTop: '1px solid #eeeeef',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 2,
          bgcolor: '#f4f4f5',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
        }}
      >
        {product.imageUrl ? (
          <Box component="img" src={product.imageUrl} alt={product.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <ImageOutlinedIcon />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900 }}>{product.name}</Typography>
        <Typography color="text.secondary" noWrap>Disponivel em: {product.categoryName || 'Cardapio iFood'}</Typography>
      </Box>
      <Typography color="text.secondary">{Number(group.min || 0) > 0 ? 'Obrigatorio' : 'Opcional'}</Typography>
      <Typography color="text.secondary">{Number(group.min || 0)}</Typography>
      <Typography color="text.secondary">{Number(group.max || 0)}</Typography>
      <IconButton title="Desvincular grupo deste produto no iFood" onClick={() => removeStockGroup(group)}>
        <DeleteOutlineRoundedIcon />
      </IconButton>
    </Box>
  );

  const renderProductEditorPreview = (product) => {
    const previewName = cleanText(stockProductForm.name) || product.name || 'Nome do produto';
    const previewDescription = cleanText(stockProductForm.description) || product.description || 'Descricao';
    const previewImageUrl = buildIfoodImageUrl(stockProductForm.imagePath) || product.imageUrl;

    return (
      <Box sx={{ width: 280, display: { xs: 'none', xl: 'block' }, flexShrink: 0 }}>
        <Paper elevation={0} sx={{ p: 1.25, mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: '#f5f5f5' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Inventory2OutlinedIcon color="action" />
            <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Essa e uma simulacao do seu produto no app do iFood</Typography>
          </Stack>
        </Paper>
        <Box
          sx={{
            height: 520,
            border: '6px solid #111',
            borderRadius: 6,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Box sx={{ width: '100%', aspectRatio: '4 / 3', bgcolor: '#050505', overflow: 'hidden' }}>
            {previewImageUrl ? (
              <Box component="img" src={previewImageUrl} alt={previewName} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: '#9ca3af', bgcolor: '#fff0f3' }}>
                <ImageOutlinedIcon sx={{ fontSize: 54 }} />
              </Box>
            )}
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ width: 48, height: 4, borderRadius: 999, bgcolor: '#e5e7eb', mx: 'auto', mb: 2 }} />
            <Typography sx={{ fontSize: 20, fontWeight: 900 }}>{previewName}</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>{previewDescription}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderProductEditorOptionRow = (option) => {
    const isUnavailable = normalizeCatalogStatus(option.status) === 'UNAVAILABLE';
    const optionScopeKey = getVisualOptionKey(option);
    const rowKey = `option:${optionScopeKey}`;
    const draftPdv = pdvDrafts[rowKey] ?? option.externalCode ?? '';
    const priceValue = getInlineComplementPriceValue(option);

    return (
      <Box
        key={optionScopeKey}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '120px minmax(260px, 1fr) 180px 180px 112px' },
          gap: 2,
          alignItems: 'center',
          py: 2,
          borderTop: '1px solid #eeeeef',
        }}
      >
        {renderCatalogImageBox(option, 84)}
        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{option.name || 'Complemento sem nome'}</Typography>
        <TextField
          value={priceValue}
          onChange={(event) => setInlineComplementPriceDrafts((prev) => ({ ...prev, [optionScopeKey]: event.target.value }))}
          onBlur={() => updateInlineComplementPrice(option, priceValue)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
          size="small"
          label="Preco"
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 54 }, '& input': { fontSize: 18 } }}
        />
        <TextField
          value={draftPdv}
          onChange={(event) => setPdvDrafts((prev) => ({ ...prev, [rowKey]: event.target.value }))}
          onBlur={() => updateStockEntryExternalCode(option, draftPdv)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              updateStockEntryExternalCode(option, draftPdv);
            }
          }}
          size="small"
          label="Codigo PDV"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 54 }, '& input': { fontSize: 18 } }}
        />
        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
          <IconButton
            aria-label={isUnavailable ? 'Ativar complemento' : 'Pausar complemento'}
            title={isUnavailable ? 'Ativar complemento no iFood' : 'Pausar complemento no iFood'}
            onClick={() => updateStockOptionStatus(option)}
          >
            {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
          </IconButton>
          <IconButton
            aria-label="Opcoes do complemento"
            title="Opcoes do complemento"
            onClick={(event) => openStockOptionMenu(event, option)}
          >
            <MoreVertRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  const renderProductEditorGroupCard = (product, group) => {
    const options = getProductComplementOptions(product, group);
    const isExpanded = Boolean(expandedProductEditorGroups[group.id]);
    const isUnavailable = normalizeCatalogStatus(group.status) === 'UNAVAILABLE';
    const min = Number(group.min || 0);
    const max = Math.max(1, Number(group.max || options.length || 1));

    return (
      <Paper key={group.id} variant="outlined" sx={{ borderRadius: 2.5, p: { xs: 2, md: 3 }, bgcolor: '#fff' }}>
        <Stack spacing={isExpanded ? 2.5 : 0}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<AddIcon />}
                onClick={() => editStockGroup(group)}
                sx={{ borderRadius: 999, fontWeight: 900, mb: 2 }}
              >
                Adicionar tipo de grupo
              </Button>
              <Typography sx={{ fontSize: 26, fontWeight: 850 }}>{group.name}</Typography>
              <Typography color="text.secondary">Contem {getStockPlural(options.length, 'complemento', 'complementos')}</Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                color="inherit"
                startIcon={<AddIcon />}
                onClick={() => editStockGroup(group)}
                sx={{ fontSize: 18, fontWeight: 850, textTransform: 'none' }}
              >
                Complementos
              </Button>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
              <IconButton title="Produtos vinculados" onClick={() => setGroupEditor({ open: true, groupId: getVisualOptionGroupKey(group), tab: 1, search: '', linkProductId: '' })}>
                <LinkRoundedIcon />
              </IconButton>
              <IconButton title={isUnavailable ? 'Ativar grupo no iFood' : 'Pausar grupo no iFood'} onClick={() => toggleStockGroupStatus(group)}>
                {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
              </IconButton>
              <IconButton title="Remover grupo no iFood" onClick={() => removeStockGroup(group)}>
                <DeleteOutlineRoundedIcon />
              </IconButton>
              <IconButton
                title={isExpanded ? 'Recolher informacoes' : 'Expandir informacoes'}
                onClick={() => setExpandedProductEditorGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
              >
                {isExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
              </IconButton>
            </Stack>
          </Stack>

          {isExpanded ? (
            <>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 20, mb: 1 }}>Este grupo e obrigatorio ou opcional?</Typography>
                  <TextField
                    value={min > 0 ? 'Obrigatorio' : 'Opcional'}
                    onChange={(event) => updateStockGroupRules(group, {
                      min: event.target.value === 'Obrigatorio' ? Math.max(1, min || 1) : 0,
                      max,
                    })}
                    fullWidth
                    select
                    SelectProps={{ native: true }}
                    InputProps={{ sx: { borderRadius: 2, minHeight: 58 } }}
                  >
                    <option value="Opcional">Opcional</option>
                    <option value="Obrigatorio">Obrigatorio</option>
                  </TextField>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ fontSize: 20, mb: 1 }}>Qtd. minima</Typography>
                  <Stack direction="row" alignItems="center" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, height: 58, overflow: 'hidden' }}>
                    <IconButton onClick={() => updateStockGroupRules(group, { min: Math.max(0, min - 1), max })} sx={{ borderRadius: 0, flex: 1 }}>−</IconButton>
                    <Typography sx={{ px: 2, fontSize: 22 }}>{min}</Typography>
                    <IconButton onClick={() => updateStockGroupRules(group, { min: Math.min(max, min + 1), max })} sx={{ borderRadius: 0, flex: 1 }}>+</IconButton>
                  </Stack>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={{ fontSize: 20, mb: 1 }}>Qtd. maxima</Typography>
                  <Stack direction="row" alignItems="center" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, height: 58, overflow: 'hidden' }}>
                    <IconButton onClick={() => updateStockGroupRules(group, { min: Math.min(min, Math.max(1, max - 1)), max: Math.max(1, max - 1) })} sx={{ borderRadius: 0, flex: 1 }}>−</IconButton>
                    <Typography sx={{ px: 2, fontSize: 22 }}>{max}</Typography>
                    <IconButton onClick={() => updateStockGroupRules(group, { min, max: max + 1 })} sx={{ borderRadius: 0, flex: 1 }}>+</IconButton>
                  </Stack>
                </Grid>
              </Grid>

              <Box>
                <Box
                  sx={{
                    display: { xs: 'none', lg: 'grid' },
                    gridTemplateColumns: '120px minmax(260px, 1fr) 180px 180px 112px',
                    gap: 2,
                    alignItems: 'center',
                    py: 1.5,
                    borderBottom: '1px solid #eeeeef',
                  }}
                >
                  <Typography sx={{ fontWeight: 900 }}>Imagem</Typography>
                  <Typography sx={{ fontWeight: 900 }}>Produto</Typography>
                  <Typography sx={{ fontWeight: 900 }}>Preco</Typography>
                  <Typography sx={{ fontWeight: 900 }}>Codigo PDV</Typography>
                  <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>Acoes</Typography>
                </Box>
                {options.length ? (
                  options.map((option) => renderProductEditorOptionRow(option))
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>Nenhum complemento importado neste grupo.</Alert>
                )}
              </Box>
            </>
          ) : null}
        </Stack>
      </Paper>
    );
  };

  const renderPdvProductRow = (product) => {
    const complementCount = getProductComplementCount(product);
    const itemIsCombo = isCatalogCombo(product);
    const rowKey = `item:${product.id}`;
    const draftValue = pdvDrafts[rowKey] ?? product.externalCode ?? '';
    return (
      <Box
        key={product.id}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(420px, 1fr) auto' },
          gap: 1.5,
          alignItems: 'center',
          px: { xs: 1.5, md: 2 },
          py: 1.5,
          borderTop: '1px solid #f0f0f0',
          bgcolor: '#fff',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2,
              bgcolor: '#f4f4f5',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {product.imageUrl ? (
              <Box component="img" src={product.imageUrl} alt={product.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <ImageOutlinedIcon />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 850 }} noWrap>{product.name}</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" variant={itemIsCombo ? 'filled' : 'outlined'} label={itemIsCombo ? 'Combo' : 'Oferta simples'} sx={{ height: 24, fontWeight: 700 }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {product.description || 'Sem descricao'}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => openStockProductDialog(product)}
            sx={{ borderRadius: 3, minHeight: 44, fontWeight: 850 }}
          >
            Complementos <Chip size="small" label={complementCount} sx={{ ml: 1, height: 22 }} />
          </Button>
          <TextField
            value={draftValue}
            onChange={(event) => setPdvDrafts((prev) => ({ ...prev, [rowKey]: event.target.value }))}
            onBlur={() => updateStockEntryExternalCode(product, draftValue)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                updateStockEntryExternalCode(product, draftValue);
              }
            }}
            size="small"
            label="PDV"
            sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 44 } }}
          />
          <IconButton
            aria-label="Acoes do produto"
            title="Acoes do produto"
            onClick={(event) => openStockProductMenu(event, product)}
            sx={{ width: 44, height: 44 }}
          >
            <MoreVertRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  const renderPdvCategoryBlock = (stockCategory) => {
    const isCollapsed = Boolean(collapsedStockCategories[stockCategory.id]);
    const isUnavailable = normalizeCatalogStatus(stockCategory.status) === 'UNAVAILABLE';
    return (
      <Paper key={stockCategory.id} elevation={0} sx={{ borderRadius: 3, bgcolor: '#f4f4f5', overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between" sx={{ px: 2, py: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <IconButton aria-label="Editar categoria" title="Editar categoria" onClick={() => openEditCategoryDialog(stockCategory)} size="small">
              <EditRoundedIcon />
            </IconButton>
            <Button color="inherit" onClick={() => openEditCategoryDialog(stockCategory)} sx={{ p: 0, minWidth: 0, textDecoration: 'underline', fontWeight: 900, fontSize: 18 }}>
              {stockCategory.name || 'Sem categoria'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              ({getStockPlural(stockCategory.products.length, 'item', 'itens')})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="contained" color="inherit" disableElevation onClick={() => createStockItem(stockCategory, 'combo')} sx={{ bgcolor: '#fff', borderRadius: 2, fontWeight: 850 }}>
              Criar combo
            </Button>
            <Button variant="contained" color="inherit" disableElevation onClick={() => createStockItem(stockCategory, 'oferta')} sx={{ bgcolor: '#fff', borderRadius: 2, fontWeight: 850 }}>
              Criar oferta
            </Button>
            <IconButton
              aria-label={isUnavailable ? 'Reativar categoria' : 'Pausar categoria'}
              onClick={() => toggleStockCategoryStatus(stockCategory)}
              sx={{ bgcolor: '#fff', borderRadius: 2, width: 44, height: 44 }}
            >
              {isUnavailable ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
            </IconButton>
            <IconButton aria-label="Acoes da categoria" onClick={(event) => openStockCategoryMenu(event, stockCategory)} sx={{ bgcolor: '#fff', borderRadius: 2, width: 44, height: 44 }}>
              <MoreVertRoundedIcon />
            </IconButton>
            <IconButton
              aria-label={isCollapsed ? 'Expandir categoria' : 'Recolher categoria'}
              onClick={() => setCollapsedStockCategories((prev) => ({ ...prev, [stockCategory.id]: !prev[stockCategory.id] }))}
              sx={{ bgcolor: '#fff', borderRadius: 2, width: 44, height: 44 }}
            >
              {isCollapsed ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}
            </IconButton>
          </Stack>
        </Stack>
        {!isCollapsed ? (
          <Box sx={{ mx: 0.75, mb: 0.75, borderRadius: 3, bgcolor: '#fff', overflow: 'hidden' }}>
            {stockCategory.products.map((product) => renderPdvProductRow(product))}
          </Box>
        ) : null}
      </Paper>
    );
  };

  const offerSteps = ['Principais informacoes', 'Preco e PDV', 'Grupos de complementos', 'Disponivel em'];
  const offerStep = Math.min(offerSteps.length - 1, Math.max(0, Number(offerForm.step || 0)));
  const offerImageUrl = buildIfoodImageUrl(offerForm.imagePath);
  const offerCategoryOptions = toArray(visualCatalog.categories)
    .filter((item) => item.id && item.id !== 'uncategorized')
    .sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));
  const filteredOfferCopyGroups = stockSharedGroups.filter((group) => {
    const term = cleanText(offerGroupDialog.copySearch).toLowerCase();
    if (!term) return true;
    return [
      group.name,
      group.externalCode,
      group.availableIn,
      ...toArray(group.linkedProductNames),
    ].some((value) => cleanText(value).toLowerCase().includes(term));
  });
  const comboSteps = ['Produtos do combo', 'Oferta e disponibilidade', 'Nome e imagem'];
  const comboStep = Math.min(comboSteps.length - 1, Math.max(0, Number(comboForm.step || 0)));
  const comboMainGroup = comboGroups.find((group) => group.kind === 'main') || null;
  const comboOptionalGroups = comboGroups.filter((group) => group.kind !== 'main');
  const comboOptionCount = comboGroups.reduce((total, group) => total + toArray(group.options).length, 0);
  const comboStartingPrice = getComboStartingPrice(comboGroups);
  const comboDrawerSearch = cleanText(comboDrawer.search).toLowerCase();
  const comboFilteredProducts = availableComboProducts.filter((product) => {
    if (!comboDrawerSearch) return true;
    return [product.name, product.description, product.categoryName, product.externalCode]
      .some((value) => cleanText(value).toLowerCase().includes(comboDrawerSearch));
  });
  const comboReusableGroups = stockSharedGroups.filter((group) => {
    const hasProducts = getReusableComboGroupOptions(group).length > 0;
    if (!hasProducts) return false;
    const groupType = cleanText(firstValue(group.optionGroupType, group.raw?.optionGroupType, group.raw?.relation?.optionGroupType, '')).toUpperCase();
    const isProductOfferGroup = !groupType || ['OFFER_UNIT', 'CROSS_SELL'].includes(groupType);
    if (!isProductOfferGroup) return false;
    if (!comboDrawerSearch) return true;
    return [
      group.name,
      group.availableIn,
      group.externalCode,
      ...toArray(group.linkedProductNames),
    ].some((value) => cleanText(value).toLowerCase().includes(comboDrawerSearch));
  });

  const goToOfferStep = (nextStep) => {
    updateOfferForm('step', Math.min(offerSteps.length - 1, Math.max(0, nextStep)));
  };

  const advanceOfferStep = () => {
    if (offerStep === 0 && !cleanText(offerForm.name)) {
      showToast('Informe o nome do produto para continuar.', 'warning');
      return;
    }
    if (offerStep === 1 && !cleanText(offerForm.externalCode)) {
      showToast('Informe o codigo PDV para continuar.', 'warning');
      return;
    }
    goToOfferStep(offerStep + 1);
  };

  const goToComboStep = (nextStep) => {
    updateComboForm('step', Math.min(comboSteps.length - 1, Math.max(0, nextStep)));
  };

  const advanceComboStep = () => {
    if (comboStep === 0 && !comboMainGroup) {
      showToast('Adicione os produtos principais do combo para continuar.', 'warning');
      return;
    }
    if (comboStep === 1 && !cleanText(comboForm.externalCode)) {
      showToast('Informe o codigo PDV do combo para continuar.', 'warning');
      return;
    }
    if (comboStep === 1 && comboForm.priceMode === 'combo' && parseCatalogMoney(comboForm.price) <= 0) {
      showToast('Informe o preco do combo para continuar.', 'warning');
      return;
    }
    goToComboStep(comboStep + 1);
  };

  const renderOfferStepBar = () => (
    <Stack direction="row" spacing={1.25} sx={{ mb: 3 }}>
      {offerSteps.map((label, index) => (
        <Box key={label} sx={{ flex: 1 }}>
          <Box
            sx={{
              height: 7,
              borderRadius: 999,
              bgcolor: index <= offerStep ? '#d92f3f' : '#d7d7d7',
              mb: 1,
            }}
          />
          <Button
            color={index === offerStep ? 'error' : 'inherit'}
            onClick={() => goToOfferStep(index)}
            sx={{ minWidth: 0, p: 0, fontSize: 12, fontWeight: 850, textTransform: 'none' }}
          >
            {index + 1}. {label}
          </Button>
        </Box>
      ))}
    </Stack>
  );

  const renderOfferPhonePreview = () => (
    <Box sx={{ width: 300, display: { xs: 'none', lg: 'block' }, flexShrink: 0 }}>
      <Paper elevation={0} sx={{ p: 1.25, mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: '#f5f5f5' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Inventory2OutlinedIcon color="action" />
          <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Simulacao do produto no app do iFood</Typography>
        </Stack>
      </Paper>
      <Box
        sx={{
          height: 560,
          border: '6px solid #111',
          borderRadius: 6,
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ width: '100%', aspectRatio: '4 / 3', bgcolor: '#050505', overflow: 'hidden' }}>
          {offerImageUrl ? (
            <Box component="img" src={offerImageUrl} alt={offerForm.name || 'Produto'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: '#9ca3af', bgcolor: '#fff0f3' }}>
              <ImageOutlinedIcon sx={{ fontSize: 54 }} />
            </Box>
          )}
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ width: 48, height: 4, borderRadius: 999, bgcolor: '#e5e7eb', mx: 'auto', mb: 2 }} />
          <Typography sx={{ fontSize: 20, fontWeight: 900 }}>
            {offerForm.name || 'Nome do produto'}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1, minHeight: 48 }}>
            {offerForm.description || 'Descricao'}
          </Typography>
          <Typography sx={{ mt: 2, fontSize: 20, fontWeight: 900 }}>
            {formatCurrency(parseCatalogMoney(offerForm.price))}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderOfferImageUploader = () => (
    <Stack spacing={1}>
      <Typography sx={{ fontWeight: 850 }}>Imagem do produto</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Box
          sx={{
            width: 180,
            aspectRatio: '4 / 3',
            borderRadius: 2,
            border: '1px dashed #d1d5db',
            bgcolor: '#f8fafc',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
          }}
        >
          {offerImageUrl ? (
            <Box component="img" src={offerImageUrl} alt={offerForm.name || 'Produto'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <ImageOutlinedIcon color="action" sx={{ fontSize: 42 }} />
          )}
        </Box>
        <Stack spacing={0.75}>
          <Button
            component="label"
            variant="outlined"
            color="error"
            startIcon={<AddPhotoAlternateRoundedIcon />}
            sx={{ borderRadius: 2, fontWeight: 900, alignSelf: 'flex-start' }}
          >
            Adicionar imagem
            <input hidden type="file" accept="image/*" onChange={(event) => uploadOfferImage(event.target.files?.[0])} />
          </Button>
          <Typography variant="body2" color="text.secondary">
            A imagem e centralizada e enviada em proporcao 4x3, igual ao recorte usado pelo iFood.
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );

  const renderStockProductImageUploader = () => {
    const imageUrl = buildIfoodImageUrl(stockProductForm.imagePath);
    return (
      <Stack spacing={1}>
        <Typography sx={{ fontWeight: 850 }}>Imagem do produto</Typography>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box
              sx={{
                width: 150,
                aspectRatio: '4 / 3',
                borderRadius: 2,
                border: '1px dashed #d1d5db',
                bgcolor: '#f8fafc',
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {imageUrl ? (
                <Box component="img" src={imageUrl} alt={stockProductForm.name || 'Produto'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <ImageOutlinedIcon color="action" sx={{ fontSize: 40 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 850 }}>
                {stockProductForm.imagePath ? stockProductForm.imagePath.split('/').pop() : 'Sem imagem cadastrada'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Button
                  component="label"
                  color="error"
                  startIcon={<AddPhotoAlternateRoundedIcon />}
                  sx={{ fontWeight: 850, textTransform: 'none' }}
                >
                  Alterar foto
                  <input hidden type="file" accept="image/*" onChange={(event) => uploadStockProductImage(event.target.files?.[0])} />
                </Button>
                {stockProductForm.imagePath ? (
                  <Button
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={() => setStockProductForm((prev) => ({ ...prev, imagePath: '' }))}
                    sx={{ fontWeight: 850, textTransform: 'none' }}
                  >
                    Remover
                  </Button>
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                A foto e enviada em proporcao 4x3, igual ao recorte usado pelo iFood.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const renderPreparedStockControl = ({ form, onFieldChange, product }) => (
    <Stack spacing={1}>
      <Typography sx={{ fontWeight: 850 }}>Estoque</Typography>
      {form.stockControlEnabled ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="Qtd."
            type="number"
            value={form.inventoryAmount}
            onChange={(event) => onFieldChange('inventoryAmount', event.target.value)}
            size="small"
            sx={{ width: { xs: '100%', sm: 180 } }}
          />
          <IconButton
            title="Desativar controle de estoque"
            onClick={() => {
              onFieldChange('stockControlEnabled', false);
              onFieldChange('inventoryAmount', '');
            }}
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
          <Typography color="text.secondary">
            Caso o estoque acabe, o produto sera pausado automaticamente.
          </Typography>
        </Stack>
      ) : (
        <Button
          color="error"
          startIcon={<Inventory2OutlinedIcon />}
          onClick={() => {
            onFieldChange('stockControlEnabled', true);
            onFieldChange('inventoryAmount', product && hasStockInventoryControl(product) ? String(product.stock) : '0');
          }}
          sx={{ alignSelf: 'flex-start', fontWeight: 900, textTransform: 'none' }}
        >
          Ativar
        </Button>
      )}
    </Stack>
  );

  const renderPreparedHighlightPanel = ({ form, onFieldChange, onToggleValue }) => (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#f3f4f6' }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ cursor: 'pointer' }}
          onClick={() => onFieldChange('highlightOpen', !form.highlightOpen)}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LocalOfferRoundedIcon color="action" />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950 }}>Destaque seu produto</Typography>
              <Typography color="text.secondary">
                Estas informacoes ajudam o algoritmo do iFood a exibir o produto no app e atrair mais clientes.
              </Typography>
            </Box>
          </Stack>
          {form.highlightOpen ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
        </Stack>

        {form.highlightOpen ? (
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography sx={{ fontWeight: 950, mb: 0.5 }}>RESTRICOES ALIMENTARES</Typography>
              <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                Informe se seu produto e adequado a restricoes alimentares e ajude seus clientes a encontra-lo.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {dietaryRestrictionOptions.map((option) => {
                  const active = toArray(form.dietaryRestrictions).includes(option.value);
                  return (
                    <Chip
                      key={option.value}
                      clickable
                      label={option.label}
                      color={active ? 'error' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => onToggleValue('dietaryRestrictions', option.value)}
                      sx={{ fontWeight: 850 }}
                    />
                  );
                })}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography sx={{ fontWeight: 950, mb: 1.5 }}>EM CASO DE BEBIDAS</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {beverageTagOptions.map((option) => {
                  const active = toArray(form.beverageTags).includes(option.value);
                  return (
                    <Chip
                      key={option.value}
                      clickable
                      label={option.label}
                      color={active ? 'error' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => onToggleValue('beverageTags', option.value)}
                      sx={{ fontWeight: 850 }}
                    />
                  );
                })}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography sx={{ fontWeight: 950, mb: 0.5 }}>TAMANHO DO ITEM</Typography>
              <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                De mais detalhes para que o cliente possa planejar a refeicao.
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Serve ate"
                    value={form.serving}
                    onChange={(event) => onFieldChange('serving', event.target.value)}
                    select
                    SelectProps={{ native: true }}
                    fullWidth
                    size="small"
                  >
                    {servingOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={7} md={4}>
                  <TextField
                    label="Peso"
                    value={form.quantityValue}
                    onChange={(event) => onFieldChange('quantityValue', event.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={5} md={3}>
                  <TextField
                    label="Unidade"
                    value={form.quantityUnit}
                    onChange={(event) => onFieldChange('quantityUnit', event.target.value)}
                    select
                    SelectProps={{ native: true }}
                    fullWidth
                    size="small"
                  >
                    {quantityUnitOptions.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );

  const renderOfferGroupOptionDraft = (group, option) => {
    const isLocked = Boolean(option.locked || group.locked);
    const optionImageUrl = buildIfoodImageUrl(option.imagePath) || option.imageUrl;
    return (
      <Box
        key={option.draftId}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '110px minmax(240px, 1fr) 150px 170px 112px' },
          gap: 1.5,
          alignItems: 'center',
          py: 1.5,
          borderTop: '1px solid #eeeeef',
        }}
      >
        <Box
          sx={{
            width: 92,
            aspectRatio: '4 / 3',
            borderRadius: 2,
            border: '1px dashed #d1d5db',
            bgcolor: '#f8fafc',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
          }}
        >
          {optionImageUrl ? (
            <Box component="img" src={optionImageUrl} alt={option.name || 'Complemento'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <ImageOutlinedIcon color="action" />
          )}
        </Box>
        <Stack spacing={1}>
          {isLocked ? (
            <>
              <Typography sx={{ fontWeight: 850 }}>{option.name || 'Complemento'}</Typography>
              <Typography color="text.secondary">{option.description || 'Sem descricao'}</Typography>
            </>
          ) : (
            <>
              <TextField
                label="Nome do complemento"
                value={option.name}
                onChange={(event) => updateOfferGroupOptionDraft(group.draftId, option.draftId, 'name', event.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Descricao"
                value={option.description}
                onChange={(event) => updateOfferGroupOptionDraft(group.draftId, option.draftId, 'description', event.target.value)}
                fullWidth
                size="small"
              />
              <Button
                component="label"
                color="inherit"
                startIcon={<AddPhotoAlternateRoundedIcon />}
                sx={{ alignSelf: 'flex-start', fontWeight: 800, textTransform: 'none' }}
              >
                Foto 4x3
                <input hidden type="file" accept="image/*" onChange={(event) => uploadOfferGroupOptionImage(group.draftId, option.draftId, event.target.files?.[0])} />
              </Button>
            </>
          )}
        </Stack>
        <TextField
          label="Preco"
          value={option.price}
          onChange={(event) => updateOfferGroupOptionDraft(group.draftId, option.draftId, 'price', event.target.value)}
          disabled={isLocked}
          size="small"
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
        />
        <TextField
          label="Codigo PDV"
          value={option.externalCode}
          onChange={(event) => updateOfferGroupOptionDraft(group.draftId, option.draftId, 'externalCode', event.target.value)}
          disabled={isLocked}
          size="small"
        />
        <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
          <IconButton
            title={normalizeCatalogStatus(option.status) === 'AVAILABLE' ? 'Pausar complemento' : 'Ativar complemento'}
            disabled={isLocked}
            onClick={() => updateOfferGroupOptionDraft(
              group.draftId,
              option.draftId,
              'status',
              getNextCatalogStatus(option.status)
            )}
          >
            {normalizeCatalogStatus(option.status) === 'AVAILABLE' ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
          </IconButton>
          <IconButton
            title="Remover complemento"
            disabled={isLocked || toArray(group.options).length <= 1}
            onClick={() => removeOfferGroupOption(group.draftId, option.draftId)}
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  const renderOfferGroupDraftCard = (group) => {
    const options = toArray(group.options);
    const min = Math.max(0, Number(group.min || 0));
    const max = Math.max(1, Number(group.max || options.length || 1));
    const isLocked = Boolean(group.locked);
    return (
      <Paper key={group.draftId} variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
        <Stack spacing={2.25}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {isLocked ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: 24, fontWeight: 900 }}>{group.name}</Typography>
                    <Chip icon={<LinkRoundedIcon />} label="Reutilizado" size="small" sx={{ fontWeight: 850 }} />
                  </Stack>
                  <Typography color="text.secondary">Disponivel em: {group.availableIn || 'produto importado'}</Typography>
                </>
              ) : (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nome do grupo"
                      value={group.name}
                      onChange={(event) => updateOfferGroupDraft(group.draftId, 'name', event.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Codigo PDV do grupo"
                      value={group.externalCode}
                      onChange={(event) => updateOfferGroupDraft(group.draftId, 'externalCode', event.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Status"
                      value={group.status}
                      onChange={(event) => updateOfferGroupDraft(group.draftId, 'status', event.target.value)}
                      fullWidth
                      size="small"
                      select
                      SelectProps={{ native: true }}
                    >
                      <option value="AVAILABLE">Ativo</option>
                      <option value="UNAVAILABLE">Pausado</option>
                    </TextField>
                  </Grid>
                </Grid>
              )}
            </Box>
            <IconButton title="Remover grupo do produto" onClick={() => removeOfferGroup(group.draftId)}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Stack>

          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                label="Obrigatoriedade"
                value={min > 0 ? 'Obrigatorio' : 'Opcional'}
                onChange={(event) => updateOfferGroupDraft(group.draftId, 'min', event.target.value === 'Obrigatorio' ? Math.max(1, min || 1) : 0)}
                fullWidth
                size="small"
                select
                SelectProps={{ native: true }}
              >
                <option value="Opcional">Opcional</option>
                <option value="Obrigatorio">Obrigatorio</option>
              </TextField>
            </Grid>
            <Grid item xs={6} md={3.5}>
              <Stack direction="row" alignItems="center" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, minHeight: 44, overflow: 'hidden' }}>
                <IconButton onClick={() => updateOfferGroupDraft(group.draftId, 'min', Math.max(0, min - 1))} sx={{ borderRadius: 0, flex: 1 }}>−</IconButton>
                <Box sx={{ px: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Min.</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{min}</Typography>
                </Box>
                <IconButton onClick={() => updateOfferGroupDraft(group.draftId, 'min', Math.min(max, min + 1))} sx={{ borderRadius: 0, flex: 1 }}>+</IconButton>
              </Stack>
            </Grid>
            <Grid item xs={6} md={3.5}>
              <Stack direction="row" alignItems="center" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, minHeight: 44, overflow: 'hidden' }}>
                <IconButton onClick={() => updateOfferGroupDraft(group.draftId, 'max', Math.max(1, max - 1))} sx={{ borderRadius: 0, flex: 1 }}>−</IconButton>
                <Box sx={{ px: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Max.</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{max}</Typography>
                </Box>
                <IconButton onClick={() => updateOfferGroupDraft(group.draftId, 'max', max + 1)} sx={{ borderRadius: 0, flex: 1 }}>+</IconButton>
              </Stack>
            </Grid>
          </Grid>

          <Box>
            <Box
              sx={{
                display: { xs: 'none', lg: 'grid' },
                gridTemplateColumns: '110px minmax(240px, 1fr) 150px 170px 112px',
                gap: 1.5,
                py: 1,
                borderBottom: '1px solid #eeeeef',
              }}
            >
              <Typography sx={{ fontWeight: 900 }}>Imagem</Typography>
              <Typography sx={{ fontWeight: 900 }}>Complemento</Typography>
              <Typography sx={{ fontWeight: 900 }}>Preco</Typography>
              <Typography sx={{ fontWeight: 900 }}>Codigo PDV</Typography>
              <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>Acoes</Typography>
            </Box>
            {options.map((option) => renderOfferGroupOptionDraft(group, option))}
          </Box>

          {!isLocked ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<AddIcon />}
              onClick={() => addOfferGroupOption(group.draftId)}
              sx={{ alignSelf: 'flex-start', borderRadius: 999, fontWeight: 900 }}
            >
              Complemento
            </Button>
          ) : null}
        </Stack>
      </Paper>
    );
  };

  const renderOfferStepContent = () => {
    if (offerStep === 0) {
      return (
        <Stack spacing={2.5}>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>Principais informacoes</Typography>
          <Field
            label="Nome do produto"
            value={offerForm.name}
            onChange={(value) => updateOfferForm('name', value)}
            inputProps={{ maxLength: 80 }}
          />
          <Field
            label="Descricao"
            value={offerForm.description}
            onChange={(value) => updateOfferForm('description', value)}
            multiline
            minRows={4}
            inputProps={{ maxLength: 1000 }}
          />
          {renderOfferImageUploader()}
          {renderPreparedStockControl({
            form: offerForm,
            onFieldChange: updateOfferForm,
          })}
          <Button
            variant={offerForm.stockEnabled ? 'contained' : 'outlined'}
            color={offerForm.stockEnabled ? 'success' : 'inherit'}
            startIcon={offerForm.stockEnabled ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
            onClick={() => updateOfferForm('stockEnabled', !offerForm.stockEnabled)}
            sx={{ alignSelf: 'flex-start', borderRadius: 2, fontWeight: 900 }}
          >
            {offerForm.stockEnabled ? 'Disponivel no cardapio' : 'Criar pausado'}
          </Button>
          {renderPreparedHighlightPanel({
            form: offerForm,
            onFieldChange: updateOfferForm,
            onToggleValue: (field, value) => {
              setOfferForm((prev) => {
                const current = toArray(prev[field]);
                return {
                  ...prev,
                  [field]: current.includes(value)
                    ? current.filter((item) => item !== value)
                    : [...current, value],
                };
              });
            },
          })}
        </Stack>
      );
    }

    if (offerStep === 1) {
      return (
        <Stack spacing={2.5}>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>Preco e codigo PDV</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                label="Preco"
                value={offerForm.price}
                onChange={(value) => updateOfferForm('price', value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                label="Preco original"
                value={offerForm.originalPrice}
                onChange={(value) => updateOfferForm('originalPrice', value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                label="Codigo PDV"
                value={offerForm.externalCode}
                onChange={(value) => updateOfferForm('externalCode', value)}
              />
            </Grid>
          </Grid>
        </Stack>
      );
    }

    if (offerStep === 2) {
      return (
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>Grupos de complementos</Typography>
              <Typography color="text.secondary">Crie grupos novos ou reutilize grupos reais ja importados do cardapio.</Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<AddIcon />}
              onClick={() => setOfferGroupDialog({ open: true, mode: 'choice', copySearch: '' })}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Adicionar grupo
            </Button>
          </Stack>
          {toArray(offerForm.groups).length ? (
            <Stack spacing={2}>
              {toArray(offerForm.groups).map((group) => renderOfferGroupDraftCard(group))}
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
              <RestaurantMenuRoundedIcon color="action" sx={{ fontSize: 42, mb: 1 }} />
              <Typography sx={{ fontWeight: 850 }}>Produto sem grupos de complementos.</Typography>
              <Typography color="text.secondary">Voce pode continuar sem complementos ou adicionar um grupo agora.</Typography>
            </Paper>
          )}
        </Stack>
      );
    }

    return (
      <Stack spacing={2.5}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>Disponivel em</Typography>
        <Typography color="text.secondary">Adicione o produto a uma categoria real do cardapio ou crie uma nova categoria na API Catalog.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant={offerForm.categoryMode === 'existing' ? 'contained' : 'outlined'}
            color={offerForm.categoryMode === 'existing' ? 'error' : 'inherit'}
            onClick={() => updateOfferForm('categoryMode', 'existing')}
            sx={{ borderRadius: 2, fontWeight: 900 }}
          >
            Categoria existente
          </Button>
          <Button
            variant={offerForm.categoryMode === 'new' ? 'contained' : 'outlined'}
            color={offerForm.categoryMode === 'new' ? 'error' : 'inherit'}
            onClick={() => updateOfferForm('categoryMode', 'new')}
            sx={{ borderRadius: 2, fontWeight: 900 }}
          >
            Nova categoria
          </Button>
        </Stack>
        {offerForm.categoryMode === 'existing' ? (
          offerCategoryOptions.length ? (
            <TextField
              label="Escolha uma categoria"
              value={offerForm.categoryId}
              onChange={(event) => {
                const selected = offerCategoryOptions.find((item) => item.id === event.target.value);
                setOfferForm((prev) => ({
                  ...prev,
                  categoryId: event.target.value,
                  categoryName: selected?.name || '',
                }));
              }}
              fullWidth
              select
              SelectProps={{ native: true }}
            >
              <option value="">Selecione</option>
              {offerCategoryOptions.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </TextField>
          ) : (
            <Alert severity="warning">Importe categorias do iFood antes de criar produto em categoria existente.</Alert>
          )
        ) : (
          <Field
            label="Nome da nova categoria"
            value={offerForm.categoryName}
            onChange={(value) => updateOfferForm('categoryName', value)}
          />
        )}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 950 }}>Disponibilidade</Typography>
              <Typography color="text.secondary">
                {offerForm.availabilityMode === 'scheduled'
                  ? `${toArray(offerForm.shifts).length} periodo(s) especifico(s) configurado(s).`
                  : 'Sempre disponivel enquanto a loja estiver aberta.'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CalendarMonthRoundedIcon />}
              onClick={() => openProductScheduleDialog('offer')}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Editar dias e horarios
            </Button>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 900 }}>Resumo</Typography>
            <Typography>Produto: {offerForm.name || 'sem nome'}</Typography>
            <Typography>PDV: {offerForm.externalCode || 'sem codigo'}</Typography>
            <Typography>Estoque: {offerForm.stockControlEnabled ? `${offerForm.inventoryAmount || 0} unidade(s)` : 'sem controle'}</Typography>
            <Typography>Grupos: {getStockPlural(toArray(offerForm.groups).length, 'grupo', 'grupos')}</Typography>
            <Typography>
              Complementos: {getStockPlural(toArray(offerForm.groups).reduce((total, group) => total + toArray(group.options).length, 0), 'complemento', 'complementos')}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const renderPizzaCategoryDialog = () => {
    const activeTabIndex = Math.max(0, pizzaCategoryTabs.findIndex((item) => item.value === pizzaCategoryForm.tab));
    const activeTab = pizzaCategoryTabs[activeTabIndex]?.value || 'details';
    const isLastTab = activeTab === 'availability';

    const renderPizzaSizeImage = (size) => {
      const imageUrl = buildIfoodImageUrl(size.imagePath);
      return (
        <Button
          component="label"
          variant="outlined"
          color="inherit"
          sx={{
            width: 96,
            aspectRatio: '4 / 3',
            minWidth: 96,
            p: 0,
            borderRadius: 2,
            overflow: 'hidden',
            borderStyle: 'dashed',
            bgcolor: '#f5f5f5',
          }}
        >
          {imageUrl ? (
            <Box component="img" src={imageUrl} alt={size.name || 'Tamanho'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <ImageOutlinedIcon color="action" />
          )}
          <input hidden type="file" accept="image/*" onChange={(event) => uploadPizzaSizeImage(size.id, event.target.files?.[0])} />
        </Button>
      );
    };

    const renderPizzaDetails = () => (
      <Stack spacing={4}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Detalhes</Typography>
          <Typography color="text.secondary">Preencha as informacoes da nova categoria.</Typography>
        </Box>
        <Box>
          <Typography sx={{ mb: 1, fontWeight: 850 }}>Tipo da categoria</Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <RestaurantMenuRoundedIcon color="error" />
              <Typography sx={{ flex: 1, fontWeight: 900 }}>Pizza</Typography>
              <Button
                color="error"
                onClick={() => {
                  closePizzaCategoryDialog();
                  setProductTypeDialog({ open: true, category: null, selected: 'pizza' });
                }}
              >
                Alterar
              </Button>
            </Stack>
          </Paper>
        </Box>
        <Box>
          <Typography sx={{ mb: 1, fontWeight: 850 }}>Nome da categoria</Typography>
          <TextField
            value={pizzaCategoryForm.name}
            onChange={(event) => updatePizzaCategoryForm('name', event.target.value.slice(0, 40))}
            fullWidth
            error={!cleanText(pizzaCategoryForm.name)}
            helperText={!cleanText(pizzaCategoryForm.name) ? 'Este campo e obrigatorio.' : ' '}
            inputProps={{ maxLength: 40 }}
          />
          <Typography align="right" color="text.secondary">{cleanText(pizzaCategoryForm.name).length}/40</Typography>
        </Box>
      </Stack>
    );

    const renderPizzaSizes = () => (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Tamanho</Typography>
          <Typography color="text.secondary">
            Indique aqui os tamanhos que suas pizzas sao produzidas, em quantos pedacos sao cortadas e ate quantos sabores sua loja monta cada tamanho.
          </Typography>
        </Box>
        <Stack spacing={2}>
          {toArray(pizzaCategoryForm.sizes).map((size) => (
            <Paper key={size.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm="auto">
                  {renderPizzaSizeImage(size)}
                </Grid>
                <Grid item xs={12} md={3}>
                  <Field
                    label="Nome do tamanho"
                    value={size.name}
                    onChange={(value) => updatePizzaSizeDraft(size.id, 'name', value)}
                  />
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <Field
                    label="Qtd. Pedacos"
                    type="number"
                    value={size.slices}
                    onChange={(value) => updatePizzaSizeDraft(size.id, 'slices', value)}
                  />
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <Typography sx={{ mb: 1, fontWeight: 850 }}>Qtd. Sabores</Typography>
                  <Stack direction="row" spacing={1}>
                    {[1, 2, 3, 4].map((number) => (
                      <Chip
                        key={number}
                        label={number}
                        color={Number(size.maxFlavors) === number ? 'error' : 'default'}
                        onClick={() => updatePizzaSizeDraft(size.id, 'maxFlavors', number)}
                        sx={{ fontWeight: 900 }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Field
                    label="Cod. PDV"
                    value={size.externalCode}
                    onChange={(value) => updatePizzaSizeDraft(size.id, 'externalCode', value)}
                  />
                </Grid>
                <Grid item xs={6} md={1.2}>
                  <Stack alignItems="center">
                    <Typography sx={{ fontSize: 12, fontWeight: 850 }}>Ativado</Typography>
                    <Switch
                      color="error"
                      checked={size.status !== 'UNAVAILABLE'}
                      onChange={(event) => updatePizzaSizeDraft(size.id, 'status', event.target.checked ? 'AVAILABLE' : 'UNAVAILABLE')}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6} md="auto">
                  <IconButton color="error" onClick={() => removePizzaSizeDraft(size.id)} disabled={toArray(pizzaCategoryForm.sizes).length <= 1}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
        <Button color="error" startIcon={<AddIcon />} onClick={addPizzaSizeDraft} sx={{ alignSelf: 'flex-start', fontWeight: 900 }}>
          Adicionar novo tamanho
        </Button>
        <Paper sx={{ p: 2, borderRadius: 3, bgcolor: '#f5f5f5' }} elevation={0}>
          <Typography sx={{ mb: 2, fontWeight: 950 }}>Resumo e status de venda</Typography>
          <Grid container spacing={2}>
            {toArray(pizzaCategoryForm.sizes).map((size) => (
              <Grid key={size.id} item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                  <RestaurantMenuRoundedIcon color={size.status !== 'UNAVAILABLE' ? 'error' : 'disabled'} />
                  <Typography sx={{ mt: 1, fontWeight: 950 }}>{size.name || 'Tamanho sem nome'}</Typography>
                  <Typography color="text.secondary">
                    Cortada em {size.slices || 0} pedaco(s)
                  </Typography>
                  <Typography color="text.secondary">
                    Aceita ate {size.maxFlavors || 1} sabor(es)
                  </Typography>
                  <Typography sx={{ mt: 1, fontWeight: 850 }}>
                    {size.status !== 'UNAVAILABLE' ? 'Ativado' : 'Pausado'}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Stack>
    );

    const renderPizzaOptionRows = (kind) => {
      const isEdge = kind === 'edge';
      const fieldName = isEdge ? 'edges' : 'doughs';
      const title = isEdge ? 'Borda' : 'Massa';
      const addLabel = isEdge ? 'Adicionar borda' : 'Adicionar massa';
      const description = isEdge
        ? 'Indique aqui os tipos de borda que sua loja trabalha.'
        : 'Indique aqui os tipos de massa que sua loja trabalha.';

      return (
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950 }}>{title}</Typography>
            <Typography color="text.secondary">{description}</Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{title}</TableCell>
                  <TableCell width="18%">Preco</TableCell>
                  <TableCell width="18%" align="center">Status de vendas</TableCell>
                  <TableCell width="18%">Cod. PDV</TableCell>
                  <TableCell width="64" />
                </TableRow>
              </TableHead>
              <TableBody>
                {toArray(pizzaCategoryForm[fieldName]).map((option) => (
                  <TableRow key={option.id}>
                    <TableCell>
                      <Field
                        label={title}
                        value={option.name}
                        onChange={(value) => updatePizzaOptionDraft(kind, option.id, 'name', value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Field
                        label="Preco"
                        value={option.price}
                        onChange={(value) => updatePizzaOptionDraft(kind, option.id, 'price', value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        color="error"
                        checked={option.status !== 'UNAVAILABLE'}
                        onChange={(event) => updatePizzaOptionDraft(kind, option.id, 'status', event.target.checked ? 'AVAILABLE' : 'UNAVAILABLE')}
                      />
                    </TableCell>
                    <TableCell>
                      <Field
                        label="Cod. PDV"
                        value={option.externalCode}
                        onChange={(value) => updatePizzaOptionDraft(kind, option.id, 'externalCode', value)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="error" onClick={() => removePizzaOptionDraft(kind, option.id)} disabled={toArray(pizzaCategoryForm[fieldName]).length <= 1}>
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ p: 2 }}>
              <Button variant="outlined" color="error" startIcon={<AddIcon />} onClick={() => addPizzaOptionDraft(kind)} sx={{ fontWeight: 900 }}>
                {addLabel}
              </Button>
            </Box>
          </TableContainer>
        </Stack>
      );
    };

    const renderPizzaAvailability = () => (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Disponibilidade</Typography>
          <Typography color="text.secondary">
            Aqui voce define em que momentos os clientes poderao comprar itens que estejam dentro dessa categoria.
          </Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Box
              onClick={() => updatePizzaCategoryForm('availabilityMode', 'always')}
              sx={{ display: 'flex', gap: 1.5, cursor: 'pointer', p: 1 }}
            >
              <Box sx={{ mt: .5, width: 12, height: 12, borderRadius: '50%', bgcolor: pizzaCategoryForm.availabilityMode === 'always' ? '#d92f3f' : '#fff', border: '1px solid #d92f3f' }} />
              <Box>
                <Typography sx={{ fontWeight: 900 }}>Sempre disponivel</Typography>
                <Typography color="text.secondary">Os itens da categoria ficarao disponiveis sempre que o restaurante estiver aberto.</Typography>
              </Box>
            </Box>
            <Box
              onClick={() => updatePizzaCategoryForm('availabilityMode', 'scheduled')}
              sx={{ display: 'flex', gap: 1.5, cursor: 'pointer', p: 1 }}
            >
              <Box sx={{ mt: .5, width: 12, height: 12, borderRadius: '50%', bgcolor: pizzaCategoryForm.availabilityMode === 'scheduled' ? '#d92f3f' : '#fff', border: '1px solid #d92f3f' }} />
              <Box>
                <Typography sx={{ fontWeight: 900 }}>Disponivel em dias e horarios especificos</Typography>
                <Typography color="text.secondary">Selecione os periodos em que a categoria ficara disponivel no aplicativo.</Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>
        {pizzaCategoryForm.availabilityMode === 'scheduled' ? (
          <Paper sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f5f5f5' }} elevation={0}>
            <Typography sx={{ mb: 1.5, fontWeight: 900 }}>Dias da semana</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {toArray(pizzaCategoryForm.availabilityDrafts).map((draft) => (
                <Chip
                  key={draft.key}
                  label={draft.label}
                  color={draft.enabled ? 'error' : 'default'}
                  onClick={() => togglePizzaAvailabilityDay(draft.key)}
                  sx={{ fontWeight: 900 }}
                />
              ))}
            </Stack>
            <Typography sx={{ mt: 2, mb: 1.5, fontWeight: 900 }}>Horarios</Typography>
            <Stack spacing={2}>
              {toArray(pizzaCategoryForm.availabilityDrafts).filter((draft) => draft.enabled).map((draft) => (
                <Paper key={draft.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography sx={{ fontWeight: 950 }}>{draft.name}</Typography>
                    {toArray(draft.slots).map((slot, index) => {
                      const invalid = slot.startTime >= slot.endTime;
                      return (
                        <Stack key={slot.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <Typography sx={{ width: 80, fontWeight: 850 }}>{index + 1}o turno</Typography>
                          <TextField
                            value={slot.startTime}
                            onChange={(event) => updatePizzaAvailabilitySlot(draft.key, slot.id, 'startTime', event.target.value)}
                            select
                            SelectProps={{ native: true }}
                            size="small"
                            sx={{ width: { xs: '100%', sm: 140 } }}
                          >
                            {pizzaTimeOptions.map((time) => <option key={time} value={time}>{time}</option>)}
                          </TextField>
                          <TextField
                            value={slot.endTime}
                            onChange={(event) => updatePizzaAvailabilitySlot(draft.key, slot.id, 'endTime', event.target.value)}
                            select
                            SelectProps={{ native: true }}
                            size="small"
                            error={invalid}
                            helperText={invalid ? 'Final maior que inicio' : ' '}
                            sx={{ width: { xs: '100%', sm: 140 } }}
                          >
                            {[...pizzaTimeOptions, '23:59'].map((time) => <option key={time} value={time}>{time}</option>)}
                          </TextField>
                          <IconButton color="error" onClick={() => removePizzaAvailabilitySlot(draft.key, slot.id)}>
                            <DeleteOutlineRoundedIcon />
                          </IconButton>
                        </Stack>
                      );
                    })}
                    <Button color="error" startIcon={<AddIcon />} onClick={() => addPizzaAvailabilitySlot(draft.key)} sx={{ alignSelf: 'flex-start', fontWeight: 900 }}>
                      Adicionar turno
                    </Button>
                  </Stack>
                </Paper>
              ))}
              {!toArray(pizzaCategoryForm.availabilityDrafts).some((draft) => draft.enabled) ? (
                <Alert severity="info">Selecione pelo menos um dia para configurar horarios especificos.</Alert>
              ) : null}
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    );

    const renderActivePizzaTab = () => {
      if (activeTab === 'sizes') return renderPizzaSizes();
      if (activeTab === 'doughs') return renderPizzaOptionRows('dough');
      if (activeTab === 'edges') return renderPizzaOptionRows('edge');
      if (activeTab === 'availability') return renderPizzaAvailability();
      return renderPizzaDetails();
    };

    return (
      <Dialog open={pizzaCategoryDialog.open} onClose={closePizzaCategoryDialog} fullScreen>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Box sx={{ flex: 1, px: { xs: 2, md: 10, lg: 18 }, pt: { xs: 3, md: 6 }, pb: 12 }}>
            <Button color="error" onClick={closePizzaCategoryDialog} startIcon={<KeyboardArrowDownRoundedIcon sx={{ transform: 'rotate(90deg)' }} />}>
              Voltar
            </Button>
            <Typography variant="h4" sx={{ mt: 3, fontWeight: 950 }}>Nova categoria</Typography>
            <Tabs
              value={activeTab}
              onChange={(_, value) => goToPizzaCategoryTab(value)}
              textColor="error"
              indicatorColor="error"
              sx={{ mt: 2, borderBottom: '1px solid #eee' }}
            >
              {pizzaCategoryTabs.map((item) => (
                <Tab key={item.value} value={item.value} label={item.label} sx={{ fontWeight: 850, textTransform: 'none' }} />
              ))}
            </Tabs>
            <Box sx={{ mt: 3, maxWidth: 1120 }}>
              {renderActivePizzaTab()}
            </Box>
          </Box>
          <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: '#fff', px: 3, py: 2, borderTop: '1px solid #eee' }}>
            <Button color="error" variant="outlined" onClick={closePizzaCategoryDialog}>Cancelar</Button>
            {activeTabIndex > 0 ? (
              <Button color="inherit" onClick={() => goToPizzaCategoryTab(pizzaCategoryTabs[activeTabIndex - 1].value)}>
                Voltar
              </Button>
            ) : null}
            {isLastTab ? (
              <Button variant="contained" color="error" onClick={submitPizzaCategory} disabled={Boolean(busyAction)}>
                Criar categoria
              </Button>
            ) : (
              <Button variant="contained" color="error" onClick={goToNextPizzaCategoryTab} disabled={activeTab === 'details' && !cleanText(pizzaCategoryForm.name)}>
                Continuar
              </Button>
            )}
          </DialogActions>
        </DialogContent>
      </Dialog>
    );
  };

  const renderProductTypeDialog = () => (
    <Dialog
      open={productTypeDialog.open}
      onClose={closeProductTypeDialog}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { ml: 'auto', mr: 1.5, height: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', borderRadius: 3 } }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Escolha um tipo de produto</Typography>
          <IconButton onClick={closeProductTypeDialog}><CloseRoundedIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {preparedProductTypes.map((item) => {
            const selected = productTypeDialog.selected === item.value;
            return (
              <Paper
                key={item.value}
                variant="outlined"
                onClick={() => setProductTypeDialog((prev) => ({ ...prev, selected: item.value }))}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  borderColor: selected ? '#d92f3f' : '#e5e7eb',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  {item.icon}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 950 }}>{item.label}</Typography>
                    <Typography color="text.secondary">{item.description}</Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: selected ? '#d92f3f' : '#e5e7eb',
                      bgcolor: selected ? '#d92f3f' : '#fff',
                    }}
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeProductTypeDialog} color="error" variant="outlined">Cancelar</Button>
        <Button variant="contained" color="error" onClick={continueProductTypeDialog}>Continuar</Button>
      </DialogActions>
    </Dialog>
  );

  const renderProductScheduleDialog = () => {
    const draftByDay = new Map(toArray(productScheduleDialog.drafts).map((draft) => [draft.key, draft]));
    const scheduled = productScheduleDialog.mode === 'scheduled';

    return (
      <Dialog
        open={productScheduleDialog.open}
        onClose={closeProductScheduleDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { ml: 'auto', mr: 1.5, height: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', borderRadius: 3 } }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 950 }}>Disponivel quando</Typography>
            <IconButton color="error" onClick={closeProductScheduleDialog}><CloseRoundedIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  onClick={() => setProductScheduleDialog((prev) => ({ ...prev, mode: 'always' }))}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    minHeight: 116,
                    borderColor: !scheduled ? '#d92f3f' : '#e5e7eb',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography sx={{ fontWeight: 950 }}>Sempre disponivel</Typography>
                      <Typography color="text.secondary">O produto ficara disponivel sempre que o restaurante estiver aberto.</Typography>
                    </Box>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', borderColor: !scheduled ? '#d92f3f' : '#e5e7eb', bgcolor: !scheduled ? '#d92f3f' : '#fff' }} />
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  onClick={() => setProductScheduleDialog((prev) => ({ ...prev, mode: 'scheduled' }))}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    minHeight: 116,
                    borderColor: scheduled ? '#d92f3f' : '#e5e7eb',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography sx={{ fontWeight: 950 }}>Dias e horarios especificos</Typography>
                      <Typography color="text.secondary">Selecione os periodos em que o produto ficara disponivel.</Typography>
                    </Box>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', borderColor: scheduled ? '#d92f3f' : '#e5e7eb', bgcolor: scheduled ? '#d92f3f' : '#fff' }} />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 950, mb: 1.5 }}>Dias da semana</Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {comboWeekDays.map((day) => {
                  const draft = draftByDay.get(day.key) || {};
                  const active = scheduled && draft.enabled;
                  return (
                    <Button
                      key={day.key}
                      variant={active ? 'contained' : 'outlined'}
                      color={active ? 'error' : 'inherit'}
                      disabled={!scheduled}
                      onClick={() => updateProductScheduleDraft(day.key, 'enabled', !draft.enabled)}
                      sx={{ minWidth: 48, width: 48, height: 48, borderRadius: '50%', p: 0, fontWeight: 950 }}
                    >
                      {day.label}
                    </Button>
                  );
                })}
              </Stack>
            </Box>

            {scheduled ? (
              <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 950, mb: 1 }}>Horarios</Typography>
                <Stack spacing={2}>
                  {toArray(productScheduleDialog.drafts).filter((draft) => draft.enabled).map((draft) => (
                    <Paper key={draft.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <Typography sx={{ width: 160, fontWeight: 850 }}>{draft.name}</Typography>
                        <TextField
                          type="time"
                          label="Inicio"
                          value={draft.startTime}
                          onChange={(event) => updateProductScheduleDraft(draft.key, 'startTime', event.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          type="time"
                          label="Fim"
                          value={draft.endTime}
                          onChange={(event) => updateProductScheduleDraft(draft.key, 'endTime', event.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                        <IconButton title="Remover horario" onClick={() => updateProductScheduleDraft(draft.key, 'enabled', false)}>
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                        {draft.startTime && draft.endTime && draft.startTime >= draft.endTime ? (
                          <Typography color="error" sx={{ fontWeight: 800 }}>O fim deve ser maior que o inicio.</Typography>
                        ) : null}
                      </Stack>
                    </Paper>
                  ))}
                  {!toArray(productScheduleDialog.drafts).some((draft) => draft.enabled) ? (
                    <Alert severity="info">Selecione pelo menos um dia para configurar horarios especificos.</Alert>
                  ) : null}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" color="error" onClick={closeProductScheduleDialog}>Fechar</Button>
          <Button variant="contained" color="error" onClick={applyProductScheduleDialog}>Aplicar</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderOfferGroupDialog = () => (
    <Dialog
      open={offerGroupDialog.open}
      onClose={() => setOfferGroupDialog({ open: false, mode: 'choice', copySearch: '' })}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>Grupo de complementos</Typography>
            <Typography color="text.secondary">Crie um novo grupo de complementos ou reutilize um grupo real do cardapio.</Typography>
          </Box>
          <IconButton onClick={() => setOfferGroupDialog({ open: false, mode: 'choice', copySearch: '' })}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {offerGroupDialog.mode === 'choice' ? (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                role="button"
                onClick={addOfferEmptyGroup}
                sx={{ p: 2.5, borderRadius: 2.5, cursor: 'pointer', height: '100%', '&:hover': { borderColor: '#d92f3f' } }}
              >
                <Stack direction="row" spacing={2}>
                  <AddIcon color="error" sx={{ fontSize: 36 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>Criar novo grupo</Typography>
                    <Typography color="text.secondary">Defina nome, regras e os complementos deste produto.</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                role="button"
                onClick={() => setOfferGroupDialog((prev) => ({ ...prev, mode: 'copy' }))}
                sx={{ p: 2.5, borderRadius: 2.5, cursor: 'pointer', height: '100%', '&:hover': { borderColor: '#d92f3f' } }}
              >
                <Stack direction="row" spacing={2}>
                  <LinkRoundedIcon color="error" sx={{ fontSize: 36 }} />
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 900 }}>Copiar grupo</Typography>
                      <Chip size="small" color="success" variant="outlined" label="mais pratico" />
                    </Stack>
                    <Typography color="text.secondary">Reutilize um grupo ja importado para manter a gestao compartilhada.</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Buscar grupos"
              value={offerGroupDialog.copySearch}
              onChange={(event) => setOfferGroupDialog((prev) => ({ ...prev, copySearch: event.target.value }))}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            {filteredOfferCopyGroups.length ? (
              <Grid container spacing={1.5}>
                {filteredOfferCopyGroups.map((group) => {
                  const optionCount = getOfferSharedGroupOptions(group).length;
                  return (
                    <Grid item xs={12} md={6} key={group.sharedGroupKey || getVisualOptionGroupKey(group)}>
                      <Paper
                        variant="outlined"
                        role="button"
                        onClick={() => addOfferCopiedGroup(group)}
                        sx={{ p: 2, borderRadius: 2.5, cursor: 'pointer', height: '100%', '&:hover': { borderColor: '#d92f3f' } }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LinkRoundedIcon color="action" />
                            <Typography sx={{ fontWeight: 900 }}>{group.name || 'Grupo sem nome'}</Typography>
                          </Stack>
                          <Typography color="text.secondary">Disponivel em: {group.availableIn || 'produto importado'}</Typography>
                          <Chip size="small" label={getStockPlural(optionCount, 'complemento', 'complementos')} sx={{ alignSelf: 'flex-start', fontWeight: 850 }} />
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert severity="info">Nenhum grupo real importado para reutilizar.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {offerGroupDialog.mode === 'copy' ? (
          <Button onClick={() => setOfferGroupDialog((prev) => ({ ...prev, mode: 'choice', copySearch: '' }))}>Voltar</Button>
        ) : null}
        <Button onClick={() => setOfferGroupDialog({ open: false, mode: 'choice', copySearch: '' })}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );

  const renderProductGroupDialog = () => {
    const productGroupOptions = toArray(productGroupForm.options).length
      ? toArray(productGroupForm.options)
      : [createProductGroupOptionDraft({
        name: productGroupForm.optionName,
        description: productGroupForm.optionDescription,
        price: productGroupForm.optionPrice,
        externalCode: productGroupForm.optionExternalCode,
        imagePath: productGroupForm.optionImagePath,
      })];
    return (
      <Dialog
        open={productGroupDialog.open}
        onClose={closeProductGroupDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>Grupo de complementos</Typography>
              <Typography color="text.secondary">
                Crie um grupo novo ou reutilize um grupo real ja cadastrado no cardapio.
              </Typography>
            </Box>
            <IconButton onClick={closeProductGroupDialog}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {productGroupDialog.mode === 'choice' ? (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  role="button"
                  onClick={() => {
                    setProductGroupForm(createEmptyProductGroupForm());
                    setProductGroupDialog((prev) => ({ ...prev, mode: 'new' }));
                  }}
                  sx={{ p: 2.5, borderRadius: 2.5, cursor: 'pointer', height: '100%', '&:hover': { borderColor: '#d92f3f' } }}
                >
                  <Stack direction="row" spacing={2}>
                    <AddIcon color="error" sx={{ fontSize: 36 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>Criar novo grupo</Typography>
                      <Typography color="text.secondary">
                        Defina nome, regras e um complemento inicial para este produto.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  role="button"
                  onClick={() => setProductGroupDialog((prev) => ({ ...prev, mode: 'copy' }))}
                  sx={{ p: 2.5, borderRadius: 2.5, cursor: 'pointer', height: '100%', '&:hover': { borderColor: '#d92f3f' } }}
                >
                  <Stack direction="row" spacing={2}>
                    <LinkRoundedIcon color="error" sx={{ fontSize: 36 }} />
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 900 }}>Copiar grupo</Typography>
                        <Chip size="small" color="success" variant="outlined" label="mais pratico" />
                      </Stack>
                      <Typography color="text.secondary">
                        Reutilize um grupo importado para manter complementos compartilhados.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          ) : null}

          {productGroupDialog.mode === 'new' ? (
            <Stack spacing={2.25} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontWeight: 900 }}>Dados do grupo</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={7}>
                  <TextField
                    label="Nome do grupo"
                    value={productGroupForm.groupName}
                    onChange={(event) => updateProductGroupForm('groupName', event.target.value.slice(0, 80))}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Codigo PDV do grupo"
                    value={productGroupForm.groupExternalCode}
                    onChange={(event) => updateProductGroupForm('groupExternalCode', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Qtd. minima"
                    type="number"
                    value={productGroupForm.min}
                    onChange={(event) => updateProductGroupForm('min', event.target.value)}
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Qtd. maxima"
                    type="number"
                    value={productGroupForm.max}
                    onChange={(event) => updateProductGroupForm('max', event.target.value)}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>

              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography sx={{ fontWeight: 900 }}>Complementos</Typography>
                <IconButton
                  color="error"
                  aria-label="Adicionar complemento"
                  title="Adicionar complemento"
                  onClick={addProductGroupOptionDraft}
                  sx={{ border: '1px solid #fecaca', bgcolor: '#fff5f5', '&:hover': { bgcolor: '#fee2e2' } }}
                >
                  <AddIcon />
                </IconButton>
              </Stack>

              <Stack spacing={1.25}>
                {productGroupOptions.map((optionDraft, optionIndex) => {
                  const isExpanded = Number(productGroupForm.activeOptionIndex || 0) === optionIndex;
                  const optionImageUrl = buildIfoodImageUrl(optionDraft.imagePath);
                  const optionTitle = cleanText(optionDraft.name) || `Complemento ${optionIndex + 1}`;
                  return (
                    <Paper key={optionDraft.draftId || optionIndex} variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                      <Button
                        fullWidth
                        color="inherit"
                        onClick={() => setProductGroupForm((prev) => ({ ...prev, activeOptionIndex: optionIndex }))}
                        sx={{ justifyContent: 'space-between', px: 2, py: 1.5, textAlign: 'left', fontWeight: 900, textTransform: 'none' }}
                      >
                        <span>{optionTitle}</span>
                        {isExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                      </Button>
                      {isExpanded ? (
                        <Box sx={{ px: 2, pb: 2 }}>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={8}>
                              <Stack spacing={1.5}>
                                <TextField
                                  label="Nome do complemento"
                                  value={optionDraft.name}
                                  onChange={(event) => updateProductGroupOptionDraft(optionIndex, 'name', event.target.value.slice(0, 80))}
                                  fullWidth
                                  required
                                  helperText={`${optionDraft.name.length}/80`}
                                />
                                <TextField
                                  label="Descricao"
                                  value={optionDraft.description}
                                  onChange={(event) => updateProductGroupOptionDraft(optionIndex, 'description', event.target.value.slice(0, 1000))}
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  helperText={`${optionDraft.description.length}/1000`}
                                />
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Button
                                component="label"
                                variant="outlined"
                                color="inherit"
                                sx={{
                                  width: '100%',
                                  aspectRatio: '4 / 3',
                                  borderStyle: 'dashed',
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  p: 0,
                                }}
                              >
                                {optionImageUrl ? (
                                  <Box
                                    component="img"
                                    src={optionImageUrl}
                                    alt="Foto do complemento"
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <Stack alignItems="center" spacing={1}>
                                    <AddPhotoAlternateRoundedIcon />
                                    <Typography sx={{ fontWeight: 850 }}>Adicionar foto</Typography>
                                  </Stack>
                                )}
                                <input
                                  hidden
                                  type="file"
                                  accept="image/*"
                                  onChange={(event) => uploadGroupComplementImage(`__productGroupNew:${optionDraft.draftId}`, event.target.files?.[0])}
                                />
                              </Button>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Preco"
                                value={optionDraft.price}
                                onChange={(event) => updateProductGroupOptionDraft(optionIndex, 'price', event.target.value)}
                                fullWidth
                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                              />
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                label="Codigo PDV do complemento"
                                value={optionDraft.externalCode}
                                onChange={(event) => updateProductGroupOptionDraft(optionIndex, 'externalCode', event.target.value)}
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      ) : null}
                    </Paper>
                  );
                })}
              </Stack>
            </Stack>
          ) : null}

          {productGroupDialog.mode === 'copy' ? (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                label="Buscar grupos"
                value={productGroupDialog.copySearch}
                onChange={(event) => setProductGroupDialog((prev) => ({ ...prev, copySearch: event.target.value }))}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
              {filteredProductGroupCopyGroups.length ? (
                <Grid container spacing={1.5}>
                  {filteredProductGroupCopyGroups.map((group) => {
                    const optionCount = getOfferSharedGroupOptions(group).length;
                    return (
                      <Grid item xs={12} md={6} key={group.sharedGroupKey || getVisualOptionGroupKey(group)}>
                        <Paper
                          variant="outlined"
                          role="button"
                          onClick={() => copyProductEditorGroup(group)}
                          sx={{ p: 2, borderRadius: 2.5, cursor: 'pointer', height: '100%', '&:hover': { borderColor: '#d92f3f' } }}
                        >
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LinkRoundedIcon color="action" />
                              <Typography sx={{ fontWeight: 900 }}>{group.name || 'Grupo sem nome'}</Typography>
                            </Stack>
                            <Typography color="text.secondary">Disponivel em: {group.availableIn || 'produto importado'}</Typography>
                            <Chip size="small" label={getStockPlural(optionCount, 'complemento', 'complementos')} sx={{ alignSelf: 'flex-start', fontWeight: 850 }} />
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Alert severity="info">Nenhum grupo real importado para reutilizar neste produto.</Alert>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {productGroupDialog.mode !== 'choice' ? (
            <Button onClick={() => setProductGroupDialog((prev) => ({ ...prev, mode: 'choice', copySearch: '' }))}>
              Voltar
            </Button>
          ) : null}
          <Button onClick={closeProductGroupDialog}>Cancelar</Button>
          {productGroupDialog.mode === 'new' ? (
            <Button variant="contained" color="error" onClick={createProductEditorGroup} disabled={Boolean(busyAction)}>
              Criar grupo
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    );
  };

  const renderComboStepBar = () => (
    <Stack direction="row" spacing={1.25} sx={{ mb: 3 }}>
      {comboSteps.map((label, index) => (
        <Box key={label} sx={{ flex: 1 }}>
          <Box
            sx={{
              height: 7,
              borderRadius: 999,
              bgcolor: index <= comboStep ? '#d92f3f' : '#d7d7d7',
              mb: 1,
            }}
          />
          <Button
            color={index === comboStep ? 'error' : 'inherit'}
            onClick={() => goToComboStep(index)}
            sx={{ minWidth: 0, p: 0, fontSize: 12, fontWeight: 850, textTransform: 'none', textAlign: 'left' }}
          >
            {index + 1}. {label}
          </Button>
        </Box>
      ))}
    </Stack>
  );

  const renderComboPhonePreview = () => {
    const imageUrl = buildIfoodImageUrl(comboForm.imagePath);
    const displayedGroups = comboGroups.filter((group) => toArray(group.options).length);
    return (
      <Box sx={{ width: 320, display: { xs: 'none', lg: 'block' }, flexShrink: 0 }}>
        <Paper elevation={0} sx={{ p: 1.25, mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: '#f5f5f5' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Inventory2OutlinedIcon color="action" />
            <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Simulacao do combo no app do iFood</Typography>
          </Stack>
        </Paper>
        <Box
          sx={{
            height: 620,
            border: '6px solid #111',
            borderRadius: 6,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Box sx={{ width: '100%', aspectRatio: '4 / 3', bgcolor: '#fff0f3', overflow: 'hidden' }}>
            {imageUrl ? (
              <Box component="img" src={imageUrl} alt={comboForm.name || 'Combo'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: '#ef4764' }}>
                <RestaurantMenuRoundedIcon sx={{ fontSize: 58 }} />
              </Box>
            )}
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ width: 48, height: 4, borderRadius: 999, bgcolor: '#e5e7eb', mx: 'auto', mb: 2 }} />
            <Typography sx={{ fontSize: 20, fontWeight: 900 }}>
              {comboForm.name || 'Nome do combo'}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, minHeight: 42 }}>
              {comboForm.description || 'Descricao'}
            </Typography>
            {displayedGroups.map((group) => (
              <Box key={group.draftId} sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 900 }}>{group.name}</Typography>
                  <Chip size="small" label={group.kind === 'main' ? 'Obrigatorio' : 'Opcional'} sx={{ fontWeight: 800 }} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  O cliente podera escolher ate {group.max} opcao{Number(group.max) === 1 ? '' : 'es'}.
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderComboGroupSummary = (group) => {
    const options = toArray(group.options);
    return (
      <Paper
        key={group.draftId}
        variant="outlined"
        sx={{ p: 2, borderRadius: 3, borderColor: group.kind === 'main' ? '#ef4444' : '#e5e7eb' }}
      >
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  bgcolor: group.kind === 'main' ? '#2f7d4b' : '#f5f5f5',
                  color: group.kind === 'main' ? '#fff' : '#111',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 950,
                }}
              >
                {group.kind === 'main' ? '✓' : '2'}
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontSize: 20, fontWeight: 950 }}>{group.name}</Typography>
                  {group.kind === 'main' ? <Chip size="small" color="error" label="Grupo principal" sx={{ fontWeight: 850 }} /> : null}
                  {group.source === 'copied' ? <Chip size="small" icon={<LinkRoundedIcon />} label="Reutilizado" sx={{ fontWeight: 850 }} /> : null}
                </Stack>
                <Typography color="text.secondary">
                  {getStockPlural(options.length, 'produto', 'produtos')} | maximo {group.max}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.75}>
              {group.source === 'new' ? (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => openComboProductDrawer(group.kind, group.draftId)}
                  sx={{ borderRadius: 2, fontWeight: 900 }}
                >
                  Editar
                </Button>
              ) : null}
              <IconButton title="Remover grupo" onClick={() => removeComboGroup(group.draftId)}>
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Grid container spacing={1}>
            {options.map((option) => (
              <Grid item xs={12} sm={6} key={option.id || option.itemId}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 1, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Box sx={{ width: 54, aspectRatio: '4 / 3', borderRadius: 1.5, bgcolor: '#f2f2f3', overflow: 'hidden', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    {option.imageUrl || option.imagePath ? (
                      <Box component="img" src={option.imageUrl || buildIfoodImageUrl(option.imagePath)} alt={option.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <ImageOutlinedIcon color="action" />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 850 }} noWrap>{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {option.categoryName || 'Cardapio'} | {formatCurrency(parseCatalogMoney(option.optionPrice))}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Paper>
    );
  };

  const renderComboStepContent = () => {
    if (comboStep === 0) {
      return (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950 }}>Crie um combo com produtos do seu cardapio</Typography>
            <Typography color="text.secondary">Agrupe produtos principais reais do iFood. Complementos puros nao aparecem nesta lista.</Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 42, height: 42, borderRadius: '50%', border: '2px solid #111', display: 'grid', placeItems: 'center', fontWeight: 950 }}>
                    1
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 950 }}>Adicione produtos</Typography>
                    <Typography color="text.secondary">Agrupe um ou mais produtos para organizar seu combo.</Typography>
                  </Box>
                </Stack>
                <Button variant="outlined" color="inherit" onClick={() => openComboProductDrawer('main')} sx={{ borderRadius: 2, fontWeight: 900 }}>
                  {comboMainGroup ? 'Editar' : 'Adicionar'}
                </Button>
              </Stack>
              {comboMainGroup ? renderComboGroupSummary(comboMainGroup) : (
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: '#fafafa', textAlign: 'center' }}>
                  <Typography color="text.secondary">Nenhum grupo principal adicionado.</Typography>
                </Paper>
              )}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, opacity: comboMainGroup ? 1 : 0.65 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 42, height: 42, borderRadius: '50%', border: '2px solid #111', display: 'grid', placeItems: 'center', fontWeight: 950 }}>
                    2
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 950 }}>Que tal oferecer outros produtos junto com o combo? (Opcional)</Typography>
                    <Typography color="text.secondary">Reutilize grupos existentes ou crie outro grupo de produtos.</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button disabled={!comboMainGroup} variant="outlined" color="inherit" onClick={() => openComboProductDrawer('optional')} sx={{ borderRadius: 2, fontWeight: 900 }}>
                    Criar grupo
                  </Button>
                  <Button disabled={!comboMainGroup} variant="outlined" color="inherit" onClick={openComboGroupDrawer} sx={{ borderRadius: 2, fontWeight: 900 }}>
                    Copiar grupo
                  </Button>
                </Stack>
              </Stack>
              {comboOptionalGroups.length ? (
                <Stack spacing={1.5}>
                  {comboOptionalGroups.map((group) => renderComboGroupSummary(group))}
                </Stack>
              ) : (
                <Typography color="text.secondary">Nenhum grupo adicional no combo.</Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      );
    }

    if (comboStep === 1) {
      return (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950 }}>Como ira ofertar seu combo?</Typography>
            <Typography color="text.secondary">Escolha a modalidade de preco, revise PDV, categoria e disponibilidade.</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                role="button"
                onClick={() => updateComboForm('priceMode', 'combo')}
                sx={{ p: 2, borderRadius: 2.5, cursor: 'pointer', borderColor: comboForm.priceMode === 'combo' ? '#d92f3f' : '#e5e7eb' }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography sx={{ fontWeight: 950 }}>Preco no combo</Typography>
                    <Typography color="text.secondary">Defina um preco final para o combo.</Typography>
                  </Box>
                  <Checkbox checked={comboForm.priceMode === 'combo'} color="error" />
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                role="button"
                onClick={() => updateComboForm('priceMode', 'products')}
                sx={{ p: 2, borderRadius: 2.5, cursor: 'pointer', borderColor: comboForm.priceMode === 'products' ? '#d92f3f' : '#e5e7eb' }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography sx={{ fontWeight: 950 }}>Precos nos produtos</Typography>
                    <Typography color="text.secondary">O valor parte das escolhas dos produtos.</Typography>
                  </Box>
                  <Checkbox checked={comboForm.priceMode === 'products'} color="error" />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 950 }}>Quanto vai custar esse combo?</Typography>
              {comboForm.priceMode === 'combo' ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      label="Preco final"
                      value={comboForm.price}
                      onChange={(value) => updateComboForm('price', value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      label="Preco original"
                      value={comboForm.originalPrice}
                      onChange={(value) => updateComboForm('originalPrice', value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Stack spacing={1.5}>
                  <TextField
                    label="Preco a partir de"
                    value={formatCurrency(comboStartingPrice)}
                    disabled
                    fullWidth
                    size="small"
                  />
                  {comboGroups.map((group) => (
                    <Box key={group.draftId} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>{group.name}</Typography>
                      <Stack spacing={1}>
                        {toArray(group.options).map((option) => (
                          <Stack key={option.id || option.itemId} direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Typography sx={{ flex: 1, fontWeight: 800 }}>{option.name}</Typography>
                            <TextField
                              value={option.optionPrice}
                              onChange={(event) => updateComboGroupOption(group.draftId, option.id, 'optionPrice', event.target.value)}
                              size="small"
                              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                              sx={{ width: { xs: '100%', sm: 160 } }}
                            />
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 950 }}>Informacoes gerais</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Categoria" value={comboDialog.category?.name || ''} disabled fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label="Codigo PDV" value={comboForm.externalCode} onChange={(value) => updateComboForm('externalCode', value)} />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    role="button"
                    onClick={() => updateComboForm('availabilityMode', 'always')}
                    sx={{ p: 2, borderRadius: 2.5, cursor: 'pointer', borderColor: comboForm.availabilityMode === 'always' ? '#111' : '#e5e7eb' }}
                  >
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography sx={{ fontWeight: 950 }}>Sempre disponivel</Typography>
                        <Typography color="text.secondary">O combo fica disponivel enquanto a loja estiver aberta.</Typography>
                      </Box>
                      <Checkbox checked={comboForm.availabilityMode === 'always'} color="error" />
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    role="button"
                    onClick={openComboScheduleDialog}
                    sx={{ p: 2, borderRadius: 2.5, cursor: 'pointer', borderColor: comboForm.availabilityMode === 'scheduled' ? '#111' : '#e5e7eb' }}
                  >
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography sx={{ fontWeight: 950 }}>Dias e horarios especificos</Typography>
                        <Typography color="text.secondary">Selecione periodos para o combo ficar visivel.</Typography>
                      </Box>
                      <Checkbox checked={comboForm.availabilityMode === 'scheduled'} color="error" />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Stack>
      );
    }

    const comboImageUrl = buildIfoodImageUrl(comboForm.imagePath);
    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Escolha o nome do seu combo</Typography>
          <Typography color="text.secondary">Capriche no nome, foto e descricao para atrair clientes.</Typography>
        </Box>
        <Field
          label="Nome do combo"
          value={comboForm.name}
          onChange={(value) => updateComboForm('name', value)}
          inputProps={{ maxLength: 80 }}
        />
        <Field
          label="Descricao"
          value={comboForm.description}
          onChange={(value) => updateComboForm('description', value)}
          multiline
          minRows={4}
          inputProps={{ maxLength: 1000 }}
        />
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 850 }}>Imagem dos produtos</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box sx={{ width: 180, aspectRatio: '4 / 3', borderRadius: 2, border: '1px dashed #d1d5db', bgcolor: '#f8fafc', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
              {comboImageUrl ? (
                <Box component="img" src={comboImageUrl} alt={comboForm.name || 'Combo'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <ImageOutlinedIcon color="action" sx={{ fontSize: 42 }} />
              )}
            </Box>
            <Button component="label" variant="outlined" color="error" startIcon={<AddPhotoAlternateRoundedIcon />} sx={{ borderRadius: 2, fontWeight: 900, alignSelf: 'flex-start' }}>
              Adicionar imagem
              <input hidden type="file" accept="image/*" onChange={(event) => uploadComboImage(event.target.files?.[0])} />
            </Button>
          </Stack>
        </Stack>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 950 }}>Resumo para envio</Typography>
            <Typography>Combo pronto para envio com um grupo principal e grupos adicionais opcionais.</Typography>
            <Typography>Produtos no combo: {getStockPlural(comboOptionCount, 'produto', 'produtos')}</Typography>
            <Typography>Preco enviado: {formatCurrency(comboForm.priceMode === 'products' ? comboStartingPrice : parseCatalogMoney(comboForm.price))}</Typography>
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const renderComboProductDrawer = () => {
    const selectedCount = toArray(comboDrawer.selectedIds).length;
    const selectedProducts = toArray(comboDrawer.selectedIds)
      .map((itemId) => availableComboProducts.find((product) => product.id === itemId))
      .filter(Boolean);
    return (
      <Dialog
        open={comboDrawer.open && comboDrawer.mode === 'products'}
        onClose={closeComboDrawer}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { ml: 'auto', mr: 1.5, height: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', borderRadius: 3 } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950 }}>Adicionar produtos</Typography>
              <Typography color="text.secondary">Selecione somente produtos principais do cardapio.</Typography>
            </Box>
            <IconButton onClick={closeComboDrawer}><CloseRoundedIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {comboDrawer.step === 'select' ? (
            <Stack spacing={2}>
              <TextField
                placeholder="Buscar produtos"
                value={comboDrawer.search}
                onChange={(event) => setComboDrawer((prev) => ({ ...prev, search: event.target.value }))}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
              {selectedProducts.length ? (
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 900 }}>Produtos adicionados:</Typography>
                  {selectedProducts.map((product) => (
                    <Stack key={product.id} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1 }}>
                      <DragIndicatorRoundedIcon color="action" />
                      <Box sx={{ width: 54, aspectRatio: '4 / 3', borderRadius: 1.5, bgcolor: '#f2f2f3', overflow: 'hidden', display: 'grid', placeItems: 'center' }}>
                        {product.imageUrl ? <Box component="img" src={product.imageUrl} alt={product.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageOutlinedIcon color="action" />}
                      </Box>
                      <Typography sx={{ flex: 1, fontWeight: 850 }}>{product.name}</Typography>
                      <IconButton onClick={() => toggleComboDrawerProduct(product.id)}><DeleteOutlineRoundedIcon /></IconButton>
                    </Stack>
                  ))}
                  <Divider />
                </Stack>
              ) : null}
              <Grid container spacing={1.25}>
                {comboFilteredProducts.map((product) => {
                  const checked = toArray(comboDrawer.selectedIds).includes(product.id);
                  return (
                    <Grid item xs={12} md={6} key={product.id}>
                      <Paper
                        variant="outlined"
                        role="button"
                        onClick={() => toggleComboDrawerProduct(product.id)}
                        sx={{ p: 1.25, borderRadius: 2, cursor: 'pointer', borderColor: checked ? '#111' : '#e5e7eb' }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Checkbox checked={checked} />
                          <Box sx={{ width: 56, aspectRatio: '4 / 3', borderRadius: 1.5, bgcolor: '#f2f2f3', overflow: 'hidden', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                            {product.imageUrl ? <Box component="img" src={product.imageUrl} alt={product.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageOutlinedIcon color="action" />}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 900 }} noWrap>{product.name}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>{product.categoryName || 'Cardapio'} | {formatCurrency(product.price)}</Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
              {!comboFilteredProducts.length ? (
                <Alert severity="info">Nenhum produto principal encontrado para o filtro atual.</Alert>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Typography sx={{ fontWeight: 950 }}>Quantos produtos o cliente podera escolher?</Typography>
              <TextField
                label="Nome dos produtos agrupados"
                value={comboDrawer.groupName}
                onChange={(event) => setComboDrawer((prev) => ({ ...prev, groupName: event.target.value }))}
                fullWidth
              />
              <Box>
                <Typography sx={{ fontWeight: 850, mb: 1 }}>Quantidade maxima</Typography>
                <Stack direction="row" alignItems="center" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: 180, minHeight: 52, overflow: 'hidden' }}>
                  <IconButton onClick={() => setComboDrawer((prev) => ({ ...prev, max: Math.max(1, Number(prev.max || 1) - 1) }))} sx={{ borderRadius: 0, flex: 1 }}>−</IconButton>
                  <Typography sx={{ flex: 1, textAlign: 'center', fontWeight: 900 }}>{comboDrawer.max}</Typography>
                  <IconButton onClick={() => setComboDrawer((prev) => ({ ...prev, max: Math.min(Math.max(1, selectedCount), Number(prev.max || 1) + 1) }))} sx={{ borderRadius: 0, flex: 1 }}>+</IconButton>
                </Stack>
              </Box>
              <Stack spacing={1}>
                {selectedProducts.map((product) => (
                  <Stack key={product.id} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1 }}>
                    <Box sx={{ width: 54, aspectRatio: '4 / 3', borderRadius: 1.5, bgcolor: '#f2f2f3', overflow: 'hidden', display: 'grid', placeItems: 'center' }}>
                      {product.imageUrl ? <Box component="img" src={product.imageUrl} alt={product.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageOutlinedIcon color="action" />}
                    </Box>
                    <Typography sx={{ fontWeight: 850 }}>{product.name}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {comboDrawer.step === 'configure' ? (
            <Button onClick={() => setComboDrawer((prev) => ({ ...prev, step: 'select' }))}>Voltar</Button>
          ) : null}
          <Button onClick={closeComboDrawer}>Cancelar</Button>
          {comboDrawer.step === 'select' ? (
            <Button
              variant="contained"
              color="inherit"
              disabled={!selectedCount}
              onClick={() => setComboDrawer((prev) => ({ ...prev, step: 'configure', groupName: prev.groupName || (prev.groupKind === 'main' ? 'Combo principal' : 'Produtos adicionais') }))}
              sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#111' } }}
            >
              Continuar
            </Button>
          ) : (
            <Button variant="contained" color="inherit" onClick={saveComboProductDrawer} sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#111' } }}>
              Concluir
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderComboGroupDrawer = () => (
    <Dialog
      open={comboDrawer.open && comboDrawer.mode === 'groups'}
      onClose={closeComboDrawer}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { ml: 'auto', mr: 1.5, height: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', borderRadius: 3 } }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950 }}>Adicionar grupo</Typography>
            <Typography color="text.secondary">Reutilize um grupo de produtos ja existente no cardapio.</Typography>
          </Box>
          <IconButton onClick={closeComboDrawer}><CloseRoundedIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            placeholder="Buscar grupos de produtos"
            value={comboDrawer.search}
            onChange={(event) => setComboDrawer((prev) => ({ ...prev, search: event.target.value }))}
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <Button
            variant="outlined"
            color="error"
            startIcon={<AddIcon />}
            onClick={() => openComboProductDrawer('optional')}
            sx={{ alignSelf: 'flex-start', borderRadius: 2, fontWeight: 900 }}
          >
            Criar grupo
          </Button>
          {comboReusableGroups.length ? (
            <Stack spacing={1.25}>
              {comboReusableGroups.map((group) => {
                const optionCount = getReusableComboGroupOptions(group).length;
                return (
                  <Paper key={group.sharedGroupKey || getVisualOptionGroupKey(group)} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <LinkRoundedIcon color="action" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950 }} noWrap>{group.name || 'Grupo sem nome'}</Typography>
                        <Typography color="text.secondary" noWrap>Disponivel em: {group.availableIn || 'produto importado'}</Typography>
                        <Chip size="small" label={getStockPlural(optionCount, 'produto', 'produtos')} sx={{ mt: 1, fontWeight: 850 }} />
                      </Box>
                      <Button variant="outlined" color="inherit" onClick={() => addComboCopiedGroup(group)} sx={{ borderRadius: 2, fontWeight: 900 }}>
                        Adicionar
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Alert severity="info">Nenhum grupo de produtos encontrado para reutilizar.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeComboDrawer}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );

  const renderComboScheduleDialog = () => (
    <Dialog
      open={comboScheduleDialog.open}
      onClose={() => setComboScheduleDialog({ open: false, drafts: createComboScheduleDrafts(comboForm.shifts) })}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { ml: 'auto', mr: 1.5, height: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', borderRadius: 3 } }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Disponivel quando</Typography>
          <IconButton onClick={() => setComboScheduleDialog({ open: false, drafts: createComboScheduleDrafts(comboForm.shifts) })}><CloseRoundedIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography sx={{ fontWeight: 850 }}>Dias da semana</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {toArray(comboScheduleDialog.drafts).map((draft) => (
              <Button
                key={draft.key}
                variant={draft.enabled ? 'contained' : 'outlined'}
                color={draft.enabled ? 'error' : 'inherit'}
                onClick={() => updateComboScheduleDraft(draft.key, 'enabled', !draft.enabled)}
                sx={{ minWidth: 42, borderRadius: '50%', width: 42, height: 42, p: 0, fontWeight: 900 }}
              >
                {draft.label}
              </Button>
            ))}
          </Stack>
          {toArray(comboScheduleDialog.drafts).map((draft) => (
            draft.enabled ? (
              <Paper key={draft.key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Typography sx={{ flex: 1, fontWeight: 900 }}>{draft.name}</Typography>
                  <TextField
                    label="Inicio"
                    type="time"
                    value={draft.startTime}
                    onChange={(event) => updateComboScheduleDraft(draft.key, 'startTime', event.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Fim"
                    type="time"
                    value={draft.endTime}
                    onChange={(event) => updateComboScheduleDraft(draft.key, 'endTime', event.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Paper>
            ) : null
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setComboScheduleDialog({ open: false, drafts: createComboScheduleDrafts(comboForm.shifts) })}>Fechar</Button>
        <Button variant="contained" color="error" onClick={applyComboSchedule}>Aplicar</Button>
      </DialogActions>
    </Dialog>
  );

  const renderComboDialog = () => (
    <>
      <Dialog open={comboDialog.open} onClose={closeComboDialog} fullScreen PaperProps={{ sx: { bgcolor: '#fff' } }}>
        <DialogTitle sx={{ px: { xs: 2, md: 7 }, pt: 4, pb: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 950 }}>Criar combo</Typography>
              <Typography color="text.secondary">Monte os grupos, preco, disponibilidade e apresentacao do combo.</Typography>
            </Box>
            <Button color="error" onClick={closeComboDialog} endIcon={<CloseRoundedIcon />} sx={{ fontWeight: 900 }}>
              Fechar
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 7 }, pt: 4, pb: 12 }}>
          {renderComboStepBar()}
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4} alignItems="flex-start">
            <Box sx={{ flex: 1, maxWidth: 980, minWidth: 0 }}>
              {renderComboStepContent()}
            </Box>
            {renderComboPhonePreview()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: '#fff', borderTop: '1px solid #e5e7eb', px: { xs: 2, md: 7 }, py: 2, zIndex: 3 }}>
          {comboStep > 0 ? (
            <Button onClick={() => goToComboStep(comboStep - 1)} color="error" variant="outlined" sx={{ borderRadius: 2, fontWeight: 900 }}>
              Voltar
            </Button>
          ) : null}
          {comboStep < comboSteps.length - 1 ? (
            <Button variant="contained" color="inherit" onClick={advanceComboStep} sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#111' }, borderRadius: 2, fontWeight: 900 }}>
              Continuar
            </Button>
          ) : (
            <Button variant="contained" color="error" onClick={submitComboItem} disabled={Boolean(busyAction)} sx={{ borderRadius: 2, fontWeight: 900 }}>
              Criar combo
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {renderComboProductDrawer()}
      {renderComboGroupDrawer()}
      {renderComboScheduleDialog()}
    </>
  );

  const promotionProduct = productPromotionDialog.product || {};
  const promotionBasePrice = Number(productPromotionDialog.basePrice || 0);
  const promotionPriceValue = parseCatalogMoney(productPromotionDialog.promotionalPrice);
  const promotionHasActiveDiscount = promotionBasePrice > Number(promotionProduct.price || 0);
  const promotionApplyDisabled = Boolean(busyAction)
    || !promotionProduct.id
    || promotionBasePrice <= 0
    || !cleanText(productPromotionDialog.promotionalPrice)
    || promotionPriceValue <= 0
    || promotionPriceValue >= promotionBasePrice;

  return (
    <Box sx={catalogPageSx}>
      <Stack spacing={2}>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <PageMenuButton />
              <Box sx={{ minWidth: 0, pt: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  Loja iFood
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {selectedMerchantId
                    ? selectedMerchantName || 'Loja selecionada'
                    : 'Selecione uma loja cadastrada para operar o catálogo.'}
                </Typography>
              </Box>
            </Stack>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Loja"
              value={selectedMerchantId}
              onChange={(event) => setSelectedMerchantId(event.target.value)}
              disabled={merchantsLoading}
              sx={{ minWidth: { xs: '100%', md: 360 } }}
              size="small"
            >
              <option value="">{merchantsLoading ? 'Carregando lojas...' : 'Selecione uma loja'}</option>
              {merchants.map((merchant) => {
                const id = getMerchantId(merchant);
                return (
                  <option key={id} value={id}>
                    {getMerchantName(merchant) || 'Loja iFood'}
                  </option>
                );
              })}
            </TextField>
          </Stack>
          {merchantsError ? (
            <Alert severity="error" sx={{ mt: 2 }}>{merchantsError}</Alert>
          ) : null}
          {workspacePersistence !== 'complete' || !merchantSelectionPersisted ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {workspacePersistence === 'drafts-only' && merchantSelectionPersisted
                ? 'O catálogo importado continua disponível nesta aba, mas é grande demais para ser restaurado após recarregar. Atualize o cardápio novamente se necessário.'
                : 'O navegador não conseguiu salvar todo o workspace temporário. Mantenha esta aba aberta até concluir seus rascunhos; o catálogo pode ser atualizado novamente.'}
            </Alert>
          ) : null}
          {!merchantsLoading && !merchantsError && !selectedMerchantId ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Nenhuma alteração de categoria, produto, complemento, preço, status, foto, estoque ou PDV fica disponível sem uma loja iFood selecionada.
            </Alert>
          ) : null}
        </Paper>

        {tab === 2 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      Catalogos e categorias
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Field label="catalogId" value={selectedCatalogId} onChange={setSelectedCatalogId} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field label="categoryId" value={selectedCategoryId} onChange={setSelectedCategoryId} />
                      </Grid>
                    </Grid>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button startIcon={<PlayArrowRoundedIcon />} variant="contained" disabled={busyAction === 'catalogs'} onClick={listCatalogs}>
                        Listar catalogos
                      </Button>
                      <Button startIcon={<RestaurantMenuRoundedIcon />} variant="contained" color="success" disabled={Boolean(busyAction)} onClick={importFullCatalogVisual}>
                        Importar visual
                      </Button>
                      <Button variant="outlined" disabled={busyAction === 'categories-list'} onClick={listCategories}>
                        Listar categorias
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => runAction({
                          id: 'sellable-items',
                          path: '/sellable-items',
                          payload: { catalogId: selectedCatalogId },
                          label: 'Itens vendiveis listados.',
                          visualImport: (result) => normalizeVisualFromItems(result.data, {
                            ...catalogScope,
                            catalogId: selectedCatalogId,
                            catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
                          }),
                        })}
                      >
                        Sellable
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => runAction({
                          id: 'unsellable-items',
                          path: '/unsellable-items',
                          payload: { catalogId: selectedCatalogId },
                          label: 'Itens nao vendiveis listados.',
                          visualImport: (result) => normalizeVisualFromItems(result.data, {
                            ...catalogScope,
                            catalogId: selectedCatalogId,
                            catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
                          }),
                        })}
                      >
                        Unsellable
                      </Button>
                      <Button
                        variant={includeItems ? 'contained' : 'outlined'}
                        color={includeItems ? 'secondary' : 'inherit'}
                        onClick={() => setIncludeItems((prev) => !prev)}
                      >
                        includeItems: {includeItems ? 'sim' : 'nao'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      Criar categoria
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <Field label="Nome" value={category.name} onChange={(value) => setCategory((prev) => ({ ...prev, name: value }))} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Field label="Status" value={category.status} onChange={(value) => setCategory((prev) => ({ ...prev, status: value }))} />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Field label="Template" value={category.template} onChange={(value) => setCategory((prev) => ({ ...prev, template: value }))} />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Field label="Sequence" type="number" value={category.sequence} onChange={(value) => setCategory((prev) => ({ ...prev, sequence: value }))} />
                      </Grid>
                    </Grid>
                    <Button startIcon={<PlayArrowRoundedIcon />} variant="contained" sx={{ alignSelf: 'flex-start' }} disabled={busyAction === 'category-create'} onClick={createCategory}>
                      Criar categoria
                    </Button>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Stack spacing={2}>
                <CatalogVisualPanel
                  visualCatalog={visualCatalog}
                  selectedItemId={refs.itemId}
                  onSelectCatalog={selectVisualCatalog}
                  onSelectCategory={selectVisualCategory}
                  onSelectItem={selectVisualItem}
                  onClear={() => setVisualCatalog(emptyVisualCatalog)}
                  title="Resumo importado"
                  compact
                />
                <ActionStatusPanel result={lastResult} />
              </Stack>
            </Grid>
          </Grid>
        ) : null}

        {tab === 3 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 850 }}>
                    Item completo
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button startIcon={<RefreshRoundedIcon />} variant="outlined" onClick={regenerateDemoPayload}>
                      Gerar massa demo
                    </Button>
                    <Button variant="outlined" onClick={syncRefsFromPayload}>
                      Sincronizar IDs
                    </Button>
                    <Button startIcon={<PlayArrowRoundedIcon />} variant="contained" disabled={busyAction === 'item-upsert'} onClick={upsertItem}>
                      Enviar PUT item
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => runAction({
                        id: 'items-list',
                        path: '/items/list',
                        payload: { categoryId: selectedCategoryId },
                        label: 'Itens da categoria listados.',
                        visualImport: (result) => normalizeVisualFromItems(result.data, {
                          ...catalogScope,
                          catalogId: selectedCatalogId,
                          catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
                          categoryId: selectedCategoryId,
                        }),
                      })}
                    >
                      Listar itens
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => runAction({
                        id: 'items-flat',
                        path: '/items/flat',
                        payload: { itemId: refs.itemId },
                        label: 'Item flat consultado.',
                        visualImport: (result) => normalizeVisualFromFlat(result.data, {
                          ...catalogScope,
                          catalogId: selectedCatalogId,
                          catalogContext: findCatalogContext(visualCatalog.catalogs, selectedCatalogId),
                          categoryId: selectedCategoryId,
                        }),
                      })}
                    >
                      Item flat
                    </Button>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Field label="itemId" value={refs.itemId || ''} onChange={(value) => setRefs((prev) => ({ ...prev, itemId: value }))} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field label="optionId" value={refs.optionId || ''} onChange={(value) => setRefs((prev) => ({ ...prev, optionId: value }))} />
                    </Grid>
                  </Grid>
                  <Alert severity="info">
                    O payload do item e gerado internamente pela massa demo e exibido abaixo em formato visual.
                  </Alert>
                  <CatalogVisualPanel
                    visualCatalog={payloadPreviewVisual}
                    selectedItemId={refs.itemId}
                    onSelectItem={selectVisualItem}
                    title="Preview humano do payload"
                    compact
                  />
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                      IDs da massa
                    </Typography>
                    {Object.entries(refs).map(([key, value]) => (
                      <Typography key={key} variant="caption" sx={{ wordBreak: 'break-all' }}>
                        <strong>{key}:</strong> {value || '-'}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
                <ActionStatusPanel result={lastResult} />
              </Stack>
            </Grid>
          </Grid>
        ) : null}

        {tab === 4 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} xl={8}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      Preco, status e codigos externos
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Field label="itemId" value={refs.itemId || ''} onChange={(value) => setRefs((prev) => ({ ...prev, itemId: value }))} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field label="optionId" value={refs.optionId || ''} onChange={(value) => setRefs((prev) => ({ ...prev, optionId: value }))} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field label="item externalCode" value={refs.itemExternalCode || ''} onChange={(value) => setRefs((prev) => ({ ...prev, itemExternalCode: value }))} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field label="option externalCode" value={refs.optionExternalCode || ''} onChange={(value) => setRefs((prev) => ({ ...prev, optionExternalCode: value }))} />
                      </Grid>
                    </Grid>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshRoundedIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                      onClick={() => setPatchPayloads(buildPatchPayloads(refs))}
                    >
                      Recriar payloads pelos IDs
                    </Button>
                  </Stack>
                </Paper>

                <Grid container spacing={2}>
                  {[
                    ['Preco do item', 'Atualiza o item selecionado para R$ 31,90.', 'item-price', '/items/price', patchPayloads.itemPrice, 'Preco de item alterado.', 'item-price'],
                    ['Status do item', 'Alterna a disponibilidade do item selecionado na loja iFood.', 'item-status', '/items/status', patchPayloads.itemStatus, 'Status de item alterado.', 'item-status'],
                    ['Preco do complemento', 'Atualiza o complemento selecionado para R$ 6,00.', 'option-price', '/options/price', patchPayloads.optionPrice, 'Preco de complemento alterado.', 'option-price'],
                    ['Status do complemento', 'Alterna a disponibilidade do complemento selecionado.', 'option-status', '/options/status', patchPayloads.optionStatus, 'Status de complemento alterado.', 'option-status'],
                    ['Codigo externo do item', 'Aplica um codigo externo de teste ao item selecionado.', 'item-external-code', '/items/external-code', patchPayloads.itemExternalCode, 'Codigo externo de item alterado.'],
                    ['Codigo externo do complemento', 'Aplica um codigo externo de teste ao complemento selecionado.', 'option-external-code', '/options/external-code', patchPayloads.optionExternalCode, 'Codigo externo de complemento alterado.'],
                  ].map(([title, description, actionId, path, payload, label, checkId]) => (
                    <Grid item xs={12} md={6} key={actionId}>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography sx={{ fontWeight: 850 }}>{title}</Typography>
                            <Typography variant="body2" color="text.secondary">{description}</Typography>
                          </Box>
                          <Button variant={checkId ? 'contained' : 'outlined'} onClick={() => runJsonPatch(actionId, path, payload, label, checkId)}>
                            Executar
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>
            <Grid item xs={12} xl={4}>
              <Stack spacing={2}>
                <CatalogVisualPanel
                  visualCatalog={visualCatalog}
                  selectedItemId={refs.itemId}
                  onSelectItem={selectVisualItem}
                  title="Produto em edicao"
                />
                <ActionStatusPanel result={lastResult} />
              </Stack>
            </Grid>
          </Grid>
        ) : null}

        {tab === 5 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      Upload de imagem
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button component="label" variant="outlined" startIcon={<AddPhotoAlternateRoundedIcon />}>
                        Selecionar imagem
                        <input hidden accept="image/png,image/jpeg" type="file" onChange={handleImageFile} />
                      </Button>
                      <Button variant="contained" disabled={!imageDataUrl || busyAction === 'image-upload'} onClick={uploadImage}>
                        Enviar imagem
                      </Button>
                    </Stack>
                    <Field label="imagePath retornado" value={imagePath} onChange={setImagePath} />
                    {imageDataUrl ? (
                      <Box
                        component="img"
                        src={imageDataUrl}
                        alt="Preview upload Catalog"
                        sx={{ width: 220, maxWidth: '100%', borderRadius: 2, border: '1px solid #e5e7eb' }}
                      />
                    ) : null}
                  </Stack>
                </Paper>

                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      Evidencia
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        startIcon={<AssignmentTurnedInRoundedIcon />}
                        variant="contained"
                        onClick={async () => {
                          const result = await listCategories();
                          const item = extractEvidenceItem(result?.data);
                          if (item) {
                            setEvidenceItem(item);
                            showToast('Evidencia atualizada.');
                          }
                        }}
                      >
                        Buscar evidencia
                      </Button>
                    </Stack>
                    {evidenceItem ? (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="overline" color="text.secondary">{evidenceItem.categoryName || 'Categoria'}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 850 }}>{evidenceItem.name || 'Item sem nome'}</Typography>
                          <Typography color="text.secondary">{evidenceItem.description || 'Sem descricao'}</Typography>
                          <Typography sx={{ fontWeight: 900 }}>{formatCurrency(evidenceItem.price)}</Typography>
                          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                            imagePath: {evidenceItem.imagePath || imagePath || '-'}
                          </Typography>
                        </Stack>
                      </Paper>
                    ) : (
                      <Alert severity="info">Liste categorias com itens para montar a evidencia.</Alert>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={5}>
              <ActionStatusPanel result={lastResult} />
            </Grid>
          </Grid>
        ) : null}

        {tab === 6 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} xl={8}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      Batch e inventario
                    </Typography>
                    <Typography color="text.secondary">
                      Operacoes auxiliares executadas com payloads internos gerados a partir dos IDs selecionados.
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        ['Batch de preco', 'Envia atualizacao em lote de preco para o externalCode do item selecionado.', 'products-price-batch', '/products/price-batch', patchPayloads.batchPrice, 'Batch de preco enviado.'],
                        ['Batch de status', 'Envia atualizacao em lote de status para o externalCode do item selecionado.', 'products-status-batch', '/products/status-batch', patchPayloads.batchStatus, 'Batch de status enviado.'],
                        ['Criar inventario', 'Cria inventario para o productId do item selecionado com quantidade de teste.', 'inventory-create', '/inventory/create', patchPayloads.inventoryCreate, 'Inventario criado.'],
                        ['Remover inventario', 'Remove o inventario criado para o productId do item selecionado.', 'inventory-delete', '/inventory/delete', patchPayloads.inventoryDelete, 'Inventario removido.'],
                      ].map(([title, description, actionId, path, payload, label]) => (
                        <Grid item xs={12} md={6} key={actionId}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack spacing={1.5}>
                              <Box>
                                <Typography sx={{ fontWeight: 850 }}>{title}</Typography>
                                <Typography variant="body2" color="text.secondary">{description}</Typography>
                              </Box>
                              <Button variant="outlined" onClick={() => runJsonPatch(actionId, path, payload, label)}>
                                Executar
                              </Button>
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                      <Grid item xs={12} md={6}>
                        <Field label="batchId" value={batchId} onChange={setBatchId} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => runAction({ id: 'batch-status', path: '/batch/status', payload: { batchId }, label: 'Batch consultado.' })}
                        >
                          Consultar batch
                        </Button>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => runAction({ id: 'inventory-get', path: '/inventory/get', payload: { productId: refs.itemProductId }, label: 'Inventario consultado.' })}
                        >
                          Consultar inventario
                        </Button>
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
            <Grid item xs={12} xl={4}>
              <ActionStatusPanel result={lastResult} />
            </Grid>
          </Grid>
        ) : null}

        {tab === 7 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} xl={8}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      Visao humana do Catalog
                    </Typography>
                    <Typography color="text.secondary">
                      Importe catalogos, categorias, produtos, grupos e complementos retornados pela API Catalog para conferir a loja selecionada sem ler JSON bruto.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button startIcon={<RestaurantMenuRoundedIcon />} variant="contained" color="success" disabled={Boolean(busyAction)} onClick={importFullCatalogVisual}>
                        Importar catalogo completo
                      </Button>
                      <Button variant="outlined" disabled={!lastResult} onClick={importLastResultToVisual}>
                        Importar ultimo retorno
                      </Button>
                      <Button variant="outlined" color="inherit" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => setVisualCatalog(emptyVisualCatalog)}>
                        Limpar visual
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
                <CatalogVisualPanel
                  visualCatalog={visualCatalog}
                  selectedItemId={refs.itemId}
                  onSelectCatalog={selectVisualCatalog}
                  onSelectCategory={selectVisualCategory}
                  onSelectItem={selectVisualItem}
                  onClear={() => setVisualCatalog(emptyVisualCatalog)}
                  title="Catalogo importado"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} xl={4}>
              <Stack spacing={2}>
                <ActionStatusPanel result={lastResult} />
                <Alert severity="info">
                  Esta importacao visual nao grava produtos no estoque do Brutos. Ela apenas atualiza a visao operacional desta pagina.
                </Alert>
              </Stack>
            </Grid>
          </Grid>
        ) : null}

        {tab === 8 ? (
          <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-start' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    Cardapio
                  </Typography>
                </Box>
              </Stack>

              <Tabs
                value={stockInnerTab}
                onChange={(_, next) => setStockInnerTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  ...catalogScrollableTabsSx,
                  borderBottom: '1px solid #e5e7eb',
                  '& .MuiTab-root': { ...catalogScrollableTabsSx['& .MuiTab-root'], fontWeight: 850, fontSize: 16, textTransform: 'none' },
                }}
              >
                <Tab label="Cardapio" />
                <Tab label="Produtos" />
                <Tab label="Complementos" />
                <Tab label="PDV" />
                <Tab label="Configurações" />
              </Tabs>

              {(stockInnerTab === 0 || stockInnerTab === 3) ? (
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                  <TextField
                    placeholder="Buscar um item"
                    value={stockSearchTerm}
                    onChange={(event) => setStockSearchTerm(event.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 4, minHeight: 58 },
                    }}
                  />
                  <TextField
                    value={stockCategoryFilter}
                    onChange={(event) => setStockCategoryFilter(event.target.value)}
                    fullWidth
                    select
                    SelectProps={{ native: true }}
                    InputProps={{ sx: { borderRadius: 2, minHeight: 58 } }}
                  >
                    <option value="all">Selecionar categoria</option>
                    {visualCategories.map((item) => (
                      <option key={item.id} value={item.id}>{item.name || 'Sem categoria'}</option>
                    ))}
                  </TextField>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<AddIcon />}
                    onClick={openCreateCategoryDialog}
                    disabled={!selectedMerchantId}
                    sx={{
                      minHeight: 58,
                      borderRadius: 3,
                      px: 2.25,
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: 0, lg: 188 },
                      fontSize: 14,
                      fontWeight: 850,
                      lineHeight: 1,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      borderColor: '#d1d5db',
                      color: '#1f2937',
                      '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
                      '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                    }}
                  >
                    Adicionar categoria
                  </Button>
                  {stockInnerTab === 0 ? (
                    <IconButton
                      aria-label="Ordenar categorias"
                      title="Ordenar categorias"
                      onClick={reorderStockCategories}
                      disabled={!selectedMerchantId}
                      sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: { xs: '100%', sm: 58 }, height: 58 }}
                    >
                      <SwapVertRoundedIcon />
                    </IconButton>
                  ) : null}
                </Stack>
              ) : null}

              {stockInnerTab === 1 ? (
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {[
                      ['all', 'Todos', allStockRows.length],
                      ['paused', 'Pausados', pausedStockRowsCount],
                      ['active', 'Ativos', activeStockRowsCount],
                    ].map(([filter, label, count]) => (
                      <Button
                        key={filter}
                        color="inherit"
                        variant={stockProductStatusFilter === filter ? 'outlined' : 'contained'}
                        disableElevation
                        onClick={() => setStockProductStatusFilter(filter)}
                        sx={{
                          minHeight: 58,
                          borderRadius: 4,
                          bgcolor: stockProductStatusFilter === filter ? '#fff' : '#fff',
                          px: 2.5,
                          fontWeight: 900,
                        }}
                      >
                        {label}
                        <Chip size="small" label={count} sx={{ ml: 1, height: 22, fontWeight: 900 }} />
                      </Button>
                    ))}
                    <IconButton
                      aria-label="Limpar filtros"
                      title="Limpar filtros"
                      onClick={() => {
                        setStockProductStatusFilter('all');
                        setStockSearchTerm('');
                      }}
                      sx={{ border: '1px solid #e5e7eb', borderRadius: 2, width: 58, height: 58 }}
                    >
                      <TuneRoundedIcon />
                    </IconButton>
                  </Stack>
                  <TextField
                    placeholder="Buscar produtos"
                    value={stockSearchTerm}
                    onChange={(event) => setStockSearchTerm(event.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 4, minHeight: 58 },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<AddIcon />}
                    onClick={() => createStockItem(
                      visualCategories.find((item) => item.id === stockCategoryFilter) || stockCategories[0] || visualCategories[0],
                      'oferta'
                    )}
                    disabled={!selectedMerchantId}
                    sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 58, borderRadius: 3, px: 3, fontWeight: 900, whiteSpace: 'nowrap', bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#222' } }}
                  >
                    Criar produto
                  </Button>
                </Stack>
              ) : null}

              {stockInnerTab === 2 ? (
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900 }}>
                      Grupos de complementos
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, fontSize: 18 }}>
                      Faca ajustes ou pause os grupos de complemento do seu cardapio, como ingredientes, produtos adicionais ou descartaveis
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                    <TextField
                      placeholder="Buscar grupos"
                      value={stockGroupSearchTerm}
                      onChange={(event) => setStockGroupSearchTerm(event.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 4, minHeight: 58 },
                      }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 58, whiteSpace: { xs: 'normal', sm: 'nowrap' }, flexWrap: 'wrap' }}>
                      <Checkbox
                        checked={filteredStockGroups.length > 0 && filteredStockGroups.every((group) => stockSelectedGroupIds.includes(getStockGroupSelectionId(group)))}
                        indeterminate={
                          filteredStockGroups.some((group) => stockSelectedGroupIds.includes(getStockGroupSelectionId(group)))
                          && !filteredStockGroups.every((group) => stockSelectedGroupIds.includes(getStockGroupSelectionId(group)))
                        }
                        onChange={(event) => {
                          const visibleIds = filteredStockGroups.map((group) => getStockGroupSelectionId(group));
                          setStockSelectedGroupIds((prev) => (
                            event.target.checked
                              ? [...new Set([...prev, ...visibleIds])]
                              : prev.filter((id) => !visibleIds.includes(id))
                          ));
                        }}
                      />
                      <Typography sx={{ fontWeight: 850 }}>Selecionar todos</Typography>
                    </Stack>
                  </Stack>
                </Stack>
              ) : null}

              {stockInnerTab === 0 ? (
                selectedMerchantId && stockCategories.length ? (
                  renderMerchantRootBlock(
                    <Stack spacing={3}>
                      {stockCategories.map((stockCategory) => renderStockCategoryBlock(stockCategory))}
                    </Stack>
                  )
                ) : selectedMerchantId ? (
                  renderMerchantRootBlock(
                    <Stack spacing={1.5} alignItems="center">
                      <RestaurantMenuRoundedIcon color="primary" />
                      <Typography variant="h6" color="text.secondary">
                        {stockSearchTerm ? 'Nenhum produto encontrado' : 'Nenhum produto importado'}
                      </Typography>
                      <Typography color="text.secondary">
                        Use o icone de atualizacao para carregar categorias, produtos e complementos diretamente do iFood.
                      </Typography>
                    </Stack>,
                    { empty: true }
                  )
                ) : (
                  <Alert severity="info">Selecione uma loja iFood para visualizar o cardápio.</Alert>
                )
              ) : null}

              {stockInnerTab === 1 ? (
                allStockRows.length ? (
                  allStockProductRows.length ? (
                    <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #f0f0f0', maxWidth: '100%' }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: stockProductGridColumns,
                          gap: 2,
                          alignItems: 'center',
                          px: 2,
                          py: 1.5,
                          minHeight: 68,
                          bgcolor: '#f4f4f5',
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Checkbox
                            checked={allStockProductRows.length > 0 && allStockProductRows.every((entry) => stockSelectedEntryIds.includes(`${entry.entryType}:${entry.id}`))}
                            indeterminate={
                              allStockProductRows.some((entry) => stockSelectedEntryIds.includes(`${entry.entryType}:${entry.id}`))
                              && !allStockProductRows.every((entry) => stockSelectedEntryIds.includes(`${entry.entryType}:${entry.id}`))
                            }
                            onChange={(event) => {
                              const visibleIds = allStockProductRows.map((entry) => `${entry.entryType}:${entry.id}`);
                              setStockSelectedEntryIds((prev) => (
                                event.target.checked
                                  ? [...new Set([...prev, ...visibleIds])]
                                  : prev.filter((id) => !visibleIds.includes(id))
                              ));
                            }}
                          />
                          <Typography sx={{ fontWeight: 900 }}>Produtos</Typography>
                        </Stack>
                        <Typography sx={{ fontWeight: 900 }}>Classificacao</Typography>
                        <Typography sx={{ fontWeight: 900 }}>Disponivel em</Typography>
                        <Box />
                      </Box>
                      {allStockProductRows.map((entry) => renderProductListRow(entry))}
                    </Paper>
                  ) : (
                    <Alert severity="info">Nenhum produto encontrado com os filtros atuais.</Alert>
                  )
                ) : (
                  <Alert severity="info">Nenhum item principal importado do iFood para esta visao.</Alert>
                )
              ) : null}

              {stockInnerTab === 2 ? (
                stockGroups.length ? (
                  filteredStockGroups.length ? (
                    <Grid container spacing={2}>
                      {filteredStockGroups.map((group) => renderOptionGroupCard(group))}
                    </Grid>
                  ) : (
                    <Alert severity="info">Nenhum grupo de complementos encontrado.</Alert>
                  )
                ) : (
                  <Alert severity="info">Nenhum grupo de complementos importado do iFood para esta visao.</Alert>
                )
              ) : null}

              {stockInnerTab === 3 ? (
                selectedMerchantId && stockCategories.length ? (
                  renderMerchantRootBlock(
                    <Stack spacing={3}>
                      {stockCategories.map((stockCategory) => renderPdvCategoryBlock(stockCategory))}
                    </Stack>
                  )
                ) : selectedMerchantId ? (
                  renderMerchantRootBlock(
                    <Stack spacing={1.5} alignItems="center">
                      <RestaurantMenuRoundedIcon color="primary" />
                      <Typography variant="h6" color="text.secondary">
                        {stockSearchTerm ? 'Nenhum produto encontrado' : 'Nenhum produto importado'}
                      </Typography>
                      <Typography color="text.secondary">
                        Use o icone de atualizacao para carregar categorias, produtos e codigos PDV diretamente do iFood.
                      </Typography>
                    </Stack>,
                    { empty: true }
                  )
                ) : (
                  <Alert severity="info">Selecione uma loja iFood para visualizar os códigos PDV.</Alert>
                )
              ) : null}

              {stockInnerTab === 4 ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={7}>
                    <Stack spacing={2}>
                      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 900 }}>
                              Atualizar cardápio pelo iFood
                            </Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                              Busca catálogos, categorias, produtos, grupos e complementos diretamente no iFood e substitui os dados importados desta aba para ficarem iguais ao retorno atual da API Catalog.
                            </Typography>
                          </Box>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<RestaurantMenuRoundedIcon />}
                              onClick={syncStockCatalogFromIfood}
                              disabled={merchantActionDisabled}
                              sx={{ minHeight: 52, borderRadius: 2, px: 3, fontWeight: 900 }}
                            >
                              Atualizar cardápio
                            </Button>
                            <Button
                              variant="outlined"
                              color="inherit"
                              startIcon={<DeleteOutlineRoundedIcon />}
                              onClick={clearImportedStockProducts}
                              disabled={Boolean(busyAction) || !selectedMerchantId || !toArray(visualCatalog.items).length}
                              sx={{ minHeight: 52, borderRadius: 2, px: 3, fontWeight: 900 }}
                            >
                              Apagar produtos importados
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>

                      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: '#f7f7f8' }}>
                        <Grid container spacing={2}>
                          {[
                            ['Categorias', visualCategories.length],
                            ['Produtos', toArray(visualCatalog.items).length],
                            ['Grupos', stockSharedGroups.length],
                            ['Complementos', toArray(visualCatalog.options).length],
                          ].map(([label, value]) => (
                            <Grid item xs={6} md={3} key={label}>
                              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #eeeeef' }}>
                                <Typography color="text.secondary" sx={{ fontSize: 13, fontWeight: 800 }}>
                                  {label}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 950 }}>
                                  {value}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} lg={5}>
                    <Stack spacing={2}>
                      <Alert severity="info">
                        Esta configuração atualiza somente o workspace visual temporário da aba Catalog. Ela não grava produtos no estoque operacional do Brutos e não apaga nada no iFood.
                      </Alert>
                      <ActionStatusPanel result={lastResult} />
                    </Stack>
                  </Grid>
                </Grid>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Stack>

      <Menu
        anchorEl={stockMenuAnchor}
        open={Boolean(stockMenuAnchor)}
        onClose={closeStockCategoryMenu}
        PaperProps={{ sx: { borderRadius: 1, mt: 1, minWidth: 260, boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)' } }}
      >
        <MenuItem
          onClick={() => {
            const nextCategory = stockMenuCategory;
            closeStockCategoryMenu();
            if (nextCategory) openEditCategoryDialog(nextCategory);
          }}
        >
          <ListItemIcon><EditRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Editar categoria" />
        </MenuItem>
        <MenuItem onClick={() => duplicateStockCategory()}>
          <ListItemIcon><ContentCopyRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Duplicar categoria" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => removeStockCategory()} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Remover categoria" />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={stockProductMenuAnchor}
        open={Boolean(stockProductMenuAnchor)}
        onClose={closeStockProductMenu}
        PaperProps={{ sx: { borderRadius: 1, mt: 1, minWidth: 240, boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)' } }}
      >
        <MenuItem
          onClick={() => {
            const product = stockMenuProduct;
            closeStockProductMenu();
            if (product) openStockProductDialog(product);
          }}
        >
          <ListItemIcon><EditRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Editar produto" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const product = stockMenuProduct;
            closeStockProductMenu();
            if (product) refreshStockProductDetails(product);
          }}
        >
          <ListItemIcon><RefreshRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Atualizar dados" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const product = stockMenuProduct;
            closeStockProductMenu();
            if (product) removeStockEntryFromBrutos(product);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Remover" />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={stockGroupMenuAnchor}
        open={Boolean(stockGroupMenuAnchor)}
        onClose={closeStockGroupMenu}
        PaperProps={{ sx: { borderRadius: 1, mt: 1, minWidth: 260, boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)' } }}
      >
        <MenuItem
          onClick={() => {
            const group = stockMenuGroup;
            closeStockGroupMenu();
            if (group) editStockGroup(group);
          }}
        >
          <ListItemIcon><EditRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Editar grupo" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const group = stockMenuGroup;
            closeStockGroupMenu();
            if (group) setGroupEditor({ open: true, groupId: getVisualOptionGroupKey(group), tab: 1, search: '', linkProductId: '' });
          }}
        >
          <ListItemIcon><LinkRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Produtos vinculados" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const group = stockMenuGroup;
            closeStockGroupMenu();
            if (group) toggleStockGroupStatus(group);
          }}
        >
          <ListItemIcon>
            {normalizeCatalogStatus(stockMenuGroup?.status) === 'UNAVAILABLE'
              ? <PlayArrowRoundedIcon fontSize="small" />
              : <PauseRoundedIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary={normalizeCatalogStatus(stockMenuGroup?.status) === 'UNAVAILABLE' ? 'Ativar grupo' : 'Pausar grupo'} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const group = stockMenuGroup;
            closeStockGroupMenu();
            if (group) removeStockGroup(group);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Remover grupo" />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={stockOptionMenuAnchor}
        open={Boolean(stockOptionMenuAnchor)}
        onClose={closeStockOptionMenu}
        PaperProps={{ sx: { borderRadius: 1, mt: 1, minWidth: 260, boxShadow: '0 16px 48px rgba(15, 23, 42, 0.16)' } }}
      >
        <MenuItem onClick={() => openComplementEditDialog(stockMenuOption)}>
          <ListItemIcon><EditRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Editar complemento" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const option = stockMenuOption;
            closeStockOptionMenu();
            if (option) updateStockOptionStatus(option);
          }}
        >
          <ListItemIcon>
            {normalizeCatalogStatus(stockMenuOption?.status) === 'UNAVAILABLE'
              ? <PlayArrowRoundedIcon fontSize="small" />
              : <PauseRoundedIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary={normalizeCatalogStatus(stockMenuOption?.status) === 'UNAVAILABLE' ? 'Ativar complemento' : 'Pausar complemento'} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const option = stockMenuOption;
            closeStockOptionMenu();
            if (option) removeGroupComplement(option);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Remover complemento" />
        </MenuItem>
      </Menu>

      <Dialog
        open={productPromotionDialog.open}
        onClose={closeProductPromotionDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 0 },
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: 440 },
            height: { xs: 'auto', sm: 'calc(100% - 32px)' },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 32px)' },
            position: { sm: 'fixed' },
            right: { sm: 16 },
            top: { sm: 16 },
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ px: 2.25, pt: 2, pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Aplicar preço promocional
            </Typography>
            <IconButton
              aria-label="Fechar"
              title="Fechar"
              onClick={closeProductPromotionDialog}
              sx={{ width: 40, height: 40, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 2.25, pt: 1, pb: 3 }}>
          <Stack spacing={3}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, bgcolor: '#f4f4f5' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                {renderCatalogImageBox(promotionProduct, 64)}
                <Typography sx={{ fontWeight: 900 }} noWrap>
                  {promotionProduct.name || 'Produto'}
                </Typography>
              </Stack>
              <TextField
                value={formatMoneyInputValue(promotionBasePrice)}
                disabled
                fullWidth
                size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Paper>

            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1.5 }}>
                Calcular e aplicar:
              </Typography>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <TextField
                  value={productPromotionDialog.promotionalPrice}
                  onChange={(event) => updateProductPromotionDialog('promotionalPrice', event.target.value)}
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  sx={{ flex: 1 }}
                />
                <SwapVertRoundedIcon sx={{ transform: 'rotate(90deg)', color: '#4b5563' }} />
                <TextField
                  value={productPromotionDialog.discountPercent}
                  onChange={(event) => updateProductPromotionDialog('discountPercent', event.target.value)}
                  size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.25,
            py: 2,
            mt: 'auto',
            borderTop: '1px solid #eeeeef',
            flexWrap: 'wrap',
            gap: 1,
            '& .MuiButton-root': { flex: { xs: '1 1 100%', sm: '0 0 auto' } },
          }}
        >
          <Button
            color="inherit"
            variant="contained"
            disableElevation
            onClick={removeProductPromotion}
            disabled={Boolean(busyAction) || !promotionHasActiveDiscount}
            sx={{ borderRadius: 2, bgcolor: '#e5e7eb', color: '#6b7280', fontWeight: 850, '&:hover': { bgcolor: '#d1d5db' } }}
          >
            Remover preço promocional
          </Button>
          <Button
            variant="contained"
            color="inherit"
            disableElevation
            onClick={applyProductPromotion}
            disabled={promotionApplyDisabled}
            sx={{ borderRadius: 2, bgcolor: '#111', color: '#fff', fontWeight: 850, '&:hover': { bgcolor: '#222' } }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={categoryOrderDialog.open}
        onClose={closeCategoryOrderDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ px: 2, pt: 2, pb: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h5" sx={{ fontWeight: 850 }}>
              Reordenar cardapio
            </Typography>
            <IconButton
              aria-label="Fechar"
              title="Fechar"
              onClick={closeCategoryOrderDialog}
              sx={{ width: 48, height: 48, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 2, pb: 3 }}>
          <Typography color="text.secondary" sx={{ fontSize: 17, lineHeight: 1.45, maxWidth: 780 }}>
            Pressione e arraste no canto direito ou clique no numero a esquerda para reordenar. Clique em salvar para enviar a nova ordem das categorias ao iFood.
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mt: 5, mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Categorias
            </Typography>
            <Tooltip title="Restaurar ordem atual">
              <IconButton
                aria-label="Restaurar ordem atual"
                onClick={resetCategoryOrderDialog}
                sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: '#e5e7eb', '&:hover': { bgcolor: '#d1d5db' } }}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack spacing={1.5}>
            {categoryOrderItems.map((categoryItem, index) => {
              const productCount = Number(categoryProductCounts.get(categoryItem.id) || categoryItem.itemCount || 0);
              const isDragging = categoryOrderDialog.draggingId === categoryItem.id;
              return (
                <Paper
                  key={categoryItem.id}
                  elevation={0}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    setCategoryOrderDialog((prev) => ({ ...prev, draggingId: categoryItem.id }));
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    moveCategoryOrder(categoryOrderDialog.draggingId, categoryItem.id);
                  }}
                  onDragEnd={() => setCategoryOrderDialog((prev) => ({ ...prev, draggingId: '' }))}
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 2.5,
                    borderRadius: 3,
                    bgcolor: isDragging ? '#e5e7eb' : '#f4f4f5',
                    opacity: isDragging ? 0.72 : 1,
                    cursor: 'grab',
                    transition: 'background-color 160ms ease, opacity 160ms ease',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => promptCategoryOrderPosition(categoryItem.id, index)}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#fff',
                        color: '#374151',
                        font: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Stack direction="row" spacing={1.25} alignItems="baseline" sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 850 }} noWrap>
                        {categoryItem.name || 'Sem categoria'}
                      </Typography>
                      <Typography color="text.secondary" noWrap>
                        ({getStockPlural(productCount, 'item', 'itens')})
                      </Typography>
                    </Stack>
                    <DragIndicatorRoundedIcon sx={{ color: '#6b7280' }} />
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeCategoryOrderDialog} color="inherit">
            Cancelar
          </Button>
          <Button variant="contained" onClick={saveCategoryOrder} disabled={Boolean(busyAction)}>
            Salvar ordem no iFood
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={groupOrderDialog.open}
        onClose={closeGroupOrderDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ px: 2, pt: 2, pb: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h5" sx={{ fontWeight: 850 }}>
              Reordenar grupos de complementos
            </Typography>
            <IconButton
              aria-label="Fechar"
              title="Fechar"
              onClick={closeGroupOrderDialog}
              sx={{ width: 48, height: 48, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 2, pb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Grupos
            </Typography>
            <Tooltip title="Restaurar ordem atual">
              <IconButton
                aria-label="Restaurar ordem atual dos grupos"
                onClick={resetGroupOrderDialog}
                sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: '#e5e7eb', '&:hover': { bgcolor: '#d1d5db' } }}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack spacing={1.5}>
            {groupOrderItems.map((groupItem, index) => {
              const groupKey = getVisualOptionGroupKey(groupItem);
              const optionCount = allStockComplementEntries.filter((option) => (
                option.itemId === groupItem.itemId && option.groupId === groupItem.id
              )).length;
              const isDragging = groupOrderDialog.draggingId === groupKey;
              return (
                <Paper
                  key={groupKey}
                  elevation={0}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    setGroupOrderDialog((prev) => ({ ...prev, draggingId: groupKey }));
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    moveGroupOrder(groupOrderDialog.draggingId, groupKey);
                  }}
                  onDragEnd={() => setGroupOrderDialog((prev) => ({ ...prev, draggingId: '' }))}
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 2.5,
                    borderRadius: 3,
                    bgcolor: isDragging ? '#e5e7eb' : '#f4f4f5',
                    opacity: isDragging ? 0.72 : 1,
                    cursor: 'grab',
                    transition: 'background-color 160ms ease, opacity 160ms ease',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => promptGroupOrderPosition(groupKey, index)}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#fff',
                        color: '#374151',
                        font: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Stack direction="row" spacing={1.25} alignItems="baseline" sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 850 }} noWrap>
                        {groupItem.name || 'Grupo sem nome'}
                      </Typography>
                      <Typography color="text.secondary" noWrap>
                        ({getStockPlural(optionCount, 'complemento', 'complementos')})
                      </Typography>
                    </Stack>
                    <DragIndicatorRoundedIcon sx={{ color: '#6b7280' }} />
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeGroupOrderDialog} color="inherit">
            Cancelar
          </Button>
          <Button variant="contained" onClick={saveGroupOrder} disabled={Boolean(busyAction)}>
            Salvar ordem no iFood
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={groupEditor.open}
        onClose={closeGroupEditor}
        fullScreen
        PaperProps={{ sx: { bgcolor: '#fff' } }}
      >
        <DialogTitle sx={{ px: { xs: 2, md: 8 }, pt: 4, pb: 0, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Typography color="text.secondary" sx={{ fontWeight: 900 }}>Grupos de compl...</Typography>
                <Typography color="text.secondary" sx={{ fontWeight: 900 }}>›</Typography>
                <Typography sx={{ fontWeight: 900 }}>Edicao de grupo</Typography>
              </Stack>
              <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: 0, overflowWrap: 'anywhere' }}>
                {editingGroup?.name || 'Grupo de complementos'}
              </Typography>
              <Tabs
                value={groupEditor.tab}
                onChange={(_, value) => setGroupEditor((prev) => ({ ...prev, tab: value }))}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ ...catalogScrollableTabsSx, mt: 1.5, minHeight: 40 }}
              >
                <Tab label="Complementos" sx={{ textTransform: 'none', fontSize: 18, minHeight: 40 }} />
                <Tab label="Produtos vinculados" sx={{ textTransform: 'none', fontSize: 18, minHeight: 40 }} />
              </Tabs>
            </Box>
            {editingGroup ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={normalizeCatalogStatus(editingGroup.status) === 'UNAVAILABLE' ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
                onClick={() => toggleStockGroupStatus(editingGroup)}
                sx={{ borderRadius: 2, px: 3, minHeight: 52, fontWeight: 900 }}
              >
                {normalizeCatalogStatus(editingGroup.status) === 'UNAVAILABLE' ? 'Ativar grupo' : 'Pausar grupo'}
              </Button>
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 8 }, py: 4, minWidth: 0, overflowX: 'hidden' }}>
          {editingGroup ? (
            groupEditor.tab === 0 ? (
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 950 }}>Complementos deste grupo</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      Edite nome, foto, preco e codigo PDV dos complementos sincronizados com o iFood.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<AddIcon />}
                    onClick={() => setShowNewComplementForm((prev) => !prev)}
                    sx={{ minHeight: 54, borderRadius: 2, px: 3, fontWeight: 900 }}
                  >
                    Complemento
                  </Button>
                </Stack>
                <TextField
                  placeholder="Buscar complemento"
                  value={groupEditor.search}
                  onChange={(event) => setGroupEditor((prev) => ({ ...prev, search: event.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="error" />
                      </InputAdornment>
                    ),
                    sx: { minHeight: 58, borderRadius: 2 },
                  }}
                />
                {showNewComplementForm ? renderNewGroupComplementForm() : null}
                <Box>
                  <Box
                    sx={{
                      display: { xs: 'none', lg: 'grid' },
                      gridTemplateColumns: '110px minmax(260px, 1.2fr) minmax(180px, .7fr) minmax(160px, .55fr) minmax(160px, .6fr) 112px',
                      gap: 2,
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: '1px solid #eeeeef',
                    }}
                  >
                    <Typography sx={{ fontWeight: 900 }}>Imagem</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Produto</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Canal de venda</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Preco</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Codigo PDV</Typography>
                    <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>Acoes</Typography>
                  </Box>
                  {editingGroupOptions.length ? (
                    editingGroupOptions.map((option) => renderGroupEditorOptionRow(option))
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>Nenhum complemento encontrado neste grupo.</Alert>
                  )}
                </Box>
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 950 }}>
                      Produtos ({editingGroupLinkedProducts.length})
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      Este grupo de complementos esta vinculado aos produtos abaixo no cardapio do iFood.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: 0, width: { xs: '100%', md: 'min(420px, 100%)' } }}>
                    <TextField
                      value={groupEditor.linkProductId}
                      onChange={(event) => setGroupEditor((prev) => ({ ...prev, linkProductId: event.target.value }))}
                      size="small"
                      select
                      SelectProps={{ native: true }}
                      fullWidth
                      InputProps={{ sx: { minHeight: 54, borderRadius: 2 } }}
                    >
                      <option value="">Selecionar produto</option>
                      {editingGroupAvailableProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} {product.externalCode ? `- ${product.externalCode}` : ''}
                        </option>
                      ))}
                    </TextField>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<AddIcon />}
                      onClick={addGroupToExistingProduct}
                      sx={{ minHeight: 54, borderRadius: 2, px: 3, fontWeight: 900, whiteSpace: 'nowrap' }}
                    >
                      Produto
                    </Button>
                  </Stack>
                </Stack>
                <Box>
                  <Box
                    sx={{
                      display: { xs: 'none', lg: 'grid' },
                      gridTemplateColumns: '120px minmax(260px, 1fr) minmax(220px, .7fr) 150px 150px 84px',
                      gap: 2,
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: '1px solid #eeeeef',
                    }}
                  >
                    <Typography sx={{ fontWeight: 900 }}>Imagem</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Produto</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Obrigatoriedade do grupo</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Qtd. minima</Typography>
                    <Typography sx={{ fontWeight: 900 }}>Qtd. maxima</Typography>
                    <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>Acoes</Typography>
                  </Box>
                  {editingGroupLinkedProducts.length ? (
                    editingGroupLinkedProducts.map((entry) => renderGroupLinkedProductRow(entry))
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>Nenhum produto vinculado a este grupo.</Alert>
                  )}
                </Box>
              </Stack>
            )
          ) : (
            <Alert severity="warning">Grupo de complementos nao encontrado na importacao atual.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: '#fff', borderTop: '1px solid #eeeeef', px: { xs: 2, md: 8 }, py: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" color="error" onClick={closeGroupEditor} sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140, minHeight: 48, borderRadius: 2, fontWeight: 900 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={saveGroupComplementDrafts}
            disabled={!editingGroup || groupEditor.tab !== 0 || Boolean(busyAction)}
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 180, minHeight: 48, borderRadius: 2, fontWeight: 900 }}
          >
            Salvar alteracoes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryDialog.open} onClose={closeCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {categoryDialog.mode === 'edit' ? 'Editar categoria' : 'Nova categoria'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Field
              label="Nome da categoria"
              value={categoryForm.name}
              onChange={(value) => setCategoryForm((prev) => ({ ...prev, name: value }))}
            />
            <TextField
              label="Status"
              value={categoryForm.status}
              onChange={(event) => setCategoryForm((prev) => ({ ...prev, status: event.target.value }))}
              fullWidth
              size="small"
              select
              SelectProps={{ native: true }}
            >
              <option value="AVAILABLE">Disponivel</option>
              <option value="UNAVAILABLE">Indisponivel</option>
            </TextField>
            <TextField
              label="Template"
              value={categoryForm.template}
              onChange={(event) => setCategoryForm((prev) => ({ ...prev, template: event.target.value }))}
              fullWidth
              size="small"
              select
              SelectProps={{ native: true }}
            >
              <option value="DEFAULT">Padrao</option>
              <option value="PIZZA">Pizza</option>
            </TextField>
            <Field
              label="Ordem"
              type="number"
              value={categoryForm.sequence}
              onChange={(value) => setCategoryForm((prev) => ({ ...prev, sequence: value }))}
            />
            <Alert severity="info">
              Ao salvar, a categoria e enviada diretamente ao iFood para a loja selecionada.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCategoryDialog}>Cancelar</Button>
          <Button variant="contained" onClick={saveCategoryFromStock} disabled={Boolean(busyAction)}>
            Salvar no iFood
          </Button>
        </DialogActions>
      </Dialog>

      {renderProductTypeDialog()}
      {renderPizzaCategoryDialog()}
      {renderProductScheduleDialog()}

      <Dialog open={offerDialog.open} onClose={closeOfferDialog} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                Criar produto preparado
              </Typography>
              <Typography color="text.secondary">
                Produto completo do Catalog, com grupos, complementos e categoria.
              </Typography>
            </Box>
            <IconButton onClick={closeOfferDialog} aria-label="Fechar criacao de produto">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: { xs: 560, lg: 680 } }}>
          {renderOfferStepBar()}
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {renderOfferStepContent()}
            </Box>
            {renderOfferPhonePreview()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeOfferDialog} color="inherit">
            Fechar
          </Button>
          {offerStep > 0 ? (
            <Button onClick={() => goToOfferStep(offerStep - 1)} color="error" variant="outlined">
              Voltar
            </Button>
          ) : null}
          {offerStep < offerSteps.length - 1 ? (
            <Button variant="contained" color="error" onClick={advanceOfferStep}>
              Continuar
            </Button>
          ) : (
            <Button variant="contained" color="error" onClick={submitOfferItem} disabled={Boolean(busyAction)}>
              Criar produto
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {renderOfferGroupDialog()}
      {renderProductGroupDialog()}
      {renderComboDialog()}

      <Dialog
        open={Boolean(currentSelectedStockProduct)}
        onClose={closeStockProductDialog}
        fullScreen
        PaperProps={{ sx: { bgcolor: '#fff' } }}
      >
        <DialogTitle sx={{ px: { xs: 2, md: 7 }, pt: 4, pb: 0, minWidth: 0 }}>
          {currentSelectedStockProduct ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ minWidth: 0 }}>
              <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: 0, overflowWrap: 'anywhere' }}>
                  {currentSelectedStockProduct.name || 'Produto'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, minWidth: 0 }}>
                  <Inventory2OutlinedIcon color="action" />
                  <Typography color="text.secondary" sx={{ fontSize: 18, overflowWrap: 'anywhere' }}>
                    Produto preparado
                  </Typography>
                </Stack>
                <Tabs
                  value={productEditorTab}
                  onChange={(_, value) => setProductEditorTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ ...catalogScrollableTabsSx, mt: 3, minHeight: 42 }}
                >
                  <Tab label="Sobre o produto" sx={{ textTransform: 'none', fontSize: 18, minHeight: 42 }} />
                  <Tab label="Grupo de complementos" sx={{ textTransform: 'none', fontSize: 18, minHeight: 42 }} />
                  <Tab label="Disponivel em" sx={{ textTransform: 'none', fontSize: 18, minHeight: 42 }} />
                </Tabs>
              </Box>
              <IconButton
                aria-label="Fechar"
                title="Fechar"
                onClick={closeStockProductDialog}
                sx={{ width: 54, height: 54, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
          ) : null}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 7 }, py: 4, minWidth: 0, overflowX: 'hidden' }}>
          {currentSelectedStockProduct ? (
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={4} alignItems="flex-start" sx={{ minWidth: 0, maxWidth: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {productEditorTab === 0 ? (
                  <Stack spacing={3}>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>Principais informacoes</Typography>
                    <TextField
                      label="Nome do Produto"
                      value={stockProductForm.name}
                      onChange={(event) => setStockProductForm((prev) => ({ ...prev, name: event.target.value.slice(0, 80) }))}
                      fullWidth
                      helperText={`${stockProductForm.name.length}/80`}
                      InputProps={{ sx: { borderRadius: 2, minHeight: 58 } }}
                    />
                    <TextField
                      label="Descricao"
                      value={stockProductForm.description}
                      onChange={(event) => setStockProductForm((prev) => ({ ...prev, description: event.target.value.slice(0, 1000) }))}
                      fullWidth
                      multiline
                      minRows={4}
                      helperText={`${stockProductForm.description.length}/1000`}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                    {renderStockProductImageUploader()}
                    {renderPreparedStockControl({
                      form: stockProductForm,
                      product: currentSelectedStockProduct,
                      onFieldChange: (field, value) => setStockProductForm((prev) => ({ ...prev, [field]: value })),
                    })}
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Button
                        variant={stockProductForm.status === 'AVAILABLE' ? 'contained' : 'outlined'}
                        color="error"
                        startIcon={stockProductForm.status === 'AVAILABLE' ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
                        onClick={() => setStockProductForm((prev) => ({
                          ...prev,
                          status: prev.status === 'AVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE',
                        }))}
                        sx={{ borderRadius: 2, fontWeight: 900, textTransform: 'none' }}
                      >
                        {stockProductForm.status === 'AVAILABLE' ? 'Produto ativo no cardapio' : 'Produto pausado'}
                      </Button>
                      <Typography color="text.secondary">
                        O status sera salvo no iFood junto com as demais alteracoes.
                      </Typography>
                    </Stack>
                    {renderPreparedHighlightPanel({
                      form: stockProductForm,
                      onFieldChange: (field, value) => setStockProductForm((prev) => ({ ...prev, [field]: value })),
                      onToggleValue: (field, value) => setStockProductForm((prev) => {
                        const currentValues = toArray(prev[field]);
                        return {
                          ...prev,
                          [field]: currentValues.includes(value)
                            ? currentValues.filter((item) => item !== value)
                            : [...currentValues, value],
                        };
                      }),
                    })}
                  </Stack>
                ) : null}

                {productEditorTab === 1 ? (
                  <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h4" sx={{ fontWeight: 950, maxWidth: 560, overflowWrap: 'anywhere' }}>
                          Grupos de Complementos: os clientes amam e voce vende mais
                        </Typography>
                        <Button
                          color="error"
                          startIcon={<SwapVertRoundedIcon />}
                          onClick={reorderProductGroups}
                          sx={{ mt: 3, fontWeight: 900, textTransform: 'none' }}
                        >
                          Reordenar
                        </Button>
                      </Box>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<AddIcon />}
                        onClick={openProductGroupDialog}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 60, borderRadius: 2, px: 4, fontSize: 18, fontWeight: 900 }}
                      >
                        Adicionar grupo
                      </Button>
                    </Stack>
                    {selectedStockGroups.length ? (
                      selectedStockGroups.map((group) => renderProductEditorGroupCard(currentSelectedStockProduct, group))
                    ) : selectedStockOptions.length ? (
                      <Paper variant="outlined" sx={{ borderRadius: 2.5, p: { xs: 2, md: 3 }, minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 900 }}>Complementos</Typography>
                        <Stack spacing={1} sx={{ mt: 2 }}>
                          {selectedStockOptions.map((option) => renderProductEditorOptionRow(option))}
                        </Stack>
                      </Paper>
                    ) : (
                      <Alert severity="info">Produto sem grupos de complementos importados da API Catalog.</Alert>
                    )}
                  </Stack>
                ) : null}

                {productEditorTab === 2 ? (
                  <Stack spacing={2.5}>
                    <Paper variant="outlined" sx={{ borderRadius: 2.5, p: { xs: 2, md: 3 }, minWidth: 0 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h4" sx={{ fontWeight: 950, overflowWrap: 'anywhere' }}>
                            Categorias (1)
                          </Typography>
                          <Typography color="text.secondary">Este produto esta nas categorias abaixo.</Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<AddIcon />}
                          onClick={() => showToast('Use o fluxo de criacao para adicionar novas categorias a um produto importado.', 'info')}
                          sx={{ width: { xs: '100%', sm: 'auto' }, borderRadius: 2, minHeight: 52, fontWeight: 900 }}
                        >
                          Adicionar categoria
                        </Button>
                      </Stack>
                      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 720 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Categoria</TableCell>
                              <TableCell>Canal de venda</TableCell>
                              <TableCell>Preco</TableCell>
                              <TableCell>Codigo PDV</TableCell>
                              <TableCell align="right">Acoes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow hover>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography sx={{ fontWeight: 850 }}>
                                    {selectedStockCategory?.name || currentSelectedStockProduct.categoryName || 'Sem categoria'}
                                  </Typography>
                                  <Tooltip title={stockProductForm.availabilityMode === 'scheduled' ? 'Produto com dias e horarios especificos. Clique no icone para editar.' : 'Este produto esta configurado como sempre disponivel. Clique no icone para editar.'}>
                                    <IconButton color="error" size="small" onClick={() => openProductScheduleDialog('edit')}>
                                      <CalendarMonthRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                              <TableCell>Aplicativo iFood</TableCell>
                              <TableCell sx={{ minWidth: 190 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <TextField
                                    value={stockProductForm.price}
                                    onChange={(event) => setStockProductForm((prev) => ({ ...prev, price: event.target.value }))}
                                    size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                  />
                                  <Tooltip title="Aplicar desconto">
                                    <IconButton
                                      aria-label="Aplicar desconto"
                                      onClick={() => openProductPromotionDialog(currentSelectedStockProduct)}
                                      sx={{ border: '1px solid #e5e7eb', color: '#166534' }}
                                    >
                                      <LocalOfferRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ minWidth: 190 }}>
                                <TextField
                                  value={stockProductForm.externalCode}
                                  onChange={(event) => setStockProductForm((prev) => ({ ...prev, externalCode: event.target.value }))}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <IconButton
                                    title={stockProductForm.status === 'AVAILABLE' ? 'Pausar produto' : 'Ativar produto'}
                                    onClick={() => setStockProductForm((prev) => ({
                                      ...prev,
                                      status: prev.status === 'AVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE',
                                    }))}
                                  >
                                    {stockProductForm.status === 'AVAILABLE' ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                                  </IconButton>
                                  <IconButton title="Remover da categoria" onClick={() => showToast('Remocao de categoria deste produto deve ser feita com um novo PUT completo quando for necessario.', 'info')}>
                                    <DeleteOutlineRoundedIcon />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 950, mb: 1 }}>
                        Grupos de complementos ({selectedStockGroups.length})
                      </Typography>
                      {selectedStockGroups.length ? (
                        <Stack spacing={1.5}>
                          {selectedStockGroups.map((group) => renderProductEditorGroupCard(currentSelectedStockProduct, group))}
                        </Stack>
                      ) : (
                        <Paper variant="outlined" sx={{ borderStyle: 'dashed', borderRadius: 2.5, p: { xs: 2, md: 5 }, textAlign: 'center', minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 850 }}>Este produto ainda nao foi adicionado a um grupo de complementos.</Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<AddIcon />}
                            onClick={() => setProductEditorTab(1)}
                            sx={{ mt: 2, borderRadius: 2, fontWeight: 900 }}
                          >
                            Adicionar a um grupo
                          </Button>
                        </Paper>
                      )}
                    </Box>
                  </Stack>
                ) : null}
              </Box>
              {renderProductEditorPreview(currentSelectedStockProduct)}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: '#fff', borderTop: '1px solid #eeeeef', px: { xs: 2, md: 7 }, py: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" color="error" onClick={closeStockProductDialog} sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 150, minHeight: 52, borderRadius: 2, fontWeight: 900 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={saveStockProduct}
            disabled={Boolean(busyAction)}
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 220, minHeight: 52, borderRadius: 2, fontWeight: 900 }}
          >
            Salvar alteracoes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={complementEditDialog.open}
        onClose={closeComplementEditDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ px: { xs: 2, md: 4 }, pt: 4, pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: 0 }}>
              Edicao do complemento
            </Typography>
            <IconButton
              aria-label="Fechar"
              title="Fechar"
              color="error"
              onClick={closeComplementEditDialog}
              sx={{ width: 56, height: 56 }}
            >
              <CloseRoundedIcon sx={{ fontSize: 42 }} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 4 }, py: 2 }}>
          <Stack spacing={4}>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>Nome</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                <TextField
                  value={complementEditForm.name}
                  onChange={(event) => setComplementEditForm((prev) => ({ ...prev, name: event.target.value.slice(0, 80) }))}
                  fullWidth
                  InputProps={{ sx: { minHeight: 68, borderRadius: 2, fontSize: 22 } }}
                />
                <Button
                  component="label"
                  variant="outlined"
                  color="inherit"
                  sx={{
                    width: { xs: '100%', md: 112 },
                    height: 112,
                    borderStyle: 'dashed',
                    borderRadius: 2,
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {complementEditForm.imagePath ? (
                    <Box
                      component="img"
                      src={buildIfoodImageUrl(complementEditForm.imagePath)}
                      alt="Foto do complemento"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <AddPhotoAlternateRoundedIcon color="action" sx={{ fontSize: 42 }} />
                  )}
                  <input hidden type="file" accept="image/*" onChange={(event) => uploadComplementEditImage(event.target.files?.[0])} />
                </Button>
              </Stack>
              <Typography align="right" color="text.secondary" sx={{ mt: 1, fontSize: 20 }}>
                {complementEditForm.name.length}/80
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>Descricao</Typography>
              <TextField
                value={complementEditForm.description}
                onChange={(event) => setComplementEditForm((prev) => ({ ...prev, description: event.target.value }))}
                fullWidth
                multiline
                minRows={8}
                InputProps={{ sx: { borderRadius: 2, fontSize: 20, alignItems: 'flex-start' } }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 4 }, py: 3, borderTop: '1px solid #eeeeef' }}>
          <Button color="error" onClick={closeComplementEditDialog} sx={{ minWidth: 160, minHeight: 54, fontWeight: 900 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={saveComplementEditDialog}
            disabled={Boolean(busyAction) || !cleanText(complementEditForm.name)}
            sx={{ minWidth: 180, minHeight: 54, borderRadius: 2, fontWeight: 900 }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3200} onClose={() => setToast((prev) => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CatalogHomologacao;
